// v0.530.1
import React, { useState, useEffect, useCallback, lazy, Suspense, useMemo, useRef } from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Toolbar from '@mui/material/Toolbar';
import AppBar from '@mui/material/AppBar';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import useMediaQuery from '@mui/material/useMediaQuery';
import useTheme from '@mui/material/styles/useTheme';
import Avatar from '@mui/material/Avatar';
import Tooltip from '@mui/material/Tooltip';
import Snackbar from '@mui/material/Snackbar';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Checkbox from '@mui/material/Checkbox';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import Pagination from '@mui/material/Pagination';
import AddIcon from '@mui/icons-material/Add';
import FavoriteIcon from '@mui/icons-material/Favorite';
import DashboardIcon from '@mui/icons-material/Dashboard';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import HomeIcon from '@mui/icons-material/Home';
import SportsScoreIcon from '@mui/icons-material/SportsScore';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import MovieIcon from '@mui/icons-material/Movie';
import LocalMoviesIcon from '@mui/icons-material/LocalMovies';
import TheatersIcon from '@mui/icons-material/Theaters';
import FolderIcon from '@mui/icons-material/Folder';
import GoogleIcon from '@mui/icons-material/Google';
import ShareIcon from '@mui/icons-material/Share';
import StarIcon from '@mui/icons-material/Star';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { ShareableVBID } from '../types/vbid';
import { useAuth } from '../contexts/AuthContext';
import { collection, query, where, orderBy, getDocs, deleteDoc, doc, updateDoc, addDoc, arrayUnion, arrayRemove, writeBatch, setDoc, serverTimestamp, limit, getDoc, startAfter, DocumentData, DocumentSnapshot } from 'firebase/firestore';
import { getDb, waitForFirebase } from '../services/firebase';
import { getSidebarConfig } from '../pages/ManagePages/components/Sidebar/SidebarConfig';
import { extractYouTubeId } from '../utils/vbidIO'; // 🔧 統一使用 vbidIO.ts 中的 extractYouTubeId 函數
import { calculateVideoStats } from './ManagePages/utils/videoStatsCalculator'; // 🔧 統一統計數據計算
// 🔧 优化：导入工具函数，减少 ManagePages 代码量
import { 
  parseISO8601Duration, 
  getRecentlyAddedCount 
} from '../utils/durationUtils';
import { convertTimestampToNumber } from '../utils/timestampUtils';
import { formatDuration } from '../utils/formatUtils';
// 注意：formatViewCount 和 formatDate 保留在组件外部定义（避免重复导入）

// 安全的 Firestore 操作包裝器
const safeCollection = (collectionName: string) => {
  const db = getDb();
  if (!db) {
    console.warn('⚠️ Firestore 未初始化，跳過 collection 操作:', collectionName);
    return null;
  }
  return collection(db, collectionName);
};

const safeDoc = (collectionName: string, docId: string) => {
  const db = getDb();
  if (!db) {
    console.warn('⚠️ Firestore 未初始化，跳過 doc 操作:', collectionName, docId);
    return null;
  }
  return doc(db, collectionName, docId);
};

const safeWriteBatch = () => {
  const db = getDb();
  if (!db) {
    console.warn('⚠️ Firestore 未初始化，跳過 writeBatch 操作');
    return null;
  }
  return writeBatch(db);
};

// 🔧 修復：添加類型斷言函數來處理 null 檢查
const assertNonNull = function<T>(value: T | null, operation: string): T {
  if (value === null) {
    throw new Error(`Firestore operation failed: ${operation}`);
  }
  return value;
};
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
// import { useGuestId } from '../contexts/GuestIdContext';
import { incrementAttention, getAttentionCount } from '../services/firebase';
import { getVideoStats } from '../services/videoStatsService';
import { updateDocument } from '../services/firestoreService';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { createUser, updateUser } from '../services/userService';
import { createTeam, parseClientsData } from '../services/teamService';
import { auth } from '../services/firebase';
import { API_BASE_URL } from '../services/auth.service';
import SearchIcon from '@mui/icons-material/Search';
import SettingsIcon from '@mui/icons-material/Settings';
import GroupIcon from '@mui/icons-material/Group';
// 🔧 優化：懶加載大型組件（減少初始 bundle 大小）
const ProfileDialog = lazy(() => import('../components/ProfileDialog'));
const TeamDialog = lazy(() => import('../components/TeamDialog'));
const GroupManager = lazy(() => import('../components/GroupManager'));
const GroupSelectorDialog = lazy(() => import('./ManagePages/GroupSelectorDialog'));
const UnifiedShareDialog = lazy(() => import('../components/UnifiedShareDialog'));
const LocalFilesTable = lazy(() => import('../components/LocalFilesTable'));
const CloudDirectoryTable = lazy(() => import('../components/CloudDirectoryTable'));
const EnhancedVideoUpload = lazy(() => import('../components/EnhancedVideoUpload'));
const ProjectManagementTable = lazy(() => import('../components/ProjectManagementTable'));
const ClientArea = lazy(() => import('../components/ClientArea'));
const ClientManagement = lazy(() => import('../components/ClientManagement'));
import { localFileService, LocalFileRecord } from '../services/localFileService';
const GoogleDriveManager = lazy(() => import('../components/GoogleDriveManager'));
const VideoUpload = lazy(() => import('../components/VideoUpload'));

// 🔧 優化：懶加載 ManagePages 子組件
const ImportYTPlaylistContent = lazy(() => import('./ManagePages/ImportYTPlaylistContent'));
const SortButtons = lazy(() => import('./ManagePages/SortButtons'));
const SectionHeader = lazy(() => import('./ManagePages/SectionHeader'));
const LoadingState = lazy(() => import('./ManagePages/LoadingState'));
const EmptyState = lazy(() => import('./ManagePages/EmptyState'));

import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import TvIcon from '@mui/icons-material/Tv';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { isAdminUser } from '../config/adminConfig';
import Slide from '@mui/material/Slide';
import useScrollTrigger from '@mui/material/useScrollTrigger';
import BottomBar from '../components/BottomBar';
import PeopleIcon from '@mui/icons-material/People';
import GroupsIcon from '@mui/icons-material/Groups';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import VideoTable from './ManagePages/VideoTable';
import { InputAdornment } from '@mui/material';
import { useInView } from 'react-intersection-observer';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PublicIcon from '@mui/icons-material/Public';
import NoteIcon from '@mui/icons-material/Note';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import QrCodeIcon from '@mui/icons-material/QrCode';
import { youtubeApi } from '../services/youtubeApi';
import { getUser } from '../services/userService';
import InfiniteScroll from 'react-infinite-scroll-component';
import VideoTagsManager from '../components/VideoTagsManager';
import UnifiedTagDisplay from '../components/UnifiedTagDisplay';
import { extractAllTagsFromVideo } from '../utils/hashtagExtractor';
import { saveDocument } from '../services/firestoreService';

// YouTube 影片資訊快取（組件外部）
// 移除全域快取，改為組件內狀態管理

declare global {
  interface Window {
    VITE_SSR_BASE_URL?: string;
  }
}

// 添加一個錯誤邊界組件
class ErrorBoundary extends React.Component<{children: React.ReactNode}, {hasError: boolean, error: any}> {
  constructor(props: {children: React.ReactNode}) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: any) {
    return { hasError: true, error };
  }

  componentDidCatch(error: any, errorInfo: any) {
    // 記錄錯誤到控制台
    console.error('❌ ManagePages ErrorBoundary 捕獲錯誤:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box sx={{ p: 4, textAlign: 'center', maxWidth: '600px', mx: 'auto' }}>
          <Typography variant="h5" color="error" gutterBottom>
            ⚠️ 頁面載入出現問題
          </Typography>
          <Typography variant="body2" paragraph color="text.secondary">
            {this.state.error?.message || '載入資料時發生錯誤，請稍後再試'}
          </Typography>
          <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button 
              variant="contained" 
              onClick={() => {
                // 重置錯誤狀態並重新渲染
                this.setState({ hasError: false, error: null });
                window.location.reload();
              }}
            >
              重新載入
            </Button>
            <Button 
              variant="outlined" 
              onClick={() => window.location.href = '/app'}
            >
              返回首頁
            </Button>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 3, display: 'block' }}>
            如果問題持續發生，請嘗試清除瀏覽器快取或聯繫技術支援
          </Typography>
        </Box>
      );
    }

    return this.props.children;
  }
}

type YouTubeInfo = {
  title?: string;
  channelTitle?: string;
  viewCount?: string;
  publishedAt?: string;
  likeCount?: string;
  commentCount?: string;
  lastStatsFetchedAt?: string;
  thumbnails?: {
    high?: { url: string };
    medium?: { url: string };
    default?: { url: string };
  };
  description?: string;
  duration?: string;
  tags?: string[];
};
type VideoInfo = {
  title?: string;
  channelTitle?: string;
  viewCount?: string;
  publishedAt?: string;
  duration?: string; // 🔧 修復：添加 duration 屬性
};

type VideoFlag = 'active' | 'archived' | 'deleted' | 'draft' | 'pending';

type VideoItem = ShareableVBID & {
  id: string;
  documentId?: string; // 新增：Firestore 文件 ID
  videoId?: string;
  mode?: string;
  mediaMode?: string;
  flag?: VideoFlag;
  addedDate?: string;
  createdAt?: string; // 新增：創建時間，用於正確排序
  lastOpenDate?: string;
  lastReadAt?: string | any; // 🔧 修復：添加 lastReadAt 屬性（用於社群影片）
  userLastReadAt?: any; // 新增：用戶層級的 lastReadAt
  checkpoints?: any[];
  title?: string;
  shareUrl?: string;
  youtubeInfo?: YouTubeInfo;
  videoInfo?: VideoInfo;
  vbids?: any[];
  readUsers?: string[];
  numberKeys?: any; // 🔧 修復：添加 numberKeys 屬性
  favoriteCount?: number;
  readCount?: number;
  zones?: string[];
  provider?: string;
  modifiedAt?: string | number;
  totalReadCount?: number;
  hiddenFor?: string[];
  groupIds?: string[];
  groupName?: string; // 新增 groupName 屬性
  tags?: string[]; // 新增 tags 欄位
  uploadKey?: string; // 新增 uploadKey
  globalCheckpointCount?: number; // 新增全域重點數
  checkpointCount?: number; // 新增 checkpointCount
  personalCheckpointCount?: number; // 新增個人重點數
  attentionCount?: number; // 新增關注度
  // 🔧 修復 TypeScript 錯誤：添加缺失的屬性
  cloudFile?: {
    id?: string;
    filename?: string;
    originalFilename?: string;
    url?: string;
    thumbnailUrl?: string;
    size?: number;
    contentType?: string;
    uploadTime?: string;
    projectName?: string;
  };
  source?: string; // 視頻來源（youtube, cloud, local 等）
  channelTitle?: string; // YouTube 頻道標題
  publishedAt?: string; // 發布日期
  description?: string; // 視頻描述
  duration?: string; // 視頻時長
  hasSRTCache?: boolean; // 是否有 SRT 字幕緩存
  // 🆕 觀點相關欄位
  viewpointName?: string; // 觀點名稱
  viewpointDescription?: string; // 觀點描述
  viewpointTags?: string[]; // 觀點標籤
  viewpointColor?: string; // 觀點顏色
  isPrimaryViewpoint?: boolean; // 是否為主觀點
  relatedViewpoints?: Array<{ // 關聯觀點
    docId: string;
    viewpointName: string;
    checkpointCount: number;
    createdAt: any;
  }>;
  // MV 模式專用欄位
  playMode?: string;
  mvUrl?: string;
  mvTitle?: string;
  mvDescription?: string;
  viewCount?: number;
  thumbnail?: string;
  creatorName?: string; // 新增：建立者名稱
  creatorEmail?: string; // 新增：建立者郵箱
  // ...other Firestore fields
};

// 獲取用戶顯示名稱的輔助函數
const getUserDisplayName = async (userId: string): Promise<string> => {
  try {
    const { getDb, waitForFirebase } = await import('../services/firebase');
    await waitForFirebase();
    const db = getDb();
    if (!db) return '';

    const { doc, getDoc } = await import('firebase/firestore');
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      const userData = userDoc.data();
      // 優先使用 displayName、name，最後使用 email
      return userData.displayName || userData.name || userData.email?.split('@')[0] || '';
    }
  } catch (error) {
    console.warn('獲取用戶顯示名稱失敗:', error);
  }
  return '';
};

// 建立者資訊顯示組件
const CreatorInfoDisplay: React.FC<{ video: VideoItem }> = ({ video }) => {
  const [creatorName, setCreatorName] = React.useState<string>('');
  const { user: authUser } = useAuth(); // 🔧 添加 useAuth 以檢查當前用戶狀態

  React.useEffect(() => {
    const fetchCreatorName = async () => {
      // 從文檔ID中提取建立者ID
      const documentId = video.id || video.documentId || '';
      let fullCreatorId = '';
      
      // 檢查是否為新的分層設計格式：{userId}_{fileId}_{version}
      const creatorMatch = documentId.match(/^([^_]+)_/);
      if (creatorMatch) {
        fullCreatorId = creatorMatch[1];
      } else {
        // 如果沒有匹配到格式，嘗試從 userId 欄位獲取
        fullCreatorId = video.userId || '';
      }

      // 優先使用 nickname、email，最後才使用系統ID
      if (video.creatorName) {
        // 如果有創建者名稱，截取適當長度
        const name = video.creatorName;
        if (/[\u4e00-\u9fff]/.test(name)) {
          // 中文字符，最多5個字
          setCreatorName(name.length > 5 ? name.substring(0, 5) + '...' : name);
        } else {
          // 英文字符，最多10個字母
          setCreatorName(name.length > 10 ? name.substring(0, 10) + '...' : name);
        }
      } else if (video.creatorEmail) {
        // 如果有創建者郵箱，保持完整郵箱格式
        setCreatorName(video.creatorEmail);
      } else if (fullCreatorId) {
        // 🔧 優化：Guest 用戶（無論是文檔 ID 中的還是當前用戶）
        if (fullCreatorId === 'guest' || fullCreatorId.startsWith('guest_')) {
          setCreatorName('訪客');
          return;
        }
        
        // 🔧 關鍵修復：檢查當前用戶是否為訪客模式
        // 訪客模式下，無法查詢 Firestore users 集合（權限問題）
        // 改為使用 video.creatorEmail 或顯示「未知用戶」
        const currentUserIsGuest = !authUser || !authUser.email;
        
        if (currentUserIsGuest) {
          // 訪客模式下，不嘗試查詢 Firestore，直接顯示替代信息
          if (fullCreatorId.includes('@')) {
            // 如果是 email 格式，顯示 @ 前面的部分
            setCreatorName(fullCreatorId.split('@')[0]);
          } else {
            // 否則顯示「建立者」+ 前3碼
            setCreatorName(`用戶${fullCreatorId.substring(0, 3)}...`);
          }
          return;
        }
        
        // 登入用戶模式：嘗試從 users 集合獲取用戶顯示名稱
        const displayName = await getUserDisplayName(fullCreatorId);
        if (displayName) {
          if (/[\u4e00-\u9fff]/.test(displayName)) {
            // 中文字符，最多5個字
            setCreatorName(displayName.length > 5 ? displayName.substring(0, 5) + '...' : displayName);
          } else {
            // 英文字符，最多10個字母
            setCreatorName(displayName.length > 10 ? displayName.substring(0, 10) + '...' : displayName);
          }
        } else {
          // Firestore 查詢失敗或用戶不存在
          if (fullCreatorId.includes('@')) {
            setCreatorName(fullCreatorId.split('@')[0]);
          } else {
            setCreatorName(`用戶${fullCreatorId.substring(0, 3)}...`);
          }
        }
      } else {
        setCreatorName('Unknown');
      }
    };

    fetchCreatorName();
  }, [video.id, video.documentId, video.userId, video.creatorName, video.creatorEmail]);

  return (
    <span style={{ fontSize: 12, color: '#888', textAlign: 'left', display: 'block' }}>
      收藏家：{creatorName}
    </span>
  );
};

// 🔧 已移除重複的 extractYouTubeId 函數定義，統一使用 vbidIO.ts 中的版本

// 格式化觀看次數
function formatViewCount(count?: string) {
  if (!count) return '-';
  const num = parseInt(count, 10);
  if (isNaN(num)) return '-';
  if (num >= 1_000_000) return (num / 1_000_000).toFixed(1) + 'M';
  if (num >= 1_000) return (num / 1_000).toFixed(1) + 'K';
  return num.toString();
}
// 格式化日期
function formatDate(dateStr?: string) {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '-';
  // 使用 UTC 日期，避免時區轉換問題
  return `${d.getUTCFullYear()}/${String(d.getUTCMonth() + 1).padStart(2, '0')}/${String(d.getUTCDate()).padStart(2, '0')}`;
}

const ZONE_OPTIONS = [
  { label: '珍藏', value: 'person' },
  { label: '群組', value: 'team' },
  { label: '公開', value: 'community' },
  //{ label: '交片區', value: 'delivery' },
  //{ label: '作品區', value: 'portfolio' },
];

const getUserType = (authUser: any): 'guest' | 'user' => {
  if (!authUser) return 'guest';
  // 可擴充付費判斷
  return 'user';
};

//側邊藍區主功能設定 - 使用新的配置
const SIDEBAR_CONFIG = (userType: 'guest' | 'user') => getSidebarConfig(userType);


// YouTube 影片資訊快取（組件外部）
const YOUTUBE_INFO_CACHE_KEY = 'youtubeInfoCache';
let youtubeInfoCache: { [videoId: string]: any } = {};
// 從 localStorage 載入快取
try {
  const cacheStr = localStorage.getItem(YOUTUBE_INFO_CACHE_KEY);
  if (cacheStr) youtubeInfoCache = JSON.parse(cacheStr);
} catch (e) { youtubeInfoCache = {}; }
function saveYoutubeInfoCache() {
  try { localStorage.setItem(YOUTUBE_INFO_CACHE_KEY, JSON.stringify(youtubeInfoCache)); } catch (e) {}
}

// 動態引入大型組件
const RegisterDialog = lazy(() => import('../components/RegisterDialog'));
const AuthDialog = lazy(() => import('../components/AuthDialog'));
const VideoPlayer = lazy(() => import('../components/VideoPlayer'));
const WelcomeDialog = lazy(() => import('../components/WelcomeDialog'));

// HideOnScroll 元件
function HideOnScroll(props: { children: React.ReactElement }) {
  const trigger = useScrollTrigger();
  const isMobile = useMediaQuery('(max-width: 1024px)');
  const isLandscape = typeof window !== 'undefined' ? window.innerHeight < 700 : false;
  
  // 在 ManagePages 下，非桌面版直式時不要隱藏 AppBar
  if (isMobile && !isLandscape) {
    return props.children;
  }
  
  return (
    <Slide appear={false} direction="down" in={!trigger}>
      {props.children}
    </Slide>
  );
}

// 🔧 優化：懶加載組件的 fallback
const ComponentFallback: React.FC = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
    <CircularProgress size={32} />
  </Box>
);

interface ManagePagesProps {
  pageState?: any;
  onStateChange?: (newState: any) => void;
  onNavigateToMain?: (data: any) => void;
  onPageChange?: (page: string) => void;
  isEmbedded?: boolean;
}

const ManagePages: React.FC<ManagePagesProps> = ({ 
  pageState, 
  onStateChange, 
  onNavigateToMain,
  onPageChange,
  isEmbedded = false 
}) => {
  // ============ ISO 8601 Duration 轉換函數 ============
  // 🔧 parseISO8601Duration 已從 '../utils/durationUtils' 導入

  // 🔧 修復：減少過度日誌，只在開發模式且需要時顯示
  // // 同步日志已移除
  
  // 移除調試日誌以防止無限渲染
  
  // 1. 將所有 hooks（useState, useEffect, useCallback, useMemo, useRef）移到 ManagePages function 頂層
  const loadingMoreTriggerRef = useRef(false);
  const [videos, setVideos] = useState<VideoItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  // 使用更明確的斷點設定，確保響應式設計正確
  // 當視窗寬度大於1024px時使用桌面版（有Sidebar），小於等於1024px時使用非桌面版（無Sidebar）
  const isMobile = useMediaQuery('(max-width: 1024px)');
  // 統一使用 window.innerHeight 判斷橫式/直式，與 MainPage 保持一致
  const isLandscape = typeof window !== 'undefined' ? window.innerHeight < 700 : false;
  
  // 手機橫式模式檢測 - 暫時移除有問題的 useMediaQuery
  // const isMobileLandscape = useMediaQuery('(max-width: 1024px)') && useMediaQuery('(max-height: 500px)');
  
  // 調試：監控狀態變化 - 暫時停用
  // useEffect(() => {
  //   console.log(`🔍 螢幕狀態變化:`, {
  //     isMobile,
  //     isLandscape, 
  //     isMobileLandscape,
  //     width: window.innerWidth,
  //     height: window.innerHeight
  //   });
  // }, [isMobile, isLandscape, isMobileLandscape]);
  

  

  
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [expandedMain, setExpandedMain] = useState<string[]>(['home', 'personalCenter']); // 預設展開 home 和個人中心
  const { logout, user: authUser, loading: authLoading } = useAuth();
  
  // 追蹤 authUser 狀態
  // console.log('🔍 ManagePages authUser 狀態:', { 
  //   authUser: !!authUser, 
  //   authUserEmail: authUser?.email, 
  //   authLoading 
  // });
  
  // 監聽 authUser 變化
  useEffect(() => {
    // console.log('🔍 authUser 變化監聽:', { 
    //   authUser: !!authUser, 
    //   authUserEmail: authUser?.email, 
    //   authLoading 
    // });
  }, [authUser, authLoading]);
  
  // 暫時移除 useGuestId，直接在 ManagePages 中處理 isGuest 邏輯
  const [guestId, setGuestId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isGuest, setIsGuest] = useState(true);
  
  // 🔧 修復：根據 authUser 狀態決定是否為 Guest - 避免重複處理
  useEffect(() => {
    // 🔧 關鍵修復：SPA 模式下跳過自動調整，由 pageState 控制
    if (isEmbedded) {
      // 调试日志已移除
      // 但仍然需要設置 isGuest 和 guestId
      const currentIsGuest = !authUser?.email;
      if (currentIsGuest !== isGuest) {
        setIsGuest(currentIsGuest);
        if (currentIsGuest && !guestId) {
          const sessionId = 'guest_' + Math.random().toString(36).substring(2, 15) + '_' + Date.now().toString(36);
          setGuestId(sessionId);
        } else if (!currentIsGuest) {
          setGuestId(null);
        }
      }
      setIsLoading(false);
      return;
    }
    
    // 避免重複處理相同的認證狀態
    const currentAuthEmail = authUser?.email;
    const currentIsGuest = !currentAuthEmail;
    
    // 如果認證狀態沒有變化，直接返回
    if ((currentIsGuest && isGuest) || (!currentIsGuest && !isGuest)) {
      setIsLoading(false);
      return;
    }
    
    if (currentAuthEmail) {
      // 已登入用戶
      setIsGuest(false);
      setGuestId(null);
      
      // 🔧 方案 A：如果用戶登入且當前是社群公開分頁（訪客預設），自動切換到瀏覽記錄
      if (currentSection === 'communityPublic' && !location.state?.currentSection) {
        setCurrentSection('personalHistory');
      }
    } else {
      // Guest 用戶 - 只在需要時生成新的 sessionId
      if (!guestId || guestId === 'guest') {
        const sessionId = 'guest_' + Math.random().toString(36).substring(2, 15) + '_' + Date.now().toString(36);
        setGuestId(sessionId);
      }
      setIsGuest(true);
      
      // 🔧 修復：訪客應該顯示社群公開分頁，而不是 mediaPoint
      // 如果用戶登出且當前是個人中心相關頁面，自動切換到社群公開
      if (['personalHistory', 'myVideos', 'myPersonalFavorite'].includes(currentSection)) {
        setCurrentSection('communityPublic');  // 修復：改為 communityPublic
      }
    }
    
    setIsLoading(false);
  }, [authUser?.email, isGuest, isEmbedded]); // 🔧 添加 isEmbedded 依賴
  // 暫時移除無限循環的 log
  // // 查询日志已移除
  
  const userId = authUser?.uid || guestId || 'guest';

  const markPersonalHistoryDirty = useCallback(() => {
    if (typeof window === 'undefined') return;
    const key = `refreshPersonalHistory_${userId || 'guest'}`;
    sessionStorage.setItem(key, 'true');
  }, [userId]);

  const navigateToMain = useCallback((state?: any) => {
    markPersonalHistoryDirty();
    if (state !== undefined) {
      navigate('/main', { state });
    } else {
      navigate('/main');
    }
  }, [markPersonalHistoryDirty, navigate]);
  
  // 根據 URL 路徑設置初始分頁
  useEffect(() => {
    // 查询日志已移除
    
    if (location.pathname === '/favorite') {
      // 导航日志已移除
      setExpandedMain(['myPoint']); // 展開「我的影片」分頁（對應我的珍藏）
    } else if (location.pathname === '/manage') {
      // 导航日志已移除
      setExpandedMain(['home']); // 預設展開 home
    }
  }, [location.pathname]);

  // 🔧 關鍵修復：監聽 vbidsUpdated 事件，及時更新快取
  useEffect(() => {
    const handleVbidsUpdated = (event: Event) => {
      const customEvent = event as CustomEvent;
      const { videoId, checkpointCount, documentId } = customEvent.detail;
      
      console.log('🔔 ManagePages 收到 vbids 更新通知:', { 
        videoId, 
        checkpointCount,
        documentId 
      });
      
      // 更新對應的影片數據
      const updateVideo = (v: VideoData) => {
        if (v.videoId === videoId || v.id === documentId || v.documentId === documentId) {
          console.log('🔄 更新影片快取:', {
            oldCheckpointCount: v.checkpointCount,
            newCheckpointCount: checkpointCount
          });
          return { 
            ...v, 
            checkpointCount, 
            vbids: [] // 清空 vbids，下次點擊時會重新載入
          };
        }
        return v;
      };
      
      // 更新所有可能包含此影片的 state
      // 🔧 修復：使用 try-catch 防止某些 state 不存在時報錯
      try {
        if (typeof setMyVideos === 'function') {
          setMyVideos(prev => prev.map(updateVideo));
        }
        if (typeof setCommunityPublicVideos === 'function') {
          setCommunityPublicVideos(prev => prev.map(updateVideo));
        }
        if (typeof setPersonalHistoryVideos === 'function') {
          setPersonalHistoryVideos(prev => prev.map(updateVideo));
        }
        if (typeof setTeamVideos === 'function') {
          setTeamVideos(prev => prev.map(updateVideo));
        }
        if (typeof setPersonalFavoriteVideos === 'function') {
          setPersonalFavoriteVideos(prev => prev.map(updateVideo));
        }
        console.log('✅ ManagePages 快取已更新');
      } catch (error) {
        console.warn('⚠️ 更新 ManagePages 快取時發生錯誤（可忽略）:', error);
      }
    };
    
    window.addEventListener('vbidsUpdated', handleVbidsUpdated);
    
    return () => {
      window.removeEventListener('vbidsUpdated', handleVbidsUpdated);
    };
  }, []);

  // 🔧 新增：處理 URL 查詢參數（含專案連結）
  useEffect(() => {
    const section = searchParams.get('section');
    const projectId = searchParams.get('projectId');
    
    // 查询日志已移除
    
    // 如果 URL 中有 projectId，自動切換到客戶專區（優先處理）
    if (projectId) {
      // 导航日志已移除
      setCurrentSection('clientArea');
      setExpandedMain(['home', 'deliveryManagement']);
      return;
    }
    
    if (section) {
      // 导航日志已移除
      
      // 根據 section 參數設置對應的分頁
      switch (section) {
        case 'communityPublic':
          setCurrentSection('communityPublic');
          setExpandedMain(['home', 'community']);
          break;
        case 'teamPoint':
          setCurrentSection('teamPoint');
          setExpandedMain(['home', 'teamPoint']);
          break;
        case 'myVideos':
          setCurrentSection('myVideos');
          setExpandedMain(['home', 'personalCenter', 'myVideos']);
          break;
        case 'personalHistory':
          setCurrentSection('personalHistory');
          setExpandedMain(['home', 'personalCenter', 'personalHistory']);
          break;
        case 'expertNotes':
          setCurrentSection('expertNotes');
          setExpandedMain(['home', 'community']);
          break;
        case 'expertKnowledge':
          setCurrentSection('expertKnowledge');
          setExpandedMain(['home', 'community', 'expertKnowledge']);
          break;
        case 'myCreations':
          setCurrentSection('myCreations');
          setExpandedMain(['home', 'personalCenter', 'myVideos']);
          break;
        // 🔧 修復：添加專案相關的 section 參數處理
        case 'fileUpload':
          setCurrentSection('fileUpload');
          setExpandedMain(['home', 'deliveryManagement']);
          break;
        case 'projectManagement':
          setCurrentSection('projectManagement');
          setExpandedMain(['home', 'deliveryManagement']);
          break;
        case 'clientArea':
          setCurrentSection('clientArea');
          setExpandedMain(['home', 'deliveryManagement']);
          break;
        case 'clientManagement':
          setCurrentSection('clientManagement');
          setExpandedMain(['home', 'deliveryManagement']);
          break;
        default:
          // 调试日志已移除
      }
    }
  }, [searchParams]);

  // 將 defaultTags、customTags 等宣告移到最上方，避免 useMemo 在宣告前被呼叫
  const defaultTags = [
    "這支影片對你有幫助",
    "請看重點！",
    "找不到重點，但很好笑！",
    "有趣的影片，一起看！",
    "影片找很久，拜託看完",
    "請勿模仿！",
    "這是趨勢！",
    "可以抽空看！",
    "只跟朋友分享！",
    "新聞快報！",
    "重要新知！",
    "我們是 (公司名稱）",
    "老闆交待看兩遍～",
    "滿滿乾貨，必看！",
  ];
  const [customTags, setCustomTags] = useState<string[]>([]);
  const [newTagInput, setNewTagInput] = useState('');

  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [currentShareUrl, setCurrentShareUrl] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);
  const [currentShareVideo, setCurrentShareVideo] = useState<VideoItem | null>(null);
  
  // 分享相關狀態（由 UnifiedShareDialog 處理）
  const [currentSection, setCurrentSection] = useState(() => {
    // 如果有路由狀態，使用路由狀態
    if (location.state?.currentSection) {
      return location.state.currentSection;
    }
    // 🔧 修復：優先檢查 URL 中的 projectId，如果有則設定為 clientArea
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('projectId')) {
      return 'clientArea';
    }
    // 🔧 修復：訪客預設顯示「社群公開」而不是「社群重點」
    // 因為訪客無法看到個人創作的社群重點內容
    return 'communityPublic';
  });
  
  // 🔧 新增：處理來自 MainLayout 的 pageState 變化（SPA 模式）
  const lastReceivedSection = useRef<string>('');
  const currentSectionRef = useRef<string>(currentSection);
  
  // 🔧 同步 currentSection 到 ref
  useEffect(() => {
    currentSectionRef.current = currentSection;
  }, [currentSection]);
  
  useEffect(() => {
    if (pageState?.currentSection && isEmbedded) {
      // 🔧 關鍵修復：使用 ref 比較，避免依賴 currentSection 導致的循環問題
      if (pageState.currentSection !== currentSectionRef.current && 
          pageState.currentSection !== lastReceivedSection.current) {
        // 导航日志已移除
        lastReceivedSection.current = pageState.currentSection;
        setCurrentSection(pageState.currentSection);
        
        // 同時更新 expandedMain
        if (pageState.expandedMain) {
          // 导航日志已移除
          setExpandedMain(pageState.expandedMain);
        }
        
        // 🔧 修復手機版訪客看不到影片：SPA 模式下也需要觸發載入
        // 使用 setTimeout 延遲檢查，避免 TDZ 錯誤
        setTimeout(() => {
          console.log('🚀 SPA 模式：準備載入數據:', pageState.currentSection, {
            loadingRef: loadingRef.current[pageState.currentSection],
            userId: userId,
            isEmbedded: isEmbedded
          });
          
          if (!loadingRef.current[pageState.currentSection]) {
            loadingRef.current[pageState.currentSection] = true;
            // 调用日志已移除
            
            loadVideos(pageState.currentSection).then(() => {
              // 调试日志已移除
            }).catch(error => {
              console.error('❌ loadVideos 失敗:', pageState.currentSection, error);
            }).finally(() => {
              loadingRef.current[pageState.currentSection] = false;
              // 配置日志已移除
            });
          } else {
            // 调试日志已移除
          }
        }, 0);
      } else if (pageState.currentSection === currentSectionRef.current) {
        // 🔧 即使相同，也更新 lastReceivedSection，避免後續阻止更新
        lastReceivedSection.current = pageState.currentSection;
      }
    }
  }, [pageState?.currentSection, pageState?.expandedMain, isEmbedded, userId]);

  // 🔧 新增：同步 currentSection 變化回到 MainLayout（SPA 模式）
  // 使用 ref 來避免循環依賴，並添加防抖機制
  const lastSyncedSection = useRef<string>('');
  const syncTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  useEffect(() => {
    if (onStateChange && isEmbedded && currentSection !== lastSyncedSection.current) {
      // 清除之前的定時器
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
      
      // 使用防抖機制，避免頻繁同步
      syncTimeoutRef.current = setTimeout(() => {
        // 同步日志已移除
        lastSyncedSection.current = currentSection;
        onStateChange({
          currentSection,
          expandedMain,
          scrollPosition: 0,
          selectedTab: 0
        });
      }, 50); // 50ms 防抖
    }
    
    // 清理函數
    return () => {
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
    };
  }, [currentSection, expandedMain, onStateChange, isEmbedded]);

  // 初始化頁面狀態：根據用戶登入狀態設置正確的 currentSection 和 expandedMain
  useEffect(() => {
    // 🔧 關鍵修復：SPA 模式下，完全跳過初始化，由 pageState useEffect 控制
    if (isEmbedded) {
      // 调试日志已移除
      return;
    }
    
    // 🔧 修復：檢查是否有 URL 參數，如果有則優先使用 URL 參數
    const section = searchParams.get('section');
    
    if (section) {
      // 导航日志已移除
      // URL 參數已在另一個 useEffect 中處理，這裡不需要重複設置
      return;
    }
    
    // 🔧 檢查是否已經有 currentSection（避免覆蓋 URL 參數設置的值）
    if (currentSection && currentSection !== 'communityPublic' && currentSection !== 'personalHistory') {
      // 调试日志已移除
      return;
    }
    
    if (authUser?.email) {
      // 已登入用戶：預設顯示瀏覽記錄，展開個人中心項目
      if (!location.state?.currentSection) {
        // 调试日志已移除
        setCurrentSection('personalHistory');
        setExpandedMain(['home', 'personalCenter', 'personalHistory', 'myVideos', 'myPersonalFavorite']);
        // 启动日志已移除
      } else {
        // 同步日志已移除
        // 启动日志已移除
      }
    } else {
      // 未登入用戶：預設顯示社群公開，展開社群項目
      if (!location.state?.currentSection) {
        // 调试日志已移除
        setCurrentSection('communityPublic');
        setExpandedMain(['home', 'community']);
        // 启动日志已移除
      } else {
        // 同步日志已移除
        // 启动日志已移除
      }
    }
  }, [authUser?.uid, location.state?.currentSection, searchParams, isEmbedded, pageState?.currentSection]); // 🔧 修復：添加 isEmbedded 和 pageState 依賴

  // 添加強制載入「我的創作」的事件監聽器
  useEffect(() => {
    const handleForceLoadMyVideos = (event: CustomEvent) => {
      // 导航日志已移除
      if (authUser?.email) {
        // 调试日志已移除
        setCurrentSection('myVideos');
        setExpandedMain(['home', 'personalCenter', 'myVideos', 'myPersonalFavorite', 'personalHistory']);
        loadVideos('myVideos', true, false, 'all');
      }
    };

    document.addEventListener('forceLoadMyVideos', handleForceLoadMyVideos as EventListener);
    
    return () => {
      document.removeEventListener('forceLoadMyVideos', handleForceLoadMyVideos as EventListener);
    };
  }, [authUser]);

  // 當 authUser 狀態變化時，自動調整 currentSection 和 expandedMain
  useEffect(() => {
    // 🔧 關鍵修復：SPA 模式下跳過自動調整，由 pageState 控制
    if (isEmbedded) {
      // 调试日志已移除
      return;
    }
    
    // 如果當前是特殊頁面（如上傳檔案），不要自動調整
    if (['videoUpload', 'localFiles', 'googleDrive'].includes(currentSection)) {
      return;
    }
    
    if (authUser?.email) {
      // 已登入用戶：自動切換到我的創作，並展開相關項目
      if (currentSection === 'expertKnowledge' && !location.state?.currentSection) {
        setCurrentSection('myVideos');
        // 展開個人相關項目，收起社群項目
        setExpandedMain(['home', 'personalCenter']);
      }
    } else {
      // 未登入用戶：自動切換到專家筆記，並展開相關項目
      if (currentSection === 'myVideos') {
        setCurrentSection('expertKnowledge');
        // 展開社群項目，收起個人項目
        setExpandedMain(['home', 'community']);
      }
    }
  }, [authUser?.email, location.state?.currentSection, isEmbedded]); // 🔧 添加 isEmbedded 依賴
  
  const [zoneLoading, setZoneLoading] = useState<{ [id: string]: boolean }>({});
  const userType = getUserType(authUser);
  const [showRegisterDialog, setShowRegisterDialog] = useState(false);
  const [showCreateTeamDialog, setShowCreateTeamDialog] = useState(false);
  const [generatedTeamCode, setGeneratedTeamCode] = useState('');
  const [currentUserEmail, setCurrentUserEmail] = useState('');
  const [registerForm, setRegisterForm] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: ''
  });
  const [teamForm, setTeamForm] = useState({
    name: '',
    taxId: '',
    members: '',
    clients: ''
  });
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  // const [communityPage, setCommunityPage] = useState(1); // 移除分頁，改用無限滾動
  const [personalVideos, setPersonalVideos] = useState<VideoItem[]>([]);
  const [communityVideos, setCommunityVideos] = useState<VideoItem[]>([]);
  const [userVideos, setUserVideos] = useState<VideoItem[]>([]); // 🔧 新增：我的創作影片狀態
  const [userFavoriteIds, setUserFavoriteIds] = useState<Set<string>>(new Set()); // 🔧 新增：用戶珍藏的影片 ID 集合（用於按鈕狀態判斷）
  const [favoriteUpdateTrigger, setFavoriteUpdateTrigger] = useState(0); // 🔧 新增：強制重新渲染觸發器
  // const pageSize = 10; // 移除分頁相關
  // const totalPages = Math.ceil(communityVideos.length / pageSize); // 移除分頁相關
  // const pagedCommunityVideos = communityVideos.slice((communityPage - 1) * pageSize, communityPage * pageSize); // 移除分頁相關
  const [personalPage, setPersonalPage] = useState(1);
  const personalPageSize = 5;
  // 顯示所有用戶的所有影片紀錄，按最後瀏覽時間排序並按日期分群
  const filteredPersonalVideos = useMemo(() => {
    return personalVideos
      .filter(v => v.userId === userId) // 只顯示當前用戶的影片
      .sort((a, b) => {
        // 按照最後瀏覽時間排序（最後瀏覽的在前）
        const getSafeDate = (item: any) => {
          const dateValue = item.lastOpenDate || item.createdAt || item.addedDate;
          if (!dateValue) return new Date().getTime();
          try {
            const date = new Date(dateValue);
            return isNaN(date.getTime()) ? new Date().getTime() : date.getTime();
          } catch (error) {
            return new Date().getTime();
          }
        };
        
        const dateA = getSafeDate(a);
        const dateB = getSafeDate(b);
        return dateB - dateA; // 降序：最後瀏覽的在前
      });
  }, [personalVideos, userId]); // 添加 userId 依賴項

  // 按日期分群的影片數據 - 將在正確位置重新定義
  const personalTotalPages = Math.ceil(filteredPersonalVideos.length / personalPageSize);
  const pagedPersonalVideos = filteredPersonalVideos.slice((personalPage - 1) * personalPageSize, personalPage * personalPageSize);
  // 1. 新增排序 state，預設為 'time'
  const [communitySortKey, setCommunitySortKey] = useState<'time' | 'vbids' | 'favoriteCount' | 'attentionCount'>('time');
  // 1. 新增排序模式 state，預設為 flat 模式
  const [communitySortMode, setCommunitySortMode] = useState<'group' | 'flat'>('flat');
  // 1. 新增狀態
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [groupDialogVideo, setGroupDialogVideo] = useState<VideoItem | null>(null);
  const [userGroups, setUserGroups] = useState<any[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]); // 存 group.id
  // Accordion Toggle List hooks（移到頂層）
  const [expandedGroupId, setExpandedGroupId] = useState<string | null>(null);
  const [groupOpenTimestamps, setGroupOpenTimestamps] = useState<{ [id: string]: number }>({});
  const [expandedTag, setExpandedTag] = useState<string | null>('ALL'); // 預設 ALL 展開
  const [groupList, setGroupList] = useState<any[]>([]);
  const [leaving, setLeaving] = useState(false);
  const [selectedKMGroup, setSelectedKMGroup] = useState<string | null>(null);
  const [expertKnowledgeViewMode, setExpertKnowledgeViewMode] = useState<'tag' | 'group' | 'user'>('user');
  const [shareGroup, setShareGroup] = useState<any>(null);
  const [expandedMyFavGroup, setExpandedMyFavGroup] = useState<string | null>(null);
  const [expertSearchKeyword, setExpertSearchKeyword] = useState('');
  const [expertPage, setExpertPage] = useState(1);

  // 個人珍藏頁面狀態
  const [personalSearchKeyword, setPersonalSearchKeyword] = useState('');
  const [personalViewMode, setPersonalViewMode] = useState<'tag' | 'group'>('tag');
  const [selectedPersonalGroups, setSelectedPersonalGroups] = useState<string[]>([]);
  const PERSONAL_PAGE_SIZE = 20;

  // 我的創作分頁月份篩選狀態
  const [myVideosSelectedMonth, setMyVideosSelectedMonth] = useState<string>('all'); // 預設為 'all'，由 useEffect 判斷是否設為當月
  const [myVideosAvailableMonths, setMyVideosAvailableMonths] = useState<string[]>([]);

  // 專家標籤相關狀態
  const [allExpertTags, setAllExpertTags] = useState<string[]>([]);
  useEffect(() => {
    // 🔧 優化：Guest 用戶跳過專家標籤載入
    if (!userId || userId === 'guest') {
      // 信息日志已移除
      return;
    }
    
    // 等待 Firebase 初始化完成後再載入專家標籤
    waitForFirebase().then(() => {
      const tagsCollection = safeCollection('tags');
      if (!tagsCollection) {
        console.warn('⚠️ 專家標籤載入失敗：無法獲取 tags collection');
        return;
      }
      
      getDocs(query(tagsCollection, where('isExpert', '==', true)))
        .then(snap => {
          const expertTagNames = snap.docs.map(doc => doc.data().name).filter(Boolean);
          setAllExpertTags(Array.from(new Set(expertTagNames)));
          if (expertTagNames.length > 0) {
            // 调试日志已移除
          }
        })
        .catch(error => {
          // 靜默處理權限錯誤（規則可能需要部署）
          if (error.code === 'permission-denied' || error.message?.includes('permissions')) {
            console.warn('⚠️ 專家標籤載入失敗（權限問題）: 請確認 Firestore 規則已部署');
          } else {
            console.error('❌ 載入專家標籤失敗:', error);
          }
          setAllExpertTags([]);
        });
    }).catch(error => {
      console.error('❌ 等待 Firebase 初始化失敗:', error);
    });
  }, [userId]);

  // 標籤編輯相關狀態
  const [tagDeleteMode, setTagDeleteMode] = useState(false);
  const [tagEditMode, setTagEditMode] = useState(false);
  const [editingTagName, setEditingTagName] = useState('');
  const [expertTags, setExpertTags] = useState<string[]>([]);
  const tagTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastClickTimeRef = useRef<number>(0);
  const lastClickedTagRef = useRef<string>('');
  const lastVisibleHistoryDocRef = useRef<any>(null);

  // 移除重複的 YouTube 資訊載入，統一由下方的 useEffect 處理

  // 載入專家 Tag
  useEffect(() => {
    // 🔧 優化：Guest 用戶跳過專家標籤載入
    if (!userId || userId === 'guest') {
      // 信息日志已移除
      return;
    }
    
    // 等待 Firebase 初始化完成後再載入專家標籤
    waitForFirebase().then(() => {
      const tagsCollection = safeCollection('tags');
      if (!tagsCollection) {
        console.warn('⚠️ 專家標籤載入失敗：無法獲取 tags collection');
        return;
      }
      
      getDocs(query(tagsCollection, where('userId', '==', userId), where('isExpert', '==', true)))
        .then(snap => {
          setExpertTags(snap.docs.map(doc => doc.data().name));
          // 调试日志已移除
        })
        .catch(error => {
          console.error('❌ 載入專家標籤失敗:', error);
        });
    }).catch(error => {
      console.error('❌ 等待 Firebase 初始化失敗:', error);
    });
  }, [userId]);

  // 載入使用者群組
  useEffect(() => {
    // 🔧 優化：Guest 用戶跳過群組載入
    if (!authUser?.email || authUser.uid === 'guest') {
      // 信息日志已移除
      return;
    }
    
    const loadUserGroups = async () => {
      try {
        const groups = await getUserGroups(authUser.email);
        setUserGroups(groups);
        // 调试日志已移除
      } catch (error) {
        console.error('❌ 載入使用者群組失敗:', error);
        setUserGroups([]);
      }
    };
    
    loadUserGroups();
  }, [authUser?.email, authUser?.uid]);

  // 🔧 新增：載入用戶珍藏列表（用於按鈕狀態判斷）
  useEffect(() => {
    // 查询日志已移除 });
    
    if (!userId || userId === 'guest' || userId.startsWith('guest_')) {
      // 跳过日志已移除
      return;
    }
    
    const loadUserFavorites = async () => {
      try {
        // 查询日志已移除
        await waitForFirebase();
        const favoritesCollection = safeCollection('userFavorites');
        if (!favoritesCollection) {
          console.warn('⚠️ [userFavorites] 無法獲取 userFavorites collection');
          return;
        }
        
        const favoritesQuery = query(
          favoritesCollection,
          where('userId', '==', userId)
        );
        const snapshot = await getDocs(favoritesQuery);
        
        const favoriteIds = new Set(
          snapshot.docs.map(doc => {
            const data = doc.data();
            // 珍藏記錄日誌已簡化
            return data.firestoreId;
          }).filter(Boolean)
        );
        
        setUserFavoriteIds(favoriteIds);
        // 调试日志已移除
      } catch (error) {
        console.error('❌ [userFavorites] 載入用戶珍藏列表失敗:', error);
        setUserFavoriteIds(new Set());
      }
    };
    
    loadUserFavorites();
  }, [userId]);

  // 全域用戶切換狀態
  const [globalSelectedUserId, setGlobalSelectedUserId] = useState<string | 'ALL'>('ALL');
  const allUserIds: string[] = useMemo(() => {
    const ids = new Set<string>();
    videos.forEach(v => {
      if (v.userId) ids.add(v.userId);
    });
    return Array.from(ids);
  }, []); // 移除會導致無限循環的依賴項

  // 本地檔案相關狀態
  const [localFileDialogOpen, setLocalFileDialogOpen] = useState(false);
  const [selectedLocalFile, setSelectedLocalFile] = useState<LocalFileRecord | null>(null);

  // 將排序邏輯移到組件頂層
  const sortedGroups = useMemo(() => {
    if (!userGroups.length) return [];
    
    const uniqueGroups = Array.from(new Map(userGroups.map(group => [group.id, group])).values());
    
    // 讀取群組使用頻率
    const groupUsage = JSON.parse(localStorage.getItem('groupUsage') || '{}');
    
    if (Object.keys(groupOpenTimestamps).length === 0) {
      // 按使用頻率排序，如果頻率相同則按創建時間排序
      return [...uniqueGroups].sort((a, b) => {
        const aUsage = groupUsage[a.id] || 0;
        const bUsage = groupUsage[b.id] || 0;
        if (aUsage !== bUsage) {
          return bUsage - aUsage; // 使用頻率高的排在前面
        }
        // 使用頻率相同時，按創建時間排序
        return new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime();
      });
    }
    
    // 有開啟時間戳時，優先按開啟時間排序，其次按使用頻率排序
    return [...uniqueGroups].sort((a, b) => {
      const aTime = groupOpenTimestamps[a.id] || new Date(a.createdAt || 0).getTime();
      const bTime = groupOpenTimestamps[b.id] || new Date(b.createdAt || 0).getTime();
      
      // 如果開啟時間相差超過1小時，按開啟時間排序
      const timeDiff = Math.abs(aTime - bTime);
      if (timeDiff > 60 * 60 * 1000) { // 1小時 = 60 * 60 * 1000 毫秒
      return bTime - aTime;
      }
      
      // 開啟時間相近時，按使用頻率排序
      const aUsage = groupUsage[a.id] || 0;
      const bUsage = groupUsage[b.id] || 0;
      if (aUsage !== bUsage) {
        return bUsage - aUsage; // 使用頻率高的排在前面
      }
      
      // 使用頻率相同時，按創建時間排序
      return new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime();
    });
  }, [userGroups, groupOpenTimestamps]);
  
  // 切換展開群組
  const handleToggleGroup = (groupId: string) => {
    setExpandedGroupId(expandedGroupId === groupId ? null : groupId);
    setGroupOpenTimestamps(prev => ({
      ...prev,
      [groupId]: Date.now(),
    }));
    
    // 更新群組使用頻率
    const groupUsage = JSON.parse(localStorage.getItem('groupUsage') || '{}');
    groupUsage[groupId] = (groupUsage[groupId] || 0) + 1;
    localStorage.setItem('groupUsage', JSON.stringify(groupUsage));
  };

  // 1. 新增 allGroups 狀態，並在 useEffect 查詢所有群組
  const [allGroups, setAllGroups] = useState<{id: string, name: string, admins: string[]}[]>([]);
  const fetchAllGroups = useCallback(async () => {
    try {
      // 等待 Firebase 初始化完成
      await waitForFirebase();
      
      const groupsCollection = safeCollection('groups');
      if (!groupsCollection) {
        // 同步日志已移除
        return;
      }
      
      const snap = await getDocs(groupsCollection);
      setAllGroups(snap.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name,
        admins: doc.data().admins || [],
      })));
    } catch (error) {
      // 同步日志已移除
    }
  }, []);
  useEffect(() => {
    // 延遲執行，等待 Firebase 初始化
    const timer = setTimeout(() => {
      fetchAllGroups();
    }, 200); // 給 Firebase 更多初始化時間
    
    return () => clearTimeout(timer);
  }, []);

  // 強化影片唯一 key 產生方式
  function getVideoKey(item: { path?: string; uploadKey?: string }) {
    if (!item) return '';
    const path = (item.path || '').trim().toLowerCase();
    const videoId = extractYouTubeId(path);
    return videoId ? videoId : (item.uploadKey ? item.uploadKey : path);
  }

  // 處理本地檔案開啟
  const handleLocalFileOpen = (file: LocalFileRecord) => {
    setSelectedLocalFile(file);
    setLocalFileDialogOpen(true);
    
    // 更新檔案的最後開啟時間
    localFileService.updateLastOpened(file.id);
    
    // 導航到播放頁面
    markPersonalHistoryDirty();
    navigate(`/v/local_${file.id}`, { 
      state: { 
        localFile: file,
        fromManagePages: true 
      } 
    });
  };

  // 🔒 新增：安全處理雲端檔案開啟
  const handleCloudFileOpen = async (file: LocalFileRecord) => {
    // 调试日志已移除
    console.log('🔍 檔案詳情:', {
      id: file.id,
      fileName: file.fileName,
      relativePath: file.relativePath,
      fileSize: file.fileSize,
      fileType: file.fileType,
      lastModified: file.lastModified
    });
    
    try {
      const db = getDb();
      
      // 🔧 修復：使用多種方式獲取 userId，避免 authUser 狀態更新延遲問題
      let currentUserId = authUser?.uid || userId;
      
      // 如果還是沒有，嘗試從 Firebase Auth 直接獲取
      if (!currentUserId) {
        const { getAuth } = await import('firebase/auth');
        const auth = getAuth();
        currentUserId = auth.currentUser?.uid;
      }
      
      // 查询日志已移除
      // 查询日志已移除
      // 查询日志已移除
      // 查询日志已移除
      
      if (!db || !currentUserId) {
        console.warn('⚠️ Firestore 未初始化或無法獲取用戶 ID');
        console.warn('⚠️ 將使用降級方案直接播放（不載入重點）');
        
        // 🔧 降級方案：直接導航播放，不創建 sharedVbids 記錄
        navigateToMain({ 
          googleDriveUrl: file.relativePath,
          googleDriveTitle: file.fileName,
          googleDrivePath: file.fileName,
          fromCloudUpload: true,
          forceVideoTab: true,
          defaultTab: 'video',
          videoMode: 'Video'
        });
        // 调试日志已移除
        return;
      }
      
      // 🔧 關鍵修復：檢測 file.id 的格式，判斷來源
      const isUUIDFormat = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(file.id);
      const isFirestoreIdFormat = file.id.includes('_') && file.id.includes('_v');
      
      let firestoreId: string;
      
      if (isFirestoreIdFormat) {
        // 來自 fileUpload（STEP 3）或其他已有正確 firestoreId 的地方
        // 调试日志已移除
        firestoreId = file.id;
      } else {
        // 來自 ProjectManagementTable（專案管理），需要生成 firestoreId
        // 调试日志已移除
        const cleanFilename = file.fileName.replace(/[^a-zA-Z0-9]/g, '_');
        const videoId = `file_${cleanFilename}`;
        firestoreId = `${currentUserId}_${videoId}_v1`;  // 🔧 使用 currentUserId
        // 配置日志已移除
      }
      
      // 导航日志已移除
      
      // 生成 videoId（用於 userWatchHistory）
      const cleanFilename = file.fileName.replace(/[^a-zA-Z0-9]/g, '_');
      const videoId = `file_${cleanFilename}`;
        
      console.log('🔧 為雲端檔案創建/檢查 sharedVbids 記錄:', {
        fileName: file.fileName,
        videoId: videoId,
        firestoreId: firestoreId,
        currentUserId: currentUserId  // 🔧 添加調試信息
      });
      
      // 檢查是否已存在 sharedVbids 記錄
      const { doc, getDoc, setDoc, serverTimestamp } = await import('firebase/firestore');
      const docRef = doc(db, 'sharedVbids', firestoreId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        // 配置日志已移除
        await setDoc(docRef, {
          videoId: videoId,
          userId: currentUserId,  // 🔧 使用 currentUserId
          title: file.fileName,
          path: file.relativePath, // 🔧 修復：使用 path 而不是 url，與其他影片保持一致
          url: file.relativePath, // GCS URL（保留兼容性）
          thumbnail: file.thumbnailUrl,  // 🔧 新增：保存縮圖 URL
          addedDate: serverTimestamp(),
          lastOpenDate: new Date().toISOString(),
          lastReadAt: serverTimestamp(),
          vbids: [],
          numberKeys: {}, // 🔧 新增：初始化 numberKeys
          checkpointCount: 0,
          mode: 'VIDEO', // 🔧 新增：設置模式
          mediaMode: 'VIDEO',  // 🔧 新增：設置 mediaMode
          privacy: 'Private',
          zones: ['personal'], // 🔧 新增：設置 zones
          source: 'cloud_upload',
          provider: 'cloud',  // 🔧 新增：標記為雲端檔案
          // 雲端檔案特有的元數據
          cloudFile: {
            id: file.id,
            originalFilename: file.fileName,
            size: file.fileSize,
            contentType: file.fileType,
            uploadTime: file.lastModified,
            thumbnailUrl: file.thumbnailUrl  // 🔧 新增：雲端檔案元數據也包含縮圖
          }
        });
        // 调试日志已移除
      } else {
        // 调试日志已移除
        // 数据统计日志已移除?.vbids?.length || 0);
        
        // 🔧 新增：如果文檔已存在但沒有縮圖，更新縮圖
        const existingData = docSnap.data();
        if (file.thumbnailUrl && !existingData?.thumbnail) {
          const { updateDoc } = await import('firebase/firestore');
          await updateDoc(docRef, {
            thumbnail: file.thumbnailUrl
          });
          // 调试日志已移除
        }
      }
      
      // 🔧 導航到播放頁面，傳遞正確的 firestoreId
      navigateToMain({ 
        // 使用 Video Tab 的格式
        googleDriveUrl: file.relativePath, // 直接使用 GCS URL
        googleDriveTitle: file.fileName,  // 原始文件名作為標題
        googleDrivePath: file.fileName,   // 原始文件名作為路徑
        fromCloudUpload: true,               // 標記來自雲端上傳
        firestoreId: firestoreId,            // 🔧 關鍵修復：使用生成或檢測到的正確 firestoreId
        fileRelativePath: file.fileName,     // 🔧 新增：傳遞原始檔名以確保 videoId 一致
        cloudFile: {
          id: file.id,                     // 原始檔案 ID（可能是 UUID 或 firestoreId）
          filename: file.relativePath.split('/').pop() || file.id, // 檔案名
          originalFilename: file.fileName, // 原始文件名
          size: file.fileSize,             // 文件大小
          contentType: file.fileType,      // 文件類型
          uploadTime: file.lastModified,   // 上傳時間
          projectName: '雲端檔案',         // 專案名稱
          url: file.relativePath // 使用實際的 GCS URL
        },
        // 強制使用 Video Tab
        forceVideoTab: true,
        // 設置預設 Tab
        defaultTab: 'video',
        videoMode: 'Video'  // 🔧 確保使用 Video Tab
      });
      
      // 调试日志已移除
    } catch (error) {
      console.error('❌ 處理雲端檔案播放失敗:', error);
      // 即使發生錯誤，也嘗試播放（使用降級方案）
      navigateToMain({ 
        googleDriveUrl: file.relativePath,
        googleDriveTitle: file.fileName,
        googleDrivePath: file.fileName,
        fromCloudUpload: true,
        forceVideoTab: true,
        defaultTab: 'video',
        videoMode: 'Video'
      });
      // 调试日志已移除
    }
  };

  // 處理 Google Drive 影片選擇
  const handleGoogleDriveVideoSelect = (fileId: string, fileName: string) => {
    // 调试日志已移除
    
    // 導航到播放頁面，使用 Google Drive 格式
    markPersonalHistoryDirty();
    navigate(`/v/gdrive_${fileId}`, { 
      state: { 
        googleDriveFile: { id: fileId, name: fileName },
        fromManagePages: true 
      } 
    });
  };

  // 優化載入邏輯 - 移除重複的 useEffect，添加分頁和快取
  const [videosCache, setVideosCache] = useState<Record<string, VideoItem[]>>({});
  const [cacheTimestamps, setCacheTimestamps] = useState<Record<string, number>>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  
  // 添加載入狀態追蹤，防止無限循環
  const loadingRef = useRef<Record<string, boolean>>({});
  
  // 個人歷史記錄快取狀態（移到頂部避免宣告順序問題）
  const [personalHistoryCache, setPersonalHistoryCache] = useState<{ [key: string]: VideoItem[] }>({});
  const [personalHistoryCacheTimestamps, setPersonalHistoryCacheTimestamps] = useState<{ [key: string]: number }>({});
  const [personalHistoryVideos, setPersonalHistoryVideos] = useState<VideoItem[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [selectedUserIdMap, setSelectedUserIdMap] = useState<{ [videoId: string]: string }>({});
  const ITEMS_PER_PAGE = 20; // 每頁載入 20 個項目
  const CACHE_EXPIRY_TIME = 15 * 60 * 1000;   // 15分鐘快取過期時間
  const TAG_UPDATE_CACHE_EXPIRY = 2 * 60 * 1000; // Tag 更新後快取過期時間縮短為 2 分鐘

  // 分階段載入配置
  const BATCH_SIZE = 100; // 每批載入100個影片
  const INITIAL_LOAD_SIZE = 600; // 🔧 修復：大幅增加初始載入數量，確保包含更多歷史數據
  const BACKGROUND_LOAD_DELAY = 1000; // 背景載入延遲1秒

  // 分階段載入狀態
  const [loadingBatches, setLoadingBatches] = useState<Set<string>>(new Set());
  const [loadedCounts, setLoadedCounts] = useState<Record<string, number>>({});
  const [hasMoreData, setHasMoreData] = useState<Record<string, boolean>>({});

  // 載入更多資料的函數
  const loadMoreVideos = useCallback(async (section: string) => {
    if (loadingBatches.has(section) || !hasMoreData[section]) {
      return;
    }
    // 載入更多資料
    // 使用 loadVideos 函數的批次模式
    await loadVideos(section, false, true);
  }, [loadingBatches, hasMoreData]);

  // 載入更多按鈕組件
  const LoadMoreButton = ({ section }: { section: string }) => {
    const isLoading = loadingBatches.has(section);
    const hasMore = hasMoreData[section];
    const loadedCount = loadedCounts[section] || 0;
    
    if (!hasMore && loadedCount > 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 2, color: 'text.secondary' }}>
          <Typography variant="body2">已載入全部資料</Typography>
        </Box>
      );
    }
    
    if (!hasMore) return null;
    
    return (
      <Box sx={{ textAlign: 'center', py: 2 }}>
        <Button
          variant="outlined"
          onClick={() => loadMoreVideos(section)}
          disabled={isLoading}
          sx={{ minWidth: 120 }}
        >
          {isLoading ? '載入中...' : `載入更多 (${loadedCount} 個)`}
        </Button>
      </Box>
    );
  };

  // 背景預載入機制
  const backgroundPreload = useCallback(async (section: string) => {
    if (loadingBatches.has(section) || !hasMoreData[section]) {
      return;
    }
    
    // 调试日志已移除
    setTimeout(() => {
      loadMoreVideos(section);
    }, BACKGROUND_LOAD_DELAY);
  }, [loadingBatches, hasMoreData, loadMoreVideos]);

  // 當用戶滾動到底部時自動載入更多
  const handleScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100; // 距離底部100px時觸發
    
    if (isNearBottom && hasMoreData[currentSection] && !loadingBatches.has(currentSection)) {
      backgroundPreload(currentSection);
    }
  }, [currentSection, hasMoreData, loadingBatches, backgroundPreload]);

  // 優化的 YouTube API 請求函數 - 直接返回原始數據，避免不必要的處理
  const fetchYouTubeInfoBatch = useCallback(async (videos: VideoItem[], batchSize = 5) => {
    // 直接返回原始視頻資料，不進行任何處理
    return videos;
  }, []);

  // 統一的影片載入函數 - 支援分階段載入
  // 🔧 修復：添加快取清理機制
  const clearCache = useCallback(async (section?: string) => {
    // 🔧 修復：移除自動創建觀看記錄的邏輯，避免批量創建異常記錄
    // 觀看記錄應該只在用戶實際觀看影片時創建，而不是在清理快取時批量創建
    
    // 原有的清理快取邏輯
    if (section) {
      // 清理特定分頁的快取
      setVideosCache(prev => {
        const newCache = { ...prev };
        delete newCache[section];
        return newCache;
      });
      setCacheTimestamps(prev => {
        const newTimestamps = { ...prev };
        delete newTimestamps[section];
        return newTimestamps;
      });
      // 调试日志已移除
    } else {
      // 清理所有快取
      setVideosCache({});
      setCacheTimestamps({});
      setPersonalHistoryCache({});
      setPersonalHistoryCacheTimestamps({});
      // 清理日志已移除
    }
  }, [authUser?.uid]);

  // 🔧 新增：監聽 MainPage 的清除快取標記
  useEffect(() => {
    const checkClearCacheFlag = () => {
      const clearFlag = sessionStorage.getItem('clearManagePagesCache');
      if (clearFlag) {
        // 调试日志已移除
        clearCache();
        sessionStorage.removeItem('clearManagePagesCache');
      }
    };

    // 初始檢查
    checkClearCacheFlag();

    // 設置定時檢查（每2秒檢查一次）
    const interval = setInterval(checkClearCacheFlag, 2000);

    return () => clearInterval(interval);
  }, [clearCache]);

  // 快取過期時間常量
  const PERSONAL_HISTORY_CACHE_EXPIRY_TIME = 30 * 60 * 1000; // 30分鐘快取過期時間

  // 🔧 新增：載入真實的觀看記錄（使用用戶層級的觀看記錄）
  const loadWatchHistory = useCallback(async (userId: string, forceRefresh = false, batchMode = false, monthFilter?: string) => {
    try {
      // 查询日志已移除
      
      // 等待 Firebase 初始化完成
      await waitForFirebase();
      
      // 清除快取（如果強制刷新）
      if (forceRefresh) {
        const personalHistoryCacheKey = `personalHistory_${userId}_${monthFilter || 'all'}`;
        setPersonalHistoryCache(prev => {
          const newCache = { ...prev };
          delete newCache[personalHistoryCacheKey];
          return newCache;
        });
        setPersonalHistoryCacheTimestamps(prev => {
          const newTimestamps = { ...prev };
          delete newTimestamps[personalHistoryCacheKey];
          return newTimestamps;
        });
      }
      
      // 檢查快取 - 使用與月份篩選一致的快取 key
      const cacheKey = `personalHistory_${userId}_${monthFilter || 'all'}`;
      const cacheTimestamp = personalHistoryCacheTimestamps[cacheKey];
      const cachedData = personalHistoryCache[cacheKey];
      
      // 缓存日志已移除
      
      if (!forceRefresh && cachedData && cacheTimestamp && (Date.now() - cacheTimestamp) < PERSONAL_HISTORY_CACHE_EXPIRY_TIME) {
        // 调试日志已移除
        setPersonalHistoryVideos(cachedData);
        setLoading(false);
        return;
      }
      
      // 🔧 新方案：使用用戶層級的觀看記錄服務
      try {
        // 🔧 修復：檢查是否正在載入中，避免重複載入
        const loadingKey = `userWatchHistory_${userId}_${monthFilter || 'all'}`;
        if ((window as any)[loadingKey]) {
          // 同步日志已移除
          // 🔧 修復：如果載入時間超過30秒，強制清除標誌
          const loadingStartTime = (window as any)[`${loadingKey}_startTime`];
          if (loadingStartTime && Date.now() - loadingStartTime > 30000) {
            // 调试日志已移除
            (window as any)[loadingKey] = false;
            delete (window as any)[`${loadingKey}_startTime`];
          } else {
            return;
          }
        }
        
        (window as any)[loadingKey] = true;
        (window as any)[`${loadingKey}_startTime`] = Date.now();
        
        const { getUserWatchHistory } = await import('../services/userWatchHistoryService');
        // 🔧 修復：根據是否為月份篩選決定載入數量
        // 如果是特定月份篩選，載入更多記錄以確保顯示該月份的所有內容
        // 如果是「前50筆」，載入較少記錄以提升性能
        const initialLoadCount = (monthFilter && monthFilter !== 'all') ? 500 : 20;
        // 调试日志已移除
        const userWatchHistory = await getUserWatchHistory(userId, initialLoadCount, monthFilter);
        
        // 调试日志已移除
        
        // 根據觀看記錄獲取影片詳情 - 分批處理以提高性能
        const videosData: VideoItem[] = [];
        // 🔧 優化：手機版減少批次大小，避免記憶體溢出
        const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
        const BATCH_SIZE = isMobile ? 10 : 20; // 手機版每批 10 個，桌面版 20 個
        
        // 调试日志已移除
        
        // 設置載入狀態
        setLoading(true);
        
        for (let i = 0; i < userWatchHistory.length; i += BATCH_SIZE) {
          const batch = userWatchHistory.slice(i, i + BATCH_SIZE);
          // 🔧 移除詳細日誌以減少控制台輸出
          // 调试日志已移除
          
          const batchPromises = batch.map(async (watchRecord) => {
            try {
              let videoSnapshot: { docs: any[]; empty: boolean } | null = null;
              
              // 🔧 修復：如果有 firestoreId，直接精確查詢該觀點
              if (watchRecord.firestoreId) {
                // ✅ 直接查詢用戶實際觀看的觀點
                const docRef = doc(safeCollection("sharedVbids"), watchRecord.firestoreId);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                  videoSnapshot = { docs: [docSnap], empty: false };
                  // 成功查詢日誌已移除（減少輸出）
                } else {
                  // 🔧 降級處理：firestoreId 對應的觀點不存在，改用 videoId 查詢
                  // 靜默處理，不污染控制台（觀點可能已被刪除，這是正常情況）
                  videoSnapshot = null; // 標記為 null，讓降級邏輯處理
                }
              }
              
              // 🔧 降級處理：firestoreId 不存在或查詢失敗，使用 videoId 查詢
              if (!videoSnapshot || videoSnapshot.empty) {
                // 🔧 降級處理：使用 videoId 查詢所有相同 videoId 的觀點
                // 日誌已簡化（避免過多輸出）
                
                // ✅ 直接查詢所有相同 videoId 的觀點（不區分用戶）
                let videoQuery = query(
                  safeCollection("sharedVbids"),
                  where("videoId", "==", watchRecord.videoId)
                );
                const allDocs = await getDocs(videoQuery);
                
                if (!allDocs.empty) {
                  // 🔧 根據觀看記錄的 lastOpenDate 時間，找出最接近的觀點
                  const watchRecordDate = new Date(watchRecord.lastOpenDate).getTime();
                  
                  let selectedDoc = allDocs.docs[0];
                  let minTimeDiff = Infinity;
                  
                  allDocs.docs.forEach(doc => {
                    const data = doc.data() as any;
                    const docLastOpenDate = data.lastOpenDate || data.updatedAt || data.createdAt;
                    if (docLastOpenDate) {
                      const docDate = new Date(docLastOpenDate).getTime();
                      const timeDiff = Math.abs(docDate - watchRecordDate);
                      
                      // 🔧 修復：記錄所有時間差，選擇最接近的（不限制5分鐘）
                      if (timeDiff < minTimeDiff) {
                        minTimeDiff = timeDiff;
                        selectedDoc = doc;
                      }
                    }
                  });
                  
                  // 🔧 修復：如果找到時間匹配的觀點，直接使用
                  // 不再優先選擇用戶自己的觀點
                  if (minTimeDiff === Infinity) {
                    // 完全沒有時間信息，使用第一個找到的觀點
                    // 查询日志已移除
                  } else {
                    console.log('🔍 舊記錄時間匹配成功:', {
                      videoId: watchRecord.videoId,
                      watchRecordDate: new Date(watchRecord.lastOpenDate).toISOString(),
                      selectedViewpoint: selectedDoc.id,
                      selectedUserId: (selectedDoc.data() as any).userId,
                      timeDiff: `${Math.round(minTimeDiff / 1000)} 秒`,
                      isUserOwn: (selectedDoc.data() as any).userId === userId
                    });
                  }
                  
                  videoSnapshot = { docs: [selectedDoc], empty: false };
                } else {
                  videoSnapshot = { docs: [], empty: true };
                }
              }
              
              if (!videoSnapshot.empty) {
                // 🔧 修改：為每個觀點創建獨立的列表項目
                // 現在每個觀點都已經在 Firestore 中包含了完整的影片資訊
                const viewpointItems: VideoItem[] = [];
                
                // 🔧 修復：直接使用查詢到的觀點，不要再過濾
                // 如果有 firestoreId，已經是精確查詢的單一觀點
                // 如果是舊記錄（無 firestoreId），則包含所有查詢到的觀點
                const viewpointsToProcess = videoSnapshot.docs;
                const primaryViewpoint = viewpointsToProcess.find(doc => {
                  const data = doc.data() as VideoItem;
                  return data.isPrimaryViewpoint === true;
                }) || viewpointsToProcess[0]; // 如果沒有主觀點，使用第一個
                
                const primaryData = primaryViewpoint.data() as VideoItem;
                
                // 🔧 修復：改用 for...of 以支持 async/await
                for (const videoDoc of viewpointsToProcess) {
                  const videoData = videoDoc.data() as VideoItem;
                  const isPrimary = videoDoc.id === primaryViewpoint.id;
                  
                  // 🔧 修復：統一計算觀點的重點數，確保與 MainPage 一致
                  const checkpointCount = (() => {
                    // 優先使用實際的 vbids 長度，確保與 MainPage 計算一致
                    if (Array.isArray(videoData.vbids) && videoData.vbids.length > 0) {
                      return videoData.vbids.length;
                    }
                    // 回退到其他計算方式
                    return videoData.checkpointCount || 
                      (videoData.numberKeys ? Object.keys(videoData.numberKeys).filter(k => videoData.numberKeys[parseInt(k)]?.isActive).length : 0);
                  })();
                  
                  // 🔧 新增：補充雲端檔案縮圖
                  let thumbnailUrl = videoData.thumbnail || videoData.cloudFile?.thumbnailUrl || primaryData.thumbnail;
                  
                  // 🔍 調試：記錄雲端檔案縮圖檢查資訊
                  if (videoData.source === 'cloud_upload' || videoData.cloudFile?.id) {
                    console.log('🔍 雲端檔案縮圖檢查:', {
                      videoId: videoDoc.id,
                      hasThumbnail: !!thumbnailUrl,
                      thumbnailUrl: thumbnailUrl,  // 🔧 顯示完整 URL
                      videoDataThumbnail: videoData.thumbnail,
                      cloudFileThumbnail: videoData.cloudFile?.thumbnailUrl,
                      primaryDataThumbnail: primaryData.thumbnail,
                      source: videoData.source,
                      hasCloudFile: !!videoData.cloudFile,
                      cloudFileId: videoData.cloudFile?.id
                    });
                  }
                  
                  // 如果沒有縮圖，且是雲端檔案，嘗試從 uploads 集合讀取
                  if (!thumbnailUrl && videoData.source === 'cloud_upload' && videoData.cloudFile?.id) {
                    try {
                      // 🔧 導入 Firestore 函數並獲取 db
                      const { collection, doc, getDoc, updateDoc } = await import('firebase/firestore');
                      const { getDb } = await import('../services/firebase');
                      const firestoreDb = getDb();
                      
                      if (!firestoreDb) {
                        console.warn('⚠️ Firestore 未初始化，跳過縮圖補充');
                      } else {
                        const uploadsCol = collection(firestoreDb, 'uploads');
                        const uploadDoc = await getDoc(doc(uploadsCol, videoData.cloudFile.id));
                        if (uploadDoc.exists()) {
                          thumbnailUrl = uploadDoc.data().thumbnailUrl;
                          
                          // 🔧 同時補充到 sharedVbids，避免下次重複查詢
                          if (thumbnailUrl && isPrimary) {
                            await updateDoc(doc(firestoreDb, 'sharedVbids', videoDoc.id), {
                              thumbnail: thumbnailUrl
                            });
                            // 调试日志已移除
                          }
                        }
                      }
                    } catch (error) {
                      console.warn('⚠️ 讀取雲端檔案縮圖失敗:', videoData.cloudFile?.id, error);
                    }
                  }
                  
                  const finalItem = {
                    ...videoData,
                    id: videoDoc.id, // 使用觀點的文檔ID作為唯一標識
                    lastOpenDate: watchRecord.lastOpenDate, // 使用用戶層級的 lastOpenDate
                    userLastReadAt: watchRecord.lastReadAt,
                    // 🔧 確保觀點名稱正確顯示
                    viewpointName: videoData.viewpointName || '預設觀點',
                    // 🔧 確保重點數正確顯示
                    checkpointCount: checkpointCount,
                    globalCheckpointCount: checkpointCount,
                    personalCheckpointCount: checkpointCount,
                    // 🔧 修復：使用補充後的縮圖
                    thumbnail: thumbnailUrl,
                    
                    // 🔧 如果不是主觀點，繼承主觀點的影片資訊
                    ...(isPrimary ? {} : {
                      // 繼承影片基本資訊
                      title: primaryData.title,
                      youtubeInfo: primaryData.youtubeInfo,
                      videoInfo: primaryData.videoInfo,
                      // 🔧 修復：繼承主觀點的縮圖
                      thumbnail: thumbnailUrl || primaryData.thumbnail || primaryData.cloudFile?.thumbnailUrl,
                      channelTitle: primaryData.channelTitle,
                      viewCount: primaryData.viewCount,
                      publishedAt: primaryData.publishedAt,
                      description: primaryData.description,
                      duration: primaryData.duration,
                      tags: primaryData.tags,
                      // 保持自己的觀點相關數據
                      numberKeys: videoData.numberKeys,
                      zones: videoData.zones,
                      groupIds: videoData.groupIds,
                      viewpointName: videoData.viewpointName,
                      viewpointDescription: videoData.viewpointDescription,
                      viewpointTags: videoData.viewpointTags,
                      viewpointColor: videoData.viewpointColor,
                      isPrimaryViewpoint: videoData.isPrimaryViewpoint,
                      relatedViewpoints: videoData.relatedViewpoints
                    })
                  };
                  
                  viewpointItems.push(finalItem);
                }
                
                return viewpointItems;
              }
              return null;
            } catch (error) {
              console.warn('⚠️ 獲取影片詳情失敗:', watchRecord.videoId, error);
              return null;
            }
          });
          
          // 等待當前批次完成
          const batchResults = await Promise.all(batchPromises);
          const validResults = batchResults.filter(result => result !== null);
          
          // 🔧 修改：處理可能包含多個觀點的結果
          validResults.forEach(result => {
            if (Array.isArray(result)) {
              // 如果結果是數組（多個觀點），展開添加到 videosData
              videosData.push(...result);
            } else {
              // 如果結果是單個項目，直接添加
              videosData.push(result);
            }
          });
          
          // 每處理完一批，更新 UI 顯示進度
          if (videosData.length > 0) {
            // 🔧 移除詳細日誌以減少控制台輸出
            // 调试日志已移除
            // 更新狀態以顯示已載入的影片
            setPersonalHistoryVideos([...videosData]);
            
            // 添加小延遲，讓用戶看到載入進度
            if (i + BATCH_SIZE < userWatchHistory.length) {
              await new Promise(resolve => setTimeout(resolve, 100));
            }
          }
        }
        
        // 按用戶的 lastOpenDate 排序
        videosData.sort((a, b) => {
          const aDate = a.lastOpenDate || a.addedDate || a.createdAt || 0;
          const bDate = b.lastOpenDate || b.addedDate || b.createdAt || 0;
          return new Date(bDate).getTime() - new Date(aDate).getTime();
        });
        
        // 🔧 修復：去重邏輯 - 使用 firestoreId（包含版本 + 用戶ID）作為唯一鍵
        // 只有完全相同的 firestoreId 才會被去重，不同版本或不同收藏者視為不同影片
        const uniqueVideosMap = new Map<string, VideoItem>();
        videosData.forEach(video => {
          const key = video.id; // 使用 firestoreId (例如: userId_videoId_v1) 作為去重鍵
          const existingVideo = uniqueVideosMap.get(key);
          if (!existingVideo) {
            uniqueVideosMap.set(key, video);
          } else {
            // 如果同一個 firestoreId 重複出現（理論上不應該），保留較新的
            const existingDate = new Date(existingVideo.lastOpenDate || 0).getTime();
            const currentDate = new Date(video.lastOpenDate || 0).getTime();
            if (currentDate > existingDate) {
              uniqueVideosMap.set(key, video);  // ✅ 保留最新的記錄
            }
          }
        });
        
        const deduplicatedVideos = Array.from(uniqueVideosMap.values());
        // 调试日志已移除

        
        // 再次排序（去重後可能順序改變）
        deduplicatedVideos.sort((a, b) => {
          const aDate = a.lastOpenDate || a.addedDate || a.createdAt || 0;
          const bDate = b.lastOpenDate || b.addedDate || b.createdAt || 0;
          return new Date(bDate).getTime() - new Date(aDate).getTime();
        });
        
        let watchHistoryVideos = deduplicatedVideos.filter(v => !Array.isArray(v.hiddenFor) || !v.hiddenFor.includes(userId));
        
        // 调试日志已移除
        
        // 🔧 新增：如果沒有個人觀看記錄，載入社群影片作為回退
        if (watchHistoryVideos.length === 0) {
          // 数据统计日志已移除
          
          try {
            // 查詢社群影片（與 NotesPage 相同的邏輯）
            let communityQuery;
            try {
              communityQuery = query(
                safeCollection("sharedVbids"),
                where("zones", "array-contains", "community"),
                orderBy("lastOpenDate", "desc"),
                limit(50) // 限制載入數量以提升性能
              );
            } catch (error) {
              // 配置日志已移除
              communityQuery = query(
                safeCollection("sharedVbids"),
                where("zones", "array-contains", "community"),
                orderBy("addedDate", "desc"),
                limit(50)
              );
            }
            
            const communitySnapshot = await getDocs(communityQuery);
            const communityVideos = communitySnapshot.docs.map(doc => ({ 
              id: doc.id, 
              ...(doc.data() as any)
            } as VideoItem));
            
            // 调试日志已移除
            
            // 載入關注度數據
            const communityVideosWithAttention = await Promise.all(communityVideos.map(async (item) => {
              const attentionCount = await getAttentionCount(item.videoId || '');
              return {
                ...item,
                globalCheckpointCount: item.checkpointCount || 0,
                checkpointCount: item.checkpointCount || 0,
                attentionCount
              };
            }));
            
            // 使用社群影片作為觀看記錄
            watchHistoryVideos = communityVideosWithAttention;
            // 调试日志已移除
            
          } catch (error) {
            console.warn('⚠️ 載入社群影片失敗:', error);
          }
        }
        
        // 🔧 修復：月份篩選現在在 Firestore 層面進行，不需要客戶端篩選
        // 如果指定了月份篩選，記錄日誌
        if (monthFilter && monthFilter !== 'all') {
          // 调试日志已移除
          setIsFirestoreMonthFiltered(true);
        } else {
          setIsFirestoreMonthFiltered(false);
        }
        
        // 🔧 新增：批量查詢用戶資訊
        const userIds = new Set();
        watchHistoryVideos.forEach(video => {
          if (video.userId) {
            userIds.add(video.userId);
          }
        });
        
        const userInfoMap = new Map();
        // 调试日志已移除
        
        for (const userId of userIds) {
          try {
            const displayName = await getUserDisplayName(userId as string);
            if (displayName) {
              userInfoMap.set(userId, {
                displayName: displayName,
                name: displayName
              });
            }
          } catch (error) {
            console.warn(`查詢用戶 ${userId} 資訊失敗:`, error);
          }
        }
        
        // 載入關注度數據並添加用戶資訊
        const videosWithAttention = await Promise.all(watchHistoryVideos.map(async (item) => {
          const attentionCount = await getAttentionCount(item.videoId || '');
          const userInfo = userInfoMap.get(item.userId) || {};
          return {
            ...item,
            globalCheckpointCount: item.checkpointCount || 0,
            // 🔧 新增：添加創建者資訊
            creatorName: userInfo.displayName || userInfo.name || '',
            creatorEmail: item.creatorEmail || '',
            checkpointCount: item.checkpointCount || 0,
            attentionCount
          };
        }));
        
        // 🚀 性能優化：不再查詢 srtCache，直接使用影片數據中的 hasSRTCache 字段
        const videosWithSRTCheck = videosWithAttention;
        const aiTagCount = videosWithSRTCheck.filter((v: any) => v.hasSRTCache).length;
        // 调试日志已移除
        
        // 更新狀態（使用檢查後的影片列表）
        setPersonalHistoryVideos(videosWithSRTCheck);
        setPersonalLoadedCount(videosWithSRTCheck.length);
        
        // 更新快取 - 使用與檢查一致的快取 key
        const finalCacheKey = `personalHistory_${userId}_${monthFilter || 'all'}`;
        setPersonalHistoryCache(prev => ({ ...prev, [finalCacheKey]: videosWithSRTCheck }));
        setPersonalHistoryCacheTimestamps(prev => ({ ...prev, [finalCacheKey]: Date.now() }));
        
        setLoading(false);
        
        // 🔧 修復：清除載入標記
        (window as any)[`userWatchHistory_${userId}_${monthFilter || 'all'}`] = false;
        delete (window as any)[`userWatchHistory_${userId}_${monthFilter || 'all'}_startTime`];
        return;
        
      } catch (error) {
        console.warn('⚠️ 用戶觀看記錄服務不可用，回退到原始查詢:', error);
        // 🔧 修復：清除載入標記
        (window as any)[`userWatchHistory_${userId}_${monthFilter || 'all'}`] = false;
        delete (window as any)[`userWatchHistory_${userId}_${monthFilter || 'all'}_startTime`];
      }
      
      // 🔧 回退方案：使用混合查詢策略
      const loadSize = batchMode ? BATCH_SIZE : INITIAL_LOAD_SIZE;
      
      // 查询日志已移除
      
      // 檢查 Firestore 是否已初始化
      const sharedVbidsCollection = safeCollection("sharedVbids");
      if (!sharedVbidsCollection) {
        console.warn('⚠️ Firestore 未初始化，跳過載入觀看記錄');
        return;
      }
      
      // 查詢1：用戶自己建立的影片（有 lastOpenDate 的）
      const userVideosQuery = query(
        sharedVbidsCollection,
        where("userId", "==", userId),
        where("lastOpenDate", "!=", null),
        orderBy("lastOpenDate", "desc"),
        limit(loadSize)
      );
      
      // 🔧 修復：只查詢用戶自己的影片，不再查詢 readUsers（避免混合其他用戶的觀看記錄）
      const userVideosSnapshot = await getDocs(userVideosQuery).catch(error => {
        console.warn('⚠️ 用戶影片查詢失敗:', error.message);
        return { docs: [] };
      });
      
      // 调试日志已移除
      
      // 只使用用戶自己的影片查詢結果
      const allDocs = userVideosSnapshot.docs;
      
      // 去重（基於文檔 ID）
      const uniqueDocs = new Map();
      allDocs.forEach(doc => {
        uniqueDocs.set(doc.id, doc);
      });
      
      // 调试日志已移除
      
      // 轉換數據格式
      let watchHistoryVideos = Array.from(uniqueDocs.values())
        .map(doc => ({ 
          id: doc.id, 
          ...(doc.data() as any),
          // 優先使用 lastReadAt，其次 lastOpenDate
          lastOpenDate: doc.data().lastReadAt || doc.data().lastOpenDate || doc.data().addedDate || doc.data().createdAt || new Date().toISOString()
        } as VideoItem))
        .filter(v => !Array.isArray(v.hiddenFor) || !v.hiddenFor.includes(userId));
      
      // 如果指定了月份篩選，進行客戶端篩選
      if (monthFilter && monthFilter !== 'all') {
        const [year, month] = monthFilter.split('-');
        const startDate = new Date(parseInt(year), parseInt(month) - 1, 1);
        const endDate = new Date(parseInt(year), parseInt(month), 0, 23, 59, 59, 999);
        
        console.log(`🔍 月份篩選範圍檢查:`, {
          monthFilter,
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
          篩選前影片數量: watchHistoryVideos.length
        });
        
        console.log(`🔧 篩選前影片清單前5個:`, watchHistoryVideos.slice(0, 5).map(v => ({
          title: v.title?.slice(0, 30),
          publishedAt: v.youtubeInfo?.publishedAt,
          addedDate: v.addedDate
        })));
        
        const filteredVideos = watchHistoryVideos.filter(video => {
          // 🔧 修正：月份篩選應該根據瀏覽時間（lastOpenDate）進行篩選
          let refDateStr = video.lastOpenDate || video.addedDate || video.createdAt || '';
          
          // 🔧 修復：處理 Firestore Timestamp 物件，並檢查 null
          if (refDateStr && refDateStr !== null && typeof refDateStr === 'object' && 'seconds' in refDateStr) {
            // 這是 Firestore Timestamp 物件，轉換為 ISO 字串
            try {
              const date = new Date((refDateStr as any).seconds * 1000);
              refDateStr = isNaN(date.getTime()) ? new Date().toISOString() : date.toISOString();
            } catch (error) {
              refDateStr = new Date().toISOString();
            }
          } else if (refDateStr && refDateStr !== null && typeof refDateStr === 'object' && 'toDate' in refDateStr) {
            // 這是 Firestore Timestamp 物件，使用 toDate() 方法
            try {
              const date = (refDateStr as any).toDate();
              refDateStr = isNaN(date.getTime()) ? new Date().toISOString() : date.toISOString();
            } catch (error) {
              refDateStr = new Date().toISOString();
            }
          } else if (!refDateStr || refDateStr === '' || refDateStr === null) {
            // 無任何可靠日期 → 不將其列入篩選結果
            if (watchHistoryVideos.indexOf(video) < 5) {
              // 调试日志已移除
            }
            return false;
          }
          
          const refDate = new Date(refDateStr);
          if (isNaN(refDate.getTime())) {
            if (watchHistoryVideos.indexOf(video) < 5) {
              // 调试日志已移除
            }
            return false;
          }
          
          // 🔧 關鍵修正：使用原始日期範圍進行嚴格篩選
          const isInRange = refDate >= startDate && refDate <= endDate;
          
          // 增強調試日誌(僅前3個影片)
          if (watchHistoryVideos.indexOf(video) < 3) {
            console.log(`🔧 月份 {${monthFilter}} 篩選計算 (按瀏覽時間):`, {
              title: video.title?.slice(0, 30),
              browseDateString: refDateStr,
              browseDateIso: refDate.toISOString(),
              startDateIso: startDate.toISOString(),
              endDateIso: endDate.toISOString(),
              inRange: isInRange,
              dateComparison: `browseDate(${refDate.toISOString()}) >= startDate(${startDate.toISOString()}) && browseDate(${refDate.toISOString()}) <= endDate(${endDate.toISOString()})`,
              dateComparisonResult: `browseDate >= startDate: ${refDate >= startDate}, browseDate <= endDate: ${refDate <= endDate}`,
              lastOpenDate: video.lastOpenDate,
              YouTubePublished: video.youtubeInfo?.publishedAt,
              AddedDate: video.addedDate
            });
          }
          
          // 🔧 正確的調試：記錄篩選決定
          if (watchHistoryVideos.indexOf(video) < 3) {
            if (isInRange) {
              console.log(`✅ 影片 "${video.title?.slice(0, 30)}" 在 ${monthFilter} 範圍內，將被包含`, {
                inputDateString: refDateStr,
                refDateIso: refDate.toISOString(),
                monthRange: `${startDate.toISOString()} 到 ${endDate.toISOString()}`
              });
            } else {
              console.log(`❌ 影片 "${video.title?.slice(0, 30)}" 不在 ${monthFilter} 範圍內，將被排除`, {
                inputDateString: refDateStr,
                refDateIso: refDate.toISOString(),
                monthRange: `${startDate.toISOString()} 到 ${endDate.toISOString()}`
              });
            }
          }
          
          return isInRange;
        });
        
        watchHistoryVideos = filteredVideos;
        // 调试日志已移除
        
        console.log(`🔧 篩選後影片清單前5個:`, watchHistoryVideos.slice(0, 5).map(v => ({
          title: v.title?.slice(0, 30),
          publishedAt: v.youtubeInfo?.publishedAt,
          addedDate: v.addedDate,
          publishedMonth: v.youtubeInfo?.publishedAt ? new Date(v.youtubeInfo.publishedAt).toISOString().slice(0, 7) : 'N/A'
        })));
      }
      
      // 按觀看時間排序（最新的在前）
      watchHistoryVideos.sort((a, b) => {
        const dateA = new Date(a.lastOpenDate || a.addedDate || a.createdAt || '');
        const dateB = new Date(b.lastOpenDate || b.addedDate || b.createdAt || '');
        return dateB.getTime() - dateA.getTime();
      });
      
      // 调试日志已移除
      
      // 🚀 性能優化：不再查詢 srtCache，直接使用影片數據中的 hasSRTCache 字段
      const videosWithSRTCheck = watchHistoryVideos;
      const aiTagCount = videosWithSRTCheck.filter((v: any) => v.hasSRTCache).length;
      // 调试日志已移除
      
      // 更新狀態和快取 - 批次模式下追加，否則覆蓋
      if (batchMode) {
        setPersonalHistoryVideos(prevVideos => {
          // 合併並去重（避免重複的影片）
          const existingVideoIds = new Set(prevVideos.map(v => v.id));
          const newVideos = videosWithSRTCheck.filter(v => !existingVideoIds.has(v.id));
          const combinedVideos = [...prevVideos, ...newVideos];
          // 调试日志已移除
          return combinedVideos;
        });
        
        // 更新快取（批次模式）
        setPersonalHistoryCache(prev => {
          const existingCache = prev[cacheKey] || [];
          const existingVideoIds = new Set(existingCache.map(v => v.id));
          const newVideos = videosWithSRTCheck.filter(v => !existingVideoIds.has(v.id));
          const combinedCache = [...existingCache, ...newVideos];
          return { ...prev, [cacheKey]: combinedCache };
        });
      } else {
        setPersonalHistoryVideos(videosWithSRTCheck);
        // 更新快取（非批次模式）
        setPersonalHistoryCache(prev => ({ ...prev, [cacheKey]: videosWithSRTCheck }));
      }
      
      setPersonalHistoryCacheTimestamps(prev => ({ ...prev, [cacheKey]: Date.now() }));
      setLoading(false);
      
    } catch (error) {
      console.error('❌ 載入觀看記錄失敗:', error);
      setLoading(false);
    }
  }, [personalHistoryCache, personalHistoryCacheTimestamps, PERSONAL_HISTORY_CACHE_EXPIRY_TIME, BATCH_SIZE, INITIAL_LOAD_SIZE]);

  // 🔧 調試：檢查用戶觀看記錄
  const debugWatchHistory = useCallback(async () => {
    if (!authUser?.uid) {
      alert('請先登入');
      return;
    }
    
    try {
      // 查询日志已移除
      
      // 檢查用戶觀看記錄
      const { getUserWatchHistory } = await import('../services/userWatchHistoryService');
      const watchHistory = await getUserWatchHistory(authUser.uid, 100);
      
      console.log('📊 用戶觀看記錄:', {
        total: watchHistory.length,
        records: watchHistory.slice(0, 5).map(record => ({
          videoId: record.videoId,
          lastOpenDate: record.lastOpenDate,
          watchCount: record.watchCount
        }))
      });
      
      // 檢查特定影片的觀看記錄
      const testVideoId = '4wPnqGZdB90'; // 金鐘獎影片ID
      const { getUserVideoWatchRecord } = await import('../services/userWatchHistoryService');
      const specificRecord = await getUserVideoWatchRecord(authUser.uid, testVideoId);
      
      console.log('🎬 特定影片觀看記錄:', {
        videoId: testVideoId,
        hasRecord: !!specificRecord,
        record: specificRecord
      });
      
      // 檢查 sharedVbids 中的影片
      const sharedVbidsQuery = query(
        safeCollection("sharedVbids"),
        where("videoId", "==", testVideoId)
      );
      const sharedVbidsSnapshot = await getDocs(sharedVbidsQuery);
      
      console.log('📹 sharedVbids 中的影片:', {
        videoId: testVideoId,
        totalDocs: sharedVbidsSnapshot.size,
        docs: sharedVbidsSnapshot.docs.map(doc => ({
          id: doc.id,
          userId: doc.data().userId,
          title: doc.data().title,
          lastOpenDate: doc.data().lastOpenDate
        }))
      });
      
      // 🔧 新增：檢查前5筆瀏覽記錄的詳細信息
      // 查询日志已移除
      const first5Videos = personalHistoryVideos.slice(0, 5);
      first5Videos.forEach((video, index) => {
        console.log(`📹 第${index + 1}筆:`, {
          title: video.title?.substring(0, 30),
          videoId: video.videoId,
          lastOpenDate: video.lastOpenDate,
          addedDate: video.addedDate,
          createdAt: video.createdAt,
          userId: video.userId,
          zones: video.zones,
          lastReadAt: video.lastReadAt
        });
      });
      
      // 🔧 新增：檢查 sharedVbids 中的 lastOpenDate 設置
      // 查询日志已移除
      const debugSharedVbidsQuery = query(
        safeCollection("sharedVbids"),
        where("userId", "==", authUser.uid),
        orderBy("lastOpenDate", "desc"),
        limit(5)
      );
      const debugSharedVbidsSnapshot = await getDocs(debugSharedVbidsQuery);
      
      // 数据统计日志已移除
      debugSharedVbidsSnapshot.docs.forEach((doc, index) => {
        const data = doc.data();
        console.log(`📹 sharedVbids 第${index + 1}筆:`, {
          id: doc.id,
          title: data.title?.substring(0, 30),
          videoId: data.videoId,
          lastOpenDate: data.lastOpenDate,
          addedDate: data.addedDate,
          createdAt: data.createdAt,
          userId: data.userId,
          zones: data.zones,
          lastReadAt: data.lastReadAt
        });
      });
      
      // 🔧 新增：檢查 userWatchHistory 集合的真實數據
      // 查询日志已移除
      const userWatchHistoryQuery = query(
        safeCollection("userWatchHistory"),
        where("userId", "==", authUser.uid),
        orderBy("lastOpenDate", "desc"),
        limit(10)
      );
      const userWatchHistorySnapshot = await getDocs(userWatchHistoryQuery);
      
      // 数据统计日志已移除
      userWatchHistorySnapshot.docs.forEach((doc, index) => {
        const data = doc.data();
        console.log(`📹 userWatchHistory 第${index + 1}筆:`, {
          id: doc.id,
          userId: data.userId,
          videoId: data.videoId,
          lastOpenDate: data.lastOpenDate,
          watchCount: data.watchCount,
          createdAt: data.createdAt,
          updatedAt: data.updatedAt
        });
      });
      
      // 🔧 新增：檢查 sharedVbids 中是否有錯誤的 lastOpenDate
      // 查询日志已移除
      const allSharedVbidsQuery = query(
        safeCollection("sharedVbids"),
        where("userId", "==", authUser.uid),
        orderBy("lastOpenDate", "desc"),
        limit(10)
      );
      const allSharedVbidsSnapshot = await getDocs(allSharedVbidsQuery);
      
      // 数据统计日志已移除
      allSharedVbidsSnapshot.docs.forEach((doc, index) => {
        const data = doc.data();
        console.log(`📹 sharedVbids 第${index + 1}筆:`, {
          id: doc.id,
          title: data.title?.substring(0, 30),
          videoId: data.videoId,
          lastOpenDate: data.lastOpenDate,
          addedDate: data.addedDate,
          createdAt: data.createdAt,
          userId: data.userId,
          zones: data.zones
        });
      });
      
      alert(`調試完成！請查看控制台輸出。\n觀看記錄總數: ${watchHistory.length}\n特定影片記錄: ${specificRecord ? '有' : '無'}\nuserWatchHistory: ${userWatchHistorySnapshot.size}筆\nsharedVbids: ${allSharedVbidsSnapshot.size}筆`);
      
    } catch (error) {
      console.error('❌ 調試失敗:', error);
      alert('調試失敗：' + error.message);
    }
  }, [authUser?.uid, personalHistoryVideos]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (currentSection !== 'personalHistory') return;
    const key = `refreshPersonalHistory_${userId || 'guest'}`;
    if (sessionStorage.getItem(key) === 'true') {
      sessionStorage.removeItem(key);
      loadWatchHistory(userId, true);
    }
  }, [currentSection, userId, loadWatchHistory]);

  const loadVideos = useCallback(async (section: string, forceRefresh = false, batchMode = false, monthFilter?: string) => {
    // ✅ 載入鎖由 useEffect 層面管理，這裡不需要檢查
    
    // 🔧 修復：允許訪客用戶訪問的分頁（不需要等待認證完成）
    const guestAccessibleSections = ['communityPublic', 'expertKnowledge', 'follow'];
    
    if (authLoading && !guestAccessibleSections.includes(section)) {
      // 调试日志已移除
      return;
    }
    
    // 🔧 修復：訪客可訪問的分頁允許在 userId 為 'guest' 時載入
    if (!userId && !guestAccessibleSections.includes(section)) {
      // 调试日志已移除
      return;
    }
    
    // 🔧 修復：強制刷新時清理快取
    if (forceRefresh) {
      // 调试日志已移除
      clearCache(section);
    }
    
    // 🔧 修復：對於社群公開影片，如果快取為空陣列，強制重新載入
    if (section === 'communityPublic') {
      const communityCacheKey = `communityPublic_${userId}`;
      const cachedVideos = videosCache[communityCacheKey];
      const isEmptyCache = Array.isArray(cachedVideos) && cachedVideos.length === 0;
      if (isEmptyCache) {
        // 同步日志已移除
        clearCache(section);
      }
    }
    
    // 特殊處理：localFiles 分頁不需要載入 Firestore 數據
    if (section === 'localFiles') {
      // 查询日志已移除
      setLoading(false);
      return;
    }
    
    // 分階段載入邏輯
      const cacheKey = `${section}_${userId}`;
      const now = Date.now();
      const cacheTimestamp = cacheTimestamps[cacheKey];
      const isCacheValid = cacheTimestamp && (now - cacheTimestamp) < CACHE_EXPIRY_TIME;
    const currentLoadedCount = loadedCounts[section] || 0;
      
    // 如果是批次模式且已經載入過，則載入更多
    if (batchMode && currentLoadedCount > 0) {
      // 调试日志已移除
    } else if (!batchMode) {
      // 非批次模式：檢查快取
      if (!forceRefresh && videosCache[cacheKey] && isCacheValid) {
        const cachedData = videosCache[cacheKey];
        
        // 🔧 關鍵修復：檢查快取是否為空，空快取視為無效
        if (!Array.isArray(cachedData) || cachedData.length === 0) {
          // 调试日志已移除
        } else {
          // 调试日志已移除
          
          // 🔧 關鍵修復：為不同分頁設置正確的 state
          if (section === 'communityPublic') {
            setCommunityPublicVideos(cachedData);
            setCommunityPublicHasMore(cachedData.length > communityPublicLoadedCount);
            // 🔧 重要：也設置 communityVideos，因為 expertKnowledge 依賴它
            setCommunityVideos(cachedData);
            setCommunityLoadedCount(cachedData.length);
          } else if (section === 'expertKnowledge') {
            // expertKnowledge 專用設置
            setVideos(cachedData);
            setCommunityVideos(cachedData);
            setCommunityLoadedCount(cachedData.length);
          } else {
            setVideos(cachedData);
            setPersonalVideos(cachedData.filter(v => v.userId === userId));
            const communityVideosFromCache = cachedData.filter(v => v.zones?.includes('community'));
            setCommunityVideos(communityVideosFromCache);
            setCommunityLoadedCount(communityVideosFromCache.length);
          }
          
          setLoading(false);
          return;
        }
      }
    }
    
    // 設定載入狀態
    if (!batchMode) {
      setLoading(true);
      setLoadedCounts(prev => ({ ...prev, [section]: 0 }));
      setHasMoreData(prev => ({ ...prev, [section]: true }));
    } else {
      setLoadingBatches(prev => new Set([...prev, section]));
    }
    
    // follow 分頁總是重新載入，不使用快取
    if (section === 'follow') {
      // 调试日志已移除
      } else if (videosCache[cacheKey] && !isCacheValid) {
        // 调试日志已移除
      } else if (!videosCache[cacheKey]) {
        // 调试日志已移除
      }

      try {
        let communityVideos: VideoItem[] = [];
        
        // 增加 Firebase 連接檢查，等待初始化完成
        const db = getDb();
        if (!db) {
          console.warn('⚠️ Firestore 尚未初始化，等待初始化完成...');
          // 等待 Firebase 初始化完成
          await new Promise(resolve => setTimeout(resolve, 1000));
          const retryDb = getDb();
          if (!retryDb) {
            console.error('❌ Firestore 初始化超時');
            setSnackbarMessage('資料庫連接失敗，請重新整理頁面');
            setSnackbarOpen(true);
            setLoading(false);
            return;
          }
        }

        // 優化：根據分頁類型選擇性查詢，支援分階段載入
        if (section === 'personal') {
          // 调试日志已移除
          let q;
          const loadSize = batchMode ? BATCH_SIZE : INITIAL_LOAD_SIZE;
          
          if (userId.startsWith('guest_')) {
            // 未登入時，查詢公開影片
            q = query(
              safeCollection( "sharedVbids"),
              where("privacy", "==", "Public"),
              orderBy("addedDate", "desc"),
              limit(loadSize)
            );
          } else {
            // 已登入查詢自己的影片
            q = query(
            safeCollection( "sharedVbids"),
            where("userId", "==", userId),
              orderBy("addedDate", "desc"),
              limit(loadSize)
          );
          }
          const querySnapshot = await getDocs(q);
          const data: VideoItem[] = querySnapshot.docs.map(doc => ({
               id: doc.id,
            documentId: doc.id, // 設置 documentId 為 doc.id
            ...(doc.data() as any),
            mediaMode: (doc.data() as any).mediaMode || 'YouTube',
            flag: (doc.data() as any).flag || 'active'
          } as VideoItem));
          
          // 載入關注度數據
          const videosWithAttention = await Promise.all(data.map(async (item) => {
            // 使用正確的 videoId：優先使用 item.videoId，如果沒有則從 path 提取
            let videoId = item.videoId;
            if (!videoId && item.path) {
              // 🔧 使用統一的 extractYouTubeId 函數（已從 vbidIO.ts 導入）
              videoId = extractYouTubeId(item.path) || item.path;
            }
            
            let attentionCount = 0;
            if (videoId) {
              attentionCount = await getAttentionCount(videoId);
              // // 查询日志已移除
            }
            const result = {
              ...item,
              videoId: videoId || item.videoId, // 🔧 修復：確保 videoId 字段被正確設置
              globalCheckpointCount: item.checkpointCount || 0,
              checkpointCount: item.checkpointCount || 0,
              attentionCount
            };
            // // 查询日志已移除
            return result;
          }));
          
          setUserVideos(videosWithAttention);
          
          // 設置 updatedData 用於分階段載入處理
          const updatedData = videosWithAttention;
          
        } else if (section === 'myVideos') {
          // 调试日志已移除
          
          // 使用新的 API 端點查詢用戶的創作影片
          const response = await fetch(`/api/my-videos?userId=${userId}`);
          const result = await response.json();
          
          if (!result.success) {
            console.error('❌ 查詢我的創作影片失敗:', result.error);
            setLoading(false);
            return;
          }
          
          const myVideosList = result.videos || [];
          // 调试日志已移除
          
          // 🔧 性能優化：跳過關注度載入，改為延遲載入（只在需要時才載入）
          // 關注度數據不影響列表顯示，可以在用戶滾動到該影片時再載入
          // 调试日志已移除
          
          const videosWithAttention = myVideosList.map((item: any) => ({
            ...item,
            globalCheckpointCount: item.checkpointCount || 0,
            checkpointCount: item.checkpointCount || 0,
            attentionCount: 0  // 預設為 0，稍後按需載入
          }));
          
          // 🚀 性能優化：不再查詢 srtCache，直接使用影片數據中的 hasSRTCache 字段
          // 字幕緩存時會自動更新此字段（參見 srtCacheService.ts）
          const videosWithSRTCheck = videosWithAttention;
          const aiTagCount = videosWithSRTCheck.filter((v: any) => v.hasSRTCache).length;
          // 调试日志已移除
          
          // 🔧 調試：檢查數據一致性
          // 查询日志已移除;
          
          setUserVideos(videosWithSRTCheck);
          
          // 設置其他必要的狀態，確保影片能正確顯示
          setVideos(videosWithSRTCheck);
          setPersonalVideos(videosWithSRTCheck);
          setCommunityVideos([]);
          setCommunityLoadedCount(0);
          
          // 🔧 強制清除快取，避免數據不一致
          const cacheKey = `${section}_${userId}`;
          setVideosCache(prev => {
            const newCache = { ...prev };
            delete newCache[cacheKey];
            return { ...newCache, [cacheKey]: videosWithSRTCheck };
          });
          setCacheTimestamps(prev => {
            const newTimestamps = { ...prev };
            delete newTimestamps[cacheKey];
            return { ...newTimestamps, [cacheKey]: Date.now() };
          });
          
          console.log(`✅ 已載入 ${section} 影片：`, {
            total: videosWithSRTCheck.length,
            personal: videosWithSRTCheck.filter(v => v.userId === userId).length,
            community: 0,
            withAITag: aiTagCount
          });
          
          // 🔧 修復：myVideos 處理完成後應該立即返回，避免繼續執行後面的邏輯
          setLoading(false);
          return;
          
        } else if (section === 'myPersonalFavorite') {
          // 调试日志已移除
          
          // 🔧 修復：改用 userFavorites collection 來查詢用戶珍藏的影片
          // 而不是依賴 zones 欄位（zones 是全域的，不是用戶專屬的）
          const userFavoritesCollection = safeCollection("userFavorites");
          if (!userFavoritesCollection) {
            console.warn('⚠️ Firestore 未初始化，跳過載入個人珍藏影片');
            setLoading(false);
            return;
          }
          
          // 查詢當前用戶的所有珍藏記錄
          const favoritesQuery = query(
            userFavoritesCollection,
            where("userId", "==", userId)
          );
          const favoritesSnapshot = await getDocs(favoritesQuery);
          
          // 调试日志已移除
          
          // 從珍藏記錄中提取 firestoreId，然後查詢對應的影片
          const firestoreIds = favoritesSnapshot.docs.map(doc => doc.data().firestoreId).filter(Boolean);
          
          if (firestoreIds.length === 0) {
            // 数据统计日志已移除
            setUserVideos([]);
            setVideos([]);
            setPersonalVideos([]);
            setCommunityVideos([]);
            setCommunityLoadedCount(0);
            setLoading(false);
            return;
          }
          
          // 批次查詢珍藏的影片（每次最多查詢 30 個，Firestore 'in' 查詢限制）
          const sharedVbidsCollection = safeCollection("sharedVbids");
          if (!sharedVbidsCollection) {
            console.warn('⚠️ Firestore 未初始化');
            setLoading(false);
            return;
          }
          
          const batchSize = 30;
          const personalFavoriteVideos: VideoItem[] = [];
          
          for (let i = 0; i < firestoreIds.length; i += batchSize) {
            const batchIds = firestoreIds.slice(i, i + batchSize);
            const batchQuery = query(
              sharedVbidsCollection,
              where("__name__", "in", batchIds)
            );
            const batchSnapshot = await getDocs(batchQuery);
            batchSnapshot.docs.forEach(doc => {
              personalFavoriteVideos.push({ id: doc.id, ...doc.data() } as VideoItem);
            });
          }
          
          // 调试日志已移除
          
          // 🔧 注意：不在這裡更新 userFavoriteIds，因為已經在初始化 useEffect 中載入了
          // 如果在這裡更新，會與初始化載入的數據不一致（例如有些影片已被刪除）
          
          // 載入關注度數據
          const videosWithAttention = await Promise.all(personalFavoriteVideos.map(async (item) => {
            // 使用正確的 videoId：優先使用 item.videoId，如果沒有則從 path 提取
            let videoId = item.videoId;
            if (!videoId && item.path) {
              // 🔧 使用統一的 extractYouTubeId 函數（已從 vbidIO.ts 導入）
              videoId = extractYouTubeId(item.path) || item.path;
            }
            
            let attentionCount = 0;
            if (videoId) {
              attentionCount = await getAttentionCount(videoId);
            }
            return {
              ...item,
              globalCheckpointCount: item.checkpointCount || 0,
              checkpointCount: item.checkpointCount || 0,
              attentionCount
            };
          }));
          
          setUserVideos(videosWithAttention);
          
          // 設置其他必要的狀態，確保影片能正確顯示
          setVideos(videosWithAttention);
          setPersonalVideos(videosWithAttention);
          setCommunityVideos([]);
          setCommunityLoadedCount(0);
          
          // 設置快取
          const cacheKey = `${section}_${userId}`;
          setVideosCache(prev => ({ ...prev, [cacheKey]: videosWithAttention }));
          setCacheTimestamps(prev => ({ ...prev, [cacheKey]: Date.now() }));
          
          console.log(`✅ 已載入 ${section} 影片：`, {
            total: videosWithAttention.length,
            favoritesCount: favoritesSnapshot.docs.length,
            videosFound: personalFavoriteVideos.length
          });
          
          // 🔧 修復：myPersonalFavorite 處理完成後應該立即返回
          setLoading(false);
          return;
          
        } else if (section === 'community') {
          // 🔧 修復：社群分頁使用全域快取，不按用戶分開快取
          const communityCacheKey = 'community_global';
          const communityCacheTimestamp = cacheTimestamps[communityCacheKey];
          const isCommunityCacheValid = communityCacheTimestamp && (Date.now() - communityCacheTimestamp) < CACHE_EXPIRY_TIME;
          
          // 檢查社群專用快取
          if (!forceRefresh && videosCache[communityCacheKey] && isCommunityCacheValid) {
            // 调试日志已移除
            const cachedCommunityVideos = videosCache[communityCacheKey];
            setCommunityVideos(cachedCommunityVideos);
            setCommunityLoadedCount(cachedCommunityVideos.length);
            setLoading(false);
            return;
          }
          
          // 查询日志已移除
          let q;
          try {
            q = query(
              safeCollection( "sharedVbids"),
              where("zones", "array-contains", "community"),
              orderBy("lastOpenDate", "desc")
            );
          } catch (error) {
            // 配置日志已移除
            q = query(
              safeCollection( "sharedVbids"),
              where("zones", "array-contains", "community"),
              orderBy("addedDate", "desc")
            );
          }
          const querySnapshot = await getDocs(q);
          const data: VideoItem[] = querySnapshot.docs.map(doc => ({
            id: doc.id,
            documentId: doc.id, // 設置 documentId 為 doc.id
            ...(doc.data() as any),
            mediaMode: (doc.data() as any).mediaMode || 'YouTube',
            flag: (doc.data() as any).flag || 'active'
          } as VideoItem));
          
          // 載入關注度數據
          const videosWithAttention = await Promise.all(data.map(async (item) => {
            // 使用正確的 videoId：優先使用 item.videoId，如果沒有則從 path 提取
            let videoId = item.videoId;
            if (!videoId && item.path) {
              // 🔧 使用統一的 extractYouTubeId 函數（已從 vbidIO.ts 導入）
              videoId = extractYouTubeId(item.path) || item.path;
            }
            
            let attentionCount = 0;
            if (videoId) {
              attentionCount = await getAttentionCount(videoId);
            }
            return {
              ...item,
              globalCheckpointCount: item.checkpointCount || 0,
              checkpointCount: item.checkpointCount || 0,
              attentionCount
            };
          }));
          
          communityVideos = videosWithAttention;
          setCommunityLoadedCount(videosWithAttention.length); // 設定為顯示所有社群影片
          
          // 🔧 修復：設置社群專用快取，所有用戶共享
          setVideosCache(prev => ({ ...prev, [communityCacheKey]: videosWithAttention }));
          setCacheTimestamps(prev => ({ ...prev, [communityCacheKey]: Date.now() }));
          
          // 🔧 修復：community 處理完成後應該立即返回
          setLoading(false);
          return;
          
        } else if (section === 'myCommunityPublic') {
          // 查询日志已移除
          
          // 檢查 Firestore 是否已初始化
          const sharedVbidsCollection = safeCollection("sharedVbids");
          if (!sharedVbidsCollection) {
            console.warn('⚠️ Firestore 未初始化，跳過載入我的貢獻影片');
            setLoading(false);
            return;
          }
          
          const q = query(
            sharedVbidsCollection,
            where("zones", "array-contains", "community"),
            orderBy("lastOpenDate", "desc")
          );
          const querySnapshot = await getDocs(q);
          const data: VideoItem[] = querySnapshot.docs.map(doc => ({ 
            id: doc.id, 
            ...doc.data() 
          } as VideoItem));
          
          console.log('📊 查詢結果:', {
            totalDocs: querySnapshot.docs.length,
            dataLength: data.length,
            sampleData: data.slice(0, 3).map(v => ({ 
              id: v.id, 
              userId: v.userId, 
              zones: v.zones,
              title: v.title 
            }))
          });
          
          // 載入關注度數據
          const videosWithAttention = await Promise.all(data.map(async (item) => {
            // 使用正確的 videoId：優先使用 item.videoId，如果沒有則從 path 提取
            let videoId = item.videoId;
            if (!videoId && item.path) {
              // 🔧 使用統一的 extractYouTubeId 函數（已從 vbidIO.ts 導入）
              videoId = extractYouTubeId(item.path) || item.path;
            }
            
            let attentionCount = 0;
            if (videoId) {
              attentionCount = await getAttentionCount(videoId);
            }
            return {
              ...item,
              globalCheckpointCount: item.checkpointCount || 0,
              checkpointCount: item.checkpointCount || 0,
              attentionCount
            };
          }));
          
          // 篩選出當前用戶的社群影片
          const myCommunityVideos = videosWithAttention.filter(v => v.userId === userId && v.zones?.includes('community'));
          // 社群影片顯示所有用戶的社群影片，不只是當前用戶的
          const allCommunityVideos = videosWithAttention.filter(v => v.zones?.includes('community'));
          setVideos(myCommunityVideos);
          setPersonalVideos(myCommunityVideos);
          setCommunityVideos(allCommunityVideos);
          setCommunityLoadedCount(allCommunityVideos.length); // 設定為顯示所有社群影片
          setVideosCache(prev => ({ ...prev, [cacheKey]: myCommunityVideos }));
          // 调试日志已移除
          setLoading(false);
          return;
        } else if (section === 'communityPublic') {
          // 檢查快取
          const communityCacheKey = `communityPublic_${userId}`;
          const communityCacheTimestamp = cacheTimestamps[communityCacheKey];
          const isCommunityCacheValid = communityCacheTimestamp && (now - communityCacheTimestamp) < CACHE_EXPIRY_TIME;
          
          // 🔧 修復：如果快取為空陣列，強制重新載入
          const cachedVideos = videosCache[communityCacheKey];
          const isEmptyCache = Array.isArray(cachedVideos) && cachedVideos.length === 0;
          
          if (!forceRefresh && cachedVideos && isCommunityCacheValid && !isEmptyCache) {
            // 调试日志已移除
            setCommunityPublicVideos(cachedVideos);
            setCommunityPublicHasMore(cachedVideos.length > communityPublicLoadedCount);
            setLoading(false);
            return;
          }
          
          // 🔧 修復：如果快取為空，記錄並強制重新載入
          if (isEmptyCache) {
            // 同步日志已移除
          }
          
          // 查询日志已移除
          
          try {
            // 🔧 關鍵修復：使用完整的後端 URL，而不是相對路徑
            const apiBaseUrl = (window as any).ENV?.VITE_API_BASE_URL || 
                              import.meta.env.VITE_API_BASE_URL || 
                              'http://localhost:8080';
            const apiUrl = `${apiBaseUrl}/api/community-public-videos`;
            // 查询日志已移除
            
            const response = await fetch(apiUrl);
            // 查询日志已移除
            
            const data = await response.json();
            // API 日志已移除
            
            // 處理不同的響應格式
            let allCommunityVideos = [];
            if (Array.isArray(data)) {
              // 如果直接返回數組
              allCommunityVideos = data;
              // 处理日志已移除
            } else if (data.success && Array.isArray(data.videos)) {
              // 如果返回標準格式
              allCommunityVideos = data.videos;
              // 处理日志已移除
            } else {
              console.error('❌ 未知的響應格式:', data);
              setCommunityPublicVideos([]);
              setCommunityPublicHasMore(false);
              return;
            }
            
            // 🚀 性能優化：不再查詢 srtCache，直接使用影片數據中的 hasSRTCache 字段
            const videosWithSRTCheck = allCommunityVideos;
            const aiTagCount = videosWithSRTCheck.filter(v => v.hasSRTCache).length;
            // 调试日志已移除
            
            setCommunityPublicVideos(videosWithSRTCheck);
            setCommunityPublicHasMore(videosWithSRTCheck.length > communityPublicLoadedCount);
            
            // 更新快取
            setVideosCache(prev => ({ ...prev, [communityCacheKey]: videosWithSRTCheck }));
            setCacheTimestamps(prev => ({ ...prev, [communityCacheKey]: now }));
            
            // 调试日志已移除
          } catch (error) {
            // 🔧 靜默處理網絡錯誤（頁面卸載時的請求中斷是正常情況）
            if (error instanceof TypeError && error.message.includes('fetch')) {
              console.warn('⚠️ 社群公開影片載入中斷（頁面切換或網絡問題）');
            } else {
              console.error('❌ 載入社群公開影片失敗:', error);
            }
            setCommunityPublicVideos([]);
            setCommunityPublicHasMore(false);
          }
          setLoading(false);
          return; // 🔧 修復：communityPublic 處理完成後應該立即返回
          
        } else if (section === 'teamPoint') {
          // 查询日志已移除
          
          // 檢查 Firestore 是否已初始化
          const sharedVbidsCollection = safeCollection("sharedVbids");
          if (!sharedVbidsCollection) {
            console.warn('⚠️ Firestore 未初始化，跳過載入 teamPoint 影片');
            setLoading(false);
            return;
          }
          
          // 檢查是否有快取數據
          const cacheKey = `${section}_${userId}`;
          const cachedVideos = videosCache[cacheKey];
          const cacheTimestamp = cacheTimestamps[cacheKey];
          const now = Date.now();
          const isCacheValid = cacheTimestamp && (now - cacheTimestamp) < CACHE_EXPIRY_TIME;
          
          // 🔧 修復：teamPoint 總是重新載入 numberKeys，不使用快取
          if (cachedVideos && isCacheValid && section !== 'teamPoint') {
            // 处理日志已移除
            setVideos(cachedVideos);
            setPersonalVideos(cachedVideos);
            setVideosCache(prev => ({ ...prev, [cacheKey]: cachedVideos }));
            setCacheTimestamps(prev => ({ ...prev, [cacheKey]: now }));
            // 调试日志已移除
            setLoading(false);
            return;
          }
          
          if (section === 'teamPoint') {
            // 同步日志已移除
          }
          
          // 🔧 修復：查詢用戶本人的 sharedVbids，支援 UID 和 Email 兩種格式
          // 先嘗試查詢 UID 格式
          let q = query(
            sharedVbidsCollection,
            where("userId", "==", userId), // 使用 UID 查詢
            orderBy("addedDate", "desc")
          );
      
          const querySnapshot = await getDocs(q);
          let data: VideoItem[] = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as VideoItem));
          
          // 🔧 修復：如果 UID 查詢結果為空，嘗試用 Email 查詢（支援舊資料）
          if (data.length === 0 && authUser?.email) {
            // 查询日志已移除
            const emailQuery = query(
              safeCollection( "sharedVbids"),
              where("userId", "==", authUser.email), // 使用 Email 查詢
              orderBy("addedDate", "desc")
            );
            const emailSnapshot = await getDocs(emailQuery);
            data = emailSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as VideoItem));
            // 查询日志已移除
          }
          
          // 查询日志已移除
          
          // 🔧 修復：去重處理，避免同一支影片出現多次
          const uniqueVideos = new Map<string, VideoItem>();
          data.forEach(video => {
            const key = video.videoId || video.id;
            if (key) {
              // 如果已存在，保留較新的版本（按 addedDate 比較）
              if (!uniqueVideos.has(key)) {
                uniqueVideos.set(key, video);
              } else {
                const existing = uniqueVideos.get(key);
                const existingDate = new Date(existing?.addedDate || 0).getTime();
                const currentDate = new Date(video.addedDate || 0).getTime();
                if (currentDate > existingDate) {
                  uniqueVideos.set(key, video);
                  // 调试日志已移除
                } else {
                  // 调试日志已移除
                }
              }
            }
          });
          
          const updatedData = Array.from(uniqueVideos.values());
          // 查询日志已移除

          // 🔧 修復：載入 numberKeys 數據
          // 查询日志已移除
          const videosWithNumberKeys = await Promise.all(updatedData.map(async (video) => {
            try {
              // 查詢 users 集合中的 numberKeys
              const userDocRef = safeDoc( 'users', userId);
              const userDoc = await getDoc(userDocRef);
              
              if (userDoc.exists()) {
                const userData = userDoc.data();
                const cleanVideoId = (video.videoId || video.id || '').replace(/[\/\\:*"<>|]/g, '_');
                
                console.log(`🔍 查詢影片 ${video.videoId || video.id} 的 numberKeys:`, {
                  cleanVideoId,
                  videoId: video.videoId,
                  id: video.id,
                  hasNumberKeys: !!userData.numberKeys,
                  numberKeysKeys: userData.numberKeys ? Object.keys(userData.numberKeys) : []
                });
                
                const numberKeys = userData.numberKeys?.[cleanVideoId] || 
                                 (video.videoId ? userData.numberKeys?.[video.videoId] : undefined) || 
                                 (video.id ? userData.numberKeys?.[video.id] : undefined);
                
                if (numberKeys) {
                  // 调试日志已移除
                  return { ...video, numberKeys };
                } else {
                  // 调试日志已移除
                }
              }
              
              // 如果 users 集合中沒有，嘗試從 sharedVbids 中獲取
              if (video.numberKeys) {
                // 调试日志已移除
                return video;
              }
              
              // 调试日志已移除
              return video;
            } catch (error) {
              console.error(`❌ 載入影片 ${video.videoId || video.id} 的 numberKeys 失敗:`, error);
              return video;
            }
          }));
          
          // 查询日志已移除

          // 🔧 新增：為 teamPoint 分頁計算 userNotesCount
          // 查询日志已移除
          const videosWithUserNotesCount = await Promise.all(videosWithNumberKeys.map(async (video) => {
            try {
              // 只查詢當前用戶的 sharedVbids 重點數
              const sharedVbidsQuery = query(
                safeCollection( 'sharedVbids'),
                where('videoId', '==', video.videoId || video.id),
                where('userId', '==', userId)
              );
              const sharedVbidsSnapshot = await getDocs(sharedVbidsQuery);
              
              let checkpointCount = 0;
              if (!sharedVbidsSnapshot.empty) {
                // 從 sharedVbids 的 vbids 數組計算活躍的重點數（只統計當前用戶）
                const sharedVbidsData = sharedVbidsSnapshot.docs[0].data();
                if (sharedVbidsData.vbids && Array.isArray(sharedVbidsData.vbids)) {
                  checkpointCount = sharedVbidsData.vbids.filter((vbid: any) => vbid.isActive).length;
                }
              }
              
              // 如果 sharedVbids 中沒有數據，回退到 userNotes（用於向後兼容）
              if (checkpointCount === 0) {
                const userNotesQuery = query(
                  safeCollection( 'userNotes'),
                  where('videoId', '==', video.videoId || video.id),
                  where('userId', '==', userId)
                );
                const userNotesSnapshot = await getDocs(userNotesQuery);
                checkpointCount = userNotesSnapshot.docs.length;
              }
              
              if (checkpointCount > 0) {
                // 调试日志已移除
              }
              
              return { ...video, userNotesCount: checkpointCount };
            } catch (error) {
              console.error(`❌ 計算 teamPoint 影片 ${video.videoId || video.id} 的重點數失敗:`, error);
              return { ...video, userNotesCount: 0 };
            }
          }));
          
          // 查询日志已移除
          
          // 🚀 性能優化：不再查詢 srtCache，直接使用影片數據中的 hasSRTCache 字段
          const videosWithSRTCheckTeam = videosWithUserNotesCount;
          const aiTagCountTeam = videosWithSRTCheckTeam.filter((v: any) => v.hasSRTCache).length;
          // 调试日志已移除

          // ====== 分群排序邏輯開始 ======
          const groupMap: Record<string, { groupId: string, groupName: string, videos: VideoItem[] }> = {};
          videosWithSRTCheckTeam.forEach((item: VideoItem) => {
            if (Array.isArray(item.groupIds)) {
              item.groupIds.forEach((gid: string) => {
                if (!groupMap[gid]) groupMap[gid] = { groupId: gid, groupName: item.groupName || '未分群', videos: [] };
                groupMap[gid].videos.push(item);
              });
            }
          });
          const groupListArr = Object.values(groupMap).map(g => ({
            ...g,
            latestAddedDate: g.videos.length ? Math.max(...g.videos.map(v => new Date(v.addedDate || 0).getTime())) : 0
          }));
          const newSortedGroups = groupListArr.filter(g => g.videos.length > 0).sort((a, b) => b.latestAddedDate - a.latestAddedDate);
          setGroupList(newSortedGroups);
          // ====== 分群排序邏輯結束 ======

          // 同時載入 userGroups
          if (authUser?.email) {
            try {
              const groups = await getUserGroups(authUser.email);
              setUserGroups(groups);
            } catch (error) {
              console.error('載入 userGroups 失敗:', error);
            }
          }
          
          // 分階段載入處理
          if (batchMode) {
            // 批次模式：追加到現有資料
            const currentVideos = videos || [];
            const newVideos = [...currentVideos, ...videosWithSRTCheckTeam];
            setVideos(newVideos);
            setPersonalVideos(newVideos);
            setVideosCache(prev => ({ ...prev, [cacheKey]: newVideos }));
            setLoadedCounts(prev => ({ ...prev, [section]: newVideos.length }));
            setHasMoreData(prev => ({ ...prev, [section]: updatedData.length === BATCH_SIZE }));
            // 调试日志已移除
          } else {
            // 初始載入模式
            setVideos(videosWithSRTCheckTeam);
            setPersonalVideos(videosWithSRTCheckTeam);
            setVideosCache(prev => ({ ...prev, [cacheKey]: videosWithSRTCheckTeam }));
            setCacheTimestamps(prev => ({ ...prev, [cacheKey]: now }));
            setLoadedCounts(prev => ({ ...prev, [section]: videosWithSRTCheckTeam.length }));
            setHasMoreData(prev => ({ ...prev, [section]: videosWithSRTCheckTeam.length === INITIAL_LOAD_SIZE }));
            // 调试日志已移除
          }
          
          setLoading(false);
          setLoadingBatches(prev => {
            const newSet = new Set(prev);
            newSet.delete(section);
            return newSet;
          });
          return;
        } else if (section === 'myPoint') {
          // // 查询日志已移除
          
          // 檢查 Firestore 是否已初始化
          const sharedVbidsCollection = safeCollection("sharedVbids");
          if (!sharedVbidsCollection) {
            console.warn('⚠️ Firestore 未初始化，跳過載入 myPoint 影片');
            setLoading(false);
            return;
          }
          
          // 檢查是否有快取數據
          const cacheKey = `${section}_${userId}`;
          const cachedVideos = videosCache[cacheKey];
          const cacheTimestamp = cacheTimestamps[cacheKey];
          const now = Date.now();
          const isCacheValid = cacheTimestamp && (now - cacheTimestamp) < CACHE_EXPIRY_TIME;
          
          if (cachedVideos && isCacheValid) {
            // 处理日志已移除
            // 確保快取中的影片有正確的 attentionCount
            const cachedUserVideos = cachedVideos.filter(v => v.userId === userId);
            const cachedCommunityVideos = cachedVideos.filter(v => v.zones?.includes('community'));
            
            // 強制重新載入所有快取影片的關注度數據
            const userVideosWithAttention = await Promise.all(cachedUserVideos.map(async (item) => {
              let videoId = item.videoId;
              if (!videoId && item.path) {
                const extractYouTubeId = (url: string): string | null => {
                  const match = url.match(/(?:v=|youtu\.be\/|embed\/)([a-zA-Z0-9_-]{11})/);
                  return match ? match[1] : null;
                };
                videoId = extractYouTubeId(item.path) || item.path;
              }
              
              let attentionCount = 0;
              if (videoId) {
                attentionCount = await getAttentionCount(videoId);
                // // 查询日志已移除
              }
              return {
                ...item,
                attentionCount
              };
            }));
            
            const communityVideosWithAttention = await Promise.all(cachedCommunityVideos.map(async (item) => {
              let videoId = item.videoId;
              if (!videoId && item.path) {
                const extractYouTubeId = (url: string): string | null => {
                  const match = url.match(/(?:v=|youtu\.be\/|embed\/)([a-zA-Z0-9_-]{11})/);
                  return match ? match[1] : null;
                };
                videoId = extractYouTubeId(item.path) || item.path;
              }
              
              let attentionCount = 0;
              if (videoId) {
                attentionCount = await getAttentionCount(videoId);
                // // 查询日志已移除
              }
              return {
                ...item,
                attentionCount
              };
            }));
            
            setUserVideos(userVideosWithAttention);
            communityVideos = communityVideosWithAttention;
          } else {
            // 智能增量更新：只查詢新增的數據
            const lastUpdateTime = cacheTimestamp || 0;
            const timeThreshold = new Date(lastUpdateTime - 60000); // 1分鐘緩衝
            
            let personalQuery;
            if (userId.startsWith('guest_')) {
              personalQuery = query(
                safeCollection( "sharedVbids"),
                where("privacy", "==", "Public"),
                where("addedDate", ">", timeThreshold.toISOString()),
                orderBy("addedDate", "desc"),
                limit(20) // 減少查詢數量
              );
            } else {
              // 🔧 修復：移除複合查詢，避免索引問題
              personalQuery = query(
                safeCollection( "sharedVbids"),
                where("userId", "==", userId),
                limit(20) // 減少查詢數量
              );
            }
            const personalSnapshot = await getDocs(personalQuery);
            const newPersonalList = personalSnapshot.docs
              .map(doc => ({ id: doc.id, ...(doc.data() as any) } as VideoItem))
              .filter(v => !Array.isArray(v.hiddenFor) || !v.hiddenFor.includes(userId));

            // 合併新數據和快取數據
            const existingPersonalVideos = cachedVideos ? cachedVideos.filter(v => v.userId === userId) : [];
            const mergedPersonalList = [...newPersonalList, ...existingPersonalVideos];
            
            // 🔧 修復：去重並按最後瀏覽時間排序
            const uniquePersonalVideos = Array.from(
              new Map(mergedPersonalList.map(v => [v.id, v])).values()
            ).sort((a, b) => {
              // 優先使用 lastOpenDate，如果沒有則使用 addedDate
              const aDate = a.lastOpenDate || a.addedDate || a.createdAt || 0;
              const bDate = b.lastOpenDate || b.addedDate || b.createdAt || 0;
              return new Date(bDate).getTime() - new Date(aDate).getTime();
            });

            // 強制為所有影片重新載入關注度數據和建立時間
            const personalVideosWithAttention = await Promise.all(uniquePersonalVideos.map(async (item) => {
              // 使用正確的 videoId：優先使用 item.videoId，如果沒有則從 path 提取
              let videoId = item.videoId;
              if (!videoId && item.path) {
                const extractYouTubeId = (url: string): string | null => {
                  const match = url.match(/(?:v=|youtu\.be\/|embed\/)([a-zA-Z0-9_-]{11})/);
                  return match ? match[1] : null;
                };
                videoId = extractYouTubeId(item.path) || item.path;
              }
              
              let attentionCount = 0;
              let earliestAddedDate = item.addedDate;
              
              if (videoId) {
                attentionCount = await getAttentionCount(videoId);
                
                // 查詢真正的最早建立時間
                try {
                  earliestAddedDate = await getEarliestAddedDate(videoId);
                  if (earliestAddedDate !== item.addedDate) {
                    // 🔧 移除詳細日誌以減少控制台輸出
                    // // 同步日志已移除
                  }
                } catch (error) {
                  console.warn('⚠️ 查詢影片最早建立時間失敗:', videoId, error);
                }
                
                // // 查询日志已移除
              }
              return {
                ...item,
                globalCheckpointCount: item.checkpointCount || 0,
                checkpointCount: item.checkpointCount || 0,
                attentionCount,
                addedDate: earliestAddedDate
              };
            }));
            
            // 社群影片：只查詢新增的，按 lastOpenDate 排序
            let communityQuery;
            try {
              communityQuery = query(
                safeCollection( "sharedVbids"),
                where("zones", "array-contains", "community"),
                where("addedDate", ">", timeThreshold.toISOString()),
                orderBy("lastOpenDate", "desc")
                // 移除 limit(15) 限制，讓社群影片可以載入全部
              );
            } catch (error) {
              // 配置日志已移除
              // 如果 lastOpenDate 索引不可用，回退到 addedDate 排序
              communityQuery = query(
                safeCollection( "sharedVbids"),
                where("zones", "array-contains", "community"),
                where("addedDate", ">", timeThreshold.toISOString()),
                orderBy("addedDate", "desc")
              );
            }
            const communitySnapshot = await getDocs(communityQuery);
            const newCommunityList = communitySnapshot.docs.map(doc => ({ id: doc.id, ...(doc.data() as any) } as VideoItem));

            // 合併社群影片
            const existingCommunityVideos = cachedVideos ? cachedVideos.filter(v => v.zones?.includes('community')) : [];
            const mergedCommunityList = [...newCommunityList, ...existingCommunityVideos];
            
            // 🔧 修復：去重並按最後瀏覽時間排序
            const uniqueCommunityVideos = Array.from(
              new Map(mergedCommunityList.map(v => [v.id, v])).values()
            ).sort((a, b) => {
              // 優先使用 lastOpenDate，如果沒有則使用 addedDate
              const aDate = a.lastOpenDate || a.addedDate || a.createdAt || 0;
              const bDate = b.lastOpenDate || b.addedDate || b.createdAt || 0;
              return new Date(bDate).getTime() - new Date(aDate).getTime();
            });

            // 為社群影片載入關注度數據和建立時間
            const communityVideosWithAttention = await Promise.all(uniqueCommunityVideos.map(async (item) => {
              // 使用正確的 videoId：優先使用 item.videoId，如果沒有則從 path 提取
              let videoId = item.videoId;
              if (!videoId && item.path) {
                const extractYouTubeId = (url: string): string | null => {
                  const match = url.match(/(?:v=|youtu\.be\/|embed\/)([a-zA-Z0-9_-]{11})/);
                  return match ? match[1] : null;
                };
                videoId = extractYouTubeId(item.path) || item.path;
              }
              
              let attentionCount = 0;
              let earliestAddedDate = item.addedDate;
              
              if (videoId) {
                attentionCount = await getAttentionCount(videoId);
                
                // 查詢真正的最早建立時間
                try {
                  earliestAddedDate = await getEarliestAddedDate(videoId);
                  if (earliestAddedDate !== item.addedDate) {
                    // 🔧 移除詳細日誌以減少控制台輸出
                    // // 同步日志已移除
                  }
                } catch (error) {
                  console.warn('⚠️ 查詢社群影片最早建立時間失敗:', videoId, error);
                }
                
                // // 查询日志已移除
              }
              return {
                ...item,
                globalCheckpointCount: item.checkpointCount || 0,
                checkpointCount: item.checkpointCount || 0,
                attentionCount,
                addedDate: earliestAddedDate
              };
            }));
            
            setUserVideos(personalVideosWithAttention);
            communityVideos = communityVideosWithAttention;
          }
          
          // 🔧 修復：myPoint 處理完成後應該立即返回
          setLoading(false);
          return;
          
        } else if (section === 'personalHistory') {
          // 🔧 新邏輯：瀏覽記錄應該顯示用戶觀看過的所有影片，不限於用戶自己建立的
          // 使用新的 loadWatchHistory 函數來載入真實的觀看記錄
          // 调试日志已移除
          
          // 檢查 Firestore 是否已初始化
          const sharedVbidsCollection = safeCollection("sharedVbids");
          if (!sharedVbidsCollection) {
            console.warn('⚠️ Firestore 未初始化，跳過載入瀏覽記錄');
            setLoading(false);
            return;
          }
          
          await loadWatchHistory(userId, forceRefresh, batchMode, monthFilter);
          return; // 直接返回，loadWatchHistory 已經處理了所有邏輯
          
        } else if (section === 'follow') {
          // // 查询日志已移除
          
          // 檢查 Firestore 是否已初始化
          const sharedVbidsCollection = safeCollection("sharedVbids");
          if (!sharedVbidsCollection) {
            console.warn('⚠️ Firestore 未初始化，跳過載入追蹤收藏影片');
            setLoading(false);
            return;
          }
          
          // 查詢所有社群影片
          let communityQuery;
          try {
            communityQuery = query(
              sharedVbidsCollection,
              where("zones", "array-contains", "community"),
              orderBy("lastOpenDate", "desc")
            );
          } catch (error) {
            // 配置日志已移除
            communityQuery = query(
              sharedVbidsCollection,
              where("zones", "array-contains", "community"),
              orderBy("addedDate", "desc")
            );
          }
          const communitySnapshot = await getDocs(communityQuery);
          const allCommunityVideos = communitySnapshot.docs.map(doc => ({ id: doc.id, ...(doc.data() as any) } as VideoItem));
          
          // 載入關注度數據
          const communityVideosWithAttention = await Promise.all(allCommunityVideos.map(async (item) => {
            let videoId = item.videoId;
            if (!videoId && item.path) {
              // 🔧 使用統一的 extractYouTubeId 函數（已從 vbidIO.ts 導入）
              videoId = extractYouTubeId(item.path) || item.path;
            }
            
            let attentionCount = 0;
            if (videoId) {
              attentionCount = await getAttentionCount(videoId);
            }
            return {
              ...item,
              globalCheckpointCount: item.checkpointCount || 0,
              checkpointCount: item.checkpointCount || 0,
              attentionCount
            };
          }));
          
          // 設置社群影片，讓 followedCommunityVideos 可以正確篩選
          communityVideos = communityVideosWithAttention;
          setUserVideos([]); // follow 分頁不需要個人影片
          
          // 🔧 修復：follow 處理完成後應該立即返回
          setLoading(false);
          return;
          
        } else if (section === 'expertKnowledge') {
          // 查询日志已移除
          
          try {
            // 🔧 關鍵修復：使用完整的後端 URL，而不是相對路徑
            const apiBaseUrl = (window as any).ENV?.VITE_API_BASE_URL || 
                              import.meta.env.VITE_API_BASE_URL || 
                              'http://localhost:8080';
            const apiUrl = `${apiBaseUrl}/api/community-public-videos`;
            // 查询日志已移除
            
            // 使用與社群公開相同的 API 端點，確保數據一致性
            const response = await fetch(apiUrl);
            const data = await response.json();
            
            if (data.success) {
              const allCommunityVideos = data.videos || [];
              // 调试日志已移除
              
              // 載入關注度數據
              const communityVideosWithAttention = await Promise.all(allCommunityVideos.map(async (item) => {
                let videoId = item.videoId;
                if (!videoId && item.path) {
                  const extractYouTubeId = (url: string): string | null => {
                    const match = url.match(/(?:v=|youtu\.be\/|embed\/)([a-zA-Z0-9_-]{11})/);
                    return match ? match[1] : null;
                  };
                  videoId = extractYouTubeId(item.path) || item.path;
                }
                
                let attentionCount = 0;
                if (videoId) {
                  attentionCount = await getAttentionCount(videoId);
                }
                return {
                  ...item,
                  globalCheckpointCount: item.checkpointCount || 0,
                  checkpointCount: item.checkpointCount || 0,
                  attentionCount
                };
              }));
              
              // 設置社群影片，讓專家筆記可以正確顯示
              communityVideos = communityVideosWithAttention;
              setVideos(communityVideosWithAttention);
              setCommunityVideos(communityVideosWithAttention);
              setCommunityLoadedCount(communityVideosWithAttention.length);
              setUserVideos([]); // expertKnowledge 分頁不需要個人影片
              
              // 更新快取
              const cacheKey = `${section}_${userId}`;
              setVideosCache(prev => ({ ...prev, [cacheKey]: communityVideosWithAttention }));
              setCacheTimestamps(prev => ({ ...prev, [cacheKey]: now }));
              
              console.log(`✅ 已載入 ${section} 影片：`, {
                total: communityVideosWithAttention.length,
                personal: 0,
                community: communityVideosWithAttention.length
              });
            } else {
              console.error('❌ 載入專家筆記影片失敗:', data.error);
              setVideos([]);
              setCommunityVideos([]);
              setCommunityLoadedCount(0);
            }
          } catch (error) {
            console.error('❌ 載入專家筆記影片失敗:', error);
            setVideos([]);
            setCommunityVideos([]);
            setCommunityLoadedCount(0);
          }
          
          // 🔧 修復：expertKnowledge 處理完成後應該立即返回
          setLoading(false);
          return;
          
        } else if (section === 'videoUpload') {
          // 查询日志已移除
          
          // 查詢當前用戶上傳的影片
          const uploadQuery = query(
            safeCollection( "videos"),
            where("source", "==", "upload"),
            where("userId", "==", userId)
          );
          
          console.log('🔍 查詢條件:', {
            collection: 'videos',
            source: 'upload',
            orderBy: 'none (客戶端排序)'
          });
          
          try {
            // 查询日志已移除
            const uploadSnapshot = await getDocs(uploadQuery);
            console.log('🔍 查詢結果:', {
              size: uploadSnapshot.size,
              empty: uploadSnapshot.empty,
              docs: uploadSnapshot.docs.length
            });
            
            const uploadVideos = uploadSnapshot.docs.map(doc => ({ 
              id: doc.id, 
              documentId: doc.id,
              ...doc.data() 
            } as VideoItem));
            
            // 移除詳細的影片數據日誌
            
            // 載入關注度數據
            const uploadVideosWithAttention = await Promise.all(uploadVideos.map(async (item) => {
              let videoId = item.videoId;
              
              // 🔧 修復：保持 GCS 影片的原始 videoId，不要覆蓋
              if (!videoId && item.path) {
                // 對於 GCS 影片，保持原有的 videoId 邏輯
                if (item.path.includes('storage.googleapis.com')) {
                  // 如果沒有 videoId，使用 gcsFileName 或從 URL 提取
                  if ((item as any).gcsFileName) {
                    videoId = `gcs_${(item as any).gcsFileName}`;
                  } else if ((item as any).originalFileName) {
                    videoId = `gcs_${(item as any).originalFileName}`;
                  } else {
                    // 從 URL 提取文件名作為 videoId
                    const urlParts = item.path.split('/');
                    const fileName = urlParts[urlParts.length - 1];
                    // 移除查詢參數
                    videoId = `gcs_${fileName.split('?')[0]}`;
                    // 配置日志已移除
                  }
                } else {
                  const extractYouTubeId = (url: string) => {
                    const match = url.match(/(?:v=|youtu\.be\/|embed\/)([a-zA-Z0-9_-]{11})/);
                    return match ? match[1] : null;
                  };
                  videoId = extractYouTubeId(item.path) || item.path;
                }
              }
              
              let attentionCount = 0;
              if (videoId) {
                attentionCount = await getAttentionCount(videoId);
              }
              
              return {
                ...item,
                // 🔧 修復：確保 GCS 影片顯示正確的標題
                title: (item as any).originalFileName || item.title,
                mediaMode: item.mediaMode || 'VIDEO',
                flag: item.flag || 'active',
                globalCheckpointCount: item.checkpointCount || 0,
                checkpointCount: item.checkpointCount || 0,
                attentionCount,
                // 確保有正確的時間字段
                addedDate: (item as any).uploadTime || item.createdAt || item.addedDate || new Date().toISOString(),
                // 🔧 修復：確保 GCS 相關屬性被正確傳遞
                originalFileName: (item as any).originalFileName,
                gcsFileName: (item as any).gcsFileName,
                fileSize: (item as any).fileSize,
                contentType: (item as any).contentType,
                uploadTime: (item as any).uploadTime,
                source: (item as any).source
              };
            }));
            
            // 在客戶端按 uploadTime 排序
            const sortedUploadVideos = uploadVideosWithAttention.sort((a, b) => {
              const timeA = (a as any).uploadTime || a.createdAt || a.addedDate || '1970-01-01';
              const timeB = (b as any).uploadTime || b.createdAt || b.addedDate || '1970-01-01';
              return new Date(timeB).getTime() - new Date(timeA).getTime();
            });
            
            // 設置上傳的影片
            setUserVideos(sortedUploadVideos);
            communityVideos = []; // videoUpload 分頁不需要社群影片
            
            // 🔧 修復：直接設置組件狀態，確保上傳的影片能正確顯示
            setVideos(sortedUploadVideos);
            setPersonalVideos(sortedUploadVideos.filter(v => v.userId === userId));
            setCommunityVideos([]);
            setCommunityLoadedCount(0);
            
            // 設置快取
            const cacheKey = `${section}_${userId}`;
            setVideosCache(prev => ({ ...prev, [cacheKey]: sortedUploadVideos }));
            setCacheTimestamps(prev => ({ ...prev, [cacheKey]: Date.now() }));
            
            // 移除詳細載入日誌
            setLoading(false);
            return;
          } catch (error) {
            console.error('❌ 查詢上傳影片失敗:', error);
            // 如果查詢失敗，設置空數組並直接返回，避免繼續執行後面的邏輯
            setUserVideos([]);
            communityVideos = [];
            
            // 🔧 修復：設置空狀態並直接返回
            setVideos([]);
            setPersonalVideos([]);
            setCommunityVideos([]);
            setCommunityLoadedCount(0);
            setLoading(false);
            return;
          }
        }

        // 🔧 修復：移除舊的無條件執行代碼，每個 section 都已有自己的 return 語句
        // 如果代碼執行到這裡，說明遇到了未知的 section
        console.warn(`⚠️ 未知的 section: ${section}`);
        setLoading(false);
        return;
        
      } catch (error) {
        console.error('❌ 載入影片失敗:', error);
          
          // 檢查是否為網路錯誤
          if (error instanceof Error) {
            if (error.message.includes('404') || error.message.includes('network')) {
              setSnackbarMessage('網路連接不穩定，請檢查網路後重試');
            } else if (error.message.includes('permission')) {
              setSnackbarMessage('權限不足，請重新登入');
            } else {
          setSnackbarMessage('載入影片失敗，請稍後再試');
            }
          } else {
            setSnackbarMessage('載入影片失敗，請稍後再試');
          }
          
          setSnackbarOpen(true);
        } finally {
          setLoading(false);
        }
      
  }, [authLoading, userId, clearCache]);

  // 🔧 修復：移除自動觸發 YouTube API 請求，避免登入時不必要的 API 調用
  // 原本的 useEffect 會在每次載入影片時自動觸發 YouTube API 請求
  // 現在改為只在用戶主動需要時才調用（例如點擊「更新」按鈕）
  // useEffect(() => {
  //   if (videos.length > 0 && !loading) {
  //     // 同步日志已移除
  //     loadYouTubeInfoForVideos(videos);
  //   }
  // }, [videos, loading]);


  // 載入更多資料
  const loadMore = useCallback(async () => {
    if (!hasMore || loading) return;
    
    setCurrentPage(prev => prev + 1);
    await loadVideos(currentSection, true);
  }, [hasMore, loading, currentSection]); // 移除 loadVideos 依賴項，避免無限循環

  // 🔧 修復：使用 useRef 防止重複初始化
  const hasInitializedRef = useRef(false);
  const lastInitializationRef = useRef({ currentSection: null, userId: null });

  // 當切換分頁時載入資料 - 統一使用 loadVideos 函數
  useEffect(() => {
    // 🔧 移除詳細日誌以減少控制台輸出
    // // 查询日志已移除
    
    // 🔧 修復：防止重複初始化
    const currentInit = { currentSection, userId };
    const lastInit = lastInitializationRef.current;
    
    if (hasInitializedRef.current && 
        currentInit.currentSection === lastInit.currentSection && 
        currentInit.userId === lastInit.userId) {
      // 同步日志已移除
      return;
    }
    
    // 確保所有條件都滿足才執行載入邏輯
    // 🔧 修復：對於 guest 用戶，即使 authLoading 為 true 也允許載入
    const isGuest = userId === 'guest' || userId?.startsWith('guest_');
    if (!currentSection || (!isGuest && authLoading) || !userId || userId === '' || userId === null) {
      // 跳过日志已移除
      return;
    }
    
    // 防止重複載入同一個分頁
    if (loadingRef.current[currentSection]) {
      // 调试日志已移除
      return;
    }
    
    // 🔧 移除詳細日誌以減少控制台輸出
    // 调试日志已移除
    // 调试日志已移除
    setCurrentPage(1);
    
    // 🔧 修復：更新初始化狀態追蹤
    lastInitializationRef.current = { currentSection, userId };
    hasInitializedRef.current = true;
    
    // 如果是首次載入，設置初始化標記
    if (!isInitialized) {
      setIsInitialized(true);
    }
    
    // 🔧 優化：對於 myVideos 分頁，也使用快取機制
    if (currentSection === 'myVideos') {
      const myVideosCacheKey = `myVideos_${userId}`;
      const cachedMyVideos = videosCache[myVideosCacheKey];
      const cacheTimestamp = cacheTimestamps[myVideosCacheKey];
      const isCacheValid = cacheTimestamp && (Date.now() - cacheTimestamp) < CACHE_EXPIRY_TIME;
      
      if (cachedMyVideos && isCacheValid) {
        // 调试日志已移除
        
        // 🔧 修復：恢復狀態，確保 UI 正確顯示
        if (userVideos.length !== cachedMyVideos.length) {
          // 调试日志已移除
          setUserVideos(cachedMyVideos);
          setVideos(cachedMyVideos);
          setPersonalVideos(cachedMyVideos);
          setCommunityVideos([]);
          setCommunityLoadedCount(0);
        }
        return;
      }
      
      // 调试日志已移除
      if (!loadingRef.current[currentSection]) {
        loadingRef.current[currentSection] = true;
        // 调试日志已移除
        loadVideos(currentSection).finally(() => {
          loadingRef.current[currentSection] = false;
        });
      }
      return;
    }
    
    // 🔧 修復：對於社群公開影片，如果快取為空，強制重新載入
    if (currentSection === 'communityPublic') {
      const communityCacheKey = `communityPublic_${userId}`;
      const cachedVideos = videosCache[communityCacheKey];
      const isEmptyCache = Array.isArray(cachedVideos) && cachedVideos.length === 0;
      if (isEmptyCache) {
        // 调试日志已移除
        if (!loadingRef.current[currentSection]) {
          loadingRef.current[currentSection] = true;
          // 调试日志已移除
          loadVideos(currentSection, true).finally(() => {
            loadingRef.current[currentSection] = false;
          });
        }
        return;
      }
    }
    
    // 🔧 修復：對於專家筆記，如果快取為空，強制重新載入
    if (currentSection === 'expertKnowledge') {
      const expertCacheKey = `expertKnowledge_${userId}`;
      const cachedVideos = videosCache[expertCacheKey];
      const isEmptyCache = !cachedVideos || (Array.isArray(cachedVideos) && cachedVideos.length === 0);
      if (isEmptyCache) {
        // 调试日志已移除
        if (!loadingRef.current[currentSection]) {
          loadingRef.current[currentSection] = true;
          // 调试日志已移除
          loadVideos(currentSection, true).finally(() => {
            loadingRef.current[currentSection] = false;
          });
        }
        return;
      }
    }
    
    // 🔧 修復：對於「我的收藏」，如果快取被清除，強制重新載入
    if (currentSection === 'myPersonalFavorite') {
      const favCacheKey = `myPersonalFavorite_${userId}`;
      const cachedVideos = videosCache[favCacheKey];
      const cacheTimestamp = cacheTimestamps[favCacheKey];
      const isCacheValid = cacheTimestamp && (Date.now() - cacheTimestamp) < CACHE_EXPIRY_TIME;
      
      // 如果快取不存在或無效，強制重新載入
      if (!cachedVideos || !isCacheValid) {
        // 调试日志已移除
        if (!loadingRef.current[currentSection]) {
          loadingRef.current[currentSection] = true;
          // 调试日志已移除
          loadVideos(currentSection).finally(() => {
            loadingRef.current[currentSection] = false;
          });
        }
        return;
      }
    }
    
    // 🔧 修復 SPA 模式性能問題：避免不必要的重新載入
    // 檢查是否已經有該分頁的快取數據
    const cacheKey = `${currentSection}_${userId}`;
    const cachedVideos = videosCache[cacheKey];
    const cacheTimestamp = cacheTimestamps[cacheKey];
    const isCacheValid = cacheTimestamp && (Date.now() - cacheTimestamp) < CACHE_EXPIRY_TIME;
    
    // 如果快取有效，跳過重新載入（包括 communityPublic）
    if (cachedVideos && isCacheValid) {
      // 调试日志已移除
      return;
    }
    
    // 🚀 性能優化：只在真正需要時才載入，避免重複調用
    // 但對於瀏覽記錄分頁，確保每次切換都重新載入，以便得到最新的觀看狀態
    // ✅ 優化：communityPublic 也使用快取機制，避免每次都重新載入
    if (!isInitialized || videos.length === 0 || currentSection === 'personalHistory') {
      // 檢查是否已經在載入中，避免重複載入
      if (!loadingRef.current[currentSection]) {
        loadingRef.current[currentSection] = true;
        // 调试日志已移除
        loadVideos(currentSection).finally(() => {
          loadingRef.current[currentSection] = false;
        });
      }
    } else if (currentSection === 'communityPublic') {
      // ✅ communityPublic 只在沒有快取或快取過期時載入
      const cacheKey = `communityPublic_${userId}`;
      const now = Date.now();
      const cacheTimestamp = cacheTimestamps[cacheKey];
      const isCacheValid = cacheTimestamp && (now - cacheTimestamp) < CACHE_EXPIRY_TIME;
      
      if (!videosCache[cacheKey] || !isCacheValid) {
        if (!loadingRef.current[currentSection]) {
          loadingRef.current[currentSection] = true;
          // 调试日志已移除
          loadVideos(currentSection).finally(() => {
            loadingRef.current[currentSection] = false;
          });
        }
      } else {
        // 调试日志已移除
      }
    }
    
    // 註解掉舊的快取邏輯，統一使用 loadVideos
    /*
      // 立即檢查快取，避免延遲
      const cacheKey = `${currentSection}_${userId}`;
      const now = Date.now();
      const cacheTimestamp = cacheTimestamps[cacheKey];
      const isCacheValid = cacheTimestamp && (now - cacheTimestamp) < CACHE_EXPIRY_TIME;
      
    // 🔧 修復：檢查快取數據是否包含最新的 Tag 信息
    const cachedVideos = videosCache[cacheKey];
    const hasRecentTagUpdates = cachedVideos && cachedVideos.some(video => {
      // 檢查是否有影片的 tags 字段是最新的
      return video.tags && Array.isArray(video.tags) && video.tags.length > 0;
    });
    
    console.log(`🔍 快取檢查:`, {
      hasCache: !!cachedVideos,
      isCacheValid,
      hasRecentTagUpdates,
      cacheKey
    });
    
    // follow 分頁總是重新載入，不使用快取
    if (currentSection === 'follow') {
      // 调试日志已移除
      loadVideos(currentSection);
    } else if (!videosCache[cacheKey] || !isCacheValid || currentSection === 'myCommunityPublic' || !hasRecentTagUpdates) {
        // 调试日志已移除
        loadVideos(currentSection);
      } else {
        // 调试日志已移除
        // 直接從快取設置數據，避免重新渲染
        setVideos(cachedVideos);
        setPersonalVideos(cachedVideos.filter(v => v.userId === userId));
        const communityVideosFromCache = cachedVideos.filter(v => v.zones?.includes('community'));
        setCommunityVideos(communityVideosFromCache);
        setCommunityLoadedCount(communityVideosFromCache.length); // 設定為顯示所有社群影片
      }
    */
  }, [currentSection, userId]); // 🔧 修復：移除 authLoading 和 isInitialized 依賴，避免重複觸發

  // 處理從 MainPage 傳來的分頁參數和MV模式VBID數據 - 只在組件初始化時執行一次
  useEffect(() => {
    // 查询日志已移除
    if (location.state?.currentSection && location.state.currentSection !== currentSection) {
      // 调试日志已移除
      setCurrentSection(location.state.currentSection);
      
      // 處理 expandedMain 狀態
      if (location.state.expandedMain) {
        // 同步日志已移除
        setExpandedMain(location.state.expandedMain);
      } else {
      // 確保對應的主項目是展開的
        if (location.state.currentSection === 'myPersonalFavorite' || location.state.currentSection === 'teamPoint') {
        if (!expandedMain.includes('myPoint')) {
          setExpandedMain(prev => [...prev, 'myPoint']);
        }
        } else if (location.state.currentSection === 'personalHistory') {
          // personalHistory 需要展開 myPoint 主項目
          if (!expandedMain.includes('myPoint')) {
            setExpandedMain(prev => [...prev, 'myPoint']);
          }
        } else if (location.state.currentSection === 'mediaPoint') {
          // mediaPoint 需要展開 mediaPoint 主項目
          if (!expandedMain.includes('mediaPoint')) {
            setExpandedMain(prev => [...prev, 'mediaPoint']);
          }
      } else if (location.state.currentSection === 'expertKnowledge' || location.state.currentSection === 'follow' || location.state.currentSection === 'myCommunityPublic') {
        if (!expandedMain.includes('mediaPoint')) {
          setExpandedMain(prev => [...prev, 'mediaPoint']);
        }
        }
      }
      
      // 清除 location.state，避免重複執行
      window.history.replaceState({}, document.title);
      
      // 滾動到頁面頂端，確保搜尋欄可見
      setTimeout(() => {
        // 查询日志已移除
        
        // 滾動 window
        window.scrollTo(0, 0);
        // 调试日志已移除
        
        // 檢查所有可能的滾動容器
        const allElements = document.querySelectorAll('*');
        let scrollableCount = 0;
        
        allElements.forEach(element => {
          const computedStyle = window.getComputedStyle(element);
          const hasOverflow = computedStyle.overflow !== 'visible' || 
                             computedStyle.overflowY !== 'visible' || 
                             computedStyle.overflowX !== 'visible';
          
          if (hasOverflow && element.scrollTop > 0) {
            element.scrollTop = 0;
            scrollableCount++;
            // 调试日志已移除
          }
        });
        
        // 特別檢查 ManagePages 的主要容器
        const managePagesContainer = document.querySelector('[data-testid="manage-pages"]') || 
                                   document.querySelector('.MuiBox-root') ||
                                   document.body;
        
        if (managePagesContainer) {
          managePagesContainer.scrollTop = 0;
          // 调试日志已移除
        }
        
        // 檢查 document.documentElement 和 document.body
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
        // 调试日志已移除
        
        // 调试日志已移除
        // 调试日志已移除
      }, 100);
    }
    
    // 處理MV模式的VBID數據
    if (location.state?.currentVbids && Array.isArray(location.state.currentVbids)) {
      // 记录日志已移除
      
      // 將MV模式的VBID轉換為VideoItem格式
      const mvVideos: VideoItem[] = location.state.currentVbids
        .filter((vbid: any) => vbid.playMode === 'MV' && vbid.mvUrl)
        .map((vbid: any) => ({
          id: `mv_${vbid.keyNo}_${Date.now()}`,
          videoId: vbid.mvUrl,
          path: vbid.mvUrl,
          title: vbid.mvTitle || `MV影片 ${vbid.keyNo}`,
          userId: vbid.userId || userId,
          mediaMode: 'Multiview',
          playMode: 'MV',
          mvUrl: vbid.mvUrl,
          mvTitle: vbid.mvTitle,
          mvDescription: vbid.mvDescription,
          thumbnail: vbid.thumbnail || (vbid.path ? `https://img.youtube.com/vi/${extractYouTubeId(vbid.path)}/hqdefault.jpg` : '/CheckPlay_Logo-100x100.png'),
          createdAt: vbid.createdAt || new Date().toISOString(),
          viewCount: vbid.viewCount || 0,
          addedDate: vbid.createdAt || new Date().toISOString(), // 暫時使用 createdAt，稍後會更新
                      flag: vbid.flag || 'active',
          privacy: 'Public',
          zones: ['person'],
          provider: 'Multiview',
          vbids: [vbid]
        }));
      
      // 数据统计日志已移除
      
      // 將MV影片添加到個人歷史記錄中
      if (mvVideos.length > 0) {
        setPersonalHistoryVideos(prev => {
          // 🔧 修復：如果當前有月份篩選，需要對MV影片也進行篩選
          let filteredMvVideos = mvVideos;
          if (selectedMonth && selectedMonth !== 'all' && currentSection === 'personalHistory') {
            const [year, month] = selectedMonth.split('-');
            const startDate = new Date(parseInt(year), parseInt(month) - 1, 1);
            const endDate = new Date(parseInt(year), parseInt(month), 0, 23, 59, 59, 999);
            
            filteredMvVideos = mvVideos.filter(video => {
              const addedDate = new Date(video.addedDate || video.createdAt || '');
              const isInRange = addedDate >= startDate && addedDate <= endDate;
              if (!isInRange) {
                // 调试日志已移除
              }
              return isInRange;
            });
          }
          
          const updated = [...filteredMvVideos, ...prev];
          // 调试日志已移除
          return updated;
        });
        
        // 同時更新個人影片列表
        setPersonalVideos(prev => {
          const updated = [...mvVideos, ...prev];
            // 调试日志已移除
          return updated;
        });
          
          // 非同步更新 addedDate 為最早建立時間
          mvVideos.forEach(async (video) => {
            if (video.videoId) {
              try {
                const earliestDate = await getEarliestAddedDate(video.videoId);
                if (earliestDate !== video.addedDate) {
                  // 🔧 移除詳細日誌以減少控制台輸出
                  // // 同步日志已移除
                  
                  // 更新個人歷史記錄中的 addedDate
                  setPersonalHistoryVideos(prev => 
                    prev.map(v => v.id === video.id ? { ...v, addedDate: earliestDate } : v)
                  );
                  
                  // 更新個人影片列表中的 addedDate
                  setPersonalVideos(prev => 
                    prev.map(v => v.id === video.id ? { ...v, addedDate: earliestDate } : v)
                  );
                }
              } catch (error) {
                console.warn('⚠️ 更新影片最早建立時間失敗:', video.videoId, error);
              }
            }
          });
        }
    }
  }, [location]); // 只監聽 location 變化，避免無限循環

  // 預載入機制：用戶登入後立即開始載入常用分頁 - 優化版本
  useEffect(() => {
    // 確保所有條件都滿足才執行預載入邏輯
    if (authLoading || !userId || !isInitialized) {
      return;
    }
    
    // 避免重複預載入
    const preloadKey = `preloaded_${userId}`;
    if (sessionStorage.getItem(preloadKey)) {
      return;
    }
    
      // 启动日志已移除
      
      // 立即預載入，不使用防抖
      const preloadSection = 'myPoint';
        const cacheKey = `${preloadSection}_${userId}`;
        
        if (!videosCache[cacheKey]) {
          // 调试日志已移除
          loadVideos(preloadSection);
        }
      
      // 預載入上半部個人歷史數據
      const personalHistoryCacheKey = `personalHistory_${userId}`;
      if (!personalHistoryCache[personalHistoryCacheKey]) {
        // 调试日志已移除
        // 觸發個人歷史數據載入
        const now = Date.now();
        const q = query(
          safeCollection( "sharedVbids"),
          where("userId", "==", userId),
          orderBy("addedDate", "desc"),
          limit(20) // 預載入時只載入20筆，提升速度
        );
        
        getDocs(q).then(snapshot => {
          const newList = snapshot.docs
            .map(doc => ({ id: doc.id, ...doc.data() } as VideoItem))
            .filter(v => !Array.isArray(v.hiddenFor) || !v.hiddenFor.includes(userId));
          
          // // 处理日志已移除
          
          // 設置快取
          setPersonalHistoryCache(prev => ({ ...prev, [personalHistoryCacheKey]: newList }));
          setPersonalHistoryCacheTimestamps(prev => ({ ...prev, [personalHistoryCacheKey]: now }));
        });
      }
    
    // 標記已預載入
    sessionStorage.setItem(preloadKey, 'true');
  }, [authLoading, userId]); // 移除 isInitialized 依賴項，避免無限循環

  // 快取最早建立時間查詢結果
  const earliestDateCache = useRef<Record<string, string>>({});
  
  // 查詢影片的最早建立時間（帶快取）
  const getEarliestAddedDate = async (videoId: string): Promise<string> => {
    // 檢查快取
    if (earliestDateCache.current[videoId]) {
      return earliestDateCache.current[videoId];
    }
    
    try {
      // 簡化查詢：只查詢 videoId，不排序，然後在記憶體中排序
      const q = query(
        safeCollection( "sharedVbids"),
        where("videoId", "==", videoId)
      );
      
      const snapshot = await getDocs(q);
      if (!snapshot.empty) {
        // 在記憶體中排序，找到最早的 createdAt
        const docs = snapshot.docs.map(doc => doc.data());
        const sortedDocs = docs.sort((a, b) => {
          const dateA = a.createdAt || a.addedDate || new Date().toISOString();
          const dateB = b.createdAt || b.addedDate || new Date().toISOString();
          return new Date(dateA).getTime() - new Date(dateB).getTime();
        });
        
        const earliestDoc = sortedDocs[0];
        const result = earliestDoc.createdAt || earliestDoc.addedDate || new Date().toISOString();
        
        // 存入快取
        earliestDateCache.current[videoId] = result;
        return result;
      }
      
      // 如果沒有找到，查詢 videos 集合
      const videosQ = query(
        safeCollection( "videos"),
        where("videoId", "==", videoId)
      );
      
      const videosSnapshot = await getDocs(videosQ);
      if (!videosSnapshot.empty) {
        // 在記憶體中排序，找到最早的 createdAt
        const docs = videosSnapshot.docs.map(doc => doc.data());
        const sortedDocs = docs.sort((a, b) => {
          const dateA = a.createdAt || a.addedDate || new Date().toISOString();
          const dateB = b.createdAt || b.addedDate || new Date().toISOString();
          return new Date(dateA).getTime() - new Date(dateB).getTime();
        });
        
        const earliestDoc = sortedDocs[0];
        const result = earliestDoc.createdAt || earliestDoc.addedDate || new Date().toISOString();
        
        // 存入快取
        earliestDateCache.current[videoId] = result;
        return result;
      }
      
      // 如果都沒有找到，返回當前時間
      const result = new Date().toISOString();
      earliestDateCache.current[videoId] = result;
      return result;
    } catch (error) {
      console.warn('⚠️ 查詢最早建立時間失敗:', videoId, error);
      const result = new Date().toISOString();
      earliestDateCache.current[videoId] = result;
      return result;
    }
  };

  // 載入 mediaPoint 資料
  const fetchMediaPointVideos = useCallback(async () => {
    if (currentSection !== 'mediaPoint') {
      return; // 早期返回，但不影響 hooks 數量
    }
    
      setLoading(true);
      // 同步日志已移除
      
      try {
        const q = query(
          safeCollection( "sharedVbids"),
          where("zones", "array-contains", "community"),
          orderBy("lastOpenDate", "desc")
        );
        const querySnapshot = await getDocs(q);
        const data: VideoItem[] = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as VideoItem));
        
        // // 查询日志已移除
        // 查詢 sharedVbids 集合來統計真正的重點數
        const sharedVbidsQuery = query(
          safeCollection( "sharedVbids"),
          where("userId", "!=", null)
        );
        const sharedVbidsSnapshot = await getDocs(sharedVbidsQuery);
        const keyToCheckpoints: Record<string, number> = {};
        
        sharedVbidsSnapshot.docs.forEach(doc => {
          const sharedVbidsData = doc.data();
          const videoId = sharedVbidsData.videoId;
          const userId = sharedVbidsData.userId;
          
          if (videoId && userId && sharedVbidsData.vbids && Array.isArray(sharedVbidsData.vbids)) {
            // 計算活躍的重點數
            const activeVbidsCount = sharedVbidsData.vbids.filter((vbid: any) => vbid.isActive).length;
            keyToCheckpoints[videoId] = activeVbidsCount;
            
            // 移除調試日誌，避免日誌污染
          }
        });
        
        const updatedData = data.map((item) => {
          const key = getVideoKey(item);
          // 嘗試多種 key 匹配方式來找到對應的重點數
          let globalCheckpointCount = keyToCheckpoints[key] || 0;
          
          // 如果沒有找到，嘗試直接使用 YouTube ID
          if (globalCheckpointCount === 0 && item.path) {
            const youtubeId = extractYouTubeId(item.path);
            if (youtubeId) {
              globalCheckpointCount = keyToCheckpoints[youtubeId] || 0;
            }
          }
          
          // 調試日誌：檢查特定影片的重點數
          if (item.title && (item.title.includes('Suno') || item.title.includes('Mureka') || item.title.includes('AI音樂'))) {
            console.log('🔍 調試 Suno 影片重點數:', {
              title: item.title,
              key: key,
              globalCheckpointCount: globalCheckpointCount,
              itemVbids: Array.isArray(item.vbids) ? item.vbids.length : 0,
              itemCheckpointCount: item.checkpointCount || 0
            });
          }
          
          return { ...item, globalCheckpointCount, checkpointCount: globalCheckpointCount };
        });
        
        setVideos(updatedData);
        setPersonalVideos(updatedData);
        // 调试日志已移除
      } catch (error) {
        console.error('❌ mediaPoint 載入失敗:', error);
        setSnackbarMessage('載入失敗，請稍後再試');
        setSnackbarOpen(true);
      } finally {
        setLoading(false);
      }
  }, [currentSection]);

  // 移除會導致無限循環的 useEffect - mediaPoint
  // useEffect(() => {
  //   // 立即載入，不使用防抖機制
  //   if (currentSection === 'mediaPoint') {
  //   fetchMediaPointVideos();
  //   }
  // }, [currentSection]);

  // 載入專家筆記資料
  const fetchExpertKnowledgeVideos = useCallback(async () => {
    if (currentSection !== 'expertKnowledge') {
      return; // 早期返回，但不影響 hooks 數量
    }
    
    setLoading(true);
    // 同步日志已移除
    
    try {
      const q = query(
            safeCollection( "sharedVbids"),
        where("zones", "array-contains", "community"),
            orderBy("lastOpenDate", "desc")
          );
        const querySnapshot = await getDocs(q);
        const data: VideoItem[] = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as VideoItem));
      
      // 載入專家標籤
      const expertTagsQuery = query(
        safeCollection( "expertTags"),
        orderBy("createdAt", "desc")
      );
      const expertTagsSnapshot = await getDocs(expertTagsQuery);
      const expertTags = expertTagsSnapshot.docs.map(doc => doc.data().tagName).filter(Boolean);
      setAllExpertTags(expertTags);
      
      setVideos(data);
      setCommunityVideos(data); // 專家筆記應該設置為社群影片
      setCommunityLoadedCount(data.length); // 設定為顯示所有社群影片
      // 调试日志已移除
    } catch (error) {
      console.error('❌ 專家筆記載入失敗:', error);
      setSnackbarMessage('載入失敗，請稍後再試');
      setSnackbarOpen(true);
    } finally {
        setLoading(false);
      }
  }, [currentSection]);

  // 移除會導致無限循環的 useEffect - expertKnowledge
  // useEffect(() => {
  //   // 立即載入專家筆記資料
  //   if (currentSection === 'expertKnowledge') {
  //     fetchExpertKnowledgeVideos();
  //   }
  // }, [currentSection]); // 移除fetchExpertKnowledgeVideos依賴，避免無限循環

  // teamPoint 載入邏輯已整合到統一的 loadVideos 函數中

  const handleDelete = useCallback(async (id: string) => {
    // 在所有可能的數組中查找影片
    let video = videos.find(v => v.id === id);
    if (!video) {
      video = userVideos.find(v => v.id === id);
    }
    if (!video) {
      video = personalVideos.find(v => v.id === id);
    }
    if (!video) {
      video = communityVideos.find(v => v.id === id);
    }
    if (!video) {
      video = personalHistoryVideos.find(v => v.id === id);
    }
    
    if (!video) {
      console.error('❌ 找不到影片:', id);
      return;
    }

    // 添加確認對話框
    if (!window.confirm('確定要永久刪除這筆觀看記錄嗎？此操作無法復原。')) {
      return;
    }
    
    // 🔧 調試：檢查用戶 ID 比較
    console.log('🔍 刪除權限檢查:', {
      videoId: video.id,
      videoUserId: video.userId,
      currentUserId: userId,
      userIdType: typeof userId,
      videoUserIdType: typeof video.userId,
      isEqual: video.userId === userId,
      strictEqual: video.userId === userId
    });
    
    // 只檢查是否是自己的影片
    if (video.userId !== userId) {
      console.error('❌ 權限檢查失敗:', {
        videoUserId: video.userId,
        currentUserId: userId,
        reason: 'User IDs do not match'
      });
      setSnackbarMessage('只能刪除自己建立的影片');
      setSnackbarOpen(true);
      return;
    }

    try {
      console.log('🗑️ 開始刪除影片:', {
        id: video.id,
        title: video.title,
        userId: video.userId,
        videoId: video.videoId,
        path: video.path,
        currentUserId: userId,
        isOwner: video.userId === userId
      });
      
      // 檢查權限
      if (video.userId !== userId) {
        console.error('❌ 權限不足，無法刪除此影片');
        setSnackbarMessage('權限不足，無法刪除此影片');
        setSnackbarOpen(true);
        return;
      }
      
      // 🔧 修復：檢查實際的文檔 ID
      let actualDocumentId = video.id;
      
      // 如果影片的 ID 是 YouTube 影片 ID，可能需要使用其他方式查找文檔
      if (video.id && video.id.length === 11 && /^[a-zA-Z0-9_-]{11}$/.test(video.id)) {
        // 查询日志已移除
        
        // 查詢包含此 videoId 的文檔
        const querySnapshot = await getDocs(
          query(
            safeCollection( "sharedVbids"),
            where("videoId", "==", video.id)
          )
        );
        
        if (!querySnapshot.empty) {
          const actualDoc = querySnapshot.docs[0];
          actualDocumentId = actualDoc.id;
          // 查询日志已移除
        } else {
          // 调试日志已移除
        }
      }
      
      // 🔧 修復：同時刪除所有相關集合的資料，避免重新出現
      const batch = safeWriteBatch();
      let deleteCount = 0;
      
      // 1. 刪除 sharedVbids 集合中的資料
      const sharedVbidRef = safeDoc( "sharedVbids", actualDocumentId);
      batch.delete(sharedVbidRef);
      deleteCount++;
      // 调试日志已移除
      
      // 2. 刪除 videos 集合中的資料（如果存在）
      const videoRef = safeDoc( "videos", actualDocumentId);
      batch.delete(videoRef);
      deleteCount++;
      // 调试日志已移除
      
      // 🔧 新增：刪除 uploads 集合中的相關記錄（雲端上傳檔案）
      // 查詢所有與此影片相關的 uploads 記錄
      if ((video as any).source === 'cloud_upload' || (video as any).cloudFile) {
        try {
          const uploadsQuery = query(
            safeCollection("uploads"),
            where("uploadedBy", "==", userId)
          );
          const uploadsSnapshot = await getDocs(uploadsQuery);
          
          // 找到匹配的 upload 記錄（通過 originalFilename）
          uploadsSnapshot.docs.forEach(uploadDoc => {
            const uploadData = uploadDoc.data();
            const uploadVideoId = `safe_${uploadData.originalFilename?.replace(/[^a-zA-Z0-9]/g, '_')}`;
            
            // 如果生成的 videoId 匹配，刪除這個 upload 記錄
            if (uploadVideoId === actualDocumentId || uploadVideoId === video.id) {
              batch.delete(uploadDoc.ref);
              deleteCount++;
              // 调试日志已移除
            }
          });
        } catch (uploadsError) {
          console.warn('⚠️ 查詢 uploads 記錄失敗:', uploadsError);
        }
      }
      
      // 3. 同時也嘗試刪除使用原始 video.id 的文檔
      if (actualDocumentId !== video.id) {
        const sharedVbidRefOriginal = safeDoc( "sharedVbids", video.id);
        batch.delete(sharedVbidRefOriginal);
        deleteCount++;
        // 调试日志已移除
        
        const videoRefOriginal = safeDoc( "videos", video.id);
        batch.delete(videoRefOriginal);
        deleteCount++;
        // 调试日志已移除
      }
      
      // 4. 刪除 videoStats 集合中的資料（如果存在）
      if (video.videoId) {
        // 檢查 videoId 是否為有效的 Firestore 文檔 ID
        let safeVideoId = video.videoId;
        
        // 如果 videoId 是 URL 或包含特殊字符，嘗試提取安全的 ID
        if (video.videoId.includes('//') || video.videoId.includes('://')) {
          // 對於 GCS URL，提取文件名作為 videoId
          if (video.videoId.includes('storage.googleapis.com')) {
            const urlParts = video.videoId.split('/');
            const fileName = urlParts[urlParts.length - 1];
            // 移除查詢參數
            safeVideoId = fileName.split('?')[0];
            // 配置日志已移除
          } else {
            // 對於其他 URL，使用 hash 或其他安全的方式
            safeVideoId = `safe_${video.videoId.replace(/[^a-zA-Z0-9]/g, '_')}`;
            // 配置日志已移除
          }
        }
        
        try {
          const videoStatsRef = safeDoc( "videoStats", safeVideoId);
          batch.delete(videoStatsRef);
          deleteCount++;
          // 调试日志已移除
        } catch (videoStatsError) {
          console.warn('⚠️ 無法刪除 videoStats，videoId 無效:', safeVideoId, videoStatsError);
        }
      }
      
      // 5. 刪除 sharedLinks 集合中的相關資料
      // 查詢所有相關的分享連結並刪除
      const sharedLinksQuery = query(
        safeCollection( "sharedLinks"),
        where("videoId", "==", video.id)
      );
      const sharedLinksSnapshot = await getDocs(sharedLinksQuery);
      sharedLinksSnapshot.docs.forEach(doc => {
        batch.delete(doc.ref);
        deleteCount++;
        // 调试日志已移除
      });
      
      // 6. 同時也查詢使用 actualDocumentId 的 sharedLinks
      const sharedLinksQuery2 = query(
        safeCollection( "sharedLinks"),
        where("videoId", "==", actualDocumentId)
      );
      const sharedLinksSnapshot2 = await getDocs(sharedLinksQuery2);
      sharedLinksSnapshot2.docs.forEach(doc => {
        batch.delete(doc.ref);
        deleteCount++;
        // 调试日志已移除
      });
      
      // 7. 🔧 新增：刪除 userWatchHistory 中的相關記錄
      if (video.videoId) {
        // 刪除當前用戶的觀看記錄
        const userWatchRecordId = `watch_${userId}_${video.videoId}`;
        const userWatchRef = safeDoc( "userWatchHistory", userWatchRecordId);
        batch.delete(userWatchRef);
        deleteCount++;
        // 调试日志已移除
        
        // 如果 actualDocumentId 不同，也嘗試刪除
        if (actualDocumentId !== video.videoId) {
          const userWatchRecordId2 = `watch_${userId}_${actualDocumentId}`;
          const userWatchRef2 = safeDoc( "userWatchHistory", userWatchRecordId2);
          batch.delete(userWatchRef2);
          deleteCount++;
          // 调试日志已移除
        }
      }
      
      // 调试日志已移除
      
      // 執行批次刪除
      try {
        await batch.commit();
        // 调试日志已移除
      } catch (commitError) {
        console.error('❌ 批次刪除執行失敗:', commitError);
        throw commitError;
      }
      
      // 🔧 新增：清理快取資料
      try {
        // 清理 sessionStorage 快取
        const cacheKeys = [
          `firestore_${video.id}`,
          `firestore_${video.id}_timestamp`,
          `firestore_${video.id}_force_reload`
        ];
        cacheKeys.forEach(key => {
          sessionStorage.removeItem(key);
        });
        // 清理日志已移除
        
        // 清理 localStorage 快取（如果有）
        const localStorageKeys = [
          `sharedVideoData_${video.id}`,
          `videoData_${video.id}`
        ];
        localStorageKeys.forEach(key => {
          localStorage.removeItem(key);
        });
        // 清理日志已移除
        
      } catch (cacheError) {
        console.warn('⚠️ 清理快取失敗:', cacheError);
      }
      
      // 更新本地狀態
      setVideos(videos => videos.filter(v => v.id !== video.id));
      setUserVideos(videos => videos.filter(v => v.id !== video.id)); // 🔧 修復：添加 userVideos 狀態更新
      setPersonalVideos(videos => videos.filter(v => v.id !== video.id));
      setCommunityVideos(videos => videos.filter(v => v.id !== video.id));
      setPersonalHistoryVideos((videos: VideoItem[]) => videos.filter((v: VideoItem) => v.id !== video.id));
      setFilteredPersonalHistoryVideos(prev => prev ? prev.filter(v => v.id !== video.id) : null);
      setFilteredCommunityVideos(prev => prev ? prev.filter(v => v.id !== video.id) : null);

      // 调试日志已移除
      setSnackbarMessage('已刪除影片');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('❌ 刪除失敗:', error);
      console.error('❌ 錯誤詳情:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : 'No stack trace',
        videoId: video.id,
        userId: video.userId,
        errorType: error instanceof Error ? error.constructor.name : 'Unknown'
      });
      
      // 提供更詳細的錯誤訊息
      let errorMessage = '刪除失敗，請稍後再試';
      if (error instanceof Error) {
        if (error.message.includes('permission-denied')) {
          errorMessage = '權限不足，無法刪除此影片';
        } else if (error.message.includes('not-found')) {
          errorMessage = '影片不存在或已被刪除';
        } else if (error.message.includes('unavailable')) {
          errorMessage = '網路連線問題，請檢查網路後重試';
        }
      }
      
      setSnackbarMessage(errorMessage);
      setSnackbarOpen(true);
    }
  }, [userId, videos, userVideos, personalVideos, communityVideos, personalHistoryVideos]); // 添加必要的依賴項

  const [tagDialogOpen, setTagDialogOpen] = useState(false);
  const [tagDialogVideo, setTagDialogVideo] = useState<VideoItem | null>(null);
  const [newTag, setNewTag] = useState('');
  
  // 新增一個同步 checkpointCount 的工具函式
  async function syncCheckpointCountToFirestore(videoKey: string) {
    // 1. 查詢所有 sharedVbids，統計該 key 的 vbids 總數
    const allSnapshot = await getDocs(safeCollection( "sharedVbids"));
    let total = 0;
    const docIds: string[] = [];
    allSnapshot.docs.forEach(doc => {
      const d = doc.data();
      const key = getVideoKey(d);
      if (key === videoKey) {
        if (Array.isArray(d.vbids)) total += d.vbids.length;
        docIds.push(doc.id);
      }
    });
    // 2. 將所有同 key 的文件 checkpointCount 欄位同步
    await Promise.all(docIds.map(id => updateDoc(safeDoc( 'sharedVbids', id), { checkpointCount: total })));
  }

  // 移除重複的 YouTube 資訊載入，統一由下方的 useEffect 處理

  // 2. 點擊播放時才查詢 YouTube API（含 24 小時快取）
  const handlePlayVideo = useCallback(async (video: VideoItem) => {
    if (!video.id) {
      alert('找不到影片 UID，無法還原');
      return;
    }

    // 🔥 優化：在背景非阻塞更新觀看記錄，不延遲導航
    const now = new Date().toISOString();
    const currentUser = auth?.currentUser;
    
    // 使用 setTimeout 確保完全非阻塞
    setTimeout(async () => {
      try {
        if (currentUser) {
          // 登入用戶：更新個人觀看記錄 + 影片最後瀏覽時間
          await updateDoc(safeDoc('sharedVbids', video.id), {
            lastOpenDate: now,
            lastReadAt: serverTimestamp()
          });
          
          // 使用用戶專屬的觀看記錄服務
          const { updateUserWatchHistory } = await import('../services/userWatchHistoryService');
          await updateUserWatchHistory(currentUser.uid, video.videoId || video.id, video.id);
          
          // 调试日志已移除
        } else {
          // 訪客：純查看模式，不寫入 Firestore
          // 訪客沒有 Firestore 寫入權限，只能查看內容
          // 可選：在 localStorage 記錄瀏覽歷史（僅限本地）
        }
      } catch (error) {
        console.warn('⚠️ 更新觀看記錄失敗:', video.id, error);
      }
    }, 0); // 🔥 非阻塞背景執行

    // MV 模式特殊處理
    if (video.playMode === 'MV') {
      // 调试日志已移除
      
      // 構建MV模式播放URL
      const author = (globalSelectedUserId || video.userId || '').toString();
      let url = `${window.location.origin}/?tab=6&mv=true&author=${encodeURIComponent(author)}`;
      
      // 如果有MV URL，添加到參數中
      if (video.mvUrl) {
        url += `&url=${encodeURIComponent(video.mvUrl)}`;
      }
      
      // 如果有MV主題，添加到參數中
      if (video.mvTitle) {
        url += `&title=${encodeURIComponent(video.mvTitle)}`;
      }
      
      // 调试日志已移除
      navigate(url);
      return;
    }

    // 🔧 修復：預先準備影片資訊，支援 YouTube 和 GCS 影片
    const videoId = extractYouTubeId(video.path || '');
    const isGCSVideo = video.path && video.path.includes('storage.googleapis.com');
    
    // 處理 YouTube 影片的統計資訊更新
    if (videoId && !isGCSVideo) {
      // 檢查是否需要更新：缺少縮圖、標題不完整、或超過24小時
      let needFetchStats = true;
      const hasIncompleteInfo = !video.youtubeInfo?.thumbnails?.high?.url || 
                               !video.youtubeInfo?.title || 
                               !video.youtubeInfo?.channelTitle;
      
      if (video.youtubeInfo && video.youtubeInfo.lastStatsFetchedAt && !hasIncompleteInfo) {
        const last = new Date(video.youtubeInfo.lastStatsFetchedAt).getTime();
        if (Date.now() - last < 24 * 60 * 60 * 1000) {
          needFetchStats = false;
        }
      }

      if (needFetchStats) {
        // 同步日志已移除
        
        // 🔥 優化：使用 setTimeout 確保完全非阻塞，讓導航立即執行
        setTimeout(async () => {
          try {
            let videoDetails: any;
            if (youtubeInfoCache[videoId] && !youtubeInfoCache[videoId].notFound && !hasIncompleteInfo) {
              videoDetails = youtubeInfoCache[videoId];
              // 处理日志已移除
            } else {
              // 查询日志已移除
              try {
                videoDetails = await youtubeApi.getVideoDetails(videoId);
                youtubeInfoCache[videoId] = videoDetails;
              } catch (apiError: any) {
                // 🔧 新增：處理 YouTube API 配額超限錯誤
                if (apiError?.response?.status === 403 || apiError?.message?.includes('quota')) {
                  console.warn('⚠️ YouTube API 配額超限，跳過更新:', videoId);
                  youtubeInfoCache[videoId] = { 
                    notFound: true, 
                    error: 'quota_exceeded',
                    lastUpdate: new Date().toISOString()
                  };
                  return; // 直接返回，不繼續處理
                }
                throw apiError; // 重新拋出其他錯誤
              }
            }
            
            // 驗證 API 回應的完整性
            if (!videoDetails || !videoDetails.title || !videoDetails.channelTitle) {
              console.warn('⚠️ YouTube API 回應不完整，跳過更新:', videoId);
              // 記錄錯誤但不更新影片狀態
              youtubeInfoCache[videoId] = { 
                notFound: true, 
                lastTriedAt: new Date().toISOString(),
                error: 'API response incomplete'
              };
              return;
            }
            
            const updatedYouTubeInfo = {
              title: videoDetails.title,
              channelTitle: videoDetails.channelTitle,
              viewCount: videoDetails.viewCount,
              publishedAt: videoDetails.publishedAt,
              thumbnails: videoDetails.thumbnails, // 確保包含縮圖資訊
              description: videoDetails.description,
              duration: videoDetails.duration,
              tags: videoDetails.tags,
              lastStatsFetchedAt: new Date().toISOString()
            };
            
            // 只有在成功取得完整資料時才更新本地 state
            const updatedVideo = {
              ...video,
              youtubeInfo: updatedYouTubeInfo,
              // 更新縮圖資訊
              thumbnail: videoDetails.thumbnails?.high?.url || videoDetails.thumbnails?.medium?.url || video.thumbnail,
              // 更新其他影片資訊
              title: videoDetails.title || video.title,
              channelTitle: videoDetails.channelTitle,
              viewCount: videoDetails.viewCount,
              publishedAt: videoDetails.publishedAt
            };
            
            setVideos(prev => prev.map(v => v.id === video.id ? updatedVideo : v));
            setPersonalVideos(prev => prev.map(v => v.id === video.id ? updatedVideo : v));
            setCommunityVideos(prev => prev.map(v => v.id === video.id ? updatedVideo : v));
            
            console.log('✅ YouTube 資訊更新完成:', {
              videoId,
              hasThumbnail: !!updatedVideo.thumbnail,
              title: updatedVideo.title,
              channelTitle: updatedVideo.channelTitle
            });
            
            // 只有在成功取得完整資料時才更新 Firestore
        if (video.id) {
              // 嘗試在 videos 集合中查找
              const videosDocRef = safeDoc( 'videos', video.id);
              getDoc(videosDocRef).then((docSnap) => {
                if (docSnap.exists()) {
                  // 在 videos 集合中找到，執行更新
                  // 调试日志已移除
                  // 清理 undefined 值
                  const cleanYouTubeInfo = Object.fromEntries(
                    Object.entries(updatedYouTubeInfo).filter(([_, value]) => value !== undefined)
                  );
                  return updateDoc(videosDocRef, { youtubeInfo: cleanYouTubeInfo });
                } else {
                  // 嘗試在 sharedVbids 集合中查找
                  const sharedVbidsDocRef = safeDoc( 'sharedVbids', video.id);
                  return getDoc(sharedVbidsDocRef).then((sharedDocSnap) => {
                    if (sharedDocSnap.exists()) {
                      // 在 sharedVbids 集合中找到，執行更新
                      // 调试日志已移除
                      // 清理 undefined 值
                      const cleanYouTubeInfo = Object.fromEntries(
                        Object.entries(updatedYouTubeInfo).filter(([_, value]) => value !== undefined)
                      );
                      return updateDoc(sharedVbidsDocRef, { youtubeInfo: cleanYouTubeInfo });
                    } else {
                      // 调试日志已移除
                      return Promise.resolve();
                    }
                  });
                }
              }).catch((e: any) => {
                // 🔧 修復：區分權限錯誤和其他錯誤，優雅處理
                if (e?.code === 'permission-denied') {
                  console.warn('⚠️ [YouTube 資訊更新] Firestore 權限不足（可忽略，不影響主要功能）');
                } else {
                  console.error('Firestore 檢查或更新失敗:', e);
                }
              });
        }
        
        // 调试日志已移除
      } catch (e) {
        console.error('YouTube API 查詢失敗', e);
        // 失敗時只記錄快取，不更新影片狀態，保持原有的 Firestore 資料
        youtubeInfoCache[videoId] = { 
          notFound: true, 
          lastTriedAt: new Date().toISOString(),
          error: e instanceof Error ? e.message : 'Unknown error'
        };
        // 不更新任何狀態，保持原有的正確資料
      }
        }, 0); // 🔥 使用 setTimeout 0ms 確保非阻塞
    }
    }

    // 構建播放 URL - 使用 videoId 而不是 id
    const author = (globalSelectedUserId || video.userId || '').toString();
    // 🔧 修復：使用 videoId 而不是 id，因為 id 是 Firestore 文檔 ID，不是 YouTube 影片 ID
    // 如果 videoId 不存在，嘗試從 path 提取
    let videoIdForUrl = video.videoId;
    if (!videoIdForUrl && video.path) {
      videoIdForUrl = extractYouTubeId(video.path) || undefined;
    }
    
    // 🔧 修復：處理標準化的文檔 ID 格式（以 vid_ 開頭）
    if (!videoIdForUrl && video.id && video.id.startsWith('vid_')) {
      // 配置日志已移除
      // 格式：vid_{userId}_{videoId}_{version}
      // 例如：vid_uc9C6sAfsPT743cQApZ4MmZVoU72_j25W8wd2s2M_v1
      const parts = video.id.split('_');
      if (parts.length >= 3) {
        // 取第三部分作為 YouTube 影片 ID
        const extractedId = parts[2];
        if (extractedId && /^[a-zA-Z0-9_-]{11}$/.test(extractedId)) {
          videoIdForUrl = extractedId;
          // 配置日志已移除
        }
      }
    }
    // 🔧 修復：處理舊的複合格式的 ID，如 uc9C6sAfsPT743cQApZ4MmZVoU72_tRrE2s713fk_v1
    // 這種格式的結構是：用戶ID_YouTube影片ID_版本號
    else if (!videoIdForUrl && video.id && video.id.includes('_')) {
      const parts = video.id.split('_');
      if (parts.length >= 2) {
        // 取第二部分作為 YouTube 影片 ID
        const extractedId = parts[1];
        // 驗證是否為有效的 YouTube 影片 ID（11位字符）
        if (extractedId && /^[a-zA-Z0-9_-]{11}$/.test(extractedId)) {
          videoIdForUrl = extractedId;
          // 配置日志已移除
        }
      }
    }
    
    // 最後的 fallback：使用 id（對於新影片，id 就是 videoId）
    if (!videoIdForUrl) {
      videoIdForUrl = video.id;
    }
    
    // 🔧 修復：對於子觀點，使用完整的 video.id 來保留觀點版本信息
    // 檢查是否為子觀點（id 包含 _v2, _v3 等）
    const isSubViewpoint = video.id && /_v\d+$/.test(video.id);
    const urlId = isSubViewpoint ? video.id : videoIdForUrl;
    
    let url = `${window.location.origin}/v/${urlId}?author=${encodeURIComponent(author)}`;
    if (Array.isArray(video.vbids) && video.vbids.length > 0) {
              const firstActiveVbid = video.vbids.find(vbid => vbid.flag === 'active');
      if (firstActiveVbid && firstActiveVbid.timestamp) {
        url += `&start=${firstActiveVbid.timestamp}`;
      }
    }
    
    // 預載入影片資料到快取
    const preloadVideoData = async () => {
      try {
        const cacheKey = `firestore_${video.id}`;
        const now = Date.now();
        
        // 檢查是否已有快取
        const cachedData = sessionStorage.getItem(cacheKey);
        if (cachedData) {
          // 处理日志已移除
          return;
        }
        
        // 預載入 Firestore 資料
        // 启动日志已移除
        const docRef = safeDoc( 'sharedVbids', video.id);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          
          // 檢查是否有強制重新載入標記，如果有則不設置快取
          const forceReload = sessionStorage.getItem(`${cacheKey}_force_reload`);
          if (!forceReload) {
          sessionStorage.setItem(cacheKey, JSON.stringify(data));
          sessionStorage.setItem(`${cacheKey}_timestamp`, now.toString());
          // 调试日志已移除
          } else {
            // 同步日志已移除
            sessionStorage.removeItem(`${cacheKey}_force_reload`);
          }
        }
      } catch (error) {
        console.error('預載入失敗:', error);
      }
    };
    
    // 在背景中預載入資料
    preloadVideoData().catch(err => console.error('背景預載入失敗:', err));
    
    // 🔧 修復：使用正確的導航路徑到 MainPage
    // 對於雲端檔案，使用 handleCloudFileOpen 函數
    if (
      (video.path && video.path.includes('storage.googleapis.com')) ||
      (video as any).source === 'cloud_upload' ||
      (video as any).cloudFile
    ) {
      // 🔧 修復：雲端檔案使用專用的 handleCloudFileOpen 函數
      // 调试日志已移除
      
      // 🔧 修復：優先從 cloudFile.url 或 url 字段獲取完整的 GCS URL
      let gcsUrl = (video as any).url || video.path; // 優先使用 url 字段
      
      // 如果有 cloudFile 對象，從中獲取 URL
      if ((video as any).cloudFile && (video as any).cloudFile.url) {
        gcsUrl = (video as any).cloudFile.url;
      }
      
      console.log('🔍 雲端檔案 URL 來源:', {
        videoUrl: (video as any).url,
        videoPath: video.path,
        cloudFileUrl: (video as any).cloudFile?.url,
        finalUrl: gcsUrl
      });
      
      // 🔧 調試：檢查影片資料中的縮圖欄位
      console.log('🔍 縮圖來源檢查:', {
        videoThumbnail: video.thumbnail,
        cloudFileThumbnail: (video as any).cloudFile?.thumbnailUrl,
        finalThumbnail: video.thumbnail || (video as any).cloudFile?.thumbnailUrl
      });
      
      // 轉換 video 格式為 LocalFileRecord 格式
      const cloudFileRecord: LocalFileRecord = {
        id: video.id,
        relativePath: gcsUrl, // 使用完整的 GCS URL
        fileName: (video as any).originalFileName || video.title || '未命名影片',
        fileType: 'video',
        fileSize: (video as any).fileSize || 0,
        lastModified: new Date((video as any).uploadTime || video.createdAt || Date.now()).getTime(),
        lastOpened: new Date().getTime(),
        notes: {},
        thumbnailUrl: video.thumbnail || (video as any).cloudFile?.thumbnailUrl  // 🔧 新增：從影片資料讀取縮圖
      };
      
      // 查询日志已移除
      
      // 使用雲端檔案專用的處理函數
      await handleCloudFileOpen(cloudFileRecord);
    } else if (
      // 🔧 修復：檢測本機檔案，但排除雲端檔案
      // 確保不是雲端檔案
      !((video as any).source === 'cloud_upload' || (video as any).cloudFile || (video.path && video.path.includes('storage.googleapis.com'))) &&
      (
        // 檢測本機檔案的多種情況
        (video.mode === 'VIDEO' && video.path && !video.path.includes('youtube.com') && !video.path.includes('youtu.be')) ||
        // 或者檔案路徑包含 .mp4, .avi, .mov 等影片副檔名（但不是 GCS URL）
        (video.path && !video.path.includes('storage.googleapis.com') && (video.path.includes('.mp4') || video.path.includes('.avi') || video.path.includes('.mov') || video.path.includes('.mkv'))) ||
        // 或者檔案 ID 包含檔案副檔名（如 test2.mp4），但不是 safe_ 開頭的雲端檔案 ID
        (video.id && !video.id.startsWith('safe_') && (video.id.includes('.mp4') || video.id.includes('.avi') || video.id.includes('.mov') || video.id.includes('.mkv')))
      )
    ) {
      // 🔧 修復：本機檔案處理
      // 调试日志已移除
      
      // 確定檔案路徑
      const filePath = video.path || video.id;
      
      // 創建一個假的 File 對象來模擬本機檔案
      const mockFile = new File([''], filePath, { 
        type: 'video/mp4',
        lastModified: new Date().getTime()
      });
      
      // 🔧 修復：檢查是否在 SPA 模式下
      if (isEmbedded && onNavigateToMain) {
        // SPA 模式：使用回調函數導航到 MainPage
        // 导航日志已移除');
        markPersonalHistoryDirty();
        onNavigateToMain({
          localFile: mockFile,
          source: 'local',
          fromManagePages: true,
          firestoreId: video.id,
          fileRelativePath: filePath
        });
      } else {
        // 傳統路由模式：使用 navigate
        // 导航日志已移除
      navigateToMain({
        localFile: mockFile,
        source: 'local',
        fromManagePages: true,
        firestoreId: video.id,
        fileRelativePath: filePath
      });
      }
    } else {
      // YouTube 影片：使用原有的導航邏輯
      const firestoreId = video.documentId || video.id;
      
      // 🔧 修復：檢查是否在 SPA 模式下
      if (isEmbedded && onNavigateToMain) {
        // SPA 模式：使用回調函數導航到 MainPage
        // 导航日志已移除');
        markPersonalHistoryDirty();
        
        // 🔥 優化：直接使用快取的 vbids 數據，不阻塞導航
        let vbids = video.vbids || [];
        console.log('⚡ 使用快取的 vbids 數據以加快載入速度:', {
          videoId: video.videoId,
          vbidsCount: vbids.length
        });
        
        // 🔥 在背景中異步獲取最新數據（不阻塞）
        const updateVbidsInBackground = async () => {
          try {
            const videoDoc = await getDoc(safeDoc( 'sharedVbids', firestoreId));
            if (videoDoc.exists()) {
              const latestData = videoDoc.data();
              const latestVbids = latestData.vbids || [];
              const latestCheckpointCount = latestVbids.length;
              
              console.log('✅ 背景更新：獲取到最新的 vbids 數據:', {
                videoId: video.videoId,
                vbidsCount: latestCheckpointCount,
                checkpointCount: latestData.checkpointCount || 0
              });
              
              // 🔧 關鍵修復：更新對應 section 的 state，觸發重新渲染
              const updateVideo = (v: VideoData) => 
                v.videoId === video.videoId 
                  ? { ...v, vbids: latestVbids, checkpointCount: latestCheckpointCount }
                  : v;
              
              // 根據當前 section 更新對應的 state
              if (currentSection === 'myVideos') {
                setMyVideos(prev => prev.map(updateVideo));
              } else if (currentSection === 'communityPublic') {
                setCommunityPublicVideos(prev => prev.map(updateVideo));
              } else if (currentSection === 'personalHistory') {
                setPersonalHistoryVideos(prev => prev.map(updateVideo));
              } else if (currentSection === 'teamPoint') {
                setTeamVideos(prev => prev.map(updateVideo));
              }
              
              console.log('🔄 已更新 ManagePages 的影片數據，觸發重新渲染');
            }
          } catch (error) {
            console.error('❌ 背景更新失敗:', error);
          }
        };
        updateVbidsInBackground().catch(err => console.error('背景更新異常:', err));
        
        console.log('🔍 ManagePage 傳遞的 vbids 數據:', {
          videoId: video.videoId,
          vbidsCount: vbids.length,
          vbids: vbids.slice(0, 3) // 只顯示前3個重點作為示例
        });
        
        onNavigateToMain({
          videoId: video.videoId,
          documentId: firestoreId,
          author: author,
          fromManagePages: true,
          source: 'manage',
          vbids: vbids, // 🔧 修復：傳遞最新的重點數據
          hasSRTCache: video.hasSRTCache || false // 🔥 新增：傳遞 AI Tag 狀態
        });
      } else {
        // 傳統路由模式：使用 navigate
        // 导航日志已移除
        // 🔧 修復：標記為用戶主動操作，確保觀看記錄被正確更新
        (window as any).__checkplay_user_initiated_play = true;
        // 调试日志已移除
        markPersonalHistoryDirty();
        navigate(`/v/${firestoreId}?author=${encodeURIComponent(author)}`, {
          state: {
            fromManagePages: true,
            documentId: firestoreId  // 🔧 修復：傳遞 documentId 給 MainPage
          }
        });
      }
    }
  }, [navigate, youtubeInfoCache, setVideos, setPersonalVideos, setCommunityVideos, isEmbedded, onNavigateToMain]);

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleBackToHome = () => {
    // 清除暫存
    localStorage.removeItem('mainPageStateBackup');
    sessionStorage.removeItem('mainPageState');
    localStorage.removeItem('returnFromFavorites');
    sessionStorage.removeItem('returnFromFavorites');
    localStorage.removeItem('preserveMainPageState');
    sessionStorage.removeItem('preserveMainPageState');
    
    // 🔧 修復：檢查是否在 SPA 模式下
    if (isEmbedded && onPageChange) {
      // SPA 模式：使用回調函數切換到首頁
      // 导航日志已移除
      onPageChange('open');
    } else {
      // 傳統路由模式：使用 navigate
      // 导航日志已移除
    navigate('/', { replace: true });
    }
  };

  const formatTimecode = (timecode: string): string => {
    if (!timecode) return '-';
    
    const [key, timeStr] = timecode.split('_');
    if (!timeStr) return key || '-';
    
    const paddedTime = timeStr.padStart(8, '0');
    const hours = paddedTime.substring(0, 2);
    const minutes = paddedTime.substring(2, 4);
    const seconds = paddedTime.substring(4, 6);
    const frames = paddedTime.substring(6, 8);
    
    return `${hours}:${minutes}:${seconds}.${frames}`;
  };

  // 🔧 getRecentlyAddedCount 已从 '../utils/durationUtils' 导入

  const handleLogout = async (forceReload = true) => {
    try {
      // 先顯示登出訊息
      setSnackbarMessage('登出中...');
      setSnackbarOpen(true);
      
      // 清除所有本地狀態和快取
      localStorage.removeItem('returnFromFavorites');
      localStorage.removeItem('mainPageStateBackup');
      sessionStorage.removeItem('mainPageState');
      sessionStorage.removeItem('guestVideos');
      sessionStorage.removeItem('videosCache');
      sessionStorage.removeItem('cacheTimestamps');
      
      // 重置所有組件狀態到初始值
      setVideos([]);
      setPersonalVideos([]);
      setCommunityVideos([]);
      setCommunityLoadedCount(0); // 重置社群影片載入數量
      setPersonalHistoryVideos([]);
      setCurrentSection('personalHistory');
      setLoading(false);
      setSnackbarOpen(false);
      
      // 執行 Firebase 登出
      await logout();
      
      if (forceReload) {
        // 強制重新載入頁面，確保完全重置到初始狀態
        navigate('/');
      } else {
        // 溫和方式：只導航到首頁
        setSnackbarMessage('已成功登出');
        setSnackbarOpen(true);
        navigate('/');
      }
      
    } catch (error) {
      console.error('登出失敗:', error);
      setSnackbarMessage('登出失敗，請稍後再試');
      setSnackbarOpen(true);
    }
  };

  useEffect(() => {
    return () => {
      // 同步日志已移除
    };
  }, []);

  // 调试日志已移除
  // 调试日志已移除

  // 取得 SSR base url
  const getSSRBaseUrl = () => {
    // 先檢查 window 變數，再 fallback
    if (typeof window !== 'undefined' && window.VITE_SSR_BASE_URL) {
      return window.VITE_SSR_BASE_URL;
    }
    return window.location.origin;
  };

  const handleShareVideo = useCallback((video: VideoItem) => {
    // 設置當前要分享的影片
    setCurrentShareVideo(video);
    setShareDialogOpen(true);
  }, [setCurrentShareVideo, setShareDialogOpen]);

  // 關閉分享對話框時重置狀態
  const handleCloseShareDialog = () => {
    setShareDialogOpen(false);
    setCurrentShareVideo(null);
  };

  // 登入後自動將 guestId 資料轉移到 email
  useEffect(() => {
    async function migrateGuestDataToUser(guestId: string, email: string) {
      const q = query(
        safeCollection( "sharedVbids"),
        where("userId", "==", guestId)
      );
      const querySnapshot = await getDocs(q);
      for (const docSnap of querySnapshot.docs) {
        await updateDoc(safeDoc( "sharedVbids", docSnap.id), { userId: email });
      }
      // localStorage.removeItem('guestId'); // 可選：轉移後清除 guestId
      if (querySnapshot.docs.length > 0) {
        // 调试日志已移除
      }
    }
    
    if (!authUser?.email || !guestId) {
      return;
    }
    
    migrateGuestDataToUser(guestId, authUser.email);
  }, [authUser, guestId]);

  const groupVideosByDate = (videos: VideoItem[]) => {
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);
    const isSameDay = (d1: Date, d2: Date) =>
      d1.getFullYear() === d2.getFullYear() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getDate() === d2.getDate();
    const todayVideos: VideoItem[] = [];
    const yesterdayVideos: VideoItem[] = [];
    const historyVideos: VideoItem[] = [];
    videos.forEach(v => {
      if (!v.addedDate) return historyVideos.push(v);
      const added = new Date(v.addedDate);
      if (isSameDay(added, today)) {
        todayVideos.push(v);
      } else if (isSameDay(added, yesterday)) {
        yesterdayVideos.push(v);
      } else {
        historyVideos.push(v);
      }
    });
    return { todayVideos, yesterdayVideos, historyVideos };
  };
  const communityList = communityVideos.filter(v => Array.isArray(v.zones) && v.zones.includes('community'));
  const { todayVideos, yesterdayVideos, historyVideos } = groupVideosByDate(communityList);

  const handleZoneChangeAll = useCallback(async (video: VideoItem, zone: string, checked: boolean) => {
    // Guest 時不寫入 Firestore，只在本機暫存
    // 查询日志已移除
    if (isGuest) {
      // 跳过日志已移除
      // 更新 sessionStorage 中的影片
      const guestVideos = JSON.parse(sessionStorage.getItem('guestVideos') || '[]');
      const updatedVideos = guestVideos.map((v: any) => 
        v.videoId === video.videoId ? { ...v, zones: checked ? [...(v.zones || []), zone] : (v.zones || []).filter((z: string) => z !== zone) } : v
      );
      sessionStorage.setItem('guestVideos', JSON.stringify(updatedVideos));
      
      // 本地 state 同步
      const newZones = checked 
        ? [...(video.zones || []), zone]
        : (video.zones || []).filter(z => z !== zone);
      const updatedVideo = { ...video, zones: newZones };
      
      setPersonalVideos(prev => prev.map(v =>
        v.videoId === video.videoId ? updatedVideo : v
      ));
      setCommunityVideos(prev => prev.map(v =>
        v.videoId === video.videoId ? updatedVideo : v
      ));
      setVideos(prev => prev.map(v =>
        v.videoId === video.videoId ? updatedVideo : v
      ));
      setPersonalHistoryVideos((prev: VideoItem[]) => prev.map((v: VideoItem) =>
        v.videoId === video.videoId ? updatedVideo : v
      ));
      return;
    }
    
    // 1. 取得目前所有 zone 狀態
    let newZones = Array.isArray(video.zones) ? [...video.zones] : [];
    if (checked) {
      newZones = Array.from(new Set([...newZones, zone]));
    } else {
      newZones = newZones.filter(z => z !== zone);
    }

    // 🔧 修復：只更新特定觀點，不影響其他觀點
    // 使用 video.id 作為文檔ID，確保只更新當前觀點
    const documentId = video.id || video.documentId;
    if (!documentId) {
      console.error('❌ 無法找到文檔ID，無法更新 zones');
      return;
    }

    // 配置日志已移除

    try {
      // 只更新當前觀點的文檔
      const docRef = safeDoc('sharedVbids', documentId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        // 文檔存在，更新它
        const updateObj: any = { zones: newZones };
        if (Array.isArray(video.groupIds)) {
          updateObj.groupIds = video.groupIds;
        }
        await updateDoc(docRef, updateObj);
        // 调试日志已移除
        
        // 🔧 修復：如果是取消珍藏（移除 person zone），同時從 userFavorites 中刪除
        if (zone === 'person' && !checked) {
          try {
            const { removeFromUserFavorites } = await import('../services/userFavoritesService');
            await removeFromUserFavorites(
              userId,
              video.videoId || video.id,
              documentId
            );
            // 调试日志已移除
          } catch (favError) {
            console.error('⚠️ 從 userFavorites 中移除失敗（可能不存在）:', favError);
          }
        }
      } else {
        console.error('❌ 文檔不存在:', documentId);
        return;
      }
    } catch (error) {
      console.error('❌ 更新 zones 失敗:', error);
      return;
    }

    // 🔧 修復：本地 state 同步 - 只更新當前觀點
    const updateVideoInState = (v: VideoItem) => {
      // 精確匹配：只更新完全相同的觀點
      const isMatch = v.id === video.id;
      if (isMatch) {
        console.log('🔧 更新觀點:', { 
          videoId: v.id, 
          viewpointName: v.viewpointName, 
          oldZones: v.zones, 
          newZones 
        });
        return { ...v, zones: newZones, groupIds: video.groupIds || [] };
      }
      return v;
    };

    setPersonalVideos(prev => prev.map(updateVideoInState));
    setCommunityVideos(prev => prev.map(updateVideoInState));
    setVideos(prev => prev.map(updateVideoInState));
    setPersonalHistoryVideos(prev => prev.map(updateVideoInState));
    
    // 🔧 修復：清除快取，強制重新載入
    const cacheKey = `${currentSection}_${userId}`;
    setVideosCache(prev => {
      const newCache = { ...prev };
      delete newCache[cacheKey];
      return newCache;
    });
    setCacheTimestamps(prev => {
      const newTimestamps = { ...prev };
      delete newTimestamps[cacheKey];
      return newTimestamps;
    });
    
    // 🔧 修復：移除強制重新渲染，避免性能問題
    // setForceUpdate(prev => prev + 1);
  }, [userId, isGuest, setPersonalVideos, setCommunityVideos, setVideos, setPersonalHistoryVideos]);

  // 統一的欄位寬度配置
  const UNIFIED_COLUMN_WIDTHS = {
    index: { xs: '50px', sm: '60px' },
    thumbnail: { xs: '160px', sm: '180px' }, // 增加縮圖寬度，避免裁切
    info: { xs: 'clamp(200px, 20vw, 300px)', sm: 'clamp(300px, 30vw, 350px)' }, // 減少影片資訊欄位寬度
    share: { xs: '0px', sm: '60px' },
    checkpoints: { xs: '160px', sm: '160px' }, // 🔧 統一 keypoint 欄位寬度為 160px
    zone: { xs: '0px', sm: '100px' },
    tags: { xs: '200px', sm: '200px' }, // 統一標籤欄位寬度為 200px
    groups: { xs: '200px', sm: '200px' },
    delete: { xs: '50px', sm: '60px' },
    source: { xs: '80px', sm: '80px' }
  };

  // 統一的欄位定義，使用 UNIFIED_COLUMN_WIDTHS 配置
  const COLUMNS = Object.entries(UNIFIED_COLUMN_WIDTHS).map(([key, widthConfig]) => {
    const baseColumn = {
      key,
      width: widthConfig,
      minWidth: { 
        xs: parseInt(widthConfig.xs.replace('px', '')), 
        sm: parseInt(widthConfig.sm.replace('px', '')) 
      }
    };

    // 為特定欄位添加標籤和特殊樣式
    const labels: Record<string, string> = {
      index: '序號',
      thumbnail: '縮圖',
      info: '影片資訊',
      share: '分享',
      checkpoints: 'Keypoint',
      zone: 'Zone',
      tags: 'Tag',
      groups: '群組',
      delete: '刪除',
      source: '來源'
    };

    const result = {
      ...baseColumn,
      label: labels[key] || key
    };

    // 為 info 欄位添加特殊樣式
    if (key === 'info') {
      return {
        ...result,
        cellStyle: {
          whiteSpace: 'nowrap',
          lineHeight: 1.4,
          maxHeight: '1.4em',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }
      };
    }

    // 為 index 欄位添加特殊樣式，縮減左右 padding
    if (key === 'index') {
      return {
        ...result,
        cellStyle: {
          paddingLeft: '2px !important',
          paddingRight: '2px !important',
          width: '40px !important',
          minWidth: '40px !important'
        }
      };
    }

    // 為 tags 欄位添加特殊樣式，強制限制寬度
    if (key === 'tags') {
      // 移除調試日誌以提升效能
      // console.log('🔍 設定 tags 欄位樣式:', {
      //   key,
      //   width: result.width,
      //   cellStyle: {
      //     maxWidth: '500px !important',
      //     width: '500px !important',
      //     minWidth: '500px !important'
      //   }
      // });
      return {
        ...result,
        cellStyle: {
          maxWidth: '200px !important',
          width: '200px !important',
          minWidth: '200px !important',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'normal'
        }
      };
    }

    return result;
  });
  // 所有分頁都在 zone 後面插入 groups 欄位
  const BASE_PAGE_COLUMN_KEYS: Record<string, string[]> = {
    myPoint:      ['index','thumbnail','info','share','checkpoints','zone','tags','groups','delete','source'],
    myPersonalFavorite: ['index','thumbnail','info','share','checkpoints','zone','tags','groups','delete','source'],
    myCommunityPublic: ['index','thumbnail','info','share','checkpoints','zone','tags','groups','delete','source'],
    myVideos:     ['index','thumbnail','info','share','checkpoints','zone','tags','groups','delete','source'], // 🔧 新增：我的創作分頁欄位配置
    communityPublic: ['index','thumbnail','info','share','checkpoints','tags','groups','delete','source'],
    teamPoint:    ['index','thumbnail','info','share','checkpoints','zone','tags','groups','delete','source'],
    mediaPoint:   ['index','thumbnail','info','share','checkpoints','zone','tags','groups','delete','source'],
    deliveryMain: ['index','thumbnail','info','share','checkpoints','zone','tags','groups','delete','source'],
    personalHistory: ['index','thumbnail','info','share','checkpoints','zone','tags','groups','delete','source'],
    videoUpload:  ['index','thumbnail','info','share','checkpoints','zone','tags','groups','delete','source'],
    expertKnowledge: ['index','thumbnail','info','share','checkpoints','zone','tags','groups','delete','source'],
    follow:       ['index','thumbnail','info','share','checkpoints','zone','tags','groups','delete','source'],
    localFiles:   ['index','thumbnail','info','share','checkpoints','zone','tags','groups','delete','source'],
    googleDrive:  ['index','thumbnail','info','share','checkpoints','zone','tags','groups','delete','source'],
  };
  
  // 統一的欄位配置系統
  const UNIFIED_COLUMN_CONFIG = {
    // 桌面版欄位順序（有 Sidebar）
    desktop: ['index','thumbnail','info','share','checkpoints','zone','tags','groups','delete','source'],
    // 手機版欄位順序（無 Sidebar）- 排除 zone 和 share，delete 移到 info 後面
    mobile: ['index','thumbnail','info','delete','checkpoints','tags','groups','source']
  };

  // 根據響應式狀態動態調整 PAGE_COLUMN_KEYS - 所有分頁使用統一配置
  const PAGE_COLUMN_KEYS = useMemo(() => {
    const result: Record<string, string[]> = {};
    
    // 所有分頁使用統一的欄位配置
    Object.keys(BASE_PAGE_COLUMN_KEYS).forEach(key => {
      result[key] = isMobile ? UNIFIED_COLUMN_CONFIG.mobile : UNIFIED_COLUMN_CONFIG.desktop;
    });
    
    return result;
  }, [isMobile]);
  
  // 1.5. 統一的 TableCell 樣式函數 - 使用 useMemo 優化
  const getTableCellStyle = useMemo(() => {
    return (col: any, isHeader: boolean = false) => {
    // 正確處理響應式寬度
    const getResponsiveWidth = (width: any) => {
      if (typeof width === 'object' && width.xs && width.sm) {
        const responsiveWidth = isMobile ? width.xs : width.sm;
        // 檢查是否包含 clamp 函數，如果是則直接返回
        if (typeof responsiveWidth === 'string' && responsiveWidth.includes('clamp')) {
          return responsiveWidth;
        }
        return responsiveWidth;
      }
      return width;
    };

    const getResponsiveMinWidth = (minWidth: any) => {
      if (typeof minWidth === 'object' && minWidth.xs && minWidth.sm) {
        const responsiveMinWidth = isMobile ? minWidth.xs : minWidth.sm;
        return responsiveMinWidth;
      }
      return minWidth;
    };

    // 新增：正確提取 clamp 函數中的最大值作為 maxWidth
    const getResponsiveMaxWidth = (width: any) => {
      if (typeof width === 'object' && width.xs && width.sm) {
        const responsiveWidth = isMobile ? width.xs : width.sm;
        if (typeof responsiveWidth === 'string' && responsiveWidth.includes('clamp')) {
          // 使用正則表達式提取 clamp 函數中的最大值
          const maxMatch = responsiveWidth.match(/clamp\([^,]+,[^,]+,\s*([^)]+)\)/);
          if (maxMatch) {
            return maxMatch[1].trim();
          }
        }
        return responsiveWidth;
      }
      return width;
    };

    const baseStyle = {
      // 將 TableCell 的上下 padding 設為 0
      py: 0,
      // 縮減 TableCell 的左右 padding，讓縮圖和 info 欄位更接近
      px: { xs: 0.25, sm: 0.25 },
      ...(isHeader && { fontWeight: 'bold' }),
      // 將 CSS 屬性正確地放在 sx 對象中，避免 DOM 警告
      sx: {
        width: getResponsiveWidth(col.width),
        minWidth: getResponsiveMinWidth(col.minWidth),
        maxWidth: getResponsiveMaxWidth(col.width),
      // 優先使用 col.cellStyle，如果沒有則使用預設樣式
      ...(col.cellStyle || (col.key === 'info' ? {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
      } : {
        overflow: (col.key === 'checkpoints' || col.key === 'info') ? 'visible' : 'hidden',
        textOverflow: col.key === 'checkpoints' ? 'clip' : 'ellipsis',
        whiteSpace: col.key === 'checkpoints' ? 'normal' : 'nowrap',
      }))
      }
    };

    

    
    return baseStyle;
  };
  }, [isMobile]); // 添加依賴項
  
  // 1.6. 優化的表格行組件 - 使用 React.memo 避免不必要的重新渲染
  const OptimizedTableRow = React.memo(({ 
    video, 
    columns, 
    section, 
    idx 
  }: { 
    video: VideoItem; 
    columns: any[]; 
    section: string; 
    idx: number; 
  }) => (
    <TableRow key={`${section}-${video.id}-${idx}`}>
      {columns.map(col => (
        <TableCell
          key={`${section}-${video.id}-${col.key}-${idx}`}
          data-col={col.key}
          align="center"
          {...getTableCellStyle(col, false)}
        >
          {renderCellContent(col.key, video, idx, section)}
        </TableCell>
      ))}
    </TableRow>
  ));
  
  // 1.7. 虛擬化表格組件 - 用於處理大量數據
  const VirtualizedTable = React.memo(({ 
    videos, 
    columns, 
    section,
    height = 600 
  }: { 
    videos: VideoItem[]; 
    columns: any[]; 
    section: string;
    height?: number;
  }) => {
    const [visibleRange, setVisibleRange] = useState({ start: 0, end: 50 });
    const containerRef = useRef<HTMLDivElement>(null);
    
    const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, clientHeight } = e.currentTarget;
      const itemHeight = 60; // 估算每行高度
      const start = Math.floor(scrollTop / itemHeight);
      const end = Math.min(start + Math.ceil(clientHeight / itemHeight) + 10, videos.length);
      
      setVisibleRange({ start, end });
    }, [videos.length]);
    
    const visibleVideos = videos.slice(visibleRange.start, visibleRange.end);
    
    return (
      <div 
        ref={containerRef}
        style={{ height, overflow: 'auto' }}
        onScroll={handleScroll}
      >
        <Table>
          <TableBody>
            {visibleVideos.map((video, idx) => (
              <OptimizedTableRow
                key={`${section}-${video.id}-${visibleRange.start + idx}`}
                video={video}
                columns={columns}
                section={section}
                idx={visibleRange.start + idx}
              />
            ))}
          </TableBody>
        </Table>
      </div>
    );
  });
  
  // 1.8. 性能監控 Hook
  const usePerformanceMonitor = (componentName: string) => {
    const renderStart = useRef<number>(0);
    
    useEffect(() => {
      renderStart.current = performance.now();
    });
    
    useEffect(() => {
      const renderTime = performance.now() - renderStart.current;
      if (renderTime > 16) { // 超過 16ms (60fps) 才記錄
        console.warn(`⚠️ ${componentName} 渲染時間過長: ${renderTime.toFixed(2)}ms`);
      }
    });
  };
  
  // 性能監控
  usePerformanceMonitor('ManagePages');
  
  // 2. renderCellContent function
  const renderCellContent = (key: string, video: VideoItem, index: number, group: string) => {
    // 🔧 安全檢查：如果 video 為 undefined 或 null，返回空內容
    if (!video) {
      console.warn('⚠️ [renderCellContent] video 對象為空，跳過渲染');
      return null;
    }
    
    // 在函數內部獲取螢幕尺寸
    const isMobileInFunction = window.innerWidth <= 1024;
    // 檢查是否為未登入者且在社群內容或頁面
    const isGuestInCommunityOrExpert = userType === 'guest' && 
      (currentSection === 'mediaPoint' || currentSection === 'expertKnowledge' || currentSection === 'communityPublic');

    switch (key) {
      case 'index':
        if (video.checkpointCount && video.checkpointCount > 0) {
          // 有重點標記：紅色底白字
          return (
            <Box 
              data-col="index"
              sx={{
                width: 24,
                height: 24,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                backgroundColor: '#e53935',
                color: '#fff',
                fontWeight: 'bold',
                fontSize: 13,
                mx: 'auto',
              }}>
              {index + 1}
            </Box>
          );
        } else {
          // 無重點標記：淺灰底黑字
          return (
            <Box 
              data-col="index"
              sx={{
                width: 24,
                height: 24,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                backgroundColor: '#f5f5f5',
                color: '#333',
                fontWeight: 'bold',
                fontSize: 13,
                mx: 'auto',
                border: '2px solid #999',
              }}>
              {index + 1}
            </Box>
          );
        }
      case 'thumbnail': {
        // 🔧 修復：優先使用 video.videoId，然後是 video.path
        const videoId = video.videoId || extractYouTubeId(video.path || '');
        const isYouTube = !!videoId && videoId.length === 11;
        // debug log
        // 调试日志已移除
        return isYouTube ? (
            <Box 
              sx={{ 
                width: '100%',
                position: 'relative', // 🔥 相對定位，用於浮水印
                cursor: 'pointer',
                display: 'inline-block' // ✅ 使容器緊貼圖片
              }}
              onClick={() => handlePlayVideo(video)}
            >
          <img
            src={`https://img.youtube.com/vi/${videoId}/hqdefault.jpg`}
            alt="YouTube縮圖"
            onError={(e) => {
              // 當 hqdefault.jpg 載入失敗時，嘗試載入 default.jpg
              const target = e.target as HTMLImageElement;
              if (target.src.includes('hqdefault.jpg')) {
                target.src = `https://img.youtube.com/vi/${videoId}/default.jpg`;
              } else if (target.src.includes('default.jpg')) {
                // 如果 default.jpg 也失敗，顯示預設圖片
                target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIwIiBoZWlnaHQ9IjE4MCIgdmlld0JveD0iMCAwIDMyMCAxODAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIzMjAiIGhlaWdodD0iMTgwIiBmaWxsPSIjRjVGNUY1Ii8+CjxwYXRoIGQ9Ik0xNDAgODBMMTYwIDEwMEwxNDAgMTIwTDEyMCAxMDBMMTQwIDgwWiIgZmlsbD0iIzk5OTk5OSIvPgo8dGV4dCB4PSIxNjAiIHk9IjEwNSIgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjE0IiBmaWxsPSIjOTk5OTk5Ij5WaWRlbzwvdGV4dD4KPC9zdmc+';
                target.alt = '影片縮圖載入失敗';
              }
            }}
            style={{ 
              width: '100%', 
              height: 'auto', 
              borderRadius: 12, 
              objectFit: 'cover', 
              background: '#000', 
              display: 'block'
            }}
            title="點擊播放"
          />
          
          {/* 🔥 影片長度浮水印（右下角） */}
          {(() => {
            const rawDuration = video.youtubeInfo?.duration || video.videoInfo?.duration || video.duration;
            if (!rawDuration) return null;
            
            // 如果是 ISO 8601 格式（如 PT28M2S），先轉換為可讀格式
            const displayDuration = rawDuration.startsWith('PT') 
              ? parseISO8601Duration(rawDuration) 
              : rawDuration;
            
            if (displayDuration) {
              return (
                <span
                  style={{
                    position: 'absolute',
                    bottom: '6px',
                    right: '6px',
                    left: 'auto',
                    top: 'auto',
                    backgroundColor: 'rgba(0, 0, 0, 0.85)',
                    color: '#ffffff',
                    borderRadius: '3px',
                    padding: '3px 6px',
                    fontSize: '11px',
                    fontWeight: 700,
                    pointerEvents: 'none',
                    zIndex: 10,
                    boxShadow: '0 1px 4px rgba(0,0,0,0.4)',
                    letterSpacing: '0.3px',
                    lineHeight: 1,
                    display: 'inline-block'
                  }}
                >
                  {displayDuration}
                </span>
              );
            }
            return null;
          })()}
          </Box>
        ) : (
          <Box 
            sx={{ 
              width: '100%',
              height: '100%',
              minHeight: 60,
              flexShrink: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              position: 'relative',
              cursor: 'pointer',
              // 🔧 優化：防止 hover 時的閃爍，啟用硬體加速
              transform: 'translateZ(0)',
              backfaceVisibility: 'hidden',
              perspective: 1000
            }}
            onClick={() => handlePlayVideo(video)}
          >
            {/* 🔧 修復：檢查多個可能的縮圖欄位 */}
            {(video.thumbnail || (video as any).thumbnailUrl || video.cloudFile?.thumbnailUrl) ? (
              <img
                src={video.thumbnail || (video as any).thumbnailUrl || video.cloudFile?.thumbnailUrl}
                alt="影片縮圖"
                onError={(e) => {
                  // 🔧 記錄縮圖載入失敗的詳細資訊（僅在開發環境）
                  if (process.env.NODE_ENV === 'development') {
                    console.error('❌ 縮圖載入失敗（非 YouTube）:', {
                      videoId: video.id,
                      thumbnailUrl: video.thumbnail || (video as any).thumbnailUrl || video.cloudFile?.thumbnailUrl,
                      source: video.source
                    });
                  }
                  
                  // 縮圖載入失敗時，顯示預設圖片
                  const target = e.target as HTMLImageElement;
                  // 🔧 防止重複觸發錯誤處理
                  if (target.src && !target.src.includes('CheckPlay_Logo')) {
                    target.src = '/CheckPlay_Logo-100x100.png';
                    target.alt = '預設縮圖';
                  }
                }}
                style={{ 
                  width: '100%', 
                  height: 'auto', 
                  borderRadius: 8, 
                  objectFit: 'cover', 
                  background: '#000', 
                  display: 'block',
                  // 🔧 優化：防止 hover 時的閃爍
                  imageRendering: 'auto',
                  transform: 'translateZ(0)' // 啟用硬體加速
                }}
                title="點擊播放"
                decoding="async"
              />
            ) : (
              <img
                src="/CheckPlay_Logo-100x100.png"
                alt="預設縮圖"
                style={{ 
                  width: '100%', 
                  height: 'auto', 
                  borderRadius: 8, 
                  objectFit: 'cover', 
                  background: '#ccc', 
                  display: 'block'
                }}
              />
            )}
          </Box>
        );
      }
      case 'info': {
        const videoId = extractYouTubeId(video.path || '');
        const isYouTube = !!videoId;
        const fileName = video.path?.split(/[\\/]/).pop() || '-';
        let modifiedAt = '-';
        if (video.modifiedAt) {
          const d = new Date(video.modifiedAt);
          if (!isNaN(d.getTime())) {
            modifiedAt = `${d.getFullYear()}/${(d.getMonth()+1).toString().padStart(2,'0')}/${d.getDate().toString().padStart(2,'0')}`;
          }
        }
        
        
        return (
            <Box 
              data-col="info"
              sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                justifyContent: 'center', 
                // 手機模式固定高度 100px，桌面模式保持 108px
                minHeight: { xs: '100px', sm: '108px' },
                gap: 0.2,
                // 手機模式保持100%寬度，但保持字體大小
                width: { xs: '100%', sm: '100%' },
                maxWidth: { xs: 'none', sm: 'none' }, // 手機模式不限制最大寬度
                // 縮減與縮圖欄位的間距
                pl: { xs: 0, sm: 0.5 }, // 左側 padding 減少
                pr: { xs: 0, sm: 1 },   // 右側 padding 保持
                py: { xs: 0, sm: 1 }    // 上下 padding 保持
              }}>
            <Typography
              variant="subtitle1"
              fontWeight="bold"
              sx={{
                lineHeight: 1.25,
                mb: 0.2,
                width: '100%',
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                textAlign: 'left',
                color: 'text.primary',
                visibility: 'visible',
                opacity: 1,
                wordBreak: 'break-word',
                cursor: 'pointer',
                '&:hover': {
                  color: 'primary.main',
                  textDecoration: 'underline'
                }
              }}
              onClick={() => handlePlayVideo(video)}
              title="點擊播放"
            >
              {isYouTube ? (video.youtubeInfo?.title || video.videoInfo?.title || (video.title && video.title !== 'undefined' ? video.title : null) || videoId || '-') : 
                // 🔧 修復：優先使用 originalFileName，然後是 title，最後是從 URL 提取的文件名
                ((video as any).originalFileName || video.title || (() => {
                  // 如果是 GCS 影片，嘗試從 URL 提取原始文件名
                  if (video.path && video.path.includes('storage.googleapis.com')) {
                    // 從 URL 中提取文件名，移除查詢參數
                    const urlParts = video.path.split('/');
                    const fileNameWithQuery = urlParts[urlParts.length - 1];
                    const fileName = fileNameWithQuery.split('?')[0];
                    // 如果是 UUID 格式，嘗試從 metadata 獲取原始文件名
                    if (fileName.match(/^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}\./)) {
                      return (video as any).originalFileName || '未命名影片';
                    }
                    return fileName;
                  }
                  return fileName;
                })())}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.2, lineHeight: 1.3, textAlign: 'left' }} noWrap>
              頻道：{(() => {
                if (isYouTube) {
                  return video.channelTitle || video.youtubeInfo?.channelTitle || video.videoInfo?.channelTitle || '-';
                }
                // 對於子觀點，即使不是 YouTube 影片，也要顯示繼承的頻道信息
                const channelTitle = video.channelTitle || video.youtubeInfo?.channelTitle || video.videoInfo?.channelTitle;
                return channelTitle || '-';
              })()}
            </Typography>
              {/* 在手機模式下隱藏觀看次數 */}
              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ 
                  mb: 0.2, 
                  lineHeight: 1.3, 
                  textAlign: 'left',
                  display: isMobile ? 'none' : 'block' // 使用isMobile變數判斷
                }}
              >
              觀看次數：{isYouTube ? formatViewCount(video.viewCount || video.youtubeInfo?.viewCount || video.videoInfo?.viewCount) : '-'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.3, textAlign: 'left' }}>
                發布：{(() => {
                  if (isYouTube) {
                    return formatDate(video.publishedAt || video.youtubeInfo?.publishedAt || video.videoInfo?.publishedAt);
                  }
                  // 對於子觀點，即使不是 YouTube 影片，也要顯示繼承的發布日期
                  const publishedAt = video.publishedAt || video.youtubeInfo?.publishedAt || video.videoInfo?.publishedAt;
                  return publishedAt ? formatDate(publishedAt) : modifiedAt;
                })()}
              </Typography>
              
              {/* 🔥 新增：AI Tag - 直接從 Firestore 讀取 */}
              {video.hasSRTCache && (
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#f44336',
                    color: '#ffffff',
                    borderRadius: '4px',
                    px: 0.75,
                    py: 0.25,
                    fontSize: '0.65rem',
                    fontWeight: 600,
                    lineHeight: 1
                  }}
                >
                  AI
                </Box>
              )}
            </Box>
            
            {/* 🆕 觀點名稱顯示 - 顯示所有觀點名稱 */}
            {(() => {
              const currentViewpoint = video.viewpointName;
              
              // 總是顯示觀點名稱，包括預設觀點
              if (currentViewpoint) {
                return (
                  <Typography variant="body2" color="primary" sx={{ lineHeight: 1.3, textAlign: 'left', mt: 0.5 }}>
                    觀點：{currentViewpoint}
                  </Typography>
                );
              }
              // 如果沒有觀點名稱，顯示預設觀點
              return (
                <Typography variant="body2" color="primary" sx={{ lineHeight: 1.3, textAlign: 'left', mt: 0.5 }}>
                  觀點：預設觀點
                </Typography>
              );
            })()}
              
                                                          {/* 手機模式下在發布下方顯示分享按鈕和三個功能按鈕 */}
                                                          {/* DEBUG: isMobileInFunction = {isMobileInFunction ? 'true' : 'false'}, window.innerWidth = {window.innerWidth} */}
                                                          <Box sx={{ 
                                                            display: isMobileInFunction ? 'flex' : 'none', // 使用函數內部的isMobile變數判斷
                                                            justifyContent: 'flex-start',
                                                            alignItems: 'center',
                                                            mt: 1,
                                                            gap: 0.5
                                                          }}>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => handleShareVideo(video)}
                  title="分享"
                  sx={{ 
                    p: 0.5,
                    '&:hover': {
                      backgroundColor: 'rgba(25, 118, 210, 0.1)'
                    }
                  }}
                >
                  <ShareIcon fontSize="small" />
                </IconButton>
                
                {/* 手機版的三個功能按鈕 */}
                {ZONE_OPTIONS.map(opt => {
                  // 🔧 修復：精確匹配當前觀點，避免找到錯誤的觀點
                  const latestVideo = personalHistoryVideos.find(v => v.id === video.id) || 
                                    personalVideos.find(v => v.id === video.id) || 
                                    communityVideos.find(v => v.id === video.id) || 
                                    videos.find(v => v.id === video.id) || 
                                    video;
                  // 🔒 安全檢查：權限控制
                  const isOwner = latestVideo.userId === userId || latestVideo.userId === authUser?.uid || latestVideo.userId === authUser?.email;
                  const isLoggedIn = authUser && (authUser.uid || authUser.email);
                  
                  // 「公開」和「群組」按鈕：只有影片擁有者才能看到
                  if (opt.value === 'community' || opt.value === 'team') {
                    if (!isOwner) {
                      return null;  // 非擁有者不顯示「公開」和「群組」按鈕
                    }
                  }
                  
                  // 「珍藏」按鈕：所有登入用戶都可以看到（可以收藏任何影片）
                  if (opt.value === 'person') {
                    if (!isLoggedIn) {
                      return null;  // 未登入用戶不顯示「珍藏」按鈕
                    }
                  }
                  
                  // 🔧 修復：改用 userFavoriteIds 判斷珍藏狀態，而不是 zones
                  const isPerson = opt.value === 'person' && userFavoriteIds.has(latestVideo.id);
                  const isTeam = opt.value === 'team' && Array.isArray(latestVideo.groupIds) && latestVideo.groupIds.length > 0;
                  const isCommunity = opt.value === 'community' && Array.isArray(latestVideo.zones) && latestVideo.zones.includes('community');
                  
                  // 🔍 診斷日誌：珍藏按鈕狀態（只在開發模式）
                  if (opt.value === 'person' && import.meta.env.DEV && latestVideo.title?.includes('AI 模式')) {
                    console.log('🔍 [珍藏按鈕] AI 模式影片:', { 
                      title: latestVideo.title?.substring(0, 30),
                      videoId: latestVideo.videoId,
                      firestoreId: latestVideo.id, 
                      isPerson, 
                      inFavorites: userFavoriteIds.has(latestVideo.id),
                      favoriteIdsSize: userFavoriteIds.size,
                      favoriteIdsSample: Array.from(userFavoriteIds).slice(0, 3)
                    });
                  }
                  
                  return (
                    <Button
                      key={opt.value}
                      size="small"
                      variant={(isPerson || isTeam || isCommunity) ? 'contained' : 'outlined'}
                      onClick={async () => {
                        if (opt.value === 'team') {
                          if (!authUser || !authUser.email) {
                            setSnackbarMessage('請先登入才能分享給好友群組');
                            setSnackbarOpen(true);
                            return;
                          }
                          setGroupDialogVideo(video);
                          setGroupDialogOpen(true);
                          // 延遲載入群組，避免重複載入
                          if (!userGroups.length) {
                            const groups = await getUserGroups(authUser.email);
                            if (Array.isArray(groups)) {
                              setUserGroups(groups);
                            } else {
                              setUserGroups([]);
                            }
                          }
                          const groupIds = Array.isArray(video.groupIds) ? video.groupIds : [];
                          setSelectedGroups(groupIds);
                        } else if (opt.value === 'person') {
                          // 🔧 修復：改用 userFavoriteIds 判斷珍藏狀態
                      const wasChecked = userFavoriteIds.has(latestVideo.id);
                          
                          if (!wasChecked) {
                            // 🔧 修改：當「珍藏」是灰色時，點擊後直接執行珍藏操作（參考公開按鈕的實現）
                            // 调试日志已移除
                            
                            // 立即更新本地狀態，提供即時反饋（參考公開按鈕的實現）
                            const newZones = [...(latestVideo.zones || []), 'person'];
                            const updatedVideo = { ...latestVideo, zones: newZones };
                            
                            // 立即更新所有相關狀態，確保按鈕立刻變綠色
                            setVideos(prev => prev.map(v => v.id === latestVideo.id ? updatedVideo : v));
                            setPersonalVideos(prev => prev.map(v => v.id === latestVideo.id ? updatedVideo : v));
                            setCommunityVideos(prev => prev.map(v => v.id === latestVideo.id ? updatedVideo : v));
                            setPersonalHistoryVideos(prev => prev.map(v => v.id === latestVideo.id ? updatedVideo : v));
                            
                            // 異步更新 Firestore 和珍藏列表
                            try {
                              // 🔧 修復：同時更新 sharedVbids 的 zones 欄位
                              const db = getDb();
                              if (db) {
                                const videoRef = doc(db, 'sharedVbids', latestVideo.id);
                                await updateDoc(videoRef, {
                                  zones: arrayUnion('person')
                                });
                                // 调试日志已移除
                              }
                              
                              const { addToUserFavorites } = await import('../services/userFavoritesService');
                              await addToUserFavorites(
                                userId,
                                latestVideo.videoId || latestVideo.id,
                                latestVideo.id
                              );
                              // 调试日志已移除
                              
                              // 🔧 修復：立即更新 userFavoriteIds，讓按鈕立刻變綠
                              setUserFavoriteIds(prev => {
                                const newSet = new Set(prev);
                                newSet.add(latestVideo.id);
                                return newSet;
                              });
                              // 调试日志已移除
                              
                              // 🔧 修復：清除「我的收藏」分頁的快取，強制重新載入
                              const favCacheKey = `myPersonalFavorite_${userId}`;
                              setVideosCache(prev => {
                                const newCache = { ...prev };
                                delete newCache[favCacheKey];
                                return newCache;
                              });
                              setCacheTimestamps(prev => {
                                const newTimestamps = { ...prev };
                                delete newTimestamps[favCacheKey];
                                return newTimestamps;
                              });
                              // 清理日志已移除
                              
                              setSnackbarMessage('已加入我的收藏');
                              setSnackbarOpen(true);
                            } catch (error) {
                              console.error('❌ 珍藏操作失敗:', error);
                              setSnackbarMessage('珍藏操作失敗');
                              setSnackbarOpen(true);
                            }
                          } else {
                            // 當「珍藏」是綠色時，點擊後變成 disable 灰色狀態
                            const newZones = (latestVideo.zones || []).filter(z => z !== 'person');
                            const updatedVideo = { ...latestVideo, zones: newZones };
                            
                            setPersonalVideos(prev =>
                              prev.map(v =>
                                v.id === video.id ? updatedVideo : v
                              )
                            );
                            setVideos(prev =>
                              prev.map(v =>
                                v.id === video.id ? updatedVideo : v
                              )
                            );
                            // 同時更新 personalHistoryVideos，確保瀏覽記錄分頁中的狀態同步
                            setPersonalHistoryVideos(prev =>
                              prev.map(v =>
                                v.id === video.id ? updatedVideo : v
                              )
                            );
                            
                            // 異步更新 Firestore
                            handleZoneChangeAll(latestVideo, 'person', false);
                            
                            // 提供視覺反饋
                            setSnackbarMessage('已從個人觀看清單移除');
                            setSnackbarOpen(true);
                          }
                        } else if (opt.value === 'community') {
                          // 公開按鈕處理
                          const wasChecked = Array.isArray(latestVideo.zones) && latestVideo.zones.includes('community');
                          
                          // 立即更新本地狀態，提供即時反饋
                          const newZones = wasChecked 
                            ? (latestVideo.zones || []).filter(z => z !== 'community')
                            : [...(latestVideo.zones || []), 'community'];
                          const updatedVideo = { ...latestVideo, zones: newZones };
                          
                          // 🔧 修復：精確匹配，避免影響其他影片
                          console.log('🔧 公開按鈕處理:', { 
                            currentVideoId: video.id, 
                            currentViewpointName: video.viewpointName,
                            newZones 
                          });
                          
                          setPersonalVideos(prev =>
                            prev.map(v => {
                              if (v.id === video.id) {
                                console.log('🔧 更新個人影片:', { 
                                  videoId: v.id, 
                                  viewpointName: v.viewpointName,
                                  oldZones: v.zones,
                                  newZones 
                                });
                                return updatedVideo;
                              }
                              return v;
                            })
                          );
                          setVideos(prev =>
                            prev.map(v => {
                              if (v.id === video.id) {
                                console.log('🔧 更新影片列表:', { 
                                  videoId: v.id, 
                                  viewpointName: v.viewpointName,
                                  oldZones: v.zones,
                                  newZones 
                                });
                                return updatedVideo;
                              }
                              return v;
                            })
                          );
                          // 同時更新 personalHistoryVideos，確保瀏覽記錄分頁中的狀態同步
                          setPersonalHistoryVideos(prev =>
                            prev.map(v => {
                              if (v.id === video.id) {
                                console.log('🔧 更新瀏覽記錄:', { 
                                  videoId: v.id, 
                                  viewpointName: v.viewpointName,
                                  oldZones: v.zones,
                                  newZones 
                                });
                                return updatedVideo;
                              }
                              return v;
                            })
                          );
                          
                          // 異步更新 Firestore
                          handleZoneChangeAll(latestVideo, 'community', !wasChecked);
                          
                          // 提供視覺反饋
                          setSnackbarMessage(wasChecked ? '已取消公開' : '已公開');
                          setSnackbarOpen(true);
                        }
                      }}
                      sx={{ 
                        p: 0.5,
                        minWidth: 'auto',
                        fontSize: '0.7rem',
                        '&:hover': {
                          backgroundColor: 'rgba(25, 118, 210, 0.1)',
                          color: '#1565c0'  // 深藍色文字，確保懸停時可見
                        }
                      }}
                                         >
                       <Typography variant="caption" sx={{ fontSize: '0.7rem', lineHeight: 1 }}>
                         {opt.label}
                       </Typography>
                     </Button>
                  );
                })}
              </Box>
          </Box>
        );
      }
      case 'checkpoints': {
        const myUserId = authUser?.email || '';
        
        // 🔧 修改：只顯示用戶本人的重點數
        let userCheckpointCount = 0;
        
        // 優先使用 userNotesCount（用戶本人的重點數）
        if ((video as any).userNotesCount) {
          userCheckpointCount = (video as any).userNotesCount;
        } else if ((video as any).numberKeys && typeof (video as any).numberKeys === 'object') {
          // 從 numberKeys 計算實際的重點數
          userCheckpointCount = Object.values((video as any).numberKeys).filter((keyState: any) => 
            keyState.isActive && (keyState.timestamp !== null || keyState.note?.trim())
          ).length;
        } else {
          // 回退到個人重點數
          userCheckpointCount = video.personalCheckpointCount || video.checkpointCount || 0;
        }
        
        // 🔧 修復：對於雲端檔案，如果重點數為 0 且有 cloudFile 標記，不顯示載入中
        // 只有在真正缺少數據時才顯示載入中
        const isCloudFile = (video as any).source === 'cloud_upload' || (video as any).cloudFile;
        const isLoading = userCheckpointCount === 0 && !(video as any).numberKeys && !isCloudFile;
        
        // 🔧 修復：使用統一的統計數據計算邏輯
        const stats = calculateVideoStats(video);

        return (
          <Box 
            data-col="checkpoints"
            sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'flex-start', 
              fontSize: 12, 
              lineHeight: 1.15, 
              gap: 0.5,
              textAlign: 'left'
            }}>
            {/* 顯示用戶本人的重點數 */}
            <span style={{ 
              color: userCheckpointCount > 0 ? 'red' : undefined, 
              fontSize: 12,
              fontWeight: 'bold',
              textAlign: 'left',
              display: 'block'
            }}>
              {isLoading ? '重點數: 載入中...' : `重點數: ${userCheckpointCount}`}
            </span>
            <span style={{ fontSize: 12, textAlign: 'left', display: 'block' }}>關注度：{stats.attentionCount}</span>
            <span style={{ fontSize: 12, textAlign: 'left', display: 'block' }}>收藏數：{stats.favoriteCount}</span>
            {/* 版本資訊：從文檔ID中提取版本資訊 */}
            {(() => {
              // 從文檔ID中提取版本資訊
              const documentId = video.id || video.documentId || '';
              let versionInfo = '';
              
              // 檢查是否為新的分層設計格式：{userId}_{fileId}_{version}
              const versionMatch = documentId.match(/_v(\d+)$/);
              if (versionMatch) {
                const versionNumber = versionMatch[1];
                versionInfo = `v${versionNumber}`;
              } else {
                // 如果沒有版本號，可能是舊格式或單一版本
                versionInfo = 'v1';
              }
              
              return (
                <span style={{ fontSize: 12, color: '#666', textAlign: 'left', display: 'block' }}>
                  版本：{versionInfo}
            </span>
              );
            })()}
            {/* 建立者資訊：優先使用nickname、email，最後才使用系統ID */}
            <CreatorInfoDisplay video={video} />
          </Box>
        );
      }
      case 'zone':
        // 手機版隱藏 Zone 欄位
        if (isMobile) {
          return null;
        }
        
        // 取得目前 user 的所有群組
        // 🔧 修復：精確匹配當前觀點，避免找到錯誤的觀點
        const latestVideo = personalHistoryVideos.find(v => v.id === video.id) || 
                          personalVideos.find(v => v.id === video.id) || 
                          communityVideos.find(v => v.id === video.id) || 
                          personalHistoryVideos.find(v => v.id === video.id) || 
                          video;
        const isTeamChecked = Array.isArray(latestVideo.groupIds) && latestVideo.groupIds.length > 0;
        
        // 如果是未登入者在社群內容或專家筆記頁面，顯示禁用狀態
        if (isGuestInCommunityOrExpert) {
          return (
            <FormGroup sx={{ gap: '0px', alignItems: 'center' }}>
            {(
              currentSection === 'mediaPoint'
                ? ZONE_OPTIONS.filter(opt => ['person', 'team'].includes(opt.value))
                : ZONE_OPTIONS
            ).map(opt => {
              // 🔧 修復：精確匹配當前觀點，避免找到錯誤的觀點
              const latestVideo = personalHistoryVideos.find(v => v.id === video.id) || 
                                personalVideos.find(v => v.id === video.id) || 
                                communityVideos.find(v => v.id === video.id) || 
                                videos.find(v => v.id === video.id) || 
                                video;
              
              // 🔧 修復：訪客模式也使用 userFavoriteIds 判斷（訪客的 userFavoriteIds 是空的）
              const isPerson = opt.value === 'person' && userFavoriteIds.has(latestVideo.id);
              const isTeam = opt.value === 'team' && isTeamChecked;
              const isCommunity = opt.value === 'community' && Array.isArray(latestVideo.zones) && latestVideo.zones.includes('community');
              return (
                <Button
                  key={opt.value}
                  variant={(isPerson || isTeam || isCommunity) ? 'contained' : 'outlined'}
                  disabled={true}
                  sx={{ 
                    borderRadius: '8px', 
                    textTransform: 'none', 
                    fontSize: 13, 
                    minWidth: '80px', 
                    mb: 0.5,
                    padding: '4px 8px',
                    backgroundColor:
                      isPerson ? '#00AA55' :
                      isTeam ? '#009FCC' :
                      isCommunity ? '#FF7744' :
                      '#f5f5f5',
                    color:
                      isPerson || isTeam || isCommunity ? 'white' : '#999',
                    borderColor: '#ddd',
                    boxShadow: 'none',
                    opacity: 0.6,
                    cursor: 'not-allowed',
                    '&:hover': {
                      backgroundColor:
                        isPerson ? '#00AA55' :
                        isTeam ? '#009FCC' :
                        isCommunity ? '#FF7744' :
                        '#f5f5f5',
                      color: isPerson || isTeam || isCommunity ? 'white' : '#999',
                    },
                  }}
                >
                  {opt.label}
                </Button>
              );
            })}
          </FormGroup>
          );
        }
        
          return (
            <FormGroup sx={{ gap: '0px', alignItems: 'center' }}>
            {(
              currentSection === 'mediaPoint'
                ? ZONE_OPTIONS.filter(opt => ['person', 'team'].includes(opt.value))
                : ZONE_OPTIONS
            ).map(opt => {
              // 🔧 修復：精確匹配當前觀點，避免找到錯誤的觀點
              const latestVideo = personalHistoryVideos.find(v => v.id === video.id) || 
                                personalVideos.find(v => v.id === video.id) || 
                                communityVideos.find(v => v.id === video.id) || 
                                videos.find(v => v.id === video.id) || 
                                video;
              
              // 🔧 修復：改用 userFavoriteIds 判斷珍藏狀態，而不是 zones
              const isPerson = opt.value === 'person' && userFavoriteIds.has(latestVideo.id);
              const isTeam = opt.value === 'team' && isTeamChecked;
              const isCommunity = opt.value === 'community' && Array.isArray(latestVideo.zones) && latestVideo.zones.includes('community');
              
              // 🔍 診斷日誌：珍藏按鈕渲染（只在開發模式，且只針對特定影片）
              // if (opt.value === 'person' && import.meta.env.DEV && latestVideo.title?.includes('AI 模式')) {
              //   console.log('🔍 [珍藏按鈕-登入渲染]', { 
              //     title: latestVideo.title?.substring(0, 20),
              //     firestoreId: latestVideo.id, 
              //     isPerson, 
              //     inFavorites: userFavoriteIds.has(latestVideo.id),
              //     favoriteIdsSize: userFavoriteIds.size
              //   });
              // }
              
              return (
                <Button
                  key={opt.value}
                  variant={(
                    (opt.value === 'person' && userFavoriteIds.has(latestVideo.id)) || 
                    isTeam || 
                    isCommunity
                  ) ? 'contained' : 'outlined'}
                  onClick={async () => {
                    if (opt.value === 'team') {
                        if (!authUser || !authUser.email) {
                          setSnackbarMessage('請先登入才能分享給好友群組');
                          setSnackbarOpen(true);
                          return;
                        }
                        setGroupDialogVideo(video);
                        setGroupDialogOpen(true);
                        // 延遲載入群組，避免重複載入
                        if (!userGroups.length) {
                        const groups = await getUserGroups(authUser.email);
                        if (Array.isArray(groups)) {
                          setUserGroups(groups);
                        } else {
                          setUserGroups([]);
                          }
                        }
                        const groupIds = Array.isArray(video.groupIds) ? video.groupIds : [];
                        setSelectedGroups(groupIds);
                    } else if (opt.value === 'person') {
                      // 🔧 修復：改用 userFavoriteIds 判斷珍藏狀態
                      const wasChecked = userFavoriteIds.has(latestVideo.id);
                      
                      console.log('🔍 「珍藏」按鈕點擊:', {
                        videoId: latestVideo.id,
                        wasChecked,
                        zones: latestVideo.zones,
                        optValue: opt.value
                      });
                      
                      if (!wasChecked) {
                        // 🔧 修改：當「珍藏」是灰色時，點擊後直接執行珍藏操作（參考公開按鈕的實現）
                        // 调试日志已移除
                        
                        // 立即更新本地狀態，提供即時反饋（參考公開按鈕的實現）
                        const newZones = [...(latestVideo.zones || []), 'person'];
                        const updatedVideo = { ...latestVideo, zones: newZones };
                        
                        // 立即更新所有相關狀態，確保按鈕立刻變綠色
                        setVideos(prev => prev.map(v => v.id === latestVideo.id ? updatedVideo : v));
                        setPersonalVideos(prev => prev.map(v => v.id === latestVideo.id ? updatedVideo : v));
                        setCommunityVideos(prev => prev.map(v => v.id === latestVideo.id ? updatedVideo : v));
                        setPersonalHistoryVideos(prev => prev.map(v => v.id === latestVideo.id ? updatedVideo : v));
                        
                        // 異步更新 Firestore 和珍藏列表
                        try {
                          // 🔧 修復：同時更新 sharedVbids 的 zones 欄位，確保重新載入後狀態正確
                          const db = getDb();
                          if (db) {
                            const videoRef = doc(db, 'sharedVbids', latestVideo.id);
                            await updateDoc(videoRef, {
                              zones: arrayUnion('person')
                            });
                            // 调试日志已移除
                          }
                          
                          const { addToUserFavorites } = await import('../services/userFavoritesService');
                          await addToUserFavorites(
                            userId,
                            latestVideo.videoId || latestVideo.id,
                            latestVideo.id
                          );
                          // 调试日志已移除
                          
                          // 🔧 修復：立即更新 userFavoriteIds，讓按鈕立刻變綠
                          setUserFavoriteIds(prev => {
                            const newSet = new Set(prev);
                            newSet.add(latestVideo.id);
                            // 查询日志已移除);
                            return newSet;
                          });
                          // 🔧 修復：強制觸發重新渲染，避免 React 批量更新延遲
                          setFavoriteUpdateTrigger(prev => prev + 1);
                          // 调试日志已移除
                          
                          // 🔧 修復：清除「我的收藏」分頁的快取，強制重新載入
                          const favCacheKey = `myPersonalFavorite_${userId}`;
                          setVideosCache(prev => {
                            const newCache = { ...prev };
                            delete newCache[favCacheKey];
                            return newCache;
                          });
                          setCacheTimestamps(prev => {
                            const newTimestamps = { ...prev };
                            delete newTimestamps[favCacheKey];
                            return newTimestamps;
                          });
                          // 清理日志已移除
                          
                          setSnackbarMessage('已加入我的收藏');
                          setSnackbarOpen(true);
                        } catch (error) {
                          console.error('❌ 珍藏操作失敗:', error);
                          setSnackbarMessage('珍藏操作失敗');
                          setSnackbarOpen(true);
                        }
                      } else {
                        // 當「珍藏」是綠色時，點擊後變成 disable 灰色狀態
                        // 调试日志已移除
                        
                        const newZones = (latestVideo.zones || []).filter(z => z !== 'person');
                        const updatedVideo = { ...latestVideo, zones: newZones };
                        
                        // 🔧 修復：如果在「我的收藏」分頁，直接從 personalVideos 中移除該影片
                        if (currentSection === 'myPersonalFavorite') {
                          // 调试日志已移除
                          setVideos(prev => prev.filter(v => v.id !== latestVideo.id));
                          setPersonalVideos(prev => prev.filter(v => v.id !== latestVideo.id));
                          setCommunityVideos(prev => prev.filter(v => v.id !== latestVideo.id));
                          setPersonalHistoryVideos(prev => prev.filter(v => v.id !== latestVideo.id));
                        } else {
                          // 其他分頁：只更新 zones，不移除影片
                          setVideos(prev => prev.map(v => v.id === latestVideo.id ? updatedVideo : v));
                          setPersonalVideos(prev => prev.map(v => v.id === latestVideo.id ? updatedVideo : v));
                          setCommunityVideos(prev => prev.map(v => v.id === latestVideo.id ? updatedVideo : v));
                          setPersonalHistoryVideos(prev => prev.map(v => v.id === latestVideo.id ? updatedVideo : v));
                        }
                        
                        // 異步更新 Firestore 和珍藏列表
                        try {
                          // 🔧 修復：同時更新 sharedVbids 的 zones 欄位
                          const db = getDb();
                          if (db) {
                            const videoRef = doc(db, 'sharedVbids', latestVideo.id);
                            await updateDoc(videoRef, {
                              zones: arrayRemove('person')
                            });
                            // 调试日志已移除
                          }
                          
                          // 🔧 修復：同時從 userFavorites 中刪除
                          const { removeFromUserFavorites } = await import('../services/userFavoritesService');
                          await removeFromUserFavorites(
                            userId,
                            latestVideo.videoId || latestVideo.id,
                            latestVideo.id
                          );
                          // 调试日志已移除
                          
                          // 🔧 修復：立即更新 userFavoriteIds，讓按鈕立刻變灰
                          setUserFavoriteIds(prev => {
                            const newSet = new Set(prev);
                            newSet.delete(latestVideo.id);
                            return newSet;
                          });
                          // 🔧 修復：強制觸發重新渲染，避免 React 批量更新延遲
                          setFavoriteUpdateTrigger(prev => prev + 1);
                          // 调试日志已移除
                          
                          // 🔧 修復：清除「我的收藏」分頁的快取，強制重新載入
                          const favCacheKey = `myPersonalFavorite_${userId}`;
                          setVideosCache(prev => {
                            const newCache = { ...prev };
                            delete newCache[favCacheKey];
                            return newCache;
                          });
                          setCacheTimestamps(prev => {
                            const newTimestamps = { ...prev };
                            delete newTimestamps[favCacheKey];
                            return newTimestamps;
                          });
                          // 清理日志已移除
                          
                          setSnackbarMessage('已從我的收藏移除');
                          setSnackbarOpen(true);
                        } catch (error) {
                          console.error('❌ 取消珍藏操作失敗:', error);
                          setSnackbarMessage('取消珍藏操作失敗');
                          setSnackbarOpen(true);
                        }
                      }
                    } else if (opt.value === 'community') {
                      // 公開按鈕處理
                      const wasChecked = Array.isArray(latestVideo.zones) && latestVideo.zones.includes('community');
                      
                      // 立即更新本地狀態，提供即時反饋
                      const newZones = wasChecked 
                        ? (latestVideo.zones || []).filter(z => z !== 'community')
                        : [...(latestVideo.zones || []), 'community'];
                      const updatedVideo = { ...latestVideo, zones: newZones };
                      
                      // 🔧 修復：精確匹配，避免影響其他影片
                      // 配置日志已移除
                      
                      setPersonalVideos(prev =>
                        prev.map(v => {
                          if (v.id === video.id) {
                            // 配置日志已移除
                            return updatedVideo;
                          }
                          return v;
                        })
                      );
                      setCommunityVideos(prev =>
                        prev.map(v => {
                          if (v.id === video.id) {
                            // 配置日志已移除
                            return updatedVideo;
                          }
                          return v;
                        })
                      );
                      setVideos(prev =>
                        prev.map(v => {
                          if (v.id === video.id) {
                            // 配置日志已移除
                            return updatedVideo;
                          }
                          return v;
                        })
                      );
                      // 同時更新 personalHistoryVideos，確保瀏覽記錄分頁中的狀態同步
                      setPersonalHistoryVideos(prev =>
                        prev.map(v => {
                          if (v.id === video.id) {
                            // 配置日志已移除
                            return updatedVideo;
                          }
                          return v;
                        })
                      );
                      
                      // 異步更新 Firestore
                      await handleZoneChangeAll(latestVideo, 'community', !wasChecked);
                      
                      // 提供視覺反饋
                      if (!wasChecked) {
                        setSnackbarMessage('已推送到知識社群');
                      } else {
                        setSnackbarMessage('已取消推送到知識社群');
                      }
                      setSnackbarOpen(true);
                    } else {
                      const wasChecked = Array.isArray(video.zones) && video.zones.includes(opt.value);
                      handleZoneChangeAll(video, opt.value, !wasChecked);
                    }
                  }}
                  sx={{ 
                    borderRadius: '8px', 
                    textTransform: 'none', 
                    fontSize: 13, 
                    minWidth: '80px', 
                    mb: 0.5,
                    padding: '4px 8px',
                    backgroundColor:
                      (opt.value === 'person' && userFavoriteIds.has(latestVideo.id)) ? '#00AA55' :
                      isTeam ? '#009FCC' :
                      isCommunity ? '#FF7744' :
                      'inherit',
                    color:
                      ((opt.value === 'person' && userFavoriteIds.has(latestVideo.id)) || isTeam || isCommunity) ? 'white' : '#888',
                    borderColor: ((opt.value === 'person' && userFavoriteIds.has(latestVideo.id)) || isTeam || isCommunity) ? 'transparent' : '#ccc',
                    boxShadow: 'none',
                    '&:hover': {
                      backgroundColor:
                        (opt.value === 'person' && userFavoriteIds.has(latestVideo.id)) ? '#e8f5e9' :
                        isTeam ? '#e1f5fe' :
                        isCommunity ? '#fff3e0' :
                        '#f5f5f5',
                      color: '#1976d2',
                      borderColor: '#1976d2',
                    },
                  }}
                >
                  {opt.label}
                </Button>
              );
            })}
          </FormGroup>
        );
      case 'tags': {
        // 從最新的 personalVideos 狀態中獲取最新的 video 對象
        const latestVideo = personalVideos.find(v => v.id === video.id) || video;
        const allTags = extractAllTagsFromVideo(latestVideo);
        
        // 如果是未登入者在社群內容或專家知識頁面，顯示禁用狀態
        if (isGuestInCommunityOrExpert) {
          return (
            <UnifiedTagDisplay
              tags={allTags}
              userTags={latestVideo.tags || []}
              maxRows={4}
              maxTagsPerRow={8}
              readOnly={true}
              variant="compact"
              sx={{ opacity: 0.6 }}
            />
          );
        }
        
        return (
          <UnifiedTagDisplay
            tags={allTags}
            userTags={latestVideo.tags || []}
            maxRows={4}
            maxTagsPerRow={8}
            showAddButton={true}
            onAddTag={async () => {
              // 先載入 YouTube 資訊，確保有最新的標籤資料
              if (video.videoId) {
                await loadYouTubeInfoForVideos([video]);
                
                // 等待狀態更新後再設定 tagDialogVideo
                setTimeout(() => {
                  // 🔧 修復：精確匹配當前觀點，避免找到錯誤的觀點
                  const updatedVideo = personalHistoryVideos.find(v => v.id === video.id) || 
                                     personalVideos.find(v => v.id === video.id) || 
                                     communityVideos.find(v => v.id === video.id) || 
                                     video;
                  setTagDialogVideo(updatedVideo);
                  setTagDialogOpen(true);
                  setNewTag('');
                }, 500);
              } else {
                setTagDialogVideo(video);
                setTagDialogOpen(true);
                setNewTag('');
              }
            }}
            onDeleteTag={(tag) => {
              // 配置日志已移除
              
              // 立即更新本地狀態
              const updatedVideo = {
                ...latestVideo,
                tags: latestVideo.tags?.filter(t => t !== tag) || []
              };
              
              // 配置日志已移除
              // 配置日志已移除
              
              // 更新所有相關的狀態
              setPersonalVideos(prev => {
                const newPersonalVideos = prev.map(v => v.id === video.id ? updatedVideo : v);
                // 配置日志已移除
                return newPersonalVideos;
              });
              
              // 同時更新 videos 狀態，確保一致性
              setVideos(prev => {
                const newVideos = prev.map(v => v.id === video.id ? updatedVideo : v);
                // 配置日志已移除
                return newVideos;
              });
              
              // 異步更新 Firestore
              handleTagChange(latestVideo, tag, false).catch(error => {
                console.error('刪除 Tag 失敗:', error);
                // 如果失敗，恢復原狀態
                setPersonalVideos(prev => prev.map(v => v.id === latestVideo.id ? latestVideo : v));
              });
            }}
            readOnly={false}
            variant="compact"
          />
        );
      }
      case 'groups': {
        // 🔧 修復：精確匹配當前觀點，避免找到錯誤的觀點
        const latestVideo = personalHistoryVideos.find(v => v.id === video.id) || 
                          personalVideos.find(v => v.id === video.id) || 
                          communityVideos.find(v => v.id === video.id) || 
                          personalHistoryVideos.find(v => v.id === video.id) || 
                          video;
        
        const groupIdsArr = Array.isArray(latestVideo.groupIds) ? latestVideo.groupIds : [];
        if (groupIdsArr.length === 0 || allGroups.length === 0) return '-';
        const sharedGroups = allGroups.filter(g => groupIdsArr.includes(g.id));
        
        // 如果是未登入者在社群內容或專家筆記頁面，顯示禁用狀態
        if (isGuestInCommunityOrExpert) {
          return sharedGroups.length > 0
            ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, opacity: 0.6 }}>
                  {sharedGroups.map(g => (
                  <Box key={`${g.id}-${g.name}`} sx={{ px: 1, py: 0.2, bgcolor: '#eee', borderRadius: 1, fontSize: 13, color: '#999' }}>
                      {g.name}
                    </Box>
                  ))}
                </Box>
              )
            : '-';
        }
        
        return sharedGroups.length > 0
          ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {sharedGroups.map(g => (
                  <Box key={`${g.id}-${g.name}`} sx={{ px: 1, py: 0.2, bgcolor: '#eee', borderRadius: 1, fontSize: 13 }}>
                    {g.name}
                  </Box>
                ))}
              </Box>
            )
          : '-';
      }
      case 'delete': {
        // 移除調試日誌以提升效能
        // console.log('🔍 渲染刪除按鈕:', {
        //   videoId: video.id,
        //   videoUserId: video.userId,
        //   currentUserId: userId,
        //   isEqual: video.userId === userId,
        //   currentSection: currentSection
        // });
        
        if (video.userId === userId) {
          // 自己建立的影片，任何區域都可刪除（永久刪除 Firestore 文件）
          return (
            <IconButton 
              size="small" 
              color="error" 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                // 查询日志已移除
                handleDelete(video.id);
              }}
              title="刪除"
              sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mx: 'auto' }}
            >
              <DeleteIcon />
            </IconButton>
          );
        } else if (currentSection === 'myPoint') {
          // 不是自己建立的影片，只能在 myPoint 區隱藏
          return (
            <IconButton
              size="small"
              color="error"
              onClick={() => handleHidePersonalRecord(video.id)}
              title="隱藏"
              sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mx: 'auto' }}
            >
              <DeleteIcon />
            </IconButton>
          );
        } else {
          // 不是自己建立的影片，顯示灰色刪除圖標（禁用狀態）
          return (
            <IconButton
              size="small"
              disabled
              title="只能刪除自己建立的影片"
              sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                mx: 'auto',
                color: 'grey.400',
                '&:hover': {
                  backgroundColor: 'transparent'
                }
              }}
            >
              <DeleteIcon />
            </IconButton>
          );
        }
      }
      case 'share':
        // 在無 Sidebar 時（isMobile = true），分享功能已整併到 info 欄位，所以隱藏原來的分享欄位
        if (isMobile) {
          return null;
        }
        return (
          <IconButton
            size="small"
            color="primary"
            onClick={() => handleShareVideo(video)}
            title="分享"
            sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mx: 'auto' }}
          >
            <ShareIcon />
          </IconButton>
        );
      case 'source':
        return video.mode || '-';
      default:
        return null;
    }
  };
  // 3. TableHead/TableBody 統一渲染
  const filteredTodayVideos = todayVideos;
  const filteredYesterdayVideos = yesterdayVideos;
  const filteredHistoryVideos = historyVideos;

  // 调试日志已移除
  // 调试日志已移除
  // 调试日志已移除

  useEffect(() => {
    // 當建立團隊 Dialog 開啟時，自動產生團隊代號與預設管理員
    if (showCreateTeamDialog) {
      setGeneratedTeamCode('TEAM' + Math.random().toString(36).substring(2, 8).toUpperCase());
      setCurrentUserEmail(authUser?.email || '');
    }
  }, [showCreateTeamDialog, authUser]);

  useEffect(() => {
    setCurrentUserEmail(authUser?.email || '');
  }, [authUser]);

  const handleRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // 1. 建立 Firebase Auth 帳號
      const userCredential = await createUserWithEmailAndPassword(auth, registerForm.email, registerForm.password);
      
      // 2. 建立使用者資料
      await createUser({
        uid: userCredential.user.uid,
        email: registerForm.email,
        firstName: registerForm.firstName,
        lastName: registerForm.lastName
      });

      setShowRegisterDialog(false);
      setSnackbarMessage('註冊成功！');
      setSnackbarOpen(true);
    } catch (error: any) {
      console.error('註冊失敗:', error);
      setSnackbarMessage(error.message || '註冊失敗，請稍後再試');
      setSnackbarOpen(true);
    }
  };

  // 渲染 YouTube 播放清單匯入內容
  const renderImportYTPlaylistContent = () => (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, textAlign: 'center' }}>
        匯入 YouTube 播放清單
      </Typography>
      
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          功能說明
        </Typography>
        <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>
          此功能可以讓您從 YouTube 帳號匯入播放清單，將清單中的影片批量添加到 CheckPlay 系統中。
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          • 需要先登入 Google 帳號進行授權<br/>
          • 可以選擇多個播放清單同時匯入<br/>
          • 匯入的影片會自動添加到您的個人影片庫中
        </Typography>
        
        <Box sx={{ textAlign: 'center' }}>
          <Button
            variant="contained"
            size="large"
            onClick={() => setShowImportDialog(true)}
            sx={{
              px: 4,
              py: 1.5,
              fontSize: 18,
              borderRadius: '24px',
              boxShadow: 2,
              fontWeight: 700,
              background: '#4285F4',
              '&:hover': { background: '#357ae8' }
            }}
          >
            開始匯入播放清單
          </Button>
        </Box>
      </Paper>

      {/* 匯入歷史記錄 */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          匯入記錄
        </Typography>
        <Typography variant="body2" color="text.secondary">
          匯入記錄功能開發中...
        </Typography>
      </Paper>
    </Box>
  );

  // 🔧 方案 A：renderMediaPointContent() 已整合到 communityPublic 分頁，此函數已移除

  const [searchQuery, setSearchQuery] = useState('');
  const [filteredPersonalHistoryVideos, setFilteredPersonalHistoryVideos] = useState<VideoItem[] | null>(null);
  const [filteredCommunityVideos, setFilteredCommunityVideos] = useState<VideoItem[] | null>(null);
  const [filteredCommunityPublicVideos, setFilteredCommunityPublicVideos] = useState<VideoItem[] | null>(null); // 🔧 新增：社群公開影片搜尋結果
  const [showWelcome, setShowWelcome] = useState(() => !localStorage.getItem('welcome_shown'));
  const [historyLoadedCount, setHistoryLoadedCount] = useState(60); // 增加初始載入數量
  
  // 月份選擇器狀態
  const [selectedMonth, setSelectedMonth] = useState<string>('all'); // 'all' 表示顯示所有月份
  const [isFirestoreMonthFiltered, setIsFirestoreMonthFiltered] = useState<boolean>(false); // 追蹤是否在 Firestore 層面已經篩選
  
  // 🔧 修復：當月份選擇變化時，重新載入數據
  useEffect(() => {
    console.log(`🔧 SPA模式月份篩選檢查:`, {
      isEmbedded,
      selectedMonth,
      userId: !!userId,
      currentSection,
      conditions: {
        selectedMonthNotAll: selectedMonth !== 'all',
        hasUserId: !!userId,
        isPersonalHistorySection: currentSection === 'personalHistory'
      },
      willTriggerReload: selectedMonth !== 'all' && userId && currentSection === 'personalHistory'
    });
    
    // 🔧 修復 SPA 模式性能問題：只在真正需要時才重新載入
    if (selectedMonth !== 'all' && userId && currentSection === 'personalHistory') {
      // 檢查是否已經有該月份的數據，避免重複載入
      const cacheKey = `personalHistory_${userId}_${selectedMonth}`;
      const cachedData = personalHistoryCache[cacheKey];
      const cacheTimestamp = personalHistoryCacheTimestamps[cacheKey];
      const isCacheValid = cacheTimestamp && (Date.now() - cacheTimestamp) < PERSONAL_HISTORY_CACHE_EXPIRY_TIME;
      
      if (cachedData && isCacheValid) {
        // 调试日志已移除
        return;
      }
      
      // 查询日志已移除
      
      // 重新載入該月份的數據 - 使用現有的 loadVideos 函數
      loadVideos('personalHistory', true, false, selectedMonth);
    }
  }, [selectedMonth, userId, currentSection, isEmbedded]);
  // 📅 獲取可用的月份列表 (基於全部歷史記錄)  
  const [availableMonths, setAvailableMonths] = useState<string[]>([]);
  const [monthSetInitialized, setMonthSetInitialized] = useState(false);
  const [lastUserIdForMonths, setLastUserIdForMonths] = useState<string>('');
  
  useEffect(() => {
    const initializeAvailableMonths = async () => {
      if (!userId || !currentSection || currentSection !== 'personalHistory') {
        setAvailableMonths([]);
        return;
      }
      
      // 🔧 防止重複初始化同一用戶的月份集  
      if (lastUserIdForMonths === userId && monthSetInitialized) {
        return;
      }
      
      // 日期日志已移除
      setMonthSetInitialized(false);
      
      try {
        // 等待 Firebase 初始化完成
        await waitForFirebase();
        // 檢查 Firestore 是否已初始化
        const sharedVbidsCollection = safeCollection('sharedVbids');
        if (!sharedVbidsCollection) {
          console.warn('⚠️ Firestore 未初始化，跳過月份列表初始化');
          setAvailableMonths(['全部']);
          return;
        }
        
        // 查詢 sharedVbids 的全部歷史記錄以此提取可用月份
        const allVideosQuery = query(
          sharedVbidsCollection,
          where('userId', '==', userId)
        );
        
        const snapshot = await getDocs(allVideosQuery);
        // 调试日志已移除
        
        const monthSet = new Set<string>();
        
        snapshot.docs.forEach(doc => {
          const data = doc.data();
          const addedDate = data.addedDate;
          let date;
          
          if (addedDate && typeof addedDate.toDate === 'function') {
            date = addedDate.toDate();
          } else if (addedDate) {
            date = new Date(addedDate);
          }
          
          // 🔧 修復：檢查日期是否有效且合理（避免 2001 年等異常日期）
          if (date && !isNaN(date.getTime())) {
            const year = date.getFullYear();
            const month = date.getMonth() + 1;
            
            // 只接受 2020 年之後的日期（避免異常的舊日期）
            if (year >= 2020) {
              const monthStr = `${year}-${month.toString().padStart(2, '0')}`;
              monthSet.add(monthStr);
            }
          }
        });
        
        const monthsArray = Array.from(monthSet).sort().reverse();
        // 日期日志已移除
        // 日期日志已移除
        console.log('🔍 瀏覽記錄月份篩選初始化:', {
          userId: userId,
          currentSection: currentSection,
          monthsArray: monthsArray,
          monthSetSize: monthSet.size
        });
        
        setAvailableMonths(monthsArray);
        setLastUserIdForMonths(userId);
        setMonthSetInitialized(true);
        
      } catch (error) {
        console.error('❌ 取得可用月份列表錯誤:', error);
        setAvailableMonths(['全部']);
      }
    };
    
    initializeAvailableMonths();
  }, [userId, currentSection]);

  // 初始化「我的創作」分頁的可用月份
  useEffect(() => {
    const initializeMyVideosAvailableMonths = async () => {
      if (currentSection === 'myVideos' && userId && userVideos.length > 0) {
        // 查询日志已移除
        
        const monthsSet = new Set<string>();
        const now = new Date();
        const currentMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
        
        userVideos.forEach(video => {
          // 使用 addedDate 作為參考日期
          const dateValue = video.addedDate || video.createdAt || '';
          if (dateValue) {
            try {
              const date = new Date(dateValue);
              if (!isNaN(date.getTime())) {
                const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
                monthsSet.add(monthKey);
              }
            } catch (error) {
              console.warn('日期解析失敗:', dateValue, error);
            }
          }
        });
        
        const sortedMonths = Array.from(monthsSet).sort((a, b) => b.localeCompare(a));
        
        // 🔧 修復：只在月份列表真的改變時才更新狀態
        const currentMonthsStr = JSON.stringify(myVideosAvailableMonths);
        const newMonthsStr = JSON.stringify(sortedMonths);
        
        if (currentMonthsStr !== newMonthsStr) {
          setMyVideosAvailableMonths(sortedMonths);
          // 调试日志已移除

          // 設置預設選中月份為當月，如果當月有影片
          if (monthsSet.has(currentMonthKey)) {
            setMyVideosSelectedMonth(currentMonthKey);
            // 调试日志已移除
          } else {
            setMyVideosSelectedMonth('all');
            // 调试日志已移除
          }
        }
      } else if (currentSection === 'myVideos' && userVideos.length === 0) {
        // 如果沒有影片，清空可用月份並預設為全部
        if (myVideosAvailableMonths.length > 0) {
          setMyVideosAvailableMonths([]);
          setMyVideosSelectedMonth('all');
        }
      }
    };
    
    initializeMyVideosAvailableMonths();
  }, [currentSection, userId, userVideos.length]); // 🔧 修復：使用 userVideos.length 而非 userVideos，避免無限循環但能響應數據變化

  // 月份篩選邏輯 - 需要考慮月份篩選和搜尋篩選
  const filteredPersonalHistoryVideosByMonth = useMemo(() => {
    // 計算過程日誌已移除
    let result = filteredPersonalHistoryVideos ?? personalHistoryVideos;
    
    // 🔧 修復：如果沒有搜尋結果但有月份篩選，且不是在 Firestore 層面已經篩選過的情況，才進行客戶端篩選
    // 注意：如果 monthFilter 不是 'all'，getUserWatchHistory 已經在 Firestore 層面篩選過了，不需要再次篩選
    if (!filteredPersonalHistoryVideos && selectedMonth && selectedMonth !== 'all' && !isFirestoreMonthFiltered) {
      console.log('🔧 進行客戶端月份篩選:', {
        selectedMonth,
        allVideosCount: personalHistoryVideos.length,
        willFilter: true
      });
      
      const [year, month] = selectedMonth.split('-');
      const startDate = new Date(parseInt(year), parseInt(month) - 1, 1);
      const endDate = new Date(parseInt(year), parseInt(month), 0, 23, 59, 59, 999);
      
      result = personalHistoryVideos.filter(video => {
        const youtubeInfo = video.youtubeInfo;
        let refDateStr = '';
        
        if (youtubeInfo?.publishedAt && typeof youtubeInfo.publishedAt === 'string' && youtubeInfo.publishedAt.trim() !== '') {
          refDateStr = youtubeInfo.publishedAt;
        } else {
          refDateStr = video.addedDate || video.createdAt || '';
        }
        
        if (!refDateStr || refDateStr === '') {
          return false;
        }
        
        const refDate = new Date(refDateStr);
        if (isNaN(refDate.getTime())) {
          return false;
        }
        
        return refDate >= startDate && refDate <= endDate;
      });
      
      console.log('🔧 客戶端月份篩選結果:', {
        selectedMonth,
        filteredCount: result.length,
        originalCount: personalHistoryVideos.length
      });
    }
    
    // 🔧 修復：移除過度調試日誌
    // console.log('🔧 filteredPersonalHistoryVideosByMonth computed:', {
    //   hasSearchFilter: filteredPersonalHistoryVideos !== null,
    //   hasMonthFilter: selectedMonth && selectedMonth !== 'all',
    //   selectedMonth,
    //   filteredCount: filteredPersonalHistoryVideos?.length ?? 0,
    //   allVideosCount: personalHistoryVideos.length,
    //   finalResult: result.length,
    //   searchVideos: filteredPersonalHistoryVideos?.map(v => v.id).slice(0,3) || 'N/A',
    //   finalVideos: result.map(v => v.id).slice(0,3)
    // });
    return result;
  }, [filteredPersonalHistoryVideos, personalHistoryVideos, selectedMonth, isFirestoreMonthFiltered]);

  // 「我的創作」分頁月份篩選邏輯
  const filteredMyVideosByMonth = useMemo(() => {
    // 計算過程日誌已移除
    
    if (!userVideos || userVideos.length === 0) return [];
    
    if (myVideosSelectedMonth === 'all') {
      // 按 addedDate 排序（最新在前）
      const sorted = userVideos.sort((a, b) => {
        const getSafeDate = (item: any) => {
          const dateValue = item.addedDate || item.createdAt || '';
          if (!dateValue) return new Date().getTime();
          try {
            const date = new Date(dateValue);
            return isNaN(date.getTime()) ? new Date().getTime() : date.getTime();
          } catch (error) {
            return new Date().getTime();
          }
        };
        
        const dateA = getSafeDate(a);
        const dateB = getSafeDate(b);
        return dateB - dateA; // 降序：最新加入的在前
      });
      
      // 結果日誌已移除
      
      return sorted;
    }
    
    // 按月份篩選
    const [year, month] = myVideosSelectedMonth.split('-');
    // 🔧 修復：使用 UTC 時間避免時區問題
    const startDate = new Date(Date.UTC(parseInt(year), parseInt(month) - 1, 1, 0, 0, 0, 0));
    const endDate = new Date(Date.UTC(parseInt(year), parseInt(month), 0, 23, 59, 59, 999));
    
    const filtered = userVideos.filter(video => {
      const dateValue = video.addedDate || video.createdAt || '';
      if (!dateValue) return false;
      
      try {
        const date = new Date(dateValue);
        if (isNaN(date.getTime())) return false;
        
        return date >= startDate && date <= endDate;
      } catch (error) {
        return false;
      }
    });
    
    // 按 addedDate 排序（最新在前）
    const sorted = filtered.sort((a, b) => {
      const getSafeDate = (item: any) => {
        const dateValue = item.addedDate || item.createdAt || '';
        if (!dateValue) return new Date().getTime();
        try {
          const date = new Date(dateValue);
          return isNaN(date.getTime()) ? new Date().getTime() : date.getTime();
        } catch (error) {
          return new Date().getTime();
        }
      };
      
      const dateA = getSafeDate(a);
      const dateB = getSafeDate(b);
      return dateB - dateA; // 降序：最新加入的在前
    });
    
    // 月份篩選結果日誌已移除
    
    return sorted;
  }, [userVideos, myVideosSelectedMonth]);

  // 🔧 新增：「我的創作」分頁按日期分組
  const myVideosGroupedByDate = useMemo(() => {
    if (!filteredMyVideosByMonth || filteredMyVideosByMonth.length === 0) {
      return [];
    }

    // 按日期分組
    const groups: { date: string; displayDate: string; videos: VideoItem[] }[] = [];
    const dateMap = new Map<string, VideoItem[]>();

    // 🔧 修復：移除過度調試日誌
    // // 查询日志已移除
    
    filteredMyVideosByMonth.forEach((video, index) => {
      const dateValue = video.addedDate || video.createdAt || '';
      if (dateValue) {
        try {
          const date = new Date(dateValue);
          if (!isNaN(date.getTime())) {
            // 格式化為日期（YYYY-MM-DD）
            const dateKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
            
            // 🔧 移除詳細日誌以減少控制台輸出
            // if (index < 4) {
            //   console.log(`🔍 影片${index + 1} 日期調試:`, {
            //     title: video.title?.substring(0, 30),
            //     addedDate: video.addedDate,
            //     createdAt: video.createdAt,
            //     dateValue,
            //     dateKey,
            //     parsedDate: date.toISOString()
            //   });
            // }
            
            if (!dateMap.has(dateKey)) {
              dateMap.set(dateKey, []);
            }
            dateMap.get(dateKey)!.push(video);
          }
        } catch (error) {
          console.warn('日期解析失敗:', dateValue, error);
        }
      }
    });

    // 轉換為陣列並排序（最新日期在前）
    const sortedDates = Array.from(dateMap.keys()).sort((a, b) => b.localeCompare(a));
    
    // 🔧 移除詳細日誌以減少控制台輸出
    // // 查询日志已移除));
    
    sortedDates.forEach(dateKey => {
      const videos = dateMap.get(dateKey) || [];
      
      // 格式化顯示日期
      const date = new Date(dateKey);
      const now = new Date();
      const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      const yesterday = new Date(now);
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayKey = `${yesterday.getFullYear()}-${String(yesterday.getMonth() + 1).padStart(2, '0')}-${String(yesterday.getDate()).padStart(2, '0')}`;

      // 🔧 移除詳細日誌以減少控制台輸出
      // console.log('🔍 日期顯示調試:', {
      //   dateKey,
      //   today,
      //   yesterdayKey,
      //   videosCount: videos.length
      // });

      let displayDate = '';
      if (dateKey === today) {
        displayDate = `今天 (${date.toLocaleDateString('zh-TW', { month: 'long', day: 'numeric' })})`;
        // // 查询日志已移除
      } else if (dateKey === yesterdayKey) {
        displayDate = `昨天 (${date.toLocaleDateString('zh-TW', { month: 'long', day: 'numeric' })})`;
        // // 查询日志已移除
      } else {
        displayDate = date.toLocaleDateString('zh-TW', { 
          year: 'numeric',
          month: 'long', 
          day: 'numeric',
          weekday: 'short'
        });
        // // 查询日志已移除
      }

      groups.push({
        date: dateKey,
        displayDate,
        videos: videos.sort((a, b) => {
          // 同一天內按時間降序排序
          const timeA = new Date(a.addedDate || a.createdAt || '').getTime();
          const timeB = new Date(b.addedDate || b.createdAt || '').getTime();
          return timeB - timeA;
        })
      });
    });

    return groups;
  }, [filteredMyVideosByMonth]);

  // 簡化的瀏覽記錄顯示邏輯
  const personalHistoryToShow = useMemo(() => {
    // 計算開始日誌已移除
    
    // 🔧 修復：優先處理搜尋結果
    if (filteredPersonalHistoryVideos && filteredPersonalHistoryVideos.length > 0) {
      return filteredPersonalHistoryVideos;
    }
    
    // 🔧 修復：使用月份篩選後的結果，但受 historyLoadedCount 限制
    const result = filteredPersonalHistoryVideosByMonth.slice(0, historyLoadedCount);
    // 計算結果日誌已移除
    
    // 🔧 修復：移除過度調試日誌
    // console.log('🔧 personalHistoryToShow computed:', {
    //   filteredPersonalHistoryVideos: filteredPersonalHistoryVideos?.length ?? 'null',
    //   filteredPersonalHistoryVideosByMonth: filteredPersonalHistoryVideosByMonth.length,
    //   selectedMonth,
    //   historyLoadedCount,
    //   currentSliceResult: result.length,
    //   isNullSearch: filteredPersonalHistoryVideos === null,
    //   hasMonthFilter: selectedMonth && selectedMonth !== 'all'
    // });
    
    return result;
  }, [filteredPersonalHistoryVideosByMonth, historyLoadedCount, filteredPersonalHistoryVideos, selectedMonth]);

  // 簡化的 hasMore 判斷
  const hasMoreHistory = useMemo(() => {
    // 如果有搜尋結果，不顯示「載入更多」
    if (filteredPersonalHistoryVideos && filteredPersonalHistoryVideos.length > 0) {
      return false;
    }
    
    // 🔧 修復：如果有月份篩選且是客戶端篩選，也不顯示「載入更多」
    if (selectedMonth && selectedMonth !== 'all' && !filteredPersonalHistoryVideos) {
      // 配置日志已移除
      return false;
    }
    
    return historyLoadedCount < filteredPersonalHistoryVideosByMonth.length;
  }, [filteredPersonalHistoryVideos, historyLoadedCount, filteredPersonalHistoryVideosByMonth.length, selectedMonth]);

  // 按日期分群的影片數據 - 使用正確的篩選結果
  const groupedPersonalVideos = useMemo(() => {
    // 🔧 修復：使用正確的影片來源（優先搜尋結果，其次月份篩選結果）
    const videosToGroup = filteredPersonalHistoryVideos ?? filteredPersonalHistoryVideosByMonth;
    
    // 計算開始日誌已移除
    //   hasSearchResults: !!filteredPersonalHistoryVideos,
    //   hasMonthFilter: selectedMonth && selectedMonth !== 'all',
    //   selectedMonth,
    //   videosToGroupCount: videosToGroup.length,
    //   filteredPersonalVideosCount: filteredPersonalVideos.length
    // });
    
    const groups: { [date: string]: VideoItem[] } = {};
    
    videosToGroup.forEach(video => {
      // 🔧 修正：月份篩選應該根據瀏覽時間進行篩選和分組
      let browseDate = video.lastOpenDate || video.createdAt || video.addedDate || new Date().toISOString();
      
      // 🔧 修復：處理 Firestore Timestamp 物件，並檢查 null
      if (browseDate && browseDate !== null && typeof browseDate === 'object' && 'seconds' in browseDate) {
        // 這是 Firestore Timestamp 物件，轉換為 Date
        browseDate = new Date((browseDate as any).seconds * 1000).toISOString();
      } else if (browseDate && browseDate !== null && typeof browseDate === 'object' && 'toDate' in browseDate) {
        // 這是 Firestore Timestamp 物件，使用 toDate() 方法
        browseDate = (browseDate as any).toDate().toISOString();
      } else if (!browseDate || browseDate === null || typeof browseDate !== 'string') {
        // 如果沒有有效日期，使用當前時間
        browseDate = new Date().toISOString();
      }
      
      const browseDateObj = new Date(browseDate);
      
      // 如果選擇了特定月份，檢查瀏覽時間是否在該月份內
      if (selectedMonth && selectedMonth !== 'all') {
        const [year, month] = selectedMonth.split('-');
        const startDate = new Date(parseInt(year), parseInt(month) - 1, 1);
        const endDate = new Date(parseInt(year), parseInt(month), 0, 23, 59, 59, 999);
        
        // 如果瀏覽時間不在選中的月份內，跳過此影片
        if (browseDateObj < startDate || browseDateObj > endDate) {
          return;
        }
      }
      
      // 🔧 修復：使用 UTC 時間進行日期分組，避免時區轉換問題
      const dateKey = `${browseDateObj.getUTCFullYear()}/${browseDateObj.getUTCMonth() + 1}/${browseDateObj.getUTCDate()}`;
      
      // 🔧 修復：移除過度調試日誌（已清除）
      
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(video);
    });
    
    // 按日期排序（最新的日期在前）
    const sortedDates = Object.keys(groups).sort((a, b) => {
      const dateA = new Date(a.replace(/\//g, '-'));
      const dateB = new Date(b.replace(/\//g, '-'));
      return dateB.getTime() - dateA.getTime();
    });
    
    const finalGroups = sortedDates.map(date => ({
      date,
      videos: groups[date]
    }));
    
    // 計算完成日誌已移除
    
    return finalGroups;
  }, [filteredPersonalHistoryVideos, filteredPersonalHistoryVideosByMonth, selectedMonth]);

  // 簡化的載入更多函數
  const loadMoreHistory = () => {
    // 🔧 如果有搜尋結果，不執行載入更多
    if (filteredPersonalHistoryVideos && filteredPersonalHistoryVideos.length > 0) {
      // 配置日志已移除
      return;
    }
    
    // 🔧 如果有月份篩選，不執行載入更多
    if (selectedMonth && selectedMonth !== 'all') {
      // 配置日志已移除
      return;
    }
    
    // 防止重複調用
    if (historyLoadedCount >= filteredPersonalHistoryVideosByMonth.length) {
      return;
    }
    
    setHistoryLoadedCount((prev) => {
      const newCount = Math.min(prev + 100, filteredPersonalHistoryVideosByMonth.length);
      return newCount;
    });
  };

  const renderDefaultContent = () => (
    <>
      <Paper sx={{ 
        p: 0, 
        m: 0, 
        width: '100%',
        // 取消所有間距，與視窗邊界貼齊
        '@media (max-width: 1024px)': {
          pl: 0,
        }
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, p: 0.625 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>近期瀏覽記錄</Typography>
          <Typography variant="body2" sx={{ ml: 2, color: 'gray', fontWeight: 400 }}>
            （總共 {(filteredPersonalHistoryVideos ?? personalHistoryVideos).length} 筆）
          </Typography>
        </Box>
        {loading ? (
          renderLoadingState('個人影片載入中...')
        ) : (
          <InfiniteScroll
            dataLength={personalHistoryToShow.length}
            next={loadMoreHistory}
            hasMore={hasMoreHistory}
            loader={null}
            endMessage={<Typography align="center" sx={{ my: 2, color: 'gray' }}>已載入全部</Typography>}
            scrollThreshold={0.8}
            scrollableTarget="main-personal-history-scroll"
          >
            <TableContainer>
              <Table size="small" sx={{
              tableLayout: 'fixed',
              width: 'auto',
              '& th:first-of-type, & td:first-of-type': {
                '@media (max-width: 1024px)': {
                  display: 'none'
                }
              }
            }}>
                <TableHead>
                  <TableRow>
                    {COLUMNS.filter(col => PAGE_COLUMN_KEYS[currentSection]?.includes(col.key)).map(col => (
                      <TableCell
                        key={col.key}
                        data-col={col.key}
                        align="center"
                      >
                        {col.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {personalHistoryToShow.map((video, idx) => {
                    // 🔧 SPA模式關鍵對沖：直接驗證UI各項演繹的影片的日期month
                    const youtubeInfo = video.youtubeInfo;
                    const refDate = youtubeInfo?.publishedAt ? new Date(youtubeInfo.publishedAt) : (video.addedDate ? new Date(video.addedDate) : null);
                    if (idx < 3 && refDate && selectedMonth && selectedMonth !== 'all' && currentSection === 'personalHistory') {
                      const refMonth = `${refDate.getFullYear()}-${(refDate.getMonth() + 1).toString().padStart(2, '0')}`;
                      if (refMonth !== selectedMonth) {
                        console.error(`❌ SPATABLE RENDER BUG: UI rendering 影片 "${video.title?.slice(0, 30)}" (${refMonth}) 不屬於當前選中月份 ${selectedMonth}!`, {
                          refMonth,
                          selectedMonth,
                          title: video.title?.slice(0, 30),
                          publishedAt: youtubeInfo?.publishedAt,
                          addedDate: video.addedDate,
                          refDate: refDate.toISOString()
                        });
                      }
                    }
                    
                    return (
                    <TableRow key={`personal-history-${video.id}-${idx}`}>
                        {COLUMNS.filter(col => PAGE_COLUMN_KEYS[currentSection]?.includes(col.key)).map(col => (
                          <TableCell
                          key={`personal-history-${video.id}-${col.key}-${idx}`}
                            data-col={col.key}
                            align="center"
                            {...getTableCellStyle(col, false)}
                          >
                          {renderCellContent(col.key, video, idx, 'personalHistory')}
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </InfiniteScroll>
        )}
      </Paper>
      <Paper sx={{ 
        p: 0, 
        m: 0, 
        width: '100%',
        // 取消所有間距，與視窗邊界貼齊
        '@media (max-width: 1024px)': {
          pl: 0,
        }
      }}>
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mr: 2 }}>
            社群
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              variant={communitySortMode === 'flat' && communitySortKey === 'vbids' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => { setCommunitySortMode('flat'); setCommunitySortKey('vbids'); setCommunityLoadedCount(60); }}
              sx={{ borderRadius: '20px' }}
            >重點數</Button>
            <Button
              variant={communitySortMode === 'flat' && communitySortKey === 'favoriteCount' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => { setCommunitySortMode('flat'); setCommunitySortKey('favoriteCount'); setCommunityLoadedCount(60); }}
              sx={{ borderRadius: '20px' }}
            >收藏數</Button>
            <Button
              variant={communitySortMode === 'flat' && communitySortKey === 'time' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => { setCommunitySortMode('flat'); setCommunitySortKey('time'); setCommunityLoadedCount(60); }}
              sx={{ borderRadius: '20px' }}
            >時間序</Button>
          </Stack>
        </Box>
        {loading ? (
          renderLoadingState('社群影片載入中...')
        ) : (
          <InfiniteScroll
            dataLength={communityVideosToShow.length}
            next={loadMoreCommunityVideos}
            hasMore={hasMoreCommunityVideos}
            loader={null}
            endMessage={<Typography align="center" sx={{ my: 2, color: 'gray' }}>已載入全部</Typography>}
            scrollThreshold={0.8}
            scrollableTarget="community-videos-scroll-2"
          >
          <TableContainer>
            <Table size="small" sx={{
              tableLayout: 'fixed',
              width: 'auto',
              '& th:first-of-type, & td:first-of-type': {
                '@media (max-width: 1024px)': {
                  display: 'none'
                }
              }
            }}>
              <TableHead>
                <TableRow>
                  {COLUMNS.filter(col => PAGE_COLUMN_KEYS[currentSection].includes(col.key)).map(col => (
                    <TableCell
                      key={col.key}
                      data-col={col.key}
                      align="center"
                      {...getTableCellStyle(col, true)}
                    >
                      {col.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                  {communityVideosToShow.map((video, idx) => (
                    <TableRow key={`community-${video.id}`}>
                      {COLUMNS.filter(col => PAGE_COLUMN_KEYS[currentSection].includes(col.key)).map(col => (
                        <TableCell
                          key={`community-${video.id}-${col.key}-${idx}`}
                          data-col={col.key}
                          align="center"
                          {...getTableCellStyle(col, false)}
                        >
                          {renderCellContent(col.key, video, idx, 'community')}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          </InfiniteScroll>
        )}
      </Paper>
    </>
  );
  const handleCreateTeam = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('🔍 handleCreateTeam 開始:', { 
      authUser: authUser?.email, 
      authUserUid: authUser?.uid, 
      guestId, 
      isGuest 
    });
    
    try {
      if (!authUser) {
        // 调试日志已移除
        throw new Error('請先登入');
      }

      // 解析團隊成員
      const members = teamForm.members
        .split(',')
        .map(email => email.trim().toLowerCase()) // 轉小寫
        .filter(email => email);

      // 解析客戶資料
      const clients = parseClientsData(teamForm.clients);

      console.log('🔍 準備建立團隊:', { 
        teamId: generatedTeamCode,
        name: teamForm.name,
        adminId: authUser.uid,
        members: [authUser.uid, ...members],
        allAdmins: [authUser.email, ...members]
      });

      // 建立團隊
      await createTeam({
        teamId: generatedTeamCode,
        name: teamForm.name,
        taxId: teamForm.taxId,
        adminId: authUser.uid,
        members: [authUser.uid, ...members],
        clients
      });
      
      // Firestore: 新增所有 admin email 到 membersEmail
      const groupRef = safeDoc( 'groups', generatedTeamCode);
      const allAdmins = [authUser.email, ...members].filter(Boolean).map(email => (email ? email.toLowerCase() : ''));
      
      console.log('🔍 準備更新 groups collection:', { 
        groupRef: generatedTeamCode, 
        allAdmins 
      });
      
      await updateDoc(groupRef, {
        membersEmail: arrayUnion(...allAdmins)
      });

      // 调试日志已移除
      setShowCreateTeamDialog(false);
      setSnackbarMessage('團隊建立成功！');
      setSnackbarOpen(true);
    } catch (error: any) {
      console.error('❌ 建立團隊失敗:', error);
      setSnackbarMessage(error.message || '建立團隊失敗，請稍後再試');
      setSnackbarOpen(true);
    }
  };

  // 個人珍藏分頁專用 filter
  const filteredPersonalTodayVideos = todayVideos.filter(v => Array.isArray(v.zones) && v.zones.includes('person'));
  const filteredPersonalYesterdayVideos = yesterdayVideos.filter(v => Array.isArray(v.zones) && v.zones.includes('person'));

  // 🔧 已移除：舊的 personalHistoryVideos 載入邏輯
  // 現在使用 loadWatchHistory 函數來載入真實的觀看記錄
  /*
  useEffect(() => {
    const loadData = async () => {
      if (!userId) return;
              // 调试日志已移除
          setLoading(true);
      try {
      const q = query(
        safeCollection( "sharedVbids"),
        where("userId", "==", userId),
          orderBy("addedDate", "desc"),
          limit(100)
      );
        const snapshot = await getDocs(q);
          const newList = snapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() } as VideoItem))
          .filter(v => !Array.isArray(v.hiddenFor) || !v.hiddenFor.includes(userId));
          const newListWithAttention = await Promise.all(newList.map(async (item) => {
            let videoId = item.videoId;
            if (!videoId && item.path) {
              // 🔧 使用統一的 extractYouTubeId 函數（已從 vbidIO.ts 導入）
              videoId = extractYouTubeId(item.path) || item.path;
            }
            let attentionCount = 0;
            if (videoId) {
              attentionCount = await getAttentionCount(videoId);
            // 调试日志已移除
            }
            // 從 userNotes 統計中獲取重點數（使用備用方案）
            let globalCheckpointCount = item.checkpointCount || 0;
            
            // 移除重複的調試日誌
            
            return {
              ...item,
              globalCheckpointCount: globalCheckpointCount,
              checkpointCount: globalCheckpointCount,
              attentionCount
            };
          }));
          
        // 🔧 修復：為瀏覽記錄添加去重邏輯
        const dedupedList = deduplicateByUserAndVideoId(newListWithAttention);
        
        // 🔧 修復：按 lastOpenDate 排序，確保最新的瀏覽記錄在前
        const sortedList = dedupedList.sort((a, b) => {
          const dateA = new Date(a.lastOpenDate || a.addedDate || a.createdAt || '');
          const dateB = new Date(b.lastOpenDate || b.addedDate || b.createdAt || '');
          return dateB.getTime() - dateA.getTime(); // 降序排列
        });
        
        // 🔧 修復：移除頻繁的 console.log，避免性能問題
        // console.log('🔍 瀏覽記錄去重:', {
        //   原始數量: newListWithAttention.length,
        //   去重後數量: dedupedList.length,
        //   重複數量: newListWithAttention.length - dedupedList.length
        // });
        
        setPersonalHistoryVideos(sortedList);
        
        // 設定最後一個文檔引用，用於分頁查詢
        if (snapshot.docs.length > 0) {
          lastVisibleHistoryDocRef.current = snapshot.docs[snapshot.docs.length - 1];
        }
        
        // hasMoreHistory 現在是動態計算的，不需要手動設定
        
    const q2 = query(
      safeCollection( "sharedVbids"),
      where("zones", "array-contains", "community"),
      orderBy("lastOpenDate", "desc")
    );
        const snapshot2 = await getDocs(q2);
        const rawList = snapshot2.docs.map(doc => ({ id: doc.id, ...doc.data() } as VideoItem));
      const deduped = deduplicateByUserAndVideoId(rawList);
      setCommunityVideos(deduped);
        setCommunityLoadedCount(deduped.length); // 設定為顯示所有社群影片
      } catch (error) {
        console.error("載入數據時發生錯誤:", error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [userId]);
  */

  // 🔧 修復：工具函式改為使用 documentId/id 去重，支援同一影片的不同觀點
  function deduplicateByUserAndVideoId(list: VideoItem[]): VideoItem[] {
    const map = new Map<string, VideoItem>();
    for (const item of list) {
      // 🔧 優先使用 documentId 或 id（包含觀點版本信息，如 _v2, _v3）
      // 這樣同一影片的不同觀點會被視為不同的記錄
      const key = item.documentId || item.id;
      if (key && !map.has(key)) {
        map.set(key, item);
      }
    }
    return Array.from(map.values());
  }

  // 1. Sidebar 設定：將「追蹤收藏」sub item 的 disabled 屬性改為 userType === 'guest'
  // 移除重複的 SIDEBAR_CONFIG 定義，使用組件外部的定義

  // 2. 主畫面分支：currentSection === 'follow' 時顯示追蹤收藏影片
  // 找出所有社群重點影片中，用戶已經按下「珍藏」的影片
  const followedCommunityVideos = useMemo(() => {
    if (!userId || userId === 'guest' || userId === 'guest168') return [];
    
    // 找出社群影片中，作者非本人且用戶已經按下「珍藏」的影片
    const followedVideos = communityVideos.filter(communityVideo => {
      // 確保作者非本人 - 使用多種方式檢查
      const currentUserEmail = authUser?.email || '';
      const currentUserId = authUser?.uid || '';
      
      // 檢查 userId 欄位是否不等於當前用戶的 email 或 uid
      const isNotOwnVideo = communityVideo.userId !== currentUserEmail && 
                           communityVideo.userId !== currentUserId &&
                           communityVideo.creatorEmail !== currentUserEmail;
      
      // 有按下「珍藏」（因為 communityVideos 已經確保有 community zone）
      const hasPersonZone = Array.isArray(communityVideo.zones) && communityVideo.zones.includes('person');
      
      return isNotOwnVideo && hasPersonZone;
    });
    
    // 追蹤收藏篩選詳細信息日誌已移除
    
    // 顯示前幾個影片的篩選結果（日誌已移除）
    if (communityVideos.length > 0) {
      // 前3個社群影片篩選結果日誌已移除（forEach 循環已簡化）
      // 篩選邏輯保留，但移除調試日誌
    }
    
    // if (communityVideos.length > 0) {
    //   console.log('- 第一個社群影片:', {
    //     id: communityVideos[0].id,
    //     userId: communityVideos[0].userId,
    //     zones: communityVideos[0].zones,
    //     title: communityVideos[0].title,
    //     isNotOwn: communityVideos[0].userId !== (authUser?.email || ''),
    //     hasPerson: Array.isArray(communityVideos[0].zones) && communityVideos[0].zones.includes('person')
    //   });
    // }
    
    // console.log('- 前3個社群影片:', communityVideos.slice(0, 3).map(v => ({
    //   id: v.id,
    //   userId: v.userId,
    //   zones: v.zones,
    //   title: v.title
    // })));
    
    return followedVideos;
  }, [userId, authUser?.email]); // 移除會導致無限循環的依賴項

  // 2. 假設有 getUserGroups(userId) 回傳 Promise<string[]>
  async function getUserGroups(userEmail: string | null | undefined): Promise<any[]> {
    // console.log('🔍 getUserGroups 開始:', { 
    //   userEmail, 
    //   authUser: authUser?.email, 
    //   guestId, 
    //   isGuest 
    // });
    
    if (!userEmail) {
      // 调试日志已移除
      return [];
    }
    
    const lowerEmail = userEmail.toLowerCase();
    // console.log('🔍 查詢 groups collection:', { 
    //   lowerEmail, 
    //   query: 'membersEmail array-contains' 
    // });
    
    const q = query(
      safeCollection( 'groups'),
      where('membersEmail', 'array-contains', lowerEmail)
    );
    const snap = await getDocs(q);
    const groups = snap.docs.map(doc => ({
      id: doc.id,
      name: doc.data().name,
      createdAt: doc.data().createdAt || doc.data().created_at || new Date().toISOString(),
      admins: doc.data().admins || [],
      members: doc.data().members || [],
      membersEmail: doc.data().membersEmail || [],
    }));
    
    // ✅ getUserGroups 成功:（详细参数已移除）
    //   groupCount: groups.length, 
    //   groups: groups.map(g => ({ id: g.id, name: g.name }))
    // });
    
    return groups;
  }

  // 2. 新增排序 state
  const [teamPointSortGroup, setTeamPointSortGroup] = useState<string>(''); // '' 代表全部
  // 3. 動態產生群組按鈕
  const teamPointGroupOptions = userGroups;
  // 4. UI: 排序按鈕群組
  // 调试日志已移除






  const renderTeamPointContent = () => {
    // 移除條件性的 return，改為在主要 return 語句中處理
    // if (loading) {
    //   return (
    //     <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 4 }}>
    //       <CircularProgress />
    //       <Typography sx={{ ml: 2 }}>影片載入中...</Typography>
    //     </Box>
    //   );
    // }

    // 如果沒有群組，顯示載入中（因為可能還在查詢群組）
    // if (userGroups.length === 0) {
    //   return (
    //     <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 4 }}>
    //       <CircularProgress />
    //       <Typography sx={{ ml: 2 }}>群組載入中...</Typography>
    //     </Box>
    //   );
    // }

    const hasAnyVideo = userGroups.some(group =>
      videos.some(v => Array.isArray(v.groupIds) && v.groupIds.includes(group.id))
    );
    
    // 如果沒有影片，也顯示載入中（因為可能影片還在載入）
    // if (!hasAnyVideo) {
    //   return (
    //     <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 4 }}>
    //       <CircularProgress />
    //       <Typography sx={{ ml: 2 }}>影片載入中...</Typography>
    //     </Box>
    //   );
    // }

    // 群組分頁應該使用所有影片，包括個人和社群影片
    const allVideosForGroups = [...personalVideos, ...communityVideos];
    


    return (
      <Box>
        {sortedGroups.map(group => {
          const groupVideos = allVideosForGroups
            .filter(v => Array.isArray(v.groupIds) && v.groupIds.includes(group.id))
            .sort((a, b) => {
              const aDate = a.lastOpenDate || a.addedDate || '';
              const bDate = b.lastOpenDate || b.addedDate || '';
              return new Date(bDate).getTime() - new Date(aDate).getTime();
            });
          
          // 暫時移除這個條件，讓所有群組都顯示，即使沒有影片
          // if (groupVideos.length === 0) return null;
          return (
            <Accordion
              key={group.id}
              expanded={expandedGroupId === group.id}
              onChange={() => handleToggleGroup(group.id)}
              sx={{ mb: 2 }}
            >
              <AccordionSummary>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {group.name}
                    <Typography 
                      component="span" 
                      sx={{ 
                        ml: 0.5, 
                        fontSize: '0.9em', 
                        color: 'text.secondary',
                        fontWeight: 400
                      }}
                    >
                      （{groupVideos.length}）
                    </Typography>
                  </Typography>
                  <IconButton size="small" sx={{ ml: 1 }} onClick={e => { e.stopPropagation(); handleShareGroup(group); }}>
                    <ShareIcon fontSize="small" />
                  </IconButton>
                  {/* 檢查用戶是否有管理權限 */}
                  {(() => {
                    const currentUserEmail = authUser?.email?.trim().toLowerCase();
                    const isAdmin = Array.isArray(group.admins) && 
                      group.admins.map((e: any) => (typeof e === 'string' ? e.trim().toLowerCase() : '')).includes(currentUserEmail || '');
                    
                    return isAdmin ? (
                      // 有管理權限：顯示「管理群組」紅字按鈕
                      <Button
                        size="small"
                        variant="outlined"
                        sx={{
                          ml: 1,
                          minWidth: 0,
                          px: 1.5,
                          py: 0.5,
                          fontSize: 12,
                          fontWeight: 600,
                          borderRadius: 1.5,
                          color: '#d32f2f',
                          borderColor: '#d32f2f',
                          backgroundColor: '#fff',
                          whiteSpace: 'nowrap',
                          height: '28px',
                          flexShrink: 0,
                          '&:hover': {
                            color: '#fff',
                            backgroundColor: '#d32f2f',
                            borderColor: '#d32f2f',
                          },
                          '&:active': {
                            color: '#fff',
                            backgroundColor: '#b71c1c',
                            borderColor: '#b71c1c',
                          }
                        }}
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation();
                          setGroupToManage(group);
                          setShowGroupManagerInGroup(true);
                        }}
                      >
                        管理群組
                      </Button>
                    ) : (
                      // 沒有管理權限：顯示「退出群組」灰字按鈕
                      <Button
                        size="small"
                        variant="outlined"
                        sx={{
                          ml: 1,
                          minWidth: 0,
                          px: 1.5,
                          py: 0.5,
                          fontSize: 12,
                          fontWeight: 600,
                          borderRadius: 1.5,
                          color: '#757575',
                          borderColor: '#bdbdbd',
                          backgroundColor: '#fff',
                          whiteSpace: 'nowrap',
                          height: '28px',
                          flexShrink: 0,
                          '&:hover': {
                            color: '#fff',
                            backgroundColor: '#bdbdbd',
                            borderColor: '#bdbdbd',
                          },
                          '&:active': {
                            color: '#fff',
                            backgroundColor: '#616161',
                            borderColor: '#616161',
                          }
                        }}
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation();
                          setLeavingGroupId(group.id);
                        }}
                      >
                        退出群組
                      </Button>
                    );
                  })()}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <TableContainer>
                  <Table size="small" sx={{
              tableLayout: 'fixed',
              width: 'auto',
              '& th:first-of-type, & td:first-of-type': {
                '@media (max-width: 1024px)': {
                  display: 'none'
                }
              }
            }}>
                    <TableHead>
                      <TableRow>
                        {COLUMNS.filter(col => PAGE_COLUMN_KEYS['expertKnowledge'].includes(col.key)).map(col => (
                          <TableCell
                            key={col.key}
                            data-col={col.key}
                            align="center"
                            {...getTableCellStyle(col, true)}
                          >
                            {col.label}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {groupVideos.map((video, idx) => (
                        <TableRow key={`${group.id}-${video.id}-${idx}`}>
                          {COLUMNS.filter(col => PAGE_COLUMN_KEYS['expertKnowledge'].includes(col.key)).map(col => (
                            <TableCell
                              key={`${group.id}-${video.id}-${col.key}-${idx}`}
                              data-col={col.key}
                              align="center"
                              {...getTableCellStyle(col, false)}
                              sx={col.key === 'tags' ? {
                                maxWidth: '500px !important',
                                width: '500px !important',
                                minWidth: '500px !important',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              } : {}}
                            >
                              {renderCellContent(col.key, video, idx, group.name)}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>
    );
  };

  // 延遲載入用戶群組，只在需要時才載入
  useEffect(() => {
    if (authUser?.email && currentSection === 'teamPoint') {
      // userGroups 載入已合併到 fetchTeamPointVideos 中，避免重複觸發
    }
  }, [authUser, currentSection]);

  // 手機效能優化：延遲載入專家筆記相關數據
  useEffect(() => {
    if (authUser?.email && currentSection === 'expertKnowledge') {
      // 延遲載入群組數據，避免立即載入造成卡頓
      const timer = setTimeout(() => {
      getUserGroups(authUser.email).then(groups => {
        setUserGroups(groups);
      }).catch(e => {
        console.error('getUserGroups 查詢失敗', e);
      });
      }, 500); // 延遲 500ms 載入

      return () => clearTimeout(timer);
    }
  }, [authUser, currentSection]);

  // 用戶ID到email的映射狀態
  const [userIdToEmailMap, setUserIdToEmailMap] = useState<Record<string, string>>({});

  // 載入用戶ID到email的映射
  useEffect(() => {
    const loadUserMappings = async () => {
      // 收集所有需要映射的用戶ID - 從所有影片中收集
      const allUserIds = new Set<string>();
      
      // 從所有影片列表收集用戶ID
      const allVideos = [
        ...personalHistoryVideos,
        ...personalVideos,
        ...communityVideos,
        ...videos
      ];
      
      allVideos.forEach(video => {
        if (video.userId && 
            !video.userId.startsWith('GUEST_') && 
            !video.userId.includes('GUEST_') &&
            !video.userId.toLowerCase().includes('guest') &&
            !video.userId.match(/GUEST_[A-Z0-9_]+/i)) {
          allUserIds.add(video.userId);
        }
      });

      // 為每個用戶ID獲取email
      const mapping: Record<string, string> = {};
      for (const userId of allUserIds) {
        try {
          // 如果userId已經是email格式，直接使用
          if (userId.includes('@')) {
            mapping[userId] = userId;
          } else {
            // 如果是UID，嘗試從Firebase獲取用戶信息
            const user = await getUser(userId);
            if (user?.email) {
              mapping[userId] = user.email;
            } else {
              // 如果無法獲取email，嘗試從影片數據中獲取創作者資訊
              const videoWithCreatorInfo = allVideos.find(video => video.userId === userId);
              if (videoWithCreatorInfo?.creatorEmail) {
                mapping[userId] = videoWithCreatorInfo.creatorEmail;
              } else if (videoWithCreatorInfo?.creatorName) {
                mapping[userId] = videoWithCreatorInfo.creatorName;
              } else {
                // 最後才使用UID作為fallback
                mapping[userId] = userId;
              }
            }
          }
        } catch (error) {
          // 🔧 修復：靜默處理權限錯誤，不顯示錯誤訊息
          if (error.code !== 'permission-denied' && error.code !== 'missing-or-insufficient-permissions') {
            console.error(`無法獲取用戶 ${userId} 的email:`, error);
          }
          // 嘗試從影片數據中獲取創作者資訊作為fallback
          const videoWithCreatorInfo = allVideos.find(video => video.userId === userId);
          if (videoWithCreatorInfo?.creatorEmail) {
            mapping[userId] = videoWithCreatorInfo.creatorEmail;
          } else if (videoWithCreatorInfo?.creatorName) {
            mapping[userId] = videoWithCreatorInfo.creatorName;
          } else {
            mapping[userId] = userId;
          }
        }
      }
      
      setUserIdToEmailMap(mapping);
    };

    loadUserMappings();
  }, [
    currentSection, 
    personalHistoryVideos, 
    personalVideos, 
    communityVideos, 
    videos
  ]);

  // 簡化的專家筆記內容計算
  const expertKnowledgeData = useMemo(() => {
    if (currentSection !== 'expertKnowledge') return null;
    
    // 使用社群影片
    const expertKnowledgeVideos = communityVideos;

    // 根據關鍵字篩選影片
    const filteredByKeyword = expertSearchKeyword
      ? expertKnowledgeVideos.filter(video => {
          const keyword = expertSearchKeyword.toLowerCase();
          return (
            (video.title && video.title.toLowerCase().includes(keyword)) ||
            (video.videoInfo?.title && video.videoInfo.title.toLowerCase().includes(keyword)) ||
            (video.youtubeInfo?.title && video.youtubeInfo.title.toLowerCase().includes(keyword)) ||
            (Array.isArray(video.tags) && video.tags.some(tag => tag.toLowerCase().includes(keyword)))
          );
        })
      : expertKnowledgeVideos;

    // 計算用戶 ID 列表（按用戶分組）
    const allUserIds = new Set<string>();
    filteredByKeyword.forEach(video => {
      if (video.userId && 
          !video.userId.startsWith('GUEST_') && 
          !video.userId.includes('GUEST_') &&
          !video.userId.toLowerCase().includes('guest') &&
          !video.userId.match(/GUEST_[A-Z0-9_]+/i)) {
        allUserIds.add(video.userId);
      }
    });
    
    const kmGroupsArray = Array.from(allUserIds).sort();

    return {
      expertKnowledgeVideos,
      filteredByKeyword,
      kmGroupsArray
    };
  }, [currentSection, expertSearchKeyword, communityVideos]);

  // 手機效能優化：虛擬化渲染專家筆記按鈕
  const renderExpertKnowledgeButtons = useCallback((kmGroupsArray: string[], filteredByKeyword: VideoItem[]) => {
    // 限制同時渲染的按鈕數量，避免手機卡頓
    const maxButtons = 20; // 手機上最多顯示 20 個按鈕
    const visibleGroups = kmGroupsArray.slice(0, maxButtons);
    
    return (
      <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {visibleGroups.map((kmGroup, idx) => (
          <Button
            key={kmGroup + '-' + idx}
            variant={selectedKMGroup === kmGroup ? "contained" : "outlined"}
            size="large"
            onClick={() => {
              setSelectedKMGroup(selectedKMGroup === kmGroup ? null : kmGroup);
              setExpertPage(1);
            }}
            sx={{
              borderRadius: '24px',
              px: 4,
              py: 1.5,
              minWidth: '120px',
              minHeight: '64px',
              fontWeight: 600,
              fontSize: 16,
              color: '#1976d2',
              borderColor: '#90caf9',
              background: '#e3f2fd',
              boxShadow: '0 4px 12px rgba(25,118,210,0.15)',
              lineHeight: 1.2,
              '&:hover': {
                background: '#e3f2fd',
                borderColor: '#1976d2',
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 16px rgba(25,118,210,0.25)',
              },
              '&:active': {
                transform: 'translateY(0px)',
              },
              transition: 'all 0.2s ease-in-out',
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
              {expertKnowledgeViewMode === 'user' ? (
                <>
                  <Box sx={{ fontSize: 14, fontWeight: 600 }}>
                    {(() => {
                      // 🔧 修復：改善用戶名稱顯示
                      if (kmGroup.includes('@')) {
                        return kmGroup.split('@')[0];
                      } else if (kmGroup.length > 20 && !kmGroup.includes('@')) {
                        // 如果是 UID 格式，顯示為「用戶」
                        return '用戶';
                      } else {
                        return kmGroup;
                      }
                    })()}
                  </Box>
                  {kmGroup.includes('@') && (
                    <Box sx={{ fontSize: 10, opacity: 0.7 }}>
                      {kmGroup.split('@')[1]}
                    </Box>
                  )}
                  {kmGroup.length > 20 && !kmGroup.includes('@') && (
                    <Box sx={{ fontSize: 8, opacity: 0.5, wordBreak: 'break-all' }}>
                      {kmGroup.substring(0, 8)}...
                    </Box>
                  )}
                  <Box sx={{ 
                    fontSize: 16, 
                    fontWeight: 700, 
                    color: '#1976d2',
                    marginTop: 0.5
                  }}>
                    {filteredByKeyword.filter(video => video.userId === kmGroup).length} 部
                  </Box>
                </>
              ) : expertKnowledgeViewMode === 'tag' ? (
                <>
                  <Box sx={{ fontSize: 14, fontWeight: 600 }}>
                    {kmGroup}
                    {allExpertTags.includes(kmGroup) && (
                      <Box component="span" sx={{ ml: 0.5, fontSize: '0.75rem' }}>
                        ⭐
                      </Box>
                    )}
                  </Box>
                  <Box sx={{ 
                    fontSize: 16, 
                    fontWeight: 700, 
                    color: '#1976d2',
                    marginTop: 0.5
                  }}>
                    {filteredByKeyword.filter(video => 
                      Array.isArray(video.tags) && video.tags.includes(kmGroup)
                    ).length} 部
                  </Box>
                </>
              ) : (
                <>
                  <Box sx={{ fontSize: 14, fontWeight: 600 }}>
                    {kmGroup}
                  </Box>
                  <Box sx={{ 
                    fontSize: 16, 
                    fontWeight: 700, 
                    color: '#1976d2',
                    marginTop: 0.5
                  }}>
                    {filteredByKeyword.filter(video => 
                      Array.isArray(video.groupIds) && video.groupIds.some(gid => {
                        const group = allGroups.find(g => g.id === gid);
                        return group?.name === kmGroup;
                      })
                    ).length} 部
                  </Box>
                </>
              )}
            </Box>
          </Button>
        ))}
        
        {/* 如果按鈕數量超過限制，顯示載入更多按鈕 */}
        {kmGroupsArray.length > maxButtons && (
          <Button
            variant="outlined"
            size="large"
            onClick={() => {
              // 這裡可以實現分頁載入更多按鈕
              // 调试日志已移除
            }}
            sx={{
              borderRadius: '24px',
              px: 4,
              py: 1.5,
              minWidth: '120px',
              minHeight: '64px',
              fontWeight: 600,
              fontSize: 16,
              color: '#666',
              borderColor: '#ccc',
            }}
          >
            載入更多...
          </Button>
        )}
      </Box>
    );
  }, [selectedKMGroup, expertKnowledgeViewMode, allExpertTags, allGroups]);



  // 调试日志已移除
  // 调试日志已移除

  const SIDEBAR_WIDTH = 140;
  const APPBAR_HEIGHT = 64;

  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  // 2. Dialog 開啟時初始化 selectedTags
  useEffect(() => {
    if (tagDialogOpen && tagDialogVideo) {
      // 確保使用最新的 tagDialogVideo 狀態
      const currentTags = Array.isArray(tagDialogVideo.tags) ? [...tagDialogVideo.tags] : [];
      setSelectedTags(currentTags);
    }
  }, [tagDialogOpen, tagDialogVideo]);

  // 處理 Tag 變更
  const handleTagChange = async (video: VideoItem, tag: string, isAdd: boolean) => {
    console.log('🔍 handleTagChange 開始:', { 
      videoId: video.id, 
      tag, 
      isAdd, 
      authUser: authUser?.email, 
      guestId, 
      isGuest, 
      userId 
    });
    
    try {
      const docRef = safeDoc( 'sharedVbids', video.id);
      const currentTags = Array.isArray(video.tags) ? [...video.tags] : [];
      let newTags: string[];
      
      if (isAdd) {
        newTags = Array.from(new Set([...currentTags, tag]));
      } else {
        newTags = currentTags.filter(t => t !== tag);
      }
      
      console.log('🔍 準備更新 Firestore:', { 
        docRef: video.id, 
        newTags, 
        userId 
      });
      
      await updateDoc(docRef, { tags: newTags });
      
      // 调试日志已移除
      
      // 同步所有本地 state
      setVideos(prev => {
        const updatedVideos = prev.map(v => v.id === video.id ? { ...v, tags: newTags } : v);
        return updatedVideos;
      });
      setPersonalVideos(prev => prev.map(v => v.id === video.id ? { ...v, tags: newTags } : v));
      setCommunityVideos(prev => prev.map(v => v.id === video.id ? { ...v, tags: newTags } : v));
      
      // 🔧 修復：同時更新 personalHistoryVideos 狀態，確保「所有瀏覽記錄」分頁即時顯示新 Tag
      setPersonalHistoryVideos(prev => prev.map(v => v.id === video.id ? { ...v, tags: newTags } : v));
      
      // 🔧 修復：同步更新個人歷史記錄快取中的 Tag 信息
      const personalHistoryCacheKey = `personalHistory_${userId}`;
      setPersonalHistoryCache(prev => {
        const newCache = { ...prev };
        if (newCache[personalHistoryCacheKey]) {
          newCache[personalHistoryCacheKey] = newCache[personalHistoryCacheKey].map(v => 
            v.id === video.id ? { ...v, tags: newTags } : v
          );
        }
        return newCache;
      });
      
      // 🔧 修復：同步更新所有相關快取中的 Tag 信息，確保所有分頁都能顯示新 Tag
      const allCacheKeysForUpdate = Object.keys(videosCache);
      const relatedCacheKeysForUpdate = allCacheKeysForUpdate.filter(key => key.includes(userId));
      
      setVideosCache(prev => {
        const newCache = { ...prev };
        relatedCacheKeysForUpdate.forEach(key => {
          if (newCache[key]) {
            newCache[key] = newCache[key].map(v => 
              v.id === video.id ? { ...v, tags: newTags } : v
            );
          }
        });
        return newCache;
      });
      
      // 🔧 修復：同步更新 personalVideos 狀態，確保「所有瀏覽記錄」分頁能即時顯示新 Tag
      setPersonalVideos(prev => prev.map(v => v.id === video.id ? { ...v, tags: newTags } : v));
      
      // 同步日志已移除
      
      // 🔧 修復：清除所有相關快取，強制重新載入
      // 清除當前分頁的快取
      const currentCacheKey = `${currentSection}_${userId}`;
      setVideosCache(prev => {
        const newCache = { ...prev };
        delete newCache[currentCacheKey];
        return newCache;
      });
      setCacheTimestamps(prev => {
        const newTimestamps = { ...prev };
        delete newTimestamps[currentCacheKey];
        return newTimestamps;
      });
      
      // 🔧 修復：清除所有可能相關的快取，確保 Tag 更新後數據一致性
      const allCacheKeys = Object.keys(videosCache);
      const relatedCacheKeys = allCacheKeys.filter(key => key.includes(userId));
      
      setVideosCache(prev => {
        const newCache = { ...prev };
        relatedCacheKeys.forEach(key => {
          delete newCache[key];
        });
        return newCache;
      });
      
      setCacheTimestamps(prev => {
        const newTimestamps = { ...prev };
        relatedCacheKeys.forEach(key => {
          delete newTimestamps[key];
        });
        return newTimestamps;
      });
      
      // 🔧 修復：清除個人歷史記錄快取，解決「所有瀏覽記錄」分頁 Tag 不顯示問題
      setPersonalHistoryCache(prev => {
        const newCache = { ...prev };
        delete newCache[personalHistoryCacheKey];
        return newCache;
      });
      setPersonalHistoryCacheTimestamps(prev => {
        const newTimestamps = { ...prev };
        delete newTimestamps[personalHistoryCacheKey];
        return newTimestamps;
      });
      
      // 清理日志已移除
      // 清理日志已移除
      
      // 🔧 修復：移除強制重新渲染，避免性能問題
      // setForceUpdate(prev => prev + 1);
      
      // 優化：避免強制重新載入，保持數據一致性
      // 移除強制重新載入邏輯，避免數據不一致問題
      // 调试日志已移除
    } catch (error) {
      console.error('❌ 更新 Tag 失敗:', error);
      setSnackbarMessage('更新 Tag 失敗');
      setSnackbarOpen(true);
    }
  };

  const [showImportDialog, setShowImportDialog] = useState(false);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [playlists, setPlaylists] = useState<any[]>([]);
  const [selectedPlaylists, setSelectedPlaylists] = useState<string[]>([]);
  const [loadingPlaylists, setLoadingPlaylists] = useState(false);
  
         // 播放清單影片匯入相關狀態
         const [playlistVideos, setPlaylistVideos] = useState<any[]>([]);
         const [loadingVideos, setLoadingVideos] = useState(false);
         const [importing, setImporting] = useState(false);
         const [importProgress, setImportProgress] = useState({ current: 0, total: 0 });
         const [importResults, setImportResults] = useState<{ success: number; failed: number; skipped: number }>({ success: 0, failed: 0, skipped: 0 });
         const [overwriteExisting, setOverwriteExisting] = useState(false);

  useEffect(() => {
    if (accessToken) {
      setLoadingPlaylists(true);
      fetch('https://www.googleapis.com/youtube/v3/playlists?part=snippet,contentDetails&mine=true&maxResults=50', {
        headers: { Authorization: `Bearer ${accessToken}` }
      })
        .then(res => res.json())
        .then(data => {
          setPlaylists(data.items || []);
          setLoadingPlaylists(false);
        });
    }
  }, [accessToken]);

  function handleGoogleLogin() {
    const clientId = '230168256043-4kfiu78ss5qvcfp6pc96cc7d44kmvutu.apps.googleusercontent.com';
    const redirectUri = window.location.origin + '/oauth2callback';
    const scope = 'https://www.googleapis.com/auth/youtube.readonly';
    const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=token&scope=${encodeURIComponent(scope)}&include_granted_scopes=true&prompt=consent`;
    
    console.log('🔍 YouTube API 登入 - 使用 popup 模式');
    
    // ✅ 使用 popup（COOP 警告可以忽略，功能正常）
    const popup = window.open(authUrl, '_blank', 'width=500,height=600');
    
    if (!popup) {
      console.error('❌ 無法打開彈出視窗，可能被瀏覽器阻擋');
      setSnackbarMessage('無法打開 Google 登入視窗，請檢查瀏覽器設定');
      setSnackbarOpen(true);
    } else {
      console.log('✅ 已打開 Google 登入視窗');
    }
  }

  // 獲取播放清單中的影片
  const fetchPlaylistVideos = async (playlistId: string): Promise<any[]> => {
    const videos: any[] = [];
    let nextPageToken = '';
    
    try {
      do {
        const url = `https://www.googleapis.com/youtube/v3/playlistItems?part=snippet,contentDetails&playlistId=${playlistId}&maxResults=50${nextPageToken ? `&pageToken=${nextPageToken}` : ''}`;
        const response = await fetch(url, {
          headers: { Authorization: `Bearer ${accessToken}` }
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        videos.push(...(data.items || []));
        nextPageToken = data.nextPageToken || '';
      } while (nextPageToken);
      
      return videos;
    } catch (error) {
      console.error(`獲取播放清單 ${playlistId} 影片失敗:`, error);
      return [];
    }
  };

  // 獲取所有選中播放清單的影片
  const fetchAllPlaylistVideos = async () => {
    if (!accessToken || selectedPlaylists.length === 0) return;
    
    setLoadingVideos(true);
    setPlaylistVideos([]);
    
    try {
      const allVideos: any[] = [];
      
      for (const playlistId of selectedPlaylists) {
        const playlist = playlists.find(p => p.id === playlistId);
        if (playlist) {
          // 调试日志已移除
          const videos = await fetchPlaylistVideos(playlistId);
          
          // 為每個影片添加播放清單資訊
          const videosWithPlaylistInfo = videos.map(video => ({
            ...video,
            playlistTitle: playlist.snippet.title,
            playlistId: playlistId
          }));
          
          allVideos.push(...videosWithPlaylistInfo);
        }
      }
      
      setPlaylistVideos(allVideos);
      // 调试日志已移除
    } catch (error) {
      console.error('獲取播放清單影片失敗:', error);
      setSnackbarMessage('獲取播放清單影片失敗');
      setSnackbarOpen(true);
    } finally {
      setLoadingVideos(false);
    }
  };

  // 匯入影片到 sharedVbids
  const importVideosToSharedVbids = async () => {
    if (!authUser?.uid || playlistVideos.length === 0) return;
    
    setImporting(true);
    setImportProgress({ current: 0, total: playlistVideos.length });
    setImportResults({ success: 0, failed: 0, skipped: 0 });
    
    try {
      const batch = safeWriteBatch();
      let batchCount = 0;
      const BATCH_LIMIT = 500; // Firestore 批次限制
      
      for (let i = 0; i < playlistVideos.length; i++) {
        const video = playlistVideos[i];
        const videoId = video.snippet.resourceId.videoId;
        const docId = `${authUser.uid}_${videoId}`;
        
        try {
          // 檢查影片是否已存在
          const existingDoc = await getDoc(safeDoc( 'sharedVbids', docId));
          
                 if (existingDoc.exists() && !overwriteExisting) {
                   const existingData = existingDoc.data();
                   // 调试日志已移除
                   setImportResults(prev => ({ ...prev, skipped: prev.skipped + 1 }));
                 } else {
            // 創建新的影片文檔
            const videoData = {
              userId: authUser.uid,
              videoId: videoId,
              title: video.snippet.title,
              description: video.snippet.description || '',
              channelTitle: video.snippet.videoOwnerChannelTitle || video.snippet.channelTitle,
              publishedAt: video.snippet.publishedAt,
              thumbnails: {
                default: video.snippet.thumbnails.default?.url || '',
                medium: video.snippet.thumbnails.medium?.url || '',
                high: video.snippet.thumbnails.high?.url || ''
              },
              url: `https://www.youtube.com/watch?v=${videoId}`,
              zones: ['person'], // 個人影片
              addedDate: new Date().toISOString(),
              lastOpenDate: new Date().toISOString(),
              vbids: [], // 空的 VBIDs 陣列
              checkpointCount: 0,
              importedFromPlaylist: {
                playlistId: video.playlistId,
                playlistTitle: video.playlistTitle,
                importedAt: new Date().toISOString()
              },
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp()
            };
            
            batch.set(safeDoc( 'sharedVbids', docId), videoData);
            batchCount++;
            
            // 如果達到批次限制，提交批次
            if (batchCount >= BATCH_LIMIT) {
              await batch.commit();
              // 调试日志已移除
              batchCount = 0;
            }
            
            setImportResults(prev => ({ ...prev, success: prev.success + 1 }));
          }
        } catch (error) {
          console.error(`處理影片 ${videoId} 失敗:`, error);
          setImportResults(prev => ({ ...prev, failed: prev.failed + 1 }));
        }
        
        setImportProgress({ current: i + 1, total: playlistVideos.length });
      }
      
      // 提交剩餘的批次
      if (batchCount > 0) {
        await batch.commit();
        // 调试日志已移除
      }
      
             // 调试日志已移除
             const { success, failed, skipped } = importResults;
             let message = '匯入完成！';
             if (success > 0) message += ` 成功匯入 ${success} 個新影片。`;
             if (skipped > 0) message += ` ${skipped} 個影片已存在，已跳過。`;
             if (failed > 0) message += ` ${failed} 個影片匯入失敗。`;
             message += ' 請檢查個人珍藏頁面查看結果。';
             
             setSnackbarMessage(message);
             setSnackbarOpen(true);
      
      // 重新載入影片列表
      loadVideos('myPersonalFavorite');
      
    } catch (error) {
      console.error('匯入影片失敗:', error);
      setSnackbarMessage('匯入影片失敗');
      setSnackbarOpen(true);
    } finally {
      setImporting(false);
    }
  };
  const lastMessageTime = useRef<number>(0);
  
  const handleMessage = useCallback((e: MessageEvent) => {
    // 🔧 修復：過濾掉 React DevTools 和其他非 OAuth 消息
    if (e.data?.source === 'react-devtools-bridge' || 
        e.data?.source === 'react-devtools-content-script' ||
        e.data?.source === 'react-devtools-inject-backend') {
      return; // 靜默忽略 React DevTools 消息
    }
    
    // // 查询日志已移除
    
    // 快速檢查，避免不必要的處理
    // 允許來自 YouTube 和 Firebase 的消息
    const allowedOrigins = [
      window.location.origin,
      'https://www.youtube.com',
      'https://youtube.com',
      'https://auth.checkplay.me'  // 添加 Firebase 認證域名
    ];
    
    if (!allowedOrigins.includes(e.origin)) {
      // 调试日志已移除
      return;
    }
    
    // 檢查是否有 hash 或 search 數據
    const hashData = e.data?.hash;
    const searchData = e.data?.search;
    
    if (!hashData && !searchData) {
      // 调试日志已移除
      return;
    }
    
    // 🔧 修復：添加防抖動，避免頻繁處理
    const now = Date.now();
    if (now - lastMessageTime.current < 500) {
      // 调试日志已移除
      return;
    }
    lastMessageTime.current = now;
    
    try {
      // 優先使用 hash，如果沒有則使用 search
      // 🔧 修復：添加類型檢查，確保是字符串
      const paramString = hashData 
        ? (typeof hashData === 'string' ? hashData.substring(1) : '')
        : (typeof searchData === 'string' ? searchData.substring(1) : '');
      // // 查询日志已移除
      
      const params = new URLSearchParams(paramString);
      const accessToken = params.get('access_token');
      // // 查询日志已移除
      
      if (accessToken) {
        // 调试日志已移除
        setAccessToken(accessToken);
        setShowImportDialog(true);
      } else {
        // 檢查是否有錯誤
        const error = params.get('error');
        if (error) {
          console.error('❌ OAuth 錯誤:', error);
          setSnackbarMessage(`Google 授權失敗: ${error}`);
          setSnackbarOpen(true);
        } else {
          // 如果沒有 access_token 也沒有錯誤，可能是非 OAuth 訊息，不顯示警告
        }
      }
    } catch (error) {
      console.error('❌ 處理消息時發生錯誤:', error);
      setSnackbarMessage('處理 Google 授權時發生錯誤');
      setSnackbarOpen(true);
    }
  }, []);

  useEffect(() => {
    window.addEventListener('message', handleMessage, { passive: true });
    return () => window.removeEventListener('message', handleMessage);
  }, [handleMessage]);

  const handleLogin = () => {
      // 调试日志已移除
    setShowAuthDialog(true);
  };

  const [showProfileDialog, setShowProfileDialog] = useState(false);

  // 1. 在 useState 區新增 allUserTags 狀態
  const [allUserTags, setAllUserTags] = useState<string[]>([]);

  // 2. 延遲載入用戶 tags，從所有影片中收集標籤
  useEffect(() => {
    if (userId && (currentSection === 'myPersonalFavorite' || tagDialogOpen)) {
      // 每次打開標籤對話框時都重新載入，確保顯示最新標籤
      const collectUserTags = async () => {
        try {
          // 等待 Firebase 初始化完成
          await waitForFirebase();
          
          // 🔧 修復：只從 tags collection 讀取用戶自己明確創建的標籤
          // 這才是真正的「用戶自己創建的 Tag」，不包含系統預設或匯入的標籤
          const tagsCollection = safeCollection('tags');
          if (!tagsCollection) {
            console.warn('⚠️ 無法獲取 tags collection');
            setAllUserTags([]);
            return;
          }
          
          const tagsQuery = query(tagsCollection, where('userId', '==', userId));
          const tagsSnap = await getDocs(tagsQuery);
          const tagsFromCollection = tagsSnap.docs.map(doc => doc.data().name).filter(Boolean);
          
          // 按照使用頻率排序
          const tagUsage = JSON.parse(localStorage.getItem('tagUsage') || '{}');
          const sortedTags = tagsFromCollection.sort((a, b) => (tagUsage[b] || 0) - (tagUsage[a] || 0));
          
          // 查询日志已移除
            
          setAllUserTags(sortedTags);
        } catch (error) {
          console.error('❌ 載入用戶標籤失敗:', error);
          // 如果失敗，設置為空陣列
          setAllUserTags([]);
        }
      };
      
      collectUserTags();
    }
  }, [userId, currentSection, tagDialogOpen]);

  const [editingTag, setEditingTag] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [deleteTagConfirm, setDeleteTagConfirm] = useState('');
  const [videoUrl, setVideoUrl] = useState<string>('');
  const [isSubmittingUrl, setIsSubmittingUrl] = useState<boolean>(false);

  // Infinite Scroll 狀態 - 個人瀏覽記錄
  const [personalLoadedCount, setPersonalLoadedCount] = useState(60); // 統一初始載入數量為 60
  const [forceUpdate, setForceUpdate] = useState(0);
  const personalVideosToShow = useMemo(() => {
    return filteredPersonalVideos.slice(0, personalLoadedCount);
  }, [personalLoadedCount, filteredPersonalVideos]); // 移除 forceUpdate 依賴
  
  // 優化 tableKey 生成，避免 Tag 變化時整個表格重新渲染
  // 使用更穩定的標識符，不依賴於會變化的數據
  const tableKey = `table-${currentSection}-${userId}`;
  
  // 穩定的 Tag 排序邏輯，避免刪除 Tag 時其他 Tag 閃爍
  const stableSortedSelectedTags = useMemo(() => {
    const tagUsage = JSON.parse(localStorage.getItem('tagUsage') || '{}');
    return [...selectedTags].sort((a, b) => (tagUsage[b] || 0) - (tagUsage[a] || 0));
  }, [selectedTags]);
  
  const stableSortedAllUserTags = useMemo(() => {
    const tagUsage = JSON.parse(localStorage.getItem('tagUsage') || '{}');
    return [...allUserTags].sort((a, b) => (tagUsage[b] || 0) - (tagUsage[a] || 0));
  }, [allUserTags]);
  
  // 移除會導致無限循環的 useEffect
  
  // 監聽 tagDialogVideo 變化，強制重新渲染
  // 暫時註釋掉，避免無限循環
  // useEffect(() => {
  //   if (tagDialogVideo) {
  //     // 移除 setTimeout，直接更新，但限制更新頻率
  //     setForceUpdate(prev => prev + 1);
  //   }
  // }, [tagDialogVideo?.id]); // 只監聽 id 的變化，避免 tags 變化導致無限循環
  
  // 移除強制重新渲染的 useEffect，避免性能問題
  // useEffect(() => {
  //   // 移除 console.log，避免無限循環的 log 輸出
  // }, [forceUpdate]);
  
  // 頁面切換時重置無限滾動狀態
  useEffect(() => {
    setPersonalLoadedCount(60);  // 統一初始載入數量為 60
    setCommunityLoadedCount(60); // 統一初始載入數量為 60
    // 不要重置 historyLoadedCount，讓它保持已載入的數量
    // setHistoryLoadedCount(60);
  }, [currentSection]);
  
  const hasMorePersonalVideos = personalLoadedCount < filteredPersonalVideos.length;
  
  const loadMorePersonalVideos = () => {
    // 同步日志已移除
    
    // 防止重複調用
    if (personalLoadedCount >= filteredPersonalVideos.length) {
      // 调试日志已移除
      return;
    }
    
    setPersonalLoadedCount((prev) => {
      const newCount = Math.min(prev + 100, filteredPersonalVideos.length);
      // 调试日志已移除
      return newCount;
    });
  };

  // Infinite Scroll 狀態 - 社群
  const [communityLoadedCount, setCommunityLoadedCount] = useState(60); // 初始為 60，與其他分頁統一
  const communityVideosToShow = communityVideos.slice(0, communityLoadedCount);
  const hasMoreCommunityVideos = communityLoadedCount < communityVideos.length;
  
  // 社群公開影片分頁狀態
  const [communityPublicLoadedCount, setCommunityPublicLoadedCount] = useState(20); // 初始顯示 20 筆
  const [communityPublicVideos, setCommunityPublicVideos] = useState<VideoItem[]>([]); // 所有社群公開影片
  const [communityPublicHasMore, setCommunityPublicHasMore] = useState(false); // 是否還有更多影片
  const communityPublicVideosToShow = communityPublicVideos.slice(0, communityPublicLoadedCount);
  
  // 載入更多社群公開影片
  const loadMoreCommunityPublicVideos = useCallback(() => {
    console.log('🔍 loadMoreCommunityPublicVideos 被調用', {
      communityPublicHasMore,
      loading,
      communityPublicLoadedCount,
      communityPublicVideosLength: communityPublicVideos.length
    });
    
    if (!communityPublicHasMore || loading) {
      // 调试日志已移除
      return;
    }
    
    const newCount = Math.min(communityPublicLoadedCount + 20, communityPublicVideos.length);
    // 调试日志已移除
    
    setCommunityPublicLoadedCount(newCount);
    setCommunityPublicHasMore(newCount < communityPublicVideos.length);
  }, [communityPublicLoadedCount, communityPublicVideos, communityPublicHasMore, loading]);
  
  // 穩定的滾動處理函數
  const handleScrollLoadMore = useCallback((event: Event) => {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    const distanceFromBottom = documentHeight - (scrollTop + windowHeight);
    
    console.log('🔄 滾動事件', {
      scrollTop,
      windowHeight,
      documentHeight,
      distanceFromBottom,
      shouldLoadMore: distanceFromBottom <= 200,
      isNearBottom: distanceFromBottom <= 500
    });
    
    // 測試：任何滾動都觸發載入更多（用於調試）
    if (scrollTop > 100) {
      // 调试日志已移除
      loadMoreCommunityPublicVideos();
      return;
    }
    
    // 當滾動到距離底部 500px 時載入更多
    if (distanceFromBottom <= 500) {
      // 同步日志已移除
      loadMoreCommunityPublicVideos();
    }
  }, [loadMoreCommunityPublicVideos]);

  // 滾動監聽 - 自動載入更多社群公開影片
  useEffect(() => {
    console.log('🔍 滾動監聽 useEffect 觸發', {
      currentSection,
      communityPublicHasMore,
      communityPublicVideosLength: communityPublicVideos.length
    });
    
    // 只在社群公開分頁且還有更多影片時啟用滾動監聽
    if (currentSection !== 'communityPublic') {
      // 滚动监听日志已移除
      return;
    }
    
    if (!communityPublicHasMore) {
      // 滚动监听日志已移除
      return;
    }
    
    // 调试日志已移除
    window.addEventListener('scroll', handleScrollLoadMore);
    return () => {
      // 清理日志已移除
      window.removeEventListener('scroll', handleScrollLoadMore);
    };
  }, [currentSection, communityPublicHasMore, handleScrollLoadMore]);
  
  // 移除會導致無限循環的 useEffect
  // useEffect(() => {
  //   setCommunityLoadedCount(communityVideos.length);
  // }, [communityVideos]);
  
  const loadMoreCommunityVideos = () => {
    // 防止重複調用
    if (communityLoadedCount >= communityVideos.length) {
      return;
    }
    
    setCommunityLoadedCount((prev) => {
      const newCount = Math.min(prev + 100, communityVideos.length);
      return newCount;
    });
  };



  // 處理影片 URL 提交
  const handleSubmitVideoUrl = async () => {
    if (!videoUrl.trim()) {
      setSnackbarMessage('請輸入影片 URL');
      setSnackbarOpen(true);
      return;
    }

    setIsSubmittingUrl(true);
    try {
      // TODO: 這裡將來會實現實際的 URL 處理邏輯
      // 调试日志已移除
      setSnackbarMessage('URL 已提交，功能開發中...');
      setSnackbarOpen(true);
      setVideoUrl(''); // 清空輸入框
    } catch (error) {
      setSnackbarMessage('提交失敗，請稍後再試');
      setSnackbarOpen(true);
    } finally {
      setIsSubmittingUrl(false);
    }
  };

  // 3. 新增 Tag 時，先檢查 tags collection 是否有同名，無則 setDoc
  async function handleAddTag(tagName: string) {
    console.log('🔍 handleAddTag 開始:', { 
      tagName, 
      userId, 
      authUser: authUser?.email, 
      guestId, 
      isGuest 
    });
    
    if (!tagName || !userId) {
      // 调试日志已移除
      return;
    }
    
    try {
      // 等待 Firebase 初始化完成
      await waitForFirebase();
      
      const tagsCollection = safeCollection('tags');
      if (!tagsCollection) {
        console.warn('⚠️ 無法獲取 tags collection');
        return;
      }
      
      const tagRef = doc(tagsCollection, `${userId}_${tagName}`);
      console.log('🔍 準備寫入 tags collection:', { 
        tagRef: `${userId}_${tagName}`, 
        userId 
      });
      const tagSnap = await getDocs(query(tagsCollection, where('userId', '==', userId), where('name', '==', tagName)));
      if (tagSnap.empty) {
        await setDoc(tagRef, { name: tagName, userId, createdAt: new Date() });
        // 只添加新 Tag 到現有列表，不重新載入所有 Tag
        const tagUsage = JSON.parse(localStorage.getItem('tagUsage') || '{}');
        setAllUserTags(prev => {
          const newTags = Array.from(new Set([...prev, tagName]));
          return newTags.sort((a, b) => (tagUsage[b] || 0) - (tagUsage[a] || 0));
        });
        
        // 移除強制重新渲染，避免表格閃爍
        // setForceUpdate(prev => prev + 1);
      }
    } catch (error) {
      console.error('❌ handleAddTag 失敗:', error);
    }
  }

  // 4. 編輯 Tag 時，同步更新 tags collection
  async function handleRenameTag(oldTag: string, newTag: string) {
    if (!oldTag || !newTag || oldTag === newTag || !userId) return;
    const oldRef = doc(safeCollection( 'tags'), `${userId}_${oldTag}`);
    const newRef = doc(safeCollection( 'tags'), `${userId}_${newTag}`);
    await setDoc(newRef, { name: newTag, userId, createdAt: new Date() });
    await deleteDoc(oldRef);
    // 同步所有影片
    const batch = safeWriteBatch();
    [...personalVideos, ...communityVideos].forEach(v => {
      if (Array.isArray(v.tags) && v.tags.includes(oldTag)) {
        const docRef = safeDoc( 'sharedVbids', v.id);
        const newTags = v.tags.map(t => t === oldTag ? newTag : t);
        batch.update(docRef, { tags: newTags });
      }
    });
    await batch.commit();
    setAllUserTags(tags => {
      const updatedTags = tags.map(t => t === oldTag ? newTag : t);
      const tagUsage = JSON.parse(localStorage.getItem('tagUsage') || '{}');
      return updatedTags.sort((a, b) => (tagUsage[b] || 0) - (tagUsage[a] || 0));
    });
    setSelectedTags(tags => tags.map(t => t === oldTag ? newTag : t));
  }

  // 5. 刪除 Tag 時，刪除 tags collection 文件並同步所有影片
  const handleDeleteTag = useCallback(async (tag: string) => {
    if (!userId) return;
    
    try {
      // 先更新本地狀態，提供即時反饋
      // 使用更精確的更新方式，保持其他 Tag 的穩定性
      setAllUserTags(prev => {
        // 只過濾掉被刪除的 Tag，保持其他 Tag 的順序
        const filteredTags = prev.filter(t => t !== tag);
        // 重新排序，但保持穩定性
        const tagUsage = JSON.parse(localStorage.getItem('tagUsage') || '{}');
        return filteredTags.sort((a, b) => (tagUsage[b] || 0) - (tagUsage[a] || 0));
      });
      
      // 更新選中的標籤（如果包含被刪除的標籤）
      // 使用更精確的更新方式，避免影響其他 Tag
      setSelectedTags(prev => {
        // 只移除被刪除的 Tag，保持其他 Tag 的順序和穩定性
        return prev.filter(t => t !== tag);
      });
      
      // 使用內聯函數進行精確的狀態更新
      const updateVideoTags = (video: VideoItem) => {
        if (Array.isArray(video.tags) && video.tags.includes(tag)) {
          return { ...video, tags: video.tags.filter(t => t !== tag) };
        }
        return video;
      };
      
      // 批量更新本地影片狀態，避免多次重新渲染
      setPersonalVideos(prev => prev.map(updateVideoTags));
      setVideos(prev => prev.map(updateVideoTags));
      setCommunityVideos(prev => prev.map(updateVideoTags));
      
      // 後台同步到 Firebase
    const tagRef = doc(safeCollection( 'tags'), `${userId}_${tag}`);
    await deleteDoc(tagRef);
      
    const batch = safeWriteBatch();
    [...personalVideos, ...communityVideos].forEach(v => {
      if (Array.isArray(v.tags) && v.tags.includes(tag)) {
        const docRef = safeDoc( 'sharedVbids', v.id);
        const newTags = v.tags.filter(t => t !== tag);
        batch.update(docRef, { tags: newTags });
      }
    });
    await batch.commit();

      // 只在必要時重置分群和分頁
      if (selectedPersonalGroups.length > 0) {
        setSelectedPersonalGroups([]);
      }
      if (personalPage > 1) {
        setPersonalPage(1);
      }
      
    } catch (error) {
      console.error('刪除標籤失敗:', error);
      // 如果失敗，恢復本地狀態
      setSnackbarMessage('刪除標籤失敗，請稍後再試');
      setSnackbarOpen(true);
      
      // 重新載入標籤以恢復狀態
      // 這裡可以添加重新載入邏輯
    }
  }, [userId]); // 移除會導致無限循環的依賴項

  const [showTeamDialogInGroup, setShowTeamDialogInGroup] = useState(false);
  const [showGroupManagerInGroup, setShowGroupManagerInGroup] = useState(false);
  const [groupToManage, setGroupToManage] = useState<any>(null);

  // 隱藏個人影片記錄
  const handleHidePersonalRecord = async (videoId: string) => {
    if (!userId) return;
    
    const video = videos.find(v => v.id === videoId);
    if (!video) return;

    // 添加確認對話框
    if (!window.confirm('確定要從個人瀏覽記錄中移除這筆影片嗎？')) {
      return;
    }

    try {
      const docRef = safeDoc( 'sharedVbids', videoId);
      await updateDoc(docRef, {
        hiddenFor: arrayUnion(userId)
      });
      setPersonalVideos(prev => prev.filter(v => v.id !== videoId));
      setSnackbarMessage('已隱藏該影片');
      setSnackbarOpen(true);
    } catch (e) {
      setSnackbarMessage('隱藏失敗，請稍後再試');
      setSnackbarOpen(true);
    }
  };

  const [leavingGroupId, setLeavingGroupId] = useState<string | null>(null);

  // 1. 定義 handleConfirmLeave
  const handleConfirmLeave = async () => {
    if (!leavingGroupId || !authUser || !authUser.email) return;
    setLeaving(true);
    try {
      const group = userGroups.find(g => g.id === leavingGroupId);
      if (!group) return;
      const userEmail = authUser.email.toLowerCase();
      const newMembers = (group.members || []).filter((m: any) => (m.email || '').toLowerCase() !== userEmail);
      const newMembersEmail = (group.membersEmail || []).filter((e: string) => (e || '').toLowerCase() !== userEmail);
      const newAdmins = (group.admins || []).filter((e: string) => (e || '').toLowerCase() !== userEmail);
      await updateDoc(safeDoc( 'groups', group.id), {
        members: newMembers,
        membersEmail: newMembersEmail,
        admins: newAdmins,
      });
      setUserGroups(prev => prev.filter(g => g.id !== group.id));
      setLeavingGroupId(null);
      setSnackbarMessage('已退出群組');
      setSnackbarOpen(true);
    } catch (e) {
      setSnackbarMessage('退出群組失敗');
      setSnackbarOpen(true);
    } finally {
      setLeaving(false);
    }
  };

  // 延遲載入用戶群組，只在需要時才載入
  useEffect(() => {
    if (authUser?.email && currentSection === 'teamPoint') {
      // userGroups 載入已合併到 fetchTeamPointVideos 中，避免重複觸發
    }
  }, [authUser, currentSection]);

  // 1. 新增 state
  const [showAdminGroupsDialog, setShowAdminGroupsDialog] = useState(false);
  // 2. 新增 adminGroups 計算
  const adminGroups = React.useMemo(() => {
    if (!authUser || !authUser.email) return [];
    const currentUserEmail = authUser.email.trim().toLowerCase();
    return allGroups.filter(
      g => Array.isArray(g.admins) &&
        g.admins.map(e => (typeof e === 'string' ? e.trim().toLowerCase() : '')).includes(currentUserEmail)
    );
  }, [allGroups, authUser]);

  // 调试日志已移除

  // 调试日志已移除
  // 调试日志已移除
  // 调试日志已移除
  // 调试日志已移除

  const filteredTagVideos = currentSection === 'myPersonalFavorite' 
    ? videos.filter(v => Array.isArray(v.zones) && v.zones.includes('person')) // 所有按下「珍藏」的影片，不限分享者
    : filteredPersonalVideos.filter(v => Array.isArray(v.zones) && v.zones.includes('person'));
  
  const pagedTagVideos = filteredTagVideos.slice((personalPage - 1) * personalPageSize, personalPage * personalPageSize);

  // 新增分享 Dialog 狀態
  const handleShareGroup = useCallback((group: any) => setShareGroup(group), []);
  const handleCloseShareGroup = () => setShareGroup(null);

  // 添加處理標題點擊的函數
  const handleTitleClick = () => {
    setShowWelcome(true);
  };

  const handleCloseWelcome = () => {
    setShowWelcome(false);
    localStorage.setItem('welcome_shown', '1');
  };

  // Dialog 顯示前可加 loading 判斷
  // 移除早期返回，避免 Hooks 調用順序問題
  // if (authLoading) {
  //   return <div>載入中...</div>;
  // }


  // 取得我的收藏列表（假設是 filteredPersonalVideos 或 communityVideos 之類）
  // 在 Tag 分類前，先 filter Zone 欄位
  // Tag 分群與未分類來源統一用 filteredTagVideos
  function groupByTagWithUnclassified(videos: VideoItem[]): Record<string, VideoItem[]> {
    const groups: Record<string, VideoItem[]> = {};
    videos.forEach((v: VideoItem) => {
      if (Array.isArray(v.tags) && v.tags.length > 0) {
        v.tags.forEach((tag: string) => {
          if (!groups[tag]) groups[tag] = [];
          groups[tag].push(v);
        });
      } else {
        if (!groups['未標籤']) groups['未標籤'] = [];
        groups['未標籤'].push(v);
      }
    });
    return groups;
  }
  const tagGroups = groupByTagWithUnclassified(filteredTagVideos);
  const unclassifiedVideos = filteredTagVideos.filter(v => !Array.isArray(v.tags) || v.tags.length === 0);

  // 🔧 修復：ALL 分類：所有 Zone 有「珍藏」的影片，按最後瀏覽時間排序
  const allPersonVideos = filteredTagVideos.sort((a, b) => {
    // 優先使用 lastOpenDate，如果沒有則使用 addedDate
    const aDate = a.lastOpenDate || a.addedDate || a.createdAt || 0;
    const bDate = b.lastOpenDate || b.addedDate || b.createdAt || 0;
    return new Date(bDate).getTime() - new Date(aDate).getTime();
  });
  //const [expandedTag, setExpandedTag] = useState<string | null>('ALL'); // 預設 ALL 展開


  // 🔧 修復：使用與 MainLayout 一致的 menuItems，避免選單內容變化
  const menuItems = [
    {
      key: 'settings',
      label: '設定',
      icon: <SettingsIcon sx={{ color: '#d32f2f', fontSize: 32 }} />,
      onClick: () => {
        // 直接彈出設定視窗
        // 调试日志已移除
        // TODO: 實作設定功能
      }
    },
    {
      key: 'communityPublic',
      label: '社群公開',
      icon: <PublicIcon sx={{ color: '#d32f2f', fontSize: 32 }} />,
      onClick: () => {
        setCurrentSection('communityPublic');
        setExpandedMain(['home', 'community']);
        if (isMobile) setDrawerOpen(false);
      }
    },
    {
      key: 'teamPoint',
      label: '群組影片',
      icon: <GroupIcon sx={{ color: '#d32f2f', fontSize: 32 }} />,
      onClick: () => {
        setCurrentSection('teamPoint');
        setExpandedMain(['home', 'teamPoint']);
        if (isMobile) setDrawerOpen(false);
      }
    },
    {
      key: 'expertNotes',
      label: '專家筆記',
      icon: <NoteIcon sx={{ color: '#d32f2f', fontSize: 32 }} />,
      onClick: () => {
        // 切換到 notes 頁面
        if (isEmbedded && onPageChange) {
          onPageChange('notes');
        }
        if (isMobile) setDrawerOpen(false);
      }
    },
    {
      key: 'myVideos',
      label: '我的創作',
      icon: <PhotoLibraryIcon sx={{ color: '#d32f2f', fontSize: 32 }} />,
      onClick: () => {
        setCurrentSection('myVideos');
        setExpandedMain(['home', 'personalCenter', 'myVideos']);
        if (isMobile) setDrawerOpen(false);
      }
    },
    {
      key: 'personalHistory',
      label: '瀏覽記錄',
      icon: <HistoryIcon sx={{ color: '#d32f2f', fontSize: 32 }} />,
      onClick: () => {
        setCurrentSection('personalHistory');
        setExpandedMain(['home', 'personalCenter', 'personalHistory']);
        if (isMobile) setDrawerOpen(false);
      }
    },
    {
      key: 'syncPlay',
      label: '同步播放管理',
      icon: <QrCodeIcon sx={{ color: '#d32f2f', fontSize: 32 }} />,
      onClick: () => {
        // 调试日志已移除
        // 觸發事件，由 MainPage 處理
        window.dispatchEvent(new CustomEvent('openSyncManager'));
        if (isMobile) setDrawerOpen(false);
      }
    },
    {
      key: 'imageManager',
      label: '圖片管理',
      icon: <PhotoLibraryIcon sx={{ color: '#d32f2f', fontSize: 32 }} />,
      onClick: () => {
        // TODO: 實作圖片管理功能
        // 调试日志已移除
        if (isMobile) setDrawerOpen(false);
      }
    }
  ];

  async function handleLeaveGroup(groupId: string) {
    // TODO: 實作退出群組邏輯
    return Promise.resolve();
  }

  // 通用載入組件
  const renderLoadingState = (message: string = '影片載入中...') => (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 4 }}>
      <CircularProgress />
      <Typography sx={{ ml: 2 }}>{message}</Typography>
      {authLoading && (
        <Typography variant="body2" sx={{ ml: 1, color: 'text.secondary' }}>
          (驗證中...)
        </Typography>
      )}
    </Box>
  );

  // 專門用來載入 myPersonalFavorite (個人珍藏) 頁面資料的函式
  const EXPERT_PAGE_SIZE = 20; // 每頁顯示20筆

  // 專家筆記影片表格組件 - 統一格式版本
  const ExpertVideoTable: React.FC<{ videos: VideoItem[] }> = ({ videos }) => {
    const [displayCount, setDisplayCount] = useState(20);
    const hasMoreVideos = videos.length > displayCount;

    const loadMoreVideos = () => {
      setDisplayCount(prev => Math.min(prev + 20, videos.length));
    };

    const videosToDisplay = videos.slice(0, displayCount);

    return (
      <Box>
        <TableContainer>
          <Table size="small" sx={{ 
            tableLayout: 'fixed', 
            width: 'auto',
            '& th:first-of-type, & td:first-of-type': {
              '@media (max-width: 1024px)': {
                display: 'none'
              }
            }
          }}>
            <TableHead>
              <TableRow>
                {COLUMNS.filter(col => PAGE_COLUMN_KEYS['expertKnowledge'].includes(col.key)).map(col => (
                  <TableCell
                    key={col.key}
                    data-col={col.key}
                    align="center"
                    {...getTableCellStyle(col, true)}
                  >
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {videosToDisplay.map((video, idx) => (
                <TableRow key={`expert-${video.id}-${idx}`}>
                  {COLUMNS.filter(col => PAGE_COLUMN_KEYS['expertKnowledge'].includes(col.key)).map(col => (
                    <TableCell
                      key={`expert-${video.id}-${col.key}-${idx}`}
                      data-col={col.key}
                      align="center"
                      {...getTableCellStyle(col, false)}
                    >
                      {renderCellContent(col.key, video, idx, 'expertKnowledge')}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* 載入更多按鈕 */}
        {hasMoreVideos && (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Button
              variant="outlined"
              onClick={loadMoreVideos}
              sx={{ minWidth: 200 }}
            >
              載入更多影片 ({displayCount} / {videos.length})
            </Button>
          </Box>
        )}
        
        {!hasMoreVideos && videos.length > 20 && (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography color="text.secondary">
              已顯示全部 {videos.length} 部影片
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  const DEFAULT_TAGS = ['AI', 'AI工具', 'AI創業', 'AI學習', 'AI影像'];

    // 簡化的專家筆記內容渲染
  const renderExpertKnowledgeContent = useCallback(() => {
    console.log('🔍 renderExpertKnowledgeContent 開始:', { 
      expertKnowledgeData: !!expertKnowledgeData,
      currentSection
    });
    
    if (!expertKnowledgeData) {
      // 调试日志已移除
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 4 }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>專家知識載入中...</Typography>
        </Box>
      );
    }

    try {
      const { filteredByKeyword, kmGroupsArray } = expertKnowledgeData;
      console.log('✅ 專家筆記數據準備完成:', { 
        filteredByKeyword: filteredByKeyword?.length,
        kmGroupsArray: kmGroupsArray?.length 
      });

      // 按照影片數量排序專家按鈕
      const sortedGroups = kmGroupsArray
        .map(kmGroup => {
          const videoCount = filteredByKeyword.filter(video => video.userId === kmGroup).length;
          return { kmGroup, videoCount };
        })
        .sort((a, b) => b.videoCount - a.videoCount) // 按影片數量降序排列
        .map(item => item.kmGroup);
      
      // 直接渲染按鈕，不使用 useMemo（避免在 useCallback 內使用 hooks）
      const maxButtons = 20; // 手機上最多顯示 20 個按鈕
      const visibleGroups = sortedGroups.slice(0, maxButtons);
      
      const renderedButtons = visibleGroups.map((kmGroup, idx) => {
        // 獲取對應的顯示名稱，優先順序：Nickname > Email > 創作者資訊
        let displayName = '';
        
        // 第一優先：從影片數據中獲取創作者名稱（Nickname）
        const videoWithCreatorInfo = filteredByKeyword.find(video => video.userId === kmGroup);
        if (videoWithCreatorInfo?.creatorName) {
          displayName = videoWithCreatorInfo.creatorName;
        } else if (videoWithCreatorInfo?.creatorEmail) {
          displayName = videoWithCreatorInfo.creatorEmail;
        } else {
          // 第二優先：從 userIdToEmailMap 獲取 email
          displayName = userIdToEmailMap[kmGroup] || '';
        }
        
        // 如果仍然沒有找到顯示名稱，跳過這個按鈕（不顯示Firestore ID）
        if (!displayName || displayName === kmGroup) {
          return null;
        }
        
        const videoCount = filteredByKeyword.filter(video => video.userId === kmGroup).length;
        
    return (
            <Button
              key={kmGroup + '-' + idx}
              variant={selectedKMGroup === kmGroup ? "contained" : "outlined"}
          size="large"
              onClick={() => {
                setSelectedKMGroup(selectedKMGroup === kmGroup ? null : kmGroup);
            setExpertPage(1);
              }}
              sx={{
            borderRadius: '24px',
            px: 4,
            py: 1.5,
            minWidth: '120px',
            minHeight: '64px',
            fontWeight: 600,
            fontSize: 16,
                color: '#1976d2',
                borderColor: '#90caf9',
            background: '#e3f2fd',
            boxShadow: '0 4px 12px rgba(25,118,210,0.15)',
                lineHeight: 1.2,
                '&:hover': {
                  background: '#e3f2fd',
                  borderColor: '#1976d2',
              transform: 'translateY(-2px)',
              boxShadow: '0 6px 16px rgba(25,118,210,0.25)',
            },
            '&:active': {
              transform: 'translateY(0px)',
            },
            transition: 'all 0.2s ease-in-out',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
            <>
              <Box sx={{ fontSize: 14, fontWeight: 600 }}>
                {displayName.includes('@') ? displayName.split('@')[0] : displayName}
              </Box>
              {displayName.includes('@') && (
                <Box sx={{ fontSize: 10, opacity: 0.7 }}>
                  {displayName.split('@')[1]}
                </Box>
              )}
              <Box sx={{ 
                fontSize: 16, 
                fontWeight: 700, 
                color: '#1976d2',
                marginTop: 0.5
              }}>
                {videoCount} 部
              </Box>
            </>
          </Box>
            </Button>
        );
      }).filter(Boolean); // 過濾掉 null 值

      // 直接過濾影片（簡化為只按用戶分組）
      const filteredVideos = selectedKMGroup 
        ? filteredByKeyword.filter(video => video.userId === selectedKMGroup)
        : filteredByKeyword;

    return (
      <Box>
          {/* 簡化的專家筆記界面 */}

        {/* KMGroup 按鈕區域 */}
        <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {renderedButtons}
            
            {/* 載入更多按鈕 */}
            {kmGroupsArray.length > 20 && (
            <Button
                variant="outlined"
                size="large"
              onClick={() => {
                  // 调试日志已移除
              }}
              sx={{
                  borderRadius: '24px',
                  px: 4,
                  py: 1.5,
                  minWidth: '120px',
                  minHeight: '64px',
                  fontWeight: 600,
                  fontSize: 16,
                  color: '#666',
                  borderColor: '#ccc',
                }}
              >
                載入更多...
            </Button>
            )}
        </Box>

        {/* 影片顯示區域 */}
        {selectedKMGroup ? (
          <Box>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                {(() => {
                  // 🔧 修復：優先顯示 Nickname，而不是 User ID
                  // 從影片數據中獲取創作者名稱（Nickname）
                  const videoWithCreatorInfo = filteredByKeyword.find(video => video.userId === selectedKMGroup);
                  if (videoWithCreatorInfo?.creatorName) {
                    return `${videoWithCreatorInfo.creatorName} 的專家筆記影片`;
                  } else if (videoWithCreatorInfo?.creatorEmail) {
                    return `${videoWithCreatorInfo.creatorEmail} 的專家筆記影片`;
                  } else {
                    // 從 userIdToEmailMap 獲取 email
                    const displayName = userIdToEmailMap[selectedKMGroup] || selectedKMGroup;
                    return `${displayName} 的專家筆記影片`;
                  }
                })()}
            </Typography>
              <ExpertVideoTable videos={filteredVideos} />
          </Box>
        ) : (
          <Box>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              所有專家筆記影片
            </Typography>
              <ExpertVideoTable videos={filteredVideos} />
          </Box>
        )}
      </Box>
    );
    } catch (error) {
      console.error('❌ renderExpertKnowledgeContent 錯誤:', error);
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 4 }}>
          <Typography color="error">專家筆記載入失敗，請重新整理頁面</Typography>
        </Box>
      );
    }
  }, [expertKnowledgeData, expertSearchKeyword, selectedKMGroup]);

  const handleToggleTag = (tag: string) => {
    setExpandedTag(prev => (prev === tag ? null : tag));
    setPersonalPage(1);
  };

  const handleToggleMyFavGroup = (groupName: string) => {
    setExpandedMyFavGroup(prev => (prev === groupName ? null : groupName));
    setPersonalPage(1);
  };
  // 新增自訂標籤
  const handleAddCustomTag = async () => {
    const tag = newTagInput.trim();
    if (!tag || customTags.includes(tag) || defaultTags.includes(tag)) return;
    const updated = [...customTags, tag];
    setCustomTags(updated);
    setNewTagInput('');
    if (authUser?.uid) {
      await updateUser(authUser.uid, { shareTags: updated });
    }
  };

  const renderMyPersonalFavoriteContent = useMemo(() => {
    // 2. 過濾資料
    const personalVideosList = filteredPersonalVideos;
    
    // 個人珍藏分頁專用：只根據標籤搜尋，不搜尋影片內容
    const filteredByKeyword = personalSearchKeyword
      ? personalVideosList.filter(video => {
          const keyword = personalSearchKeyword.toLowerCase();
          // 只搜尋標籤匹配
          const tagsMatch = Array.isArray(video.tags) && video.tags.some(tag => tag.toLowerCase().includes(keyword));
          return tagsMatch;
        })
      : personalVideosList;

    // 🔧 修復：使用 allUserTags（只有用戶自己創建的標籤），而不是從影片中收集所有標籤
    // allUserTags 已經在 useEffect 中載入，只包含用戶在「+ 新增標籤」時明確創建的標籤
    const userTags = allUserTags; // 使用已載入的用戶自創標籤（12個）

    // Tag 合併邏輯：只顯示用戶自己創建的標籤，不合併系統預設標籤
    let allTags: string[] = [];
    if (!authUser) {
      allTags = [...DEFAULT_TAGS];
    } else {
      // 🔧 修復：只使用用戶自己創建的標籤，不合併系統預設標籤
      allTags = [...userTags];
      // 按照使用頻率排序
      const tagUsage = JSON.parse(localStorage.getItem('tagUsage') || '{}');
      allTags.sort((a, b) => (tagUsage[b] || 0) - (tagUsage[a] || 0));
    }

    // 個人珍藏分頁專用：根據搜尋關鍵字過濾標籤
    const filteredTags = personalSearchKeyword
      ? allTags.filter(tag => tag.toLowerCase().includes(personalSearchKeyword.toLowerCase()))
      : allTags;

    // 4. 依選取 tag 過濾（支援多選）
    const filteredByTag = selectedPersonalGroups.length > 0
      ? filteredByKeyword.filter(video => 
          Array.isArray(video.tags) && 
          selectedPersonalGroups.some(selectedTag => video.tags?.includes(selectedTag))
        )
      : filteredByKeyword;

    // 3. 分群
    const allGroupsWithVideos = new Set<string>();
    allGroups.forEach(group => {
      const hasVideosInGroup = filteredByTag.some(v =>
        Array.isArray(v.groupIds) && v.groupIds.includes(group.id)
      );
      if (hasVideosInGroup) {
        allGroupsWithVideos.add(group.name);
      }
    });
    const groupArray: string[] = [];
    if (personalViewMode === 'tag') {
      groupArray.push(...filteredTags); // 使用過濾後的標籤
    } else {
      // 只顯示自己建立或被加入的群組
      groupArray.push(...userGroups.map(g => g.name).sort());
              }

    // 5. 無限滾動 - 使用全域的過濾邏輯
    const personalHistoryVideosToShow = filteredByTag.slice(0, personalLoadedCount);
    const hasMorePersonalHistoryVideos = personalLoadedCount < filteredByTag.length;
    
    // 調試信息 - 已移除，避免重複渲染
    
    // 簡化的載入更多函數
    const loadMorePersonalHistoryVideos = () => {
      // 防止重複調用
      if (personalLoadedCount >= filteredByTag.length) {
        return;
      }
      
      setPersonalLoadedCount((prev) => {
        const newCount = Math.min(prev + 20, filteredByTag.length);
        return newCount;
      });
    };

    // 5. UI
    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* 目前選取的 tag 顯示（支援多選） */}
        {selectedPersonalGroups.length > 0 && (
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="body2" color="text.secondary">
              目前篩選：
            </Typography>
            {selectedPersonalGroups.map(tag => (
            <Chip
                key={tag}
                label={tag}
              color="primary"
                onDelete={() => setSelectedPersonalGroups(prev => prev.filter(t => t !== tag))}
              size="small"
            />
            ))}
            <Button
              size="small"
              color="secondary"
              onClick={() => setSelectedPersonalGroups([])}
              sx={{ ml: 1 }}
            >
              清除全部
            </Button>
          </Box>
        )}
        {/* 個人珍藏分頁專用搜尋欄 - 只搜尋標籤 */}
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="搜尋標籤名稱..."
            value={personalSearchKeyword}
            onChange={(e) => setPersonalSearchKeyword(e.target.value)}
            sx={{ 
              '& .MuiOutlinedInput-root': { 
                backgroundColor: 'white',
                borderRadius: '8px',
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#1976d2',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#1976d2',
                  borderWidth: 2,
                }
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#1976d2' }} />
                </InputAdornment>
              ),
              endAdornment: personalSearchKeyword && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setPersonalSearchKeyword('')}
                    sx={{ color: '#666' }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
        </Box>

        {/* 檢視模式切換 */}
        <ToggleButtonGroup
          value={personalViewMode}
          exclusive
          onChange={(event, newMode) => {
            if (newMode) {
              setPersonalViewMode(newMode);
              setSelectedPersonalGroups([]);
              setPersonalPage(1);
            }
          }}
          aria-label="view mode"
          sx={{ mb: 2 }}
        >
          <ToggleButton value="tag" aria-label="view by tag">
            依標籤檢視
          </ToggleButton>
          <ToggleButton value="group" aria-label="view by group">
            依群組檢視
          </ToggleButton>
        </ToggleButtonGroup>
        {/* 搜尋結果提示 */}
        {personalSearchKeyword && (
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              找到 {filteredTags.length} 個匹配的標籤
            </Typography>
            {filteredTags.length === 0 && (
              <Typography variant="body2" color="error">
                (無匹配的標籤)
              </Typography>
            )}
          </Box>
        )}

        {/* 分群按鈕 */}
        <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {groupArray.map(tag => (
            <Box key={tag} sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {tagEditMode && editingTagName === tag ? (
                <TextField
                  value={editingValue}
                  size="small"
                  autoFocus
                  onChange={e => setEditingValue(e.target.value)}
                  onBlur={async () => {
                    if (editingValue.trim() && editingValue !== tag && !defaultTags.includes(editingValue) && !customTags.includes(editingValue)) {
                      const updated = customTags.map(t => t === tag ? editingValue.trim() : t);
                      setCustomTags(updated);
                      setEditingTag(null);
                      setEditingValue('');
                      if (authUser?.uid) await updateUser(authUser.uid, { shareTags: updated });
                    } else {
                      setEditingTag(null);
                      setEditingValue('');
                    }
                  }}
                  onKeyDown={async e => {
                    if (e.key === 'Enter') {
                      if (editingValue.trim() && editingValue !== tag && !defaultTags.includes(editingValue) && !customTags.includes(editingValue)) {
                        const updated = customTags.map(t => t === tag ? editingValue.trim() : t);
                        setCustomTags(updated);
                        setEditingTag(null);
                        setEditingValue('');
                        if (authUser?.uid) await updateUser(authUser.uid, { shareTags: updated });
                      } else {
                        setEditingTag(null);
                        setEditingValue('');
                      }
          }
                  }}
                  sx={{ width: 100, mr: 0.5 }}
                />
              ) : (
                <Button
                  variant={selectedPersonalGroups.includes(tag) ? 'contained' : 'outlined'}
                  size="small"
                  sx={{
                    borderRadius: '18px', // 稍大圓角
                    px: 2, // 稍大左右 padding
                    py: 0.4, // 稍大上下 padding
                    minWidth: 0,
                    minHeight: 0,
                    fontWeight: 500,
                    fontSize: 12, // 字體變小
                    color: '#1976d2',
                    borderColor: '#90caf9',
                    background: '#f0f7ff',
                    boxShadow: '0 1px 4px rgba(25,118,210,0.04)',
                    lineHeight: 1.2,
                    '&:hover': {
                      background: '#e3f2fd',
                      borderColor: '#1976d2',
                    },
                  }}
                  onClick={() => {
                    setSelectedPersonalGroups(prev => 
                      prev.includes(tag) 
                        ? prev.filter(t => t !== tag)
                        : [...prev, tag]
                    );
                    setPersonalPage(1);
                  }}
                  onDoubleClick={() => {
                    if (customTags.includes(tag)) {
                      setEditingTag(tag);
                      setEditingValue(tag);
                    }
                  }}
                >
                  {tag}
                </Button>
              )}
              {/* 只有自訂標籤可刪除 */}
              {customTags.includes(tag) && !editingTag && (
                <IconButton
                  size="small"
                  color="error"
                  sx={{ ml: 0, p: 0.5 }}
                  onClick={async () => {
                    if (window.confirm('確定要刪除此自訂標籤？')) {
                      const updated = customTags.filter(t => t !== tag);
                      setCustomTags(updated);
                      if (authUser?.uid) await updateUser(authUser.uid, { shareTags: updated });
                    }
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          ))}
        </Box>


        {/* 影片渲染區塊 */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          {personalHistoryVideosToShow.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">
                {selectedPersonalGroups.length > 0 
                  ? `沒有標籤為「${selectedPersonalGroups.join('、')}」的影片` 
                : '尚無收藏影片'}
            </Typography>
          </Box>
        ) : (
          <>
            <InfiniteScroll
              key={`personal-scroll-${personalLoadedCount}-${filteredByTag.length}`}
              dataLength={personalHistoryVideosToShow.length}
              next={loadMorePersonalHistoryVideos}
              hasMore={hasMorePersonalHistoryVideos}
              loader={null}
              endMessage={<Typography align="center" sx={{ my: 2, color: 'gray' }}>已載入全部</Typography>}
              scrollThreshold={0.8}
              scrollableTarget="main-personal-history-scroll"
            >
              <TableContainer>
                <Table size="small" sx={{
              tableLayout: 'fixed',
              width: 'auto',
              '& th:first-of-type, & td:first-of-type': {
                '@media (max-width: 1024px)': {
                  display: 'none'
                }
              }
            }}>
                  <TableHead>
                    <TableRow>
                      {COLUMNS.filter(col => PAGE_COLUMN_KEYS['myPersonalFavorite'].includes(col.key)).map(col => (
                        <TableCell
                          key={col.key}
                          data-col={col.key}
                          align="center"
                          {...getTableCellStyle(col, true)}
                        >
                          {col.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {personalHistoryVideosToShow.map((video, idx) => (
                      <TableRow key={`personal-${video.id}`}>
                        {COLUMNS.filter(col => PAGE_COLUMN_KEYS['myPersonalFavorite'].includes(col.key)).map(col => (
                          <TableCell
                            key={`personal-${video.id}-${col.key}`}
                            data-col={col.key}
                            align="center"
                            {...getTableCellStyle(col, false)}
                          >
                            {renderCellContent(col.key, video, idx, 'personal')}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </InfiniteScroll>
          </>
        )}
        </Box>
      </Box>
    );
  }, [personalSearchKeyword, selectedPersonalGroups, personalViewMode, authUser, userGroups, allGroups, personalLoadedCount, personalVideos, userFavoriteIds, favoriteUpdateTrigger]); // 🔧 修復：添加 userFavoriteIds 和 favoriteUpdateTrigger 依賴項，確保珍藏按鈕狀態立即更新

  // 長按進入編輯模式
  const handleTagMouseDown = (tag: string) => {
    tagTimerRef.current = setTimeout(() => {
      setTagEditMode(true);
      setEditingTagName(tag);
    }, 1000);
  };

  // 長按進入刪除模式（用於個人珍藏頁面）
  const handleTagLongPressForDelete = (tag: string) => {
    tagTimerRef.current = setTimeout(() => {
      setTagDeleteMode(true);
    }, 1000);
  };

  // 點擊處理（包含雙擊邏輯）
  const handleTagClick = (tag: string) => {
    const currentTime = Date.now();
    
    // 檢查是否為雙擊
    if (currentTime - lastClickTimeRef.current < 300 && lastClickedTagRef.current === tag) {
      // 雙擊：切換專家模式
      if (expertTags.includes(tag)) {
        // 如果已經是專家 Tag，則取消專家模式
        handleCancelExpertTag(tag);
      } else {
        // 如果不是專家 Tag，則設為專家模式
        handleMakeTagExpert(tag);
      }
      lastClickTimeRef.current = 0;
      lastClickedTagRef.current = '';
    } else {
      // 單擊：根據模式處理
      if (tagEditMode) {
        // 編輯模式：點擊 Tag 進入編輯狀態
        setEditingTagName(tag);
        setEditingValue(tag);
      } else if (!tagDeleteMode) {
        // 正常模式：選擇 Tag
        setSelectedPersonalGroups(prev => 
          prev.includes(tag) 
            ? prev.filter(t => t !== tag)
            : [...prev, tag]
        );
        setPersonalPage(1);
      }
      lastClickTimeRef.current = currentTime;
      lastClickedTagRef.current = tag;
    }
  };

  // 滑鼠放開時清除計時器
  const handleTagMouseUp = () => {
    if (tagTimerRef.current) {
      clearTimeout(tagTimerRef.current);
      tagTimerRef.current = null;
    }
  };

  // 將 Tag 設為專家模式
  const handleMakeTagExpert = async (tag: string) => {
    if (!userId) return;
    const tagRef = doc(safeCollection( 'tags'), `${userId}_${tag}`);
    const tagSnap = await getDoc(tagRef);
    if (!tagSnap.exists()) {
      await setDoc(tagRef, { name: tag, userId, createdAt: new Date(), isExpert: true, expertAt: new Date() });
    } else {
      await updateDoc(tagRef, { isExpert: true, expertAt: new Date() });
    }
    setExpertTags(prev => Array.from(new Set([...prev, tag])));
    setSnackbarMessage(`「${tag}」已設為專家標籤`);
    setSnackbarOpen(true);
  };

  // 取消專家模式
  const handleCancelExpertTag = async (tag: string) => {
    if (!userId) return;
    try {
      const tagRef = doc(safeCollection( 'tags'), `${userId}_${tag}`);
      await updateDoc(tagRef, { 
        isExpert: false,
        expertAt: null
      });
      setExpertTags(prev => prev.filter(t => t !== tag));
    } catch (error) {
      console.error('取消專家標籤失敗:', error);
      setSnackbarMessage('取消專家標籤失敗');
      setSnackbarOpen(true);
    }
  };

  // 在載入影片資料後，聚合為每個 videoId 只顯示一次
  function deduplicateByVideoId(list: VideoItem[]): VideoItem[] {
    const map = new Map<string, VideoItem & { checkpointCount: number }>();
    for (const item of list) {
      if (!item.videoId) continue;
      const key = item.videoId;
      if (!map.has(key)) {
        map.set(key, { ...item, checkpointCount: Array.isArray(item.vbids) ? item.vbids.length : 0 });
      } else {
        // 合併重點數
        const prev = map.get(key)!;
        const newCount = prev.checkpointCount + (Array.isArray(item.vbids) ? item.vbids.length : 0);
        // 以最新一筆為主
        map.set(key, { ...item, checkpointCount: newCount });
      }
    }
    return Array.from(map.values());
  }

  // 載入 YouTube 影片資訊
  const loadYouTubeInfo = async (video: VideoItem) => {
    if (!video.videoId) return;
    // 先檢查快取與 lastStatsFetchedAt
    const cached = youtubeInfoCache[video.videoId];
    const lastFetched = cached?.lastStatsFetchedAt ? new Date(cached.lastStatsFetchedAt).getTime() : 0;
    // 檢查是否為 notFound 且 24 小時內查過
    if (cached?.notFound) {
      const lastTried = cached.lastTriedAt ? new Date(cached.lastTriedAt).getTime() : 0;
      if (Date.now() - lastTried < 24 * 60 * 60 * 1000) return;
    }
    // 檢查是否為正常快取且 24 小時內查過
    if (cached && !cached.notFound && Date.now() - lastFetched < 24 * 60 * 60 * 1000) {
      const updatedVideo = {
        ...video,
        youtubeInfo: youtubeInfoCache[video.videoId],
        youtubeHashtags: youtubeInfoCache[video.videoId].tags || [], // 新增 YouTube 標籤映射
        youtubeMetadata: youtubeInfoCache[video.videoId].tags ? {
          hashtags: youtubeInfoCache[video.videoId].tags,
          title: youtubeInfoCache[video.videoId].title || '',
          description: youtubeInfoCache[video.videoId].description || '',
          channelTitle: youtubeInfoCache[video.videoId].channelTitle || '',
          publishedAt: youtubeInfoCache[video.videoId].publishedAt || '',
          viewCount: youtubeInfoCache[video.videoId].viewCount || '0',
          thumbnail: youtubeInfoCache[video.videoId].thumbnails?.high?.url || youtubeInfoCache[video.videoId].thumbnails?.medium?.url || '',
          duration: youtubeInfoCache[video.videoId].duration || '0:00'
        } : undefined
      };
      setVideos(prev => prev.map(v => v.id === video.id ? updatedVideo : v));
      setPersonalVideos(prev => prev.map(v => v.id === video.id ? updatedVideo : v));
      setCommunityVideos(prev => prev.map(v => v.id === video.id ? updatedVideo : v));
      return;
    }
    try {
      // 檢查快取，避免重複調用
      if (youtubeInfoCache[video.videoId] && !youtubeInfoCache[video.videoId].notFound) {
        // 处理日志已移除
        return;
      }
      
      // 查询日志已移除
      const videoDetails = await youtubeApi.getVideoDetails(video.videoId);
      
      // 驗證 API 回應的完整性
      if (!videoDetails || !videoDetails.title || !videoDetails.channelTitle) {
        console.warn('⚠️ YouTube API 回應不完整，跳過更新:', video.videoId);
        // 記錄錯誤但不更新影片狀態
        youtubeInfoCache[video.videoId] = { 
          notFound: true, 
          lastTriedAt: new Date().toISOString(),
          error: 'API response incomplete'
        };
        saveYoutubeInfoCache();
        return;
      }
      
      youtubeInfoCache[video.videoId] = { ...videoDetails, lastStatsFetchedAt: new Date().toISOString() };
      saveYoutubeInfoCache();
      const updatedVideo = {
        ...video,
        youtubeInfo: youtubeInfoCache[video.videoId],
        youtubeHashtags: youtubeInfoCache[video.videoId].tags || [], // 新增 YouTube 標籤映射
        youtubeMetadata: youtubeInfoCache[video.videoId].tags ? {
          hashtags: youtubeInfoCache[video.videoId].tags,
          title: youtubeInfoCache[video.videoId].title || '',
          description: youtubeInfoCache[video.videoId].description || '',
          channelTitle: youtubeInfoCache[video.videoId].channelTitle || '',
          publishedAt: youtubeInfoCache[video.videoId].publishedAt || '',
          viewCount: youtubeInfoCache[video.videoId].viewCount || '0',
          thumbnail: youtubeInfoCache[video.videoId].thumbnails?.high?.url || youtubeInfoCache[video.videoId].thumbnails?.medium?.url || '',
          duration: youtubeInfoCache[video.videoId].duration || '0:00'
        } : undefined
      };
      setVideos(prev => prev.map(v => v.id === video.id ? updatedVideo : v));
      setPersonalVideos(prev => prev.map(v => v.id === video.id ? updatedVideo : v));
      setCommunityVideos(prev => prev.map(v => v.id === video.id ? updatedVideo : v));
      
      // 只有在成功取得完整資料時才更新 Firestore
      if (userId && userId !== 'guest' && video.id) {
        const videoRef = safeDoc( 'videos', video.id!);
        await updateDoc(videoRef, { youtubeInfo: youtubeInfoCache[video.videoId] });
      }
      
      // 调试日志已移除
    } catch (error) {
      console.error('❌ 載入 YouTube 資訊失敗:', error);
      // 失敗時只記錄快取，不更新影片狀態，保持原有的 Firestore 資料
      youtubeInfoCache[video.videoId] = { 
        notFound: true, 
        lastTriedAt: new Date().toISOString(),
        error: error instanceof Error ? error.message : 'Unknown error'
      };
      saveYoutubeInfoCache();
      // 不更新任何狀態，保持原有的正確資料
    }
  };

  // 批量載入 YouTube 資訊
  const loadYouTubeInfoForVideos = async (videoList: VideoItem[]) => {
    // 查询日志已移除
    const now = Date.now();
    const videosToFetch = videoList.filter(v => {
      if (!v.videoId) return false;
      if (v.videoId.startsWith('blob:') || v.videoId.includes('drive.google.com') || !v.videoId.match(/^[a-zA-Z0-9_-]{11}$/)) return false;
      const cached = youtubeInfoCache[v.videoId];
      // 檢查是否為 notFound 且 24 小時內查過
      if (cached?.notFound) {
        const lastTried = cached.lastTriedAt ? new Date(cached.lastTriedAt).getTime() : 0;
        if (Date.now() - lastTried < 24 * 60 * 60 * 1000) return false;
      }
      // 檢查是否為正常快取且 24 小時內查過
      const lastFetched = cached?.lastStatsFetchedAt ? new Date(cached.lastStatsFetchedAt).getTime() : 0;
      if (cached && !cached.notFound && Date.now() - lastFetched < 24 * 60 * 60 * 1000) return false;
      return true;
    });
    
    // 查询日志已移除
    
    // 🔧 修復重點數消失問題：如果沒有需要處理的影片，直接返回
    if (videosToFetch.length === 0) {
      // 调试日志已移除
      return;
    }
    
    for (const video of videosToFetch) {
      try {
        const videoId = video.videoId as string;
        const startTime = Date.now();
        // 查询日志已移除.toISOString());
        const videoDetails = await youtubeApi.getVideoDetails(videoId);
        const endTime = Date.now();
        // 调试日志已移除
        
        // 驗證 API 回應的完整性
        if (!videoDetails || !videoDetails.title || !videoDetails.channelTitle) {
          console.warn('⚠️ YouTube API 回應不完整，跳過更新:', videoId);
          // 記錄錯誤但不更新影片狀態
          youtubeInfoCache[videoId] = { 
            notFound: true, 
            lastTriedAt: new Date().toISOString(),
            error: 'API response incomplete'
          };
          saveYoutubeInfoCache();
          continue;
        }
        
        youtubeInfoCache[videoId] = { ...videoDetails, lastStatsFetchedAt: new Date().toISOString() };
        saveYoutubeInfoCache();
        const updatedYouTubeInfo = youtubeInfoCache[videoId];
        
        // 只有在成功取得完整資料時才更新影片狀態
        // 🔧 修復重點數被重置問題：從當前狀態中獲取正確的重點數數據
        const updateVideo = (v: VideoItem) => {
          if (v.id === video.id) {
            // 從當前狀態中獲取正確的重點數數據，而不是依賴傳入的 video 參數
            const currentVideo = personalHistoryVideos.find(pv => pv.id === video.id) || 
                                videos.find(pv => pv.id === video.id) ||
                                personalVideos.find(pv => pv.id === video.id) ||
                                communityVideos.find(pv => pv.id === video.id);
            
            console.log('🔍 updateVideo 調試:', {
              videoId: video.id,
              foundInPersonalHistory: !!personalHistoryVideos.find(pv => pv.id === video.id),
              foundInVideos: !!videos.find(pv => pv.id === video.id),
              currentVideoCheckpointCount: currentVideo?.checkpointCount,
              currentVideoGlobalCheckpointCount: currentVideo?.globalCheckpointCount,
              originalCheckpointCount: v.checkpointCount,
              originalGlobalCheckpointCount: v.globalCheckpointCount
            });
            
            return { 
          ...v, 
              // 保留當前狀態中的重點數數據，避免被重置
              checkpointCount: currentVideo?.checkpointCount || v.checkpointCount || 0,
              globalCheckpointCount: currentVideo?.globalCheckpointCount || v.globalCheckpointCount || 0,
              // 更新 YouTube 相關資料
          youtubeInfo: updatedYouTubeInfo,
          youtubeHashtags: updatedYouTubeInfo.tags || [], // 新增 YouTube 標籤映射
          youtubeMetadata: updatedYouTubeInfo.tags ? {
            hashtags: updatedYouTubeInfo.tags,
            title: updatedYouTubeInfo.title || '',
            description: updatedYouTubeInfo.description || '',
            channelTitle: updatedYouTubeInfo.channelTitle || '',
            publishedAt: updatedYouTubeInfo.publishedAt || '',
            viewCount: updatedYouTubeInfo.viewCount || '0',
            thumbnail: updatedYouTubeInfo.thumbnails?.high?.url || updatedYouTubeInfo.thumbnails?.medium?.url || '',
            duration: updatedYouTubeInfo.duration || '0:00'
          } : undefined
            };
          }
          return v;
        };
        console.log('🔍 更新影片狀態前，當前重點數:', {
          videoId: video.id,
          checkpointCount: personalHistoryVideos.find(pv => pv.id === video.id)?.checkpointCount,
          globalCheckpointCount: personalHistoryVideos.find(pv => pv.id === video.id)?.globalCheckpointCount
        });
        
        setVideos(prev => prev.map(updateVideo));
        setPersonalVideos(prev => prev.map(updateVideo));
        setCommunityVideos(prev => prev.map(updateVideo));
        setPersonalHistoryVideos(prev => prev.map(updateVideo));
        
        // 调试日志已移除
        setVideosCache(prev => {
          const currentSectionKey = `${currentSection ?? ''}_${userId ?? ''}`;
          const updated = prev[currentSectionKey]?.map(updateVideo) || [];
          return { ...prev, [currentSectionKey]: updated };
        });
        
        // 调试日志已移除
      } catch (error) {
        console.error('❌ 更新 YouTube 資訊失敗:', video.id, error);
        // 失敗時只記錄快取，不更新影片狀態，保持原有的 Firestore 資料
        const videoId = video.videoId as string;
        youtubeInfoCache[videoId] = { 
          notFound: true, 
          lastTriedAt: new Date().toISOString(),
          error: error instanceof Error ? error.message : 'Unknown error'
        };
        saveYoutubeInfoCache();
        // 不更新任何狀態，保持原有的正確資料
    }
    }
  };

  // 🔧 新增：篩選和更新使用 CheckPlay logo 的 YouTube 影片
  const findAndUpdateCheckPlayLogoVideos = useCallback(async () => {
    // 查询日志已移除
    
    // 收集所有影片來源
    const allVideos = [
      ...videos,
      ...personalVideos,
      ...communityVideos,
      ...personalHistoryVideos,
      ...userVideos
    ];
    
    // 去重複
    const uniqueVideos = allVideos.filter((video, index, self) => 
      index === self.findIndex(v => v.id === video.id)
    );
    
    // 调试日志已移除
    
    // 篩選 YouTube 影片
    const youtubeVideos = uniqueVideos.filter(video => {
      const videoId = extractYouTubeId(video.path || '');
      return !!videoId && videoId.match(/^[a-zA-Z0-9_-]{11}$/);
    });
    
    // 调试日志已移除
    
    // 篩選使用 CheckPlay logo 的影片（沒有 YouTube API 縮圖的影片）
    const checkPlayLogoVideos = youtubeVideos.filter(video => {
      const videoId = extractYouTubeId(video.path || '');
      const hasYouTubeThumbnail = video.youtubeInfo?.thumbnails?.high?.url || 
                                 video.youtubeInfo?.thumbnails?.medium?.url;
      const hasCustomThumbnail = video.thumbnail && 
                                !video.thumbnail.includes('ytimg.com') && 
                                !video.thumbnail.includes('youtube.com');
      
      // 如果沒有 YouTube API 縮圖，且沒有自定義縮圖，就認為是使用 CheckPlay logo
      return !hasYouTubeThumbnail && !hasCustomThumbnail;
    });
    
    // 调试日志已移除
    
    if (checkPlayLogoVideos.length > 0) {
      // 调试日志已移除
      checkPlayLogoVideos.forEach((video, index) => {
        const videoId = extractYouTubeId(video.path || '');
        // 调试日志已移除
      });
      
      // 批量更新這些影片的 YouTube 資訊
      // 同步日志已移除
      await loadYouTubeInfoForVideos(checkPlayLogoVideos);
      
      // 调试日志已移除
      
      // 顯示成功訊息
      setSnackbarMessage(`成功更新 ${checkPlayLogoVideos.length} 個使用 CheckPlay logo 的 YouTube 影片！`);
      setSnackbarOpen(true);
    } else {
      // 调试日志已移除
      setSnackbarMessage('沒有找到使用 CheckPlay logo 的 YouTube 影片');
      setSnackbarOpen(true);
    }
    
    return checkPlayLogoVideos;
  }, [videos, personalVideos, communityVideos, personalHistoryVideos, userVideos, extractYouTubeId, loadYouTubeInfoForVideos]);

  // 在組件內部 - 修復無限循環問題
  const [isLoadingYouTubeInfo, setIsLoadingYouTubeInfo] = useState(false);
  
  // 🔧 修復：移除自動觸發 YouTube API 請求，避免登入時不必要的 API 調用
  // 原本的 useEffect 會在初始載入時自動補齊缺少 YouTube 資訊的影片
  // 現在改為只在用戶主動需要時才調用（例如點擊「更新」按鈕）
  // useEffect(() => {
  //   // 只在初始載入時補齊缺少 YouTube 資訊的影片
  //   if (videos && videos.length > 0 && !isLoadingYouTubeInfo) {
  //     const videosWithoutInfo = videos.filter(v => v.videoId && !v.youtubeInfo);
  //     if (videosWithoutInfo.length > 0) {
  //       setIsLoadingYouTubeInfo(true);
  //       // 使用 setTimeout 避免在 render 期間更新狀態
  //       setTimeout(async () => {
  //         await loadYouTubeInfoForVideos(videosWithoutInfo);
  //         setIsLoadingYouTubeInfo(false);
  //       }, 100);
  //     }
  //   }
  // }, []); // 🔧 修復：移除 isLoadingYouTubeInfo 依賴，避免無限循環

  // 1. 新增 state: 每支影片的作者選擇


  // 載入自訂標籤
  useEffect(() => {
    if (authUser?.uid && shareDialogOpen) {
      getUser(authUser.uid).then(user => {
        if (!user?.shareTags || user.shareTags.length === 0) {
          // 首次登入，複製 defaultTags
          updateUser(authUser.uid, { shareTags: defaultTags });
          setCustomTags(defaultTags);
        } else {
          setCustomTags(user.shareTags);
        }
      });
    }
  }, [authUser?.uid, shareDialogOpen]);


  // Tag Dialog 勾選時，將Tag移到最前面並記錄次數
  const handleTagCheckboxChange = async (tag: string, checked: boolean) => {
    // 调试日志已移除
    
    if (!tagDialogVideo) {
      console.error('❌ 沒有選中的影片，無法更新 Tag');
      return;
    }
    
    try {
      // 🔧 修復：立即調用 handleTagChange 更新 Firestore 和同步所有狀態
      await handleTagChange(tagDialogVideo, tag, checked);
      
      // 更新 selectedTags 狀態
    if (checked) {
      setSelectedTags(prev => {
        const tagUsage = JSON.parse(localStorage.getItem('tagUsage') || '{}');
        tagUsage[tag] = (tagUsage[tag] || 0) + 1;
        localStorage.setItem('tagUsage', JSON.stringify(tagUsage));
        const newTags = [tag, ...prev.filter(t => t !== tag)];
        // 立即依照tagUsage排序
          const sortedTags = [...newTags].sort((a, b) => (tagUsage[b] || 0) - (tagUsage[a] || 0));
          // 调试日志已移除
          return sortedTags;
      });
    } else {
        setSelectedTags(prev => {
          const filteredTags = prev.filter(t => t !== tag);
          // 调试日志已移除
          return filteredTags;
        });
      }
      
      // 调试日志已移除
    } catch (error) {
      console.error('❌ Tag 更新失敗:', error);
      setSnackbarMessage('Tag 更新失敗，請稍後再試');
      setSnackbarOpen(true);
    }
  };

  // Dialog開啟或selectedTags初始化時，依tagUsage排序
  useEffect(() => {
    if (tagDialogOpen) {
      const tagUsage = JSON.parse(localStorage.getItem('tagUsage') || '{}');
      setSelectedTags(prev => [...prev].sort((a, b) => (tagUsage[b] || 0) - (tagUsage[a] || 0)));
      // 只在 allUserTags 為空時才重新載入，避免覆蓋已刪除的 Tag
      setAllUserTags(prev => {
        if (prev.length === 0) {
          // 如果沒有本地 Tag，從 localStorage 或其他地方載入
          return [];
        }
        // 如果已有本地 Tag，只進行排序，不重新載入
        return [...prev].sort((a, b) => (tagUsage[b] || 0) - (tagUsage[a] || 0));
      });
    }
  }, [tagDialogOpen, tagDialogVideo]);

  // 1. State
  

  // 2. 載入個人瀏覽記錄的地方 - 使用快取機制
  // 🔧 修復重複觸發問題：使用 useRef 來防止重複執行
  const lastUserIdRef = useRef<string>('');
  const isLoadingPersonalHistoryRef = useRef<boolean>(false);
  
  useEffect(() => {
    // 同步日志已移除
    
    // 🔧 修復重複載入問題：loadVideos 已經處理所有分頁，包括 personalHistory
    // 因此 loadPersonalHistoryData 應該完全跳過執行，避免重複載入
    // 跳过日志已移除
    return;
    
    // 防止重複執行：如果 userId 沒有變化或正在載入中，則跳過
    if (userId === lastUserIdRef.current || isLoadingPersonalHistoryRef.current) {
      // 跳过日志已移除
      return;
    }
    
    lastUserIdRef.current = userId;
    isLoadingPersonalHistoryRef.current = true;
    
    const loadPersonalHistoryData = async () => {
      if (userId) {
        // 檢查快取
        const cacheKey = `personalHistory_${userId}`;
        const now = Date.now();
        const cacheTimestamp = personalHistoryCacheTimestamps[cacheKey];
        const isCacheValid = cacheTimestamp && (now - cacheTimestamp) < PERSONAL_HISTORY_CACHE_EXPIRY_TIME;
        
        if (personalHistoryCache[cacheKey] && isCacheValid) {
          // // 处理日志已移除
          
          // 🔧 修復重點數被重置：快取載入時也統計正確的重點數
          // 查询日志已移除...');
          const sharedVbidsQuery = query(
            safeCollection( "sharedVbids"),
            where("userId", "==", userId)
          );
          const sharedVbidsSnapshot = await getDocs(sharedVbidsQuery);
          const keyToCheckpoints: Record<string, number> = {};
          
          sharedVbidsSnapshot.docs.forEach(doc => {
            const sharedVbidsData = doc.data();
            const videoId = sharedVbidsData.videoId;
            if (videoId && sharedVbidsData.vbids && Array.isArray(sharedVbidsData.vbids)) {
              const activeVbidsCount = sharedVbidsData.vbids.filter((vbid: any) => vbid.isActive).length;
              keyToCheckpoints[videoId] = activeVbidsCount;
              
              // 调试日志已移除
            }
          });
          
          // 查询日志已移除
          
          // 為快取數據重新載入關注度
          const cachedVideos = personalHistoryCache[cacheKey];
          // 為快取數據載入關注度和建立時間
          const cachedVideosWithAttention = await Promise.all(cachedVideos.map(async (item) => {
            let videoId = item.videoId;
            if (!videoId && item.path) {
              // 🔧 使用統一的 extractYouTubeId 函數（已從 vbidIO.ts 導入）
              videoId = extractYouTubeId(item.path) || item.path;
            }
            
            let attentionCount = 0;
            let earliestAddedDate = item.addedDate;
            
            if (videoId) {
              attentionCount = await getAttentionCount(videoId);
              
              // 查詢真正的最早建立時間
              try {
                earliestAddedDate = await getEarliestAddedDate(videoId);
                if (earliestAddedDate !== item.addedDate) {
                  // 同步日志已移除
                }
              } catch (error) {
                console.warn('⚠️ 查詢快取個人歷史影片最早建立時間失敗:', videoId, error);
              }
              
              // // 查询日志已移除
            }
            
            // 🔧 修復重點數被重置：使用統計後的正確重點數
            let globalCheckpointCount = 0;
            if (videoId) {
              globalCheckpointCount = keyToCheckpoints[videoId] || 0;
            }
            
            // 移除重複的調試日誌
            
            return {
              ...item,
              globalCheckpointCount: globalCheckpointCount,
              checkpointCount: globalCheckpointCount,
              attentionCount,
              addedDate: earliestAddedDate
            };
          }));
          setPersonalHistoryVideos(cachedVideosWithAttention);
          setLoading(false);
        } else {
          // // 同步日志已移除
          setLoading(true);
          
          // 先查詢 sharedVbids 集合來統計真正的重點數
          // 查询日志已移除...');
          const sharedVbidsQuery = query(
            safeCollection( "sharedVbids"),
            where("userId", "==", userId)
          );
          const sharedVbidsSnapshot = await getDocs(sharedVbidsQuery);
          const keyToCheckpoints: Record<string, number> = {};
          
          sharedVbidsSnapshot.docs.forEach(doc => {
            const sharedVbidsData = doc.data();
            const videoId = sharedVbidsData.videoId;
            
            if (videoId && sharedVbidsData.vbids && Array.isArray(sharedVbidsData.vbids)) {
              const activeVbidsCount = sharedVbidsData.vbids.filter((vbid: any) => vbid.isActive).length;
              keyToCheckpoints[videoId] = activeVbidsCount;
              
              // 调试日志已移除
            }
          });
          
          // 查询日志已移除
          
          // 🔧 修復：查詢所有瀏覽記錄，按最後瀏覽時間排序
          const q = query(
            safeCollection( "sharedVbids"),
            where("userId", "==", userId),
            orderBy("lastOpenDate", "desc")
          );
          
          getDocs(q).then(async snapshot => {
            const newList = snapshot.docs
              .map(doc => ({ id: doc.id, ...doc.data() } as VideoItem))
              .filter(v => !Array.isArray(v.hiddenFor) || !v.hiddenFor.includes(userId));
            
            // 為個人歷史數據載入關注度和建立時間
            const newListWithAttention = await Promise.all(newList.map(async (item) => {
              let videoId = item.videoId;
              if (!videoId && item.path) {
                const extractYouTubeId = (url: string): string | null => {
                  const match = url.match(/(?:v=|youtu\.be\/|embed\/)([a-zA-Z0-9_-]{11})/);
                  return match ? match[1] : null;
                };
                videoId = extractYouTubeId(item.path) || item.path;
              }
              
              let attentionCount = 0;
              let earliestAddedDate = item.addedDate;
              
              if (videoId) {
                attentionCount = await getAttentionCount(videoId);
                
                // 查詢真正的最早建立時間
                try {
                  earliestAddedDate = await getEarliestAddedDate(videoId);
                  // 🔧 修復：移除頻繁的 console.log，避免性能問題
                  // if (earliestAddedDate !== item.addedDate) {
                  //   // 同步日志已移除
                  // }
                } catch (error) {
                  console.warn('⚠️ 查詢個人歷史影片最早建立時間失敗:', videoId, error);
                }
                
                // // 查询日志已移除
              }
              
              // 從 sharedVbids 統計中獲取重點數
              let globalCheckpointCount = 0;
              if (videoId) {
                globalCheckpointCount = keyToCheckpoints[videoId] || 0;
              }
              
              // 移除重複的調試日誌
              
              return {
                ...item,
                globalCheckpointCount: globalCheckpointCount,
                checkpointCount: globalCheckpointCount,
                attentionCount,
                addedDate: earliestAddedDate
              };
            }));
            
            // 直接使用新載入的數據
            // 调试日志已移除
            
            // 🔧 修復：為瀏覽記錄添加去重邏輯
            const dedupedList = deduplicateByUserAndVideoId(newListWithAttention);
            // 🔧 修復：移除頻繁的 console.log，避免性能問題
            // console.log('🔍 瀏覽記錄去重:', {
            //   原始數量: newListWithAttention.length,
            //   去重後數量: dedupedList.length,
            //   重複數量: newListWithAttention.length - dedupedList.length
            // });
            
            // 設置快取
            setPersonalHistoryCache(prev => ({ ...prev, [cacheKey]: dedupedList }));
            setPersonalHistoryCacheTimestamps(prev => ({ ...prev, [cacheKey]: now }));
            
            // 🔧 修復時序問題：先設置重點數，再載入 YouTube 資訊
            setPersonalHistoryVideos(dedupedList);
            
            // 設定最後一個文檔引用，用於分頁查詢
            if (snapshot.docs.length > 0) {
              lastVisibleHistoryDocRef.current = snapshot.docs[snapshot.docs.length - 1];
            }
            
            // hasMoreHistory 現在是動態計算的，不需要手動設定
            
            // 🚨 緊急修復：暫時禁用自動載入 YouTube 資訊，避免API配額超限
            // 為 personalHistoryVideos 載入 YouTube 資訊
            const videosWithoutInfo = dedupedList.filter(v => !v.youtubeInfo && !v.videoInfo);
            if (videosWithoutInfo.length > 0) {
              // 调试日志已移除
              // 调试日志已移除
              // 调试日志已移除
              // await loadYouTubeInfoForVideos(videosWithoutInfo);
              // 调试日志已移除
            }
            
            setLoading(false);
          });
        }
      } else {
        setPersonalHistoryVideos([]);
        setLoading(false);
      }
      
      // 社群影片查詢（保持原有邏輯）
      const q2 = query(
        safeCollection( "sharedVbids"),
        where("zones", "array-contains", "community"),
        orderBy("lastOpenDate", "desc")
      );
      getDocs(q2).then(async snapshot => {
        const rawList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as VideoItem));
        const deduped = deduplicateByUserAndVideoId(rawList);
        
        // 為社群影片載入關注度數據
        const communityVideosWithAttention = await Promise.all(deduped.map(async (item) => {
          let videoId = item.videoId;
          if (!videoId && item.path) {
            const extractYouTubeId = (url: string): string | null => {
              const match = url.match(/(?:v=|youtu\.be\/|embed\/)([a-zA-Z0-9_-]{11})/);
              return match ? match[1] : null;
            };
            videoId = extractYouTubeId(item.path) || item.path;
          }
          
          let attentionCount = 0;
          if (videoId) {
            attentionCount = await getAttentionCount(videoId);
            // // 查询日志已移除
          }
          return {
            ...item,
            attentionCount
          };
        }));
        
        setCommunityVideos(communityVideosWithAttention);
        setCommunityLoadedCount(communityVideosWithAttention.length); // 設定為顯示所有社群影片
      });
      
      // 🔧 修復：載入完成後重置載入狀態
      isLoadingPersonalHistoryRef.current = false;
    };
    
    loadPersonalHistoryData().finally(() => {
      isLoadingPersonalHistoryRef.current = false;
    });
  }, [userId, currentSection]); // 添加 currentSection 依賴，確保只在 personalHistory 分頁時執行

  // 3. renderPersonalHistoryContent - 按日期分群顯示瀏覽記錄
  const renderPersonalHistoryContent = useMemo(() => {
    // 🔧 修復：使用正確的篩選結果
    const videosToShow = filteredPersonalHistoryVideos ?? filteredPersonalHistoryVideosByMonth;
    
    // 🔧 優化：減少日誌輸出頻率，只在必要時輸出
    if (process.env.NODE_ENV === 'development' && Math.random() < 0.1) { // 只有 10% 的機率輸出日誌
      console.log('🔧 renderPersonalHistoryContent 影片來源:', {
        hasSearchResults: !!filteredPersonalHistoryVideos,
        hasMonthFilter: selectedMonth && selectedMonth !== 'all',
        selectedMonth,
        filteredPersonalHistoryVideosCount: filteredPersonalHistoryVideos?.length ?? 0,
        filteredPersonalHistoryVideosByMonthCount: filteredPersonalHistoryVideosByMonth.length,
        personalHistoryVideosCount: personalHistoryVideos.length,
        finalVideosToShowCount: videosToShow.length
      });
    }
    
    return (
      <>
        <Paper>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, p: 0.625 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}> 瀏覽記錄</Typography>
            <Typography variant="body2" sx={{ ml: 2, color: 'gray', fontWeight: 400 }}>
              （按最後瀏覽時間排序，共 {filteredPersonalHistoryVideosByMonth.length} 筆）
            </Typography>
            
            
            {/* 月份選擇器 */}
            <Box sx={{ ml: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Chip
                label="全部"
                onClick={() => {
                  console.log(`🔧 SPA模式: 點擊全部標籤`, { 
                    isEmbedded, 
                    currentSelectedMonth: selectedMonth,
                    willUpdateTo: 'all' 
                  });
                  setSelectedMonth('all');
                }}
                variant={selectedMonth === 'all' ? 'filled' : 'outlined'}
                color={selectedMonth === 'all' ? 'primary' : 'default'}
                size="small"
                sx={{ 
                  cursor: 'pointer',
                  fontSize: '12px',
                  '&:hover': {
                    backgroundColor: selectedMonth === 'all' ? undefined : 'primary.light',
                    color: selectedMonth === 'all' ? undefined : 'white'
                  }
                }}
              />
              {availableMonths.length > 0 && availableMonths.map(month => (
                <Chip
                  key={month}
                  label={new Date(month + '-01').toLocaleDateString('zh-TW', { 
                    year: 'numeric', 
                    month: 'long' 
                  })}
                  onClick={() => {
                    console.log(`🔧 SPA模式: 點擊月份標籤 `, { 
                      month, 
                      isEmbedded, 
                      currentSelectedMonth: selectedMonth,
                      willUpdateTo: month 
                    });
                    setSelectedMonth(month);
                  }}
                  variant={selectedMonth === month ? 'filled' : 'outlined'}
                  color={selectedMonth === month ? 'primary' : 'default'}
                  size="small"
                  sx={{ 
                    cursor: 'pointer',
                    fontSize: '12px',
                    '&:hover': {
                      backgroundColor: selectedMonth === month ? undefined : 'primary.light',
                      color: selectedMonth === month ? undefined : 'white'
                    }
                  }}
                />
              ))}
              
            </Box>
          </Box>
          {loading ? (
            renderLoadingState('瀏覽記錄載入中...')
          ) : (
              <InfiniteScroll
                dataLength={personalHistoryToShow.length}
                next={loadMoreHistory}
                hasMore={hasMoreHistory}
                loader={null}
                endMessage={<Typography align="center" sx={{ my: 2, color: 'gray' }}>已載入全部</Typography>}
                scrollThreshold={0.5}
                scrollableTarget="personal-history-scroll"
              >
                {/* 按日期分群顯示 */}
                {groupedPersonalVideos.map((group, groupIdx) => (
                <Box key={`date-group-${group.date}`} sx={{ mb: 3 }}>
                  {/* 日期標題 */}
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    mb: 1, 
                    p: 1,
                    backgroundColor: '#f5f5f5',
                    borderRadius: 1,
                    border: '1px solid #e0e0e0'
                  }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#1976d2' }}>
                      📅 {group.date}
                    </Typography>
                    <Typography variant="body2" sx={{ ml: 2, color: 'gray' }}>
                      （{group.videos.length} 個影片）
                    </Typography>
                  </Box>
                  
                  {/* 該日期的影片表格 */}
                  <TableContainer>
                    <Table size="small" sx={{
                      tableLayout: 'fixed',
                      width: 'auto',
                      '& th:first-of-type, & td:first-of-type': {
                        '@media (max-width: 1024px)': {
                          display: 'none'
                        }
                      }
                    }}>
                      <TableHead>
                        <TableRow>
                          {COLUMNS.filter(col => PAGE_COLUMN_KEYS[currentSection]?.includes(col.key)).map(col => (
                            <TableCell
                              key={col.key}
                              data-col={col.key}
                              align="center"
                              {...getTableCellStyle(col, true)}
                            >
                              {col.label}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {group.videos.map((video, idx) => (
                          <TableRow key={`${group.date}-${video.id}-${idx}`}>
                            {COLUMNS.filter(col => PAGE_COLUMN_KEYS[currentSection]?.includes(col.key)).map(col => (
                              <TableCell
                                key={`${group.date}-${video.id}-${col.key}-${idx}`}
                                data-col={col.key}
                                align="center"
                                {...getTableCellStyle(col, false)}
                              >
                                {renderCellContent(col.key, video, idx, 'personalHistory')}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                ))}
              
              {/* 如果沒有分群數據，顯示原始表格 */}
              {groupedPersonalVideos.length === 0 && videosToShow.length > 0 && (
                <TableContainer>
                  <Table size="small" sx={{
                    tableLayout: 'fixed',
                    width: 'auto',
                    '& th:first-of-type, & td:first-of-type': {
                      '@media (max-width: 1024px)': {
                        display: 'none'
                      }
                    }
                  }}>
                    <TableHead>
                      <TableRow>
                        {COLUMNS.filter(col => PAGE_COLUMN_KEYS[currentSection]?.includes(col.key)).map(col => (
                          <TableCell
                            key={col.key}
                            data-col={col.key}
                            align="center"
                            {...getTableCellStyle(col, true)}
                          >
                            {col.label}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {personalHistoryToShow.map((video, idx) => {
                        // 🔧 調試：檢查實際渲染的影片月份
                        if (idx < 3) {
                          const youtubeDate = video.youtubeInfo?.publishedAt;
                          const addedDate = video.addedDate;
                          
                          // 安全的日期格式化函數
                          const safeDateFormat = (dateValue: any) => {
                            if (!dateValue) return 'N/A';
                            try {
                              const date = new Date(dateValue);
                              if (isNaN(date.getTime())) return 'Invalid Date';
                              return date.toISOString().slice(0, 7);
                            } catch (error) {
                              return 'Error';
                            }
                          };
                          
                          console.log(`🔧 渲染影片 ${idx}: ${video.title}`, {
                            youtubePublishedAt: youtubeDate,
                            addedDate: addedDate,
                            youtubeMonth: safeDateFormat(youtubeDate),
                            addedMonth: safeDateFormat(addedDate)
                          });
                        }
                        return (
                        <TableRow key={`personal-history-${video.id}-${idx}`}>
                          {COLUMNS.filter(col => PAGE_COLUMN_KEYS[currentSection]?.includes(col.key)).map(col => (
                            <TableCell
                              key={`personal-history-${video.id}-${col.key}-${idx}`}
                              data-col={col.key}
                              align="center"
                              {...getTableCellStyle(col, false)}
                              sx={col.key === 'tags' ? {
                                maxWidth: '500px !important',
                                width: '500px !important',
                                minWidth: '500px !important',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              } : {}}
                            >
                              {renderCellContent(col.key, video, idx, 'personalHistory')}
                            </TableCell>
                          ))}
                        </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </InfiniteScroll>
          )}
        </Paper>
        {searchQuery && filteredPersonalHistoryVideos && filteredPersonalHistoryVideos.length === 0 && isTrulyNoMatch(personalHistoryVideos, searchQuery) && (
          <Box sx={{ textAlign: 'center', my: 4 }}>
            <Typography color="text.secondary">大海撈針中...</Typography>
          </Box>
        )}
      </>
    );
  }, [
    filteredPersonalHistoryVideos,
    filteredPersonalHistoryVideosByMonth,
    selectedMonth,
    personalHistoryVideos.length,
    loading,
    personalHistoryToShow.length,
    hasMoreHistory,
    groupedPersonalVideos,
    availableMonths,
    isEmbedded,
    userFavoriteIds, // 🔧 修復：添加 userFavoriteIds，確保珍藏按鈕狀態更新
    favoriteUpdateTrigger // 🔧 修復：添加觸發器，強制立即重新渲染
  ]);


  
    // 搜尋功能 - 優化版：快速全區搜尋，包括字幕白板 Note 文字
  const handleSearch = async () => {
    // 查询日志已移除
    
    const q = searchQuery.trim().toLowerCase();
    const extractedId = extractYouTubeId(searchQuery.trim());
    
    // 查询日志已移除
    
    if (!q) {
      // 查询日志已移除
      setFilteredPersonalHistoryVideos(null);
      setFilteredCommunityVideos(null);
      setFilteredCommunityPublicVideos(null); // 🔧 新增：清除社群公開搜尋結果
      return;
    }
    
    // 🔧 快速基礎搜尋：影片標題、頻道、ID、URL 等 + 擴展欄位檢查
    const basicMatchFn = (v: VideoItem) => {
      // 🔧 增強搜尋邏輯：更全面的字串匹配
      const searchInText = (text: string | undefined) => {
        if (!text) return false;
        return text.toLowerCase().includes(q) || text.includes(q);
      };
      
      const result = (
        searchInText(v.title) ||
        searchInText(v.youtubeInfo?.title) ||
        searchInText(v.youtubeInfo?.channelTitle) ||
        searchInText(v.id) ||
        searchInText(v.videoId) ||
        searchInText(v.path) ||
        searchInText(v.shareUrl) ||
       // MV 模式搜尋
        searchInText(v.mvTitle) ||
        searchInText(v.mvUrl) ||
        // 🔧 新增：擴展搜尋欄位 - 檢查點數目、標籤、描述等  
        (v.checkpointCount && v.checkpointCount.toString().includes(q)) ||
        (v.vbids?.length && v.vbids.length.toString().includes(q)) ||
        (Array.isArray(v.tags) && v.tags.some(tag => tag?.toLowerCase().includes(q) || tag?.includes(q))) ||
        (Array.isArray(v.zones) && v.zones.some(zone => zone?.toLowerCase().includes(q) || zone?.includes(q))) ||
        searchInText(v.mvDescription) ||
        searchInText(v.uploadKey) ||
        // 🔧 新增：搜尋影片描述、備註等更多欄位
        searchInText(v.mvDescription) ||
        searchInText(v.note) ||
        searchInText((v as any).memo) ||
        // VBIDs 內容搜尋
        (Array.isArray(v.vbids) && v.vbids.some(vbid => 
          searchInText(vbid.note) || 
          searchInText(vbid.content) ||
          searchInText(vbid.description)
        )) ||
       (extractedId && (
         v.videoId === extractedId ||
         v.path?.includes(extractedId) ||
         v.shareUrl?.includes(extractedId) ||
         v.mvUrl?.includes(extractedId)
       ))
      );
      
      // 🔧 調試：記錄前幾個搜尋結果
      if (result && Math.random() < 0.1) { // 10% 機率記錄，避免太多日誌
        console.log(`✅ 找到匹配影片: "${v.title}" (搜尋詞: "${q}")`, {
          title: v.title,
          youtubeTitle: v.youtubeInfo?.title,
          tags: v.tags,
          searchQuery: q
        });
      }
      
      return result;
    };
    
    // 🔧 立即執行基礎搜尋並顯示結果
    const basicFilteredPersonal = personalHistoryVideos.filter(basicMatchFn);
    // 🔧 修復：訪客模式下應該搜尋 communityPublicVideos（社群公開），而非 communityVideos（空陣列）
    const communityVideosToSearch = authUser?.uid ? communityVideos : communityPublicVideos;
    const basicFilteredCommunity = communityVideosToSearch.filter(basicMatchFn);
    
    console.log('🔍 基礎搜尋結果:', {
      searchQuery: q,
      isGuest: !authUser?.uid,
      personalHistoryVideosCount: personalHistoryVideos.length,
      communityVideosCount: communityVideos.length,
      communityPublicVideosCount: communityPublicVideos.length,
      usedCommunitySource: authUser?.uid ? 'communityVideos' : 'communityPublicVideos',
      actualSearchedCount: communityVideosToSearch.length,
      basicFilteredPersonalCount: basicFilteredPersonal.length,
      basicFilteredCommunityCount: basicFilteredCommunity.length,
      // 🔧 新增：顯示前幾個匹配的影片標題
      matchedPersonalTitles: basicFilteredPersonal.slice(0, 3).map(v => v.title),
      matchedCommunityTitles: basicFilteredCommunity.slice(0, 3).map(v => v.title)
    });
    
    // 🔧 新增：調試搜尋過程，檢查為什麼找不到結果
    console.log('🔍 調試搜尋過程:', {
      searchQuery: q,
      personalVideos: personalHistoryVideos.length,
      communityVideos: communityVideos.length,
      basicFilteredPersonalCount: basicFilteredPersonal.length,
      basicFilteredCommunityCount: basicFilteredCommunity.length,
      // 🔧 顯示更多可搜尋的影片樣本
      personalSampleTitles: personalHistoryVideos.slice(0, 5).map(v => ({ 
        title: v.title, 
        youtubeTitle: v.youtubeInfo?.title,
        tags: v.tags,
        zones: v.zones 
      })),
      communitySampleTitles: communityVideos.slice(0, 5).map(v => ({ 
        title: v.title, 
        youtubeTitle: v.youtubeInfo?.title,
        tags: v.tags,
        zones: v.zones 
      }))
    });
    
    // 🔧 立即顯示基礎搜尋結果，讓用戶快速看到結果
    console.log('🔧 設定搜尋結果前:', {
      currentFilteredPersonal: filteredPersonalHistoryVideos?.length ?? 'null',
      willSetPersonal: basicFilteredPersonal.length,
      willSetCommunity: basicFilteredCommunity.length,
      isGuest: !authUser?.uid
    });
    
    setFilteredPersonalHistoryVideos(basicFilteredPersonal);
    // 🔧 修復：訪客模式下設置 communityPublicVideos 搜尋結果，登入用戶設置 communityVideos 結果
    if (authUser?.uid) {
      setFilteredCommunityVideos(basicFilteredCommunity);
    } else {
      setFilteredCommunityPublicVideos(basicFilteredCommunity);
    }
    
    console.log('🔧 已設定基礎搜尋結果:', {
      basicPersonals: basicFilteredPersonal.length,
      basicCommunitys: basicFilteredCommunity.length,
      setTo: authUser?.uid ? 'filteredCommunityVideos' : 'filteredCommunityPublicVideos'
    });
    
    // 🔧 修復：自動切換到會顯示搜尋結果的分頁
    if (basicFilteredPersonal.length > 0 || basicFilteredCommunity.length > 0) {
      // 查询日志已移除
      if (authUser?.email) {
        // 登入用戶：優先個人記錄，否則媒體點
        if (basicFilteredPersonal.length > 0 && currentSection !== 'personalHistory') {
          setCurrentSection('personalHistory');
        } else if (basicFilteredCommunity.length > 0 && currentSection !== 'mediaPoint') {
          setCurrentSection('mediaPoint');
        }
      } else {
        // 訪客：顯示社群公開分頁
        if (basicFilteredCommunity.length > 0 && currentSection !== 'communityPublic') {
          setCurrentSection('communityPublic');
        }
      }
    }
    
    // 🔧 如果不是 URL，立即開始進階搜尋，不等待
    const isUrl = extractedId || searchQuery.trim().includes('youtube.com') || searchQuery.trim().includes('youtu.be');
    
         if (!isUrl) {
      // 查询日志已移除
      
      // 🔧 修復：訪客模式下跳過進階搜尋，避免 undefined 查詢錯誤
      if (!authUser?.uid) {
        // 查询日志已移除
        if (basicFilteredPersonal.length === 0 && basicFilteredCommunity.length === 0) {
          setSnackbarMessage('🔍 未找到匹配的影片');
        } else {
          setSnackbarMessage(`🔍 搜尋完成！找到 ${basicFilteredPersonal.length + basicFilteredCommunity.length} 個結果`);
        }
        setSnackbarOpen(true);
        return;
      }
      
      // 🔧 並行執行進階搜尋（字幕白板 Note）
      try {
        // 🔧 先保存基礎搜尋結果，避免後續被錯誤覆蓋
        const originalBasicPersonal = [...basicFilteredPersonal];
        const originalBasicCommunity = [...basicFilteredCommunity];
        
        console.log('🔧 保存原始基礎結果至進階搜尋:', {
          originalPersonalCount: originalBasicPersonal.length,
          originalCommunityCount: originalBasicCommunity.length
        });
        
        // 1. 搜尋自己 ID 下所有影片的 Note - 從 sharedVbids 獲取
        const mySharedVbidsQuery = query(
          safeCollection( 'sharedVbids'),
          where('userId', '==', authUser.uid)
        );
        
        // 2. 搜尋其他用戶已設公開「公開」的影片下的 Note - 從 sharedVbids 獲取
        const publicKnowledgeSharedVbidsQuery = query(
          safeCollection( 'sharedVbids'),
          where('userId', '!=', authUser.uid)
        );
        
        // 查询日志已移除
        // 查询日志已移除
        
        // 🔧 並行執行兩個查詢
        const [mySharedVbidsSnapshot, publicSharedVbidsSnapshot] = await Promise.all([
          getDocs(mySharedVbidsQuery),
          getDocs(publicKnowledgeSharedVbidsQuery)
        ]);
         
         console.log('🔍 查詢結果:', {
           mySharedVbidsSnapshotSize: mySharedVbidsSnapshot.size,
           publicSharedVbidsSnapshotSize: publicSharedVbidsSnapshot.size,
           mySharedVbidsDocs: mySharedVbidsSnapshot.docs.map(doc => ({ id: doc.id, data: doc.data() })),
           publicSharedVbidsDocs: publicSharedVbidsSnapshot.docs.map(doc => ({ id: doc.id, data: doc.data() }))
         });
         
         const noteVideoIds = new Set<string>();
         const noteDetails = new Map<string, { note: string, userId: string, isPublic: boolean }>();
         
         // 處理自己的 Note
         mySharedVbidsSnapshot.forEach(doc => {
           const sharedVbidsData = doc.data();
           console.log(`🔍 檢查自己的 sharedVbids:`, { 
             docId: doc.id, 
             videoId: sharedVbidsData.videoId,
             vbidsCount: sharedVbidsData.vbids?.length || 0,
             fullData: sharedVbidsData // 🔧 新增：顯示完整的文檔資料
           });
           
           if (sharedVbidsData.videoId && sharedVbidsData.vbids && Array.isArray(sharedVbidsData.vbids)) {
             // 檢查每個 vbid 的 note 是否包含查詢詞
             sharedVbidsData.vbids.forEach((vbid: any) => {
               if (vbid.note && vbid.note.toLowerCase().includes(q)) {
                 noteVideoIds.add(sharedVbidsData.videoId);
                 noteDetails.set(sharedVbidsData.videoId, {
                   note: vbid.note,
                   userId: sharedVbidsData.userId,
               isPublic: false // 自己的 Note
             });
                 // 调试日志已移除
               }
             });
           }
         });
         
         // 處理其他用戶的 Note（需要檢查是否為「公開」影片）
         publicSharedVbidsSnapshot.forEach(doc => {
           const sharedVbidsData = doc.data();
           console.log(`🔍 檢查其他用戶的 sharedVbids:`, { 
             docId: doc.id, 
             videoId: sharedVbidsData.videoId,
             vbidsCount: sharedVbidsData.vbids?.length || 0
           });
           
           if (sharedVbidsData.videoId && sharedVbidsData.vbids && Array.isArray(sharedVbidsData.vbids)) {
             // 檢查每個 vbid 的 note 是否包含查詢詞
             sharedVbidsData.vbids.forEach((vbid: any) => {
               if (vbid.note && vbid.note.toLowerCase().includes(q)) {
             // 檢查該影片是否設為「公開」
                 const video = [...personalHistoryVideos, ...communityVideos].find(v => v.videoId === sharedVbidsData.videoId);
             if (video && Array.isArray(video.zones) && video.zones.includes('community')) {
                   noteVideoIds.add(sharedVbidsData.videoId);
                   noteDetails.set(sharedVbidsData.videoId, {
                     note: vbid.note,
                     userId: sharedVbidsData.userId,
                 isPublic: true // 其他用戶的公開 Note
               });
                   // 调试日志已移除
             }
               }
             });
           }
         });
         
         console.log('🔍 Note 查詢完成:', {
           mySharedVbidsCount: mySharedVbidsSnapshot.size,
           publicSharedVbidsCount: publicSharedVbidsSnapshot.size,
           matchedVideoIdsCount: noteVideoIds.size,
           noteDetailsCount: noteDetails.size
         });
         
        // 🔧 快速篩選包含 Note 匹配的影片（使用原始基礎結果檢查去重）
         const noteMatchedPersonal = personalHistoryVideos.filter(v => 
          !originalBasicPersonal.some(basic => basic.id === v.id) && v.videoId && noteVideoIds.has(v.videoId)
         );
         
         const noteMatchedCommunity = communityVideos.filter(v => 
          !originalBasicCommunity.some(basic => basic.id === v.id) && v.videoId && noteVideoIds.has(v.videoId)
        );
        
        // 🔧 修復：如果找到Note匹配但影片不在現有列表中，從Firestore創建影片對象
        const missingNoteVideos: VideoItem[] = [];
        
        for (const videoId of noteVideoIds) {
          // 檢查是否已經在基礎搜尋結果中
          const alreadyFound = originalBasicPersonal.some(v => v.videoId === videoId) || 
                              originalBasicCommunity.some(v => v.videoId === videoId) ||
                              noteMatchedPersonal.some(v => v.videoId === videoId) ||
                              noteMatchedCommunity.some(v => v.videoId === videoId);
          
          if (!alreadyFound) {
            // 從 sharedVbids 中找到對應的文檔
            const foundDoc = [...mySharedVbidsSnapshot.docs, ...publicSharedVbidsSnapshot.docs]
              .find(doc => doc.data().videoId === videoId);
            
            if (foundDoc) {
              const docData = foundDoc.data();
              const noteInfo = noteDetails.get(videoId);
              
              // 創建影片對象
              const videoItem: VideoItem = {
                id: foundDoc.id,
                videoId: videoId,
                title: docData.title || docData.youtubeInfo?.title || `影片 ${videoId}`,
                youtubeInfo: docData.youtubeInfo || {},
                userId: docData.userId,
                addedDate: docData.addedDate || docData.createdAt || new Date().toISOString(),
                createdAt: docData.createdAt || new Date().toISOString(),
                checkpointCount: docData.checkpointCount || 0,
                vbids: docData.vbids || [],
                tags: docData.tags || [],
                zones: docData.zones || [],
                privacy: docData.privacy || 'Private',
                provider: docData.provider || 'YouTube',
                keyNo: docData.keyNo || 0,
                timecode: docData.timecode || 0,
                mediaMode: docData.mediaMode || 'video'
              } as VideoItem;
              
              missingNoteVideos.push(videoItem);
              console.log(`🔧 從Note搜尋創建影片對象: ${videoItem.title} (videoId: ${videoId})`, {
                noteInfo: noteInfo?.note?.slice(0, 50) + '...',
                searchQuery: q
              });
            }
          }
        }
         
        // 🔧 合併結果並更新（包含從Note搜尋創建的影片）
        const finalFilteredPersonal = [...originalBasicPersonal, ...noteMatchedPersonal, ...missingNoteVideos];
        const finalFilteredCommunity = [...originalBasicCommunity, ...noteMatchedCommunity];
         
         console.log('🔍 進階搜尋完成:', {
           noteVideoIdsCount: noteVideoIds.size,
           noteMatchedPersonalCount: noteMatchedPersonal.length,
           noteMatchedCommunityCount: noteMatchedCommunity.length,
          missingNoteVideosCount: missingNoteVideos.length,
           finalFilteredPersonalCount: finalFilteredPersonal.length,
           finalFilteredCommunityCount: finalFilteredCommunity.length
        });
         
         console.log('🔧 進階搜尋最終結果設定:', {
           finalPersonals: finalFilteredPersonal.length,
           finalCommunitys: finalFilteredCommunity.length,
           basicTestPersons: basicFilteredPersonal.length,
           basicTestCommunitys: basicFilteredCommunity.length
         });
         
         console.log('🔧 進階搜尋設定前檢查:', {
           willSetPersonal: finalFilteredPersonal.length,
           willSetCommunity: finalFilteredCommunity.length
         });
         
         setFilteredPersonalHistoryVideos(finalFilteredPersonal);
         setFilteredCommunityVideos(finalFilteredCommunity);
        
        // 配置日志已移除
        
        // 🔧 額外確保狀態被正確更新/強制觸發重新渲染
        setTimeout(() => {
          console.log('🔧 延遲確認最最終搜尋結果:', {
            finalFilteredPersonalLength: finalFilteredPersonal.length,
            finalFilteredCommunityLength: finalFilteredCommunity.length
          });
        }, 100);
         
         // 🔧 確保在進階搜尋完成後也能正確顯示結果，檢查分頁
         console.log('🔧 進階搜尋後檢查是否需要切換分頁:', {
           currentSection,
           hasPersonalResults: finalFilteredPersonal.length > 0,
           hasCommunityResults: finalFilteredCommunity.length > 0,
           personalCount: finalFilteredPersonal.length,
           communityCount: finalFilteredCommunity.length
         });
         
         // 🔧 顯示搜尋結果統計，區分自己的 Note 和公開 Note
         if (noteMatchedPersonal.length > 0 || noteMatchedCommunity.length > 0) {
           const myNoteCount = Array.from(noteDetails.values()).filter(d => !d.isPublic).length;
           const publicNoteCount = Array.from(noteDetails.values()).filter(d => d.isPublic).length;
           
           let message = `🔍 搜尋完成！找到 ${finalFilteredPersonal.length + finalFilteredCommunity.length} 個結果`;
           if (myNoteCount > 0) {
             message += `，其中 ${myNoteCount} 個來自自己的字幕白板 Note`;
           }
           if (publicNoteCount > 0) {
             message += `，${publicNoteCount} 個來自其他用戶的公開「公開」Note`;
           }
           
           setSnackbarMessage(message);
           setSnackbarOpen(true);
         } else {
           setSnackbarMessage(`🔍 搜尋完成！找到 ${finalFilteredPersonal.length + finalFilteredCommunity.length} 個結果`);
           setSnackbarOpen(true);
         }
         
       } catch (error) {
         console.error('🔍 進階搜尋失敗，使用基礎搜尋結果:', error);
         // 進階搜尋失敗時，基礎搜尋結果已經顯示，無需額外處理
       }
    } else {
      // 🔧 如果是 URL 查詢，檢查是否需要新增影片
      if (basicFilteredPersonal.length === 0 && basicFilteredCommunity.length === 0) {
        // 查询日志已移除
        handleAddNewVideo(searchQuery);
      } else {
        setSnackbarMessage(`🔍 搜尋完成！找到 ${basicFilteredPersonal.length + basicFilteredCommunity.length} 個結果`);
        setSnackbarOpen(true);
      }
    }
  };

  // 影片列表渲染時，根據 currentSection 決定 sourceVideos，並優先用 filteredVideos
  const videosToShow = useMemo(() => {
    return currentSection === 'personalHistory' ? (filteredPersonalHistoryVideos ?? personalHistoryVideos) :
           currentSection === 'myPersonalFavorite' ? personalVideos :
           currentSection === 'myVideos' ? filteredMyVideosByMonth : // 🔧 修改：使用月份篩選後的數據
           currentSection === 'teamPoint' ? videos :
           currentSection === 'myPoint' ? personalVideos :
           currentSection === 'communityPublic' ? (filteredCommunityPublicVideos ?? communityPublicVideosToShow) : // 🔧 方案 A：社群公開分頁優先使用搜尋結果
           videos;
  }, [currentSection, filteredPersonalHistoryVideos, personalHistoryVideos, filteredCommunityPublicVideos, communityPublicVideosToShow, personalVideos, filteredMyVideosByMonth, videos]);

  // 新增影片流程 function
  const handleAddNewVideo = async (url: string) => {
    // 启动日志已移除
    try {
      setSnackbarMessage('新增中...');
      setSnackbarOpen(true);

      const videoId = extractYouTubeId(url);
      // 调试日志已移除

      let title = '';
      let channelTitle = '';
      let viewCount = '';
      let publishedAt = '';
      if (videoId) {
        try {
          const videoDetails = await youtubeApi.getVideoDetails(videoId);
          // 调试日志已移除
          title = videoDetails.title;
          channelTitle = videoDetails.channelTitle;
          viewCount = videoDetails.viewCount;
          publishedAt = videoDetails.publishedAt;
        } catch (e) {
          console.error('getVideoDetails error', e);
          title = url;
        }
      } else {
        title = url;
      }

      // 使用全局的 userId，確保一致性
      const now = new Date().toISOString();
      
      // 🔧 修復：使用 videoId 作為文檔 ID，確保 SSR 服務器能正確查詢
      const documentId = videoId || `temp_${Date.now()}`;
      
      const newVideo = {
        id: documentId, // 🔧 修復：使用 videoId 作為文檔 ID
        videoId: videoId || '',
        path: url,
        title,
        youtubeInfo: {
          title,
          channelTitle,
          viewCount,
          publishedAt,
        },
        userId,
        createdAt: now,
        updatedAt: now,
        provider: 'YouTube',
      };
      // 调试日志已移除

      // 準備完整的 YouTube 資訊和 metadata
      const youtubeInfo = {
        title,
        channelTitle,
        viewCount,
        publishedAt,
      };

      const youtubeMetadata = {
        hashtags: [], // 從 YouTube API 獲取，這裡先設為空陣列
        title: title || '',
        description: '', // 從 YouTube API 獲取，這裡先設為空字串
        channelTitle: channelTitle || '',
        publishedAt: publishedAt || '',
        viewCount: viewCount || '0',
        thumbnail: '', // 從 YouTube API 獲取縮圖 URL
        duration: '' // 從 YouTube API 獲取影片長度
      };

      // 準備影片數據
      const videoData = {
        videoId: videoId || '',
        path: url,
        title,
        youtubeInfo,
        youtubeMetadata,
        provider: 'YouTube',
        flag: 'active',
        privacy: 'Public',
        zones: ['personal'],
        groupIds: []
      };

      // 🔧 修復：檢查是否已存在該影片，避免重複創建
      const { getDb } = await import('../services/firebase');
      const db = await getDb();
      const { collection, query, where, getDocs } = await import('firebase/firestore');
      
      // 檢查是否已存在該影片的觀點
      const existingQuery = query(
        collection(db, 'sharedVbids'),
        where('userId', '==', userId),
        where('videoId', '==', videoId)
      );
      const existingSnapshot = await getDocs(existingQuery);
      
      // 🔧 修復：定義 compositeId 變量
      let compositeId: string;
      
      if (existingSnapshot.empty) {
        // 只有當不存在時才創建新影片
        const { saveVideoDataUnified } = await import('../services/videoDataService');
        compositeId = await saveVideoDataUnified(videoData, userId, 'v1');
        // 调试日志已移除
      } else {
        // 调试日志已移除
        compositeId = existingSnapshot.docs[0].id; // 使用現有文檔 ID
      }

      // *** 新增：初始化 videoStats 關注度為 1 ***
      if (videoId && videoId.match(/^[a-zA-Z0-9_-]{11}$/) && compositeId) {
        try {
          const videoStatsRef = safeDoc('videoStats', compositeId);
          if (videoStatsRef) {
            const videoStatsSnap = await getDoc(videoStatsRef);
            
            if (!videoStatsSnap.exists()) {
              // 如果 videoStats 不存在，創建並設定關注度為 1
              await setDoc(videoStatsRef, {
                attentionCount: 1,
                userAttention: { [userId]: 1 },
                createdAt: new Date().toISOString()
              });
              // 调试日志已移除
            }
          }
        } catch (e) {
          console.warn('❌ 初始化 videoStats 失敗:', e);
        }
      }

      // 方案一：立即推入 state，讓畫面即時顯示
      setPersonalHistoryVideos(prev => [
        {
          ...videoData,
          id: compositeId, // 🔧 修復：使用複合ID，保持一致性
          keyNo: 0,
          timecode: '',
          mediaMode: 'YouTube',
          flag: 'active',
          privacy: 'Public', // 明確指定型別
          // 確保所有必要欄位都存在
          vbids: [],
          numberKeys: {},
          checkpointCount: 0,
          globalCheckpointCount: 0,
          mode: 'YouTube',
          playMode: 'M' as const,
        } as VideoItem,
        ...prev,
      ]);

      setSnackbarMessage('影片已新增！');
      setSnackbarOpen(true);
      setSearchQuery('');
      setFilteredPersonalHistoryVideos(null);
      setFilteredCommunityVideos(null);
      setFilteredCommunityPublicVideos(null); // 🔧 新增：清除社群公開搜尋結果
    } catch (e) {
      console.error('❌ 新增影片失敗', e);
      setSnackbarMessage('新增失敗，請稍後再試');
      setSnackbarOpen(true);
    }
  };

  // 新增 function 判斷是否完全沒有 match
  function isTrulyNoMatch(videos: VideoItem[], searchQuery: string): boolean {
    const extractedId = extractYouTubeId(searchQuery.trim());
    const q = searchQuery.trim().toLowerCase();
    
    const hasMatch = videos.some(v =>
      v.videoId === extractedId ||
      v.videoId === q ||
      v.path === searchQuery ||
      v.shareUrl === searchQuery ||
      v.id === q ||
      v.title?.toLowerCase() === q ||
      v.youtubeInfo?.title?.toLowerCase() === q ||
      v.youtubeInfo?.channelTitle?.toLowerCase() === q ||
      // MV 模式匹配
      v.mvTitle?.toLowerCase() === q ||
      v.mvUrl === searchQuery ||
      v.mvUrl?.toLowerCase() === q
    );
    
    return !hasMatch;
  }

  return (
    <Container>
      <ErrorBoundary>
        <Suspense fallback={<CircularProgress />}>
          {/* AppBar 置於最外層 */}
          <HideOnScroll>
            <AppBar 
              position="fixed" 
              color="default" 
              elevation={1} 
              sx={{ 
                backgroundColor: '#1976d2',
                zIndex: 1100,
                ...(isMobile
                  ? { width: '100%', left: 0 }
                  : { left: SIDEBAR_WIDTH, width: `calc(100% - ${SIDEBAR_WIDTH}px)` }
                ),
                top: 0
              }}
            >
              <Toolbar sx={{ 
                  position: 'relative',
                minHeight: '55px !important',
                height: '55px',
                padding: '0 16px'
              }}>
                  {/* Logo 和文字在 AppBar 右上方 */}
                  <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, cursor: 'pointer' }} onClick={handleTitleClick}>
                    {isMobile && (
                      <img 
                        src="/logo.png" 
                        alt="Check Play Logo" 
                        style={{ 
                          width: '28px',
                          height: 'auto',
                          marginRight: '8px'
                        }} 
                      />
                    )}
                    <Typography
                      variant="subtitle1"
                      component="div"
                      sx={{
                        flexGrow: 1,
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        userSelect: 'none',
                        fontSize: '1rem',
                        '& .english': {
                          color: '#FFFFFF',
                          display: isMobile ? 'inline' : 'none'
                        },
                        '& .chinese': {
                          color: '#FFFFFF',
                          marginLeft: isMobile ? '4px' : '0'
                        },
                        '& .bold-text': {
                          fontWeight: 'bold',
                          color: '#FFFFFF'
                        }
                      }}
                    >
                      <span className="english">CHECK PLAY</span>
                      <span className="chinese">{isMobile ? '｜' : ''}<span className="bold-text">簡 播</span></span>
                    </Typography>
                    

                  </Box>
                  <Box
                    sx={{
                      position: 'absolute',
                      right: 24,
                      top: 0,
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      zIndex: 1300
                    }}
                  >
                {authUser ? (
                  <>
                    <Typography variant="body2" sx={{ mr: 0.25, color: 'white', fontSize: '0.875rem' }}>
                      Hi {(authUser.name || authUser.email || '').slice(0, 4)}
                    </Typography>
                    <Button 
                      color="inherit" 
                      onClick={() => handleLogout(true)} 
                      sx={{ 
                        mr: 0.5,
                        px: 1,
                        minWidth: 'auto',
                        color: 'white',
                        fontSize: '0.875rem',
                        height: '32px'
                      }}
                    >
                      登出
                    </Button>
                  </>
                ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                        <Button color="inherit" onClick={() => setShowRegisterDialog(true)} sx={{ color: '#fff', minWidth: 0, px: 1, fontSize: '0.95rem' }}>
                          註冊
                        </Button>
                        <Typography sx={{ color: '#fff', fontWeight: 500, fontSize: '1.1rem' }}>|</Typography>
                        <Button color="inherit" onClick={() => setShowAuthDialog(true)} sx={{ color: '#fff', minWidth: 0, px: 1, fontSize: '0.95rem' }}>
                          登入
                        </Button>
                      </Box>
                )}
                  </Box>
              </Toolbar>
            </AppBar>
          </HideOnScroll>
          <Toolbar />
          
          {/* 調試信息：顯示當前佈局狀態 */}

          
          <Box sx={{ display: 'flex' }}>
            {/* 只在桌面顯示 Drawer */}
            {!isMobile && (
              <Drawer
                variant="permanent"
                open={drawerOpen}
                sx={{
                  width: SIDEBAR_WIDTH,
                  flexShrink: 0,
                  '& .MuiDrawer-paper': {
                    width: SIDEBAR_WIDTH,
                    boxSizing: 'border-box',
                    backgroundColor: '#1976d2',
                    color: 'white',
                    position: 'fixed',
                    left: 0,
                    top: 0,
                    height: '100vh',
                    zIndex: 1200,
                    display: 'flex',
                    flexDirection: 'column',
                    p: 0,
                  }
                }}
              >
                <Box sx={{ flex: '1 1 auto', display: 'flex', flexDirection: 'column', height: '100%' }}>
                {/* Logo 和標題 */}
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                    ml: 1,
                    mb: 2,
                    mt: 2
                }}>
                  <Box
                    component="img"
                    src="/logo.png"
                    alt="Check Play Logo"
                    sx={{
                      width: '24px',
                      height: '24px',
                      mr: 0.5,
                      cursor: 'pointer'
                    }}
                    onClick={handleTitleClick}
                  />
                  <Typography 
                    variant="h6" 
                    component="div" 
                    sx={{ 
                      color: 'white', 
                      fontSize: '1.05rem', 
                      fontWeight: 700,
                      cursor: 'pointer',
                      '&:hover': {
                        textDecoration: 'underline'
                      }
                    }}
                    onClick={handleTitleClick}
                  >
                    CheckPlay
                  </Typography>
                </Box>
                <Divider sx={{ backgroundColor: '#4d97df' }} />
                  
                  {/* 主選單可滾動 */}
                  <Box sx={{ flex: 1, overflowY: 'auto', mt: 1 }}>
              <List>
                {SIDEBAR_CONFIG(userType).map((main, idx) => (
                  <React.Fragment key={main.key}>
                    {idx > 0 && (
                            <Box sx={{ height: 10 }} /> // 主選單間距加大
                    )}
                          <ListItem 
                            button 
                            sx={{ 
                              alignItems: 'center', 
                              py: 1, 
                              flexDirection: 'column', 
                              backgroundColor: currentSection === main.key ? '#154c8a' : 'inherit' 
                            }}
                      onClick={() => {
                        if (main.key === 'home') {
                          navigateToMain();
                          return;
                        }
                        
                        // 如果有子項目，處理展開/收合
                        if (main.children && main.children.length > 0) {
                          setExpandedMain(expandedMain.includes(main.key)
                            ? expandedMain.filter(k => k !== main.key)
                            : [...expandedMain, main.key]);
                          
                          // 根據主分頁類型設定預設子分頁
                          if (main.key === 'personalCenter' && authUser?.email) {
                            // 個人中心：預設顯示瀏覽記錄（僅限已登入用戶）
                            setCurrentSection('personalHistory');
                          } else if (main.key === 'community') {
                            // 社群：預設顯示社群公開
                            setCurrentSection('communityPublic');
                          } else if (main.key === 'fileManagement') {
                            // 檔案管理：預設顯示檔案上傳
                            setCurrentSection('fileUpload');
                          } else if (main.key === 'deliveryManagement') {
                            // 交片管理：優先顯示品牌專區
                            setCurrentSection('clientArea');
                          }
                        } else {
                          // 沒有子項目的直接導航
                          setCurrentSection(main.key);
                        }

                        if (isMobile) setDrawerOpen(false);
                      }}
                      selected={currentSection === main.key}
                    >
                            <ListItemIcon sx={{ minWidth: 24, mb: 0.7, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              {main.icon}
                            </ListItemIcon>
                            <ListItemText 
                              primary={
                                <span style={{ display: 'block', textAlign: 'center', fontSize: 18, fontWeight: 700 }}>
                                  {main.label}
                                </span>
                              } 
                            />
                    </ListItem>
                    {expandedMain.includes(main.key) && main.children && main.children.map(sub => (
                      <ListItem
                        key={sub.key}
                        button
                        disabled={sub.disabled}
                        onClick={() => {
                          if (!sub.disabled) {
                            setCurrentSection(sub.key);
                            
                            // 根據子菜單類型決定展開哪些項目
                            if (['myVideos', 'myPersonalFavorite', 'personalHistory'].includes(sub.key)) {
                              setExpandedMain(['home', 'personalCenter']);
                            } else if (['teamPoint', 'expertKnowledge', 'follow'].includes(sub.key)) {
                              setExpandedMain(['home', 'community']);
                            } else if (['fileUpload', 'localDirectory', 'cloudDirectory', 'projectManagement'].includes(sub.key)) {
                              setExpandedMain(['home', 'fileManagement']);
                            } else if (['clientArea', 'clientManagement'].includes(sub.key)) {
                              setExpandedMain(['home', 'deliveryManagement']);
                            }
                            
                            if (isMobile) setDrawerOpen(false);
                          }
                        }}
                        disableGutters
                              sx={{ 
                                pl: 4, 
                                pr: 0, 
                                py: 0.5, 
                                justifyContent: 'flex-start', 
                                alignItems: 'center', 
                                opacity: sub.disabled ? 0.5 : 1 
                              }}
                        selected={currentSection === sub.key}
                      >
                        <ListItemText
                                primary={
                                  <span style={{ fontSize: 15, textAlign: 'left', width: '100%', display: 'block' }}>
                                    {sub.label}
                                  </span>
                                }
                          sx={{ m: 0, textAlign: 'left' }}
                        />
                      </ListItem>
                    ))}
                  </React.Fragment>
                ))}
                      {/* 在這裡插入匯入YT播放清單按鈕 */}
                      <ListItem button onClick={() => setShowImportDialog(true)} sx={{ mt: 2, justifyContent: 'center' }}>
                        <ListItemText primary={<span style={{ color: 'white', fontWeight: 500, fontSize: 16 }}>匯入YT清單</span>} sx={{ textAlign: 'center' }} />
                      </ListItem>
              </List>
                  </Box>

                  {/* Drawer 藍區底部只保留個人設定與（免費試用期） */}
              <Box sx={{
                    width: '100%',
                background: '#1976d2',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-end',
                zIndex: 1201,
                    pb: 6,
                    pt: 2,
              }}>
                    {authUser && (
                  <Typography
                    variant="body2"
                        sx={{
                          color: 'white',
                          fontWeight: 500,
                          textAlign: 'center',
                          cursor: 'pointer',
                          textDecoration: 'underline',
                          fontSize: '0.8rem',
                          mb: 1
                        }}
                        onClick={() => setShowProfileDialog(true)}
                    component="span"
                  >
                        個人設定
                  </Typography>
                    )}
                    
                    {/* 管理員選項 - 只有admin用戶才能看到 */}
                    {authUser && isAdminUser(authUser.email, authUser.name) && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'white',
                          fontWeight: 500,
                          textAlign: 'center',
                          cursor: 'pointer',
                          textDecoration: 'underline',
                          fontSize: '0.8rem',
                          mb: 1,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate('/admin');
                        }}
                        component="span"
                      >
                        <AdminPanelSettingsIcon sx={{ fontSize: 16 }} />
                        系統管理員
                      </Typography>
                    )}
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.5 }}>
                  <Typography
                    variant="body2"
                          sx={{ color: 'white', fontWeight: 400, textAlign: 'center', letterSpacing: 1 }}
                    component="span"
                  >
                          （7/31前免費試用）
                  </Typography>
                    </Box>
                  </Box>
              </Box>
              </Drawer>
            )}
            {/* 顯示 BottomBar */}
              <BottomBar menuItems={menuItems} />
            {/* 主內容區 */}
              <Box
                component="main"
                onClick={() => isMobile && setDrawerOpen(false)}
                sx={{
                  position: 'fixed',
                  top: APPBAR_HEIGHT,
                  left: isMobile ? 0 : SIDEBAR_WIDTH,
                  right: 0,
                  bottom: 0,
                  backgroundColor: '#f5f5f5',
                  overflow: 'auto',
              display: 'flex',
              flexDirection: 'column',
                  p: 2,
                  zIndex: 1
                }}
              >
              {/* URL 輸入區域 - 精簡空間（個人珍藏分頁和檔案管理分頁時隱藏） */}
              {currentSection !== 'myPersonalFavorite' && 
               !['fileUpload', 'localDirectory', 'cloudDirectory', 'projectManagement', 'clientArea', 'clientManagement'].includes(currentSection) && (
                <Box sx={{ 
                  mb: 1, 
                  p: 1, 
                  backgroundColor: '#f8f9fa',
                  borderRadius: 1,
                  width: '100%',
                  boxSizing: 'border-box',
                  // 手機版減少上下空間
                  '@media (max-width: 1024px)': {
                    mb: 0.5,
                    p: 0.5,
                  }
                }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}> 
                  <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="請貼入URL、輸入關鍵字或搜尋字幕白板Note文字"
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    onKeyPress={e => { if (e.key === 'Enter') handleSearch(); }}
                    sx={{ 
                      '& .MuiOutlinedInput-root': { 
                        backgroundColor: 'white',
                        '@media (max-width: 1024px)': {
                          '& .MuiOutlinedInput-input': {
                            padding: '8px 12px',
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            padding: '0',
                          }
                        }
                      }
                    }}
                  />
                  <Button
                    variant="outlined"
                      onClick={() => {
                      // 同步日志已移除
                      // 清除搜尋結果
                      setFilteredPersonalHistoryVideos(null);
                      setFilteredCommunityVideos(null);
                      setFilteredCommunityPublicVideos(null); // 🔧 新增：清除社群公開搜尋結果
                      setSearchQuery('');
                      // 清除快取，強制重新載入
                      const cacheKey = `personalHistory_${userId}`;
                      delete personalHistoryCache[cacheKey];
                      delete personalHistoryCacheTimestamps[cacheKey];
                      // 清除其他相關快取
                      const videosCacheKey = `personalHistory_${userId}`;
                      delete videosCache[videosCacheKey];
                      delete cacheTimestamps[videosCacheKey];
                      // 重新載入當前分頁的數據，不重新載入頁面
                      loadVideos(currentSection, true);
                    }}
                    sx={{ 
                      minWidth: 80, 
                      height: 48, 
                      backgroundColor: '#f5f5f5', 
                      borderColor: '#1976d2',
                      color: '#1976d2',
                      '&:hover': { 
                        backgroundColor: '#e3f2fd',
                        borderColor: '#1565c0',
                        color: '#1565c0'
                      },
                      '@media (max-width: 1024px)': {
                        height: 40,
                        minWidth: 70,
                      }
                    }}
                  >
                    更新
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSearch}
                    sx={{ 
                      minWidth: 80, 
                      height: 48, 
                      backgroundColor: '#1976d2', 
                      '&:hover': { backgroundColor: '#1565c0' },
                      '@media (max-width: 1024px)': {
                        height: 40,
                        minWidth: 70,
                      }
                    }}
                  >
                    搜尋
                  </Button>
                  {searchQuery && (
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={() => { 
                        setSearchQuery(''); 
                        setFilteredPersonalHistoryVideos(null); 
                        setFilteredCommunityVideos(null); 
                        setFilteredCommunityPublicVideos(null); // 🔧 新增：清除社群公開搜尋結果
                      }}
                      sx={{ 
                        minWidth: 80, 
                        height: 48,
                        '@media (max-width: 1024px)': {
                          height: 40,
                          minWidth: 70,
                        }
                      }}
                    >
                      清除
                    </Button>
                  )}
                </Box>
                
                {searchQuery && filteredPersonalHistoryVideos && filteredPersonalHistoryVideos.length === 0 && isTrulyNoMatch(personalHistoryVideos, searchQuery) && (
                  <Box sx={{ textAlign: 'center', mt: 2, p: 2, backgroundColor: '#e3f2fd', borderRadius: 1 }}>
                    <Typography color="text.secondary" gutterBottom>大海撈針中...</Typography>
                  </Box>
                )}
                
                </Box>
              )}

              <Box sx={{ mb: 0.5, display: 'flex', alignItems: 'center' }}> {/* 精簡 margin */}
                  {/* 暫時隱藏手機版漢堡按鈕，因為目前沒有作用 */}
                  {/* {isMobile && !drawerOpen && (
                  <IconButton 
                    color="inherit" 
                      aria-label="open drawer" 
                    edge="start" 
                      onClick={() => setDrawerOpen(true)}
                    sx={{ mr: 2, color: '#1976d2' }}
                  >
                    <MenuIcon />
                  </IconButton>
                )} */}
                {/* 暫時隱藏標題以節省空間 */}
                {/* <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', mb: 0 }}>
                  {currentSection === 'myPoint'
                      ? '熱門簡播'
                    : currentSection === 'myPersonalFavorite'
                    ? '我的收藏列表'
                    : currentSection === 'mediaPoint'
                        ? '社群重點列表'
                        : currentSection === 'expertKnowledge'
                            ? '專家筆記'
                        : currentSection === 'acopy'
                            ? '客戶交片區'
                            : currentSection === 'teamPoint'
                              ? '群組影片'
                            : currentSection === 'myCommunityPublic'
                              ? '我的公開社群'
                                : currentSection === 'follow'
                                  ? '社群追蹤收藏'
                              : ''}
                </Typography> */}
                {/* 只在顯示排序按鈕 */}
                {currentSection === 'mediaPoint' && (
                  <Box sx={{ display: 'flex', alignItems: 'center', ml: 3 }}>
                  </Box>
                )}
              </Box>
              {currentSection === 'myVideos' ? (
                <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                    我的創作
                  </Typography>
                  
                  {/* 月份篩選 UI */}
                  {!loading && myVideosAvailableMonths.length > 0 && (
                    <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                      <Chip
                        label="全部"
                        onClick={() => setMyVideosSelectedMonth('all')}
                        variant={myVideosSelectedMonth === 'all' ? 'filled' : 'outlined'}
                        color={myVideosSelectedMonth === 'all' ? 'primary' : 'default'}
                        size="small"
                        sx={{ 
                          cursor: 'pointer',
                          fontSize: '12px',
                          '&:hover': {
                            backgroundColor: myVideosSelectedMonth === 'all' ? undefined : 'primary.light',
                            color: myVideosSelectedMonth === 'all' ? undefined : 'white'
                          }
                        }}
                      />
                      {myVideosAvailableMonths.map(month => {
                        const now = new Date();
                        const currentMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
                        const isCurrentMonth = month === currentMonthKey;
                        const label = isCurrentMonth ? '當月' : new Date(month + '-01').toLocaleDateString('zh-TW', { 
                          year: 'numeric', 
                          month: 'long' 
                        });
                        
                        return (
                          <Chip
                            key={month}
                            label={label}
                            onClick={() => {
                              console.log(`🔧 我的創作: 點擊月份標籤 `, { 
                                month, 
                                currentSelectedMonth: myVideosSelectedMonth,
                                willUpdateTo: month 
                              });
                              setMyVideosSelectedMonth(month);
                            }}
                            variant={myVideosSelectedMonth === month ? 'filled' : 'outlined'}
                            color={myVideosSelectedMonth === month ? 'primary' : 'default'}
                            size="small"
                            sx={{ 
                              cursor: 'pointer',
                              fontSize: '12px',
                              '&:hover': {
                                backgroundColor: myVideosSelectedMonth === month ? undefined : 'primary.light',
                                color: myVideosSelectedMonth === month ? undefined : 'white'
                              }
                            }}
                          />
                        );
                      })}
                    </Box>
                  )}
                  
                  {loading ? (
                    renderLoadingState('我的創作載入中...')
                  ) : videosToShow.length === 0 ? (
                    <Typography color="text.secondary" sx={{ mt: 2 }}>
                      {myVideosSelectedMonth === 'all' ? '目前沒有創作影片' : '該月份沒有創作影片'}
                    </Typography>
                  ) : (
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                      {/* 🔧 新增：按日期分組顯示 */}
                      {myVideosGroupedByDate.map((group, groupIdx) => (
                        <Box key={group.date} sx={{ mb: 3 }}>
                          {/* 日期標題 */}
                          <Typography 
                            variant="subtitle1" 
                            sx={{ 
                              fontWeight: 600, 
                              mb: 1.5, 
                              color: '#1976d2',
                              borderBottom: '2px solid #1976d2',
                              pb: 0.5,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1
                            }}
                          >
                            <span>{group.displayDate}</span>
                            <Chip 
                              label={`${group.videos.length} 個影片`} 
                              size="small" 
                              sx={{ 
                                height: '20px',
                                fontSize: '11px',
                                fontWeight: 500
                              }} 
                            />
                          </Typography>
                          
                          {/* 該日期的影片列表 */}
                          <TableContainer>
                            <Table size="small" sx={{ 
                              tableLayout: 'fixed', 
                              width: 'auto',
                              '& th:first-of-type, & td:first-of-type': {
                                '@media (max-width: 1024px)': {
                                  display: 'none'
                                }
                              }
                            }}>
                              {groupIdx === 0 && (
                                <TableHead>
                                  <TableRow>
                                    {COLUMNS.filter(col => PAGE_COLUMN_KEYS['myVideos'].includes(col.key)).map(col => (
                                      <TableCell
                                        key={col.key}
                                        data-col={col.key}
                                        align="center"
                                        {...getTableCellStyle(col, true)}
                                      >
                                        {col.label}
                                      </TableCell>
                                    ))}
                                  </TableRow>
                                </TableHead>
                              )}
                              <TableBody>
                                {group.videos.map((video, idx) => (
                                  <TableRow key={`my-videos-${group.date}-${video.id}-${idx}`}>
                                    {COLUMNS.filter(col => PAGE_COLUMN_KEYS['myVideos'].includes(col.key)).map(col => (
                                      <TableCell
                                        key={`my-videos-${group.date}-${video.id}-${col.key}-${idx}`}
                                        align="center"
                                        {...getTableCellStyle(col, false)}
                                      >
                                        {renderCellContent(col.key, video, idx, 'myVideos')}
                                      </TableCell>
                                    ))}
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              ) : currentSection === 'teamPoint' ? (
                loading ? (
                  renderLoadingState('群組影片載入中...')
                ) : (
                renderTeamPointContent()
                )
              ) : currentSection === 'expertKnowledge' ? (
                loading ? (
                  renderLoadingState('專家筆記載入中...')
                ) : (
                  renderExpertKnowledgeContent()
                )
              ) : currentSection === 'fileUpload' ? (
                <Box>
                  <EnhancedVideoUpload
                    onUploadSuccess={(result: any) => {
                      // 调试日志已移除
                    }}
                    onUploadError={(error: string) => {
                      console.error('影片上傳失敗:', error);
                    }}
                  />
                </Box>
              ) : currentSection === 'localDirectory' ? (
                <Box>
                  <LocalFilesTable onFileOpen={handleLocalFileOpen} />
                </Box>
              ) : currentSection === 'cloudDirectory' ? (
                <Suspense fallback={<ComponentFallback />}>
                  <CloudDirectoryTable onFileOpen={handleCloudFileOpen} />
                </Suspense>
              ) : currentSection === 'projectManagement' ? (
                <Suspense fallback={<ComponentFallback />}>
                  <ProjectManagementTable onFileOpen={handleCloudFileOpen} />
                </Suspense>
              ) : currentSection === 'clientArea' ? (
                <Suspense fallback={<ComponentFallback />}>
                  <ClientArea onFileOpen={handleCloudFileOpen} />
                </Suspense>
              ) : currentSection === 'clientManagement' ? (
                <Suspense fallback={<ComponentFallback />}>
                  <ClientManagement />
                </Suspense>
              ) : currentSection === 'localFiles' ? (
                <Suspense fallback={<ComponentFallback />}>
                  <LocalFilesTable onFileOpen={handleLocalFileOpen} />
                </Suspense>
              ) : currentSection === 'workDirectory' ? (
                <Suspense fallback={<ComponentFallback />}>
                  <LocalFilesTable onFileOpen={handleLocalFileOpen} />
                </Suspense>
              ) : currentSection === 'googleDrive' ? (
                <Box>
                  <GoogleDriveManager onVideoSelect={handleGoogleDriveVideoSelect} />
                </Box>
              ) : currentSection === 'videoUpload' ? (
                <Box>
                  <VideoUpload
                    onUploadSuccess={(result: any) => {
                      // 调试日志已移除
                      // 可以在這裡添加成功後的處理邏輯
                    }}
                    onUploadError={(error: string) => {
                      console.error('影片上傳失敗:', error);
                      // 可以在這裡添加錯誤處理邏輯
                    }}
                  />
                </Box>
              ) : currentSection === 'importYTPlaylist' ? (
                <Box>
                  {renderImportYTPlaylistContent()}
                </Box>
              ) : currentSection === 'myPoint' ? (
                <Box>
                  {/* 載入狀態檢查 */}
                  {loading ? (
                    renderLoadingState('影片載入中...')
                  ) : (
                    <>
                      {/* 響應式佈局：桌面版左右並排，手機版水平滑動 */}
                      <Box 
                        data-scroll-container
                        sx={{ 
                          display: 'flex', 
                          gap: 2, 
                          height: 'calc(100vh - 120px)', // 增加高度，因為節省了空間
                          position: 'relative',
                          // 手機版：水平滑動佈局
                          '@media (max-width: 1024px)': {
                            display: 'flex',
                            gap: 0,
                            overflowX: 'auto',
                            scrollSnapType: 'x mandatory',
                            scrollBehavior: 'smooth',
                            height: 'calc(100vh - 80px)', // 減少高度，節省更多空間
                            margin: 0, // 移除邊距
                            padding: 0, // 移除內距
                            '&::-webkit-scrollbar': {
                              display: 'none',
                            },
                            '&::-ms-overflow-style': {
                              display: 'none',
                            },
                            '&::-moz-scrollbar': {
                              display: 'none',
                            },
                          }
                        }}
                      >
                        {/* 手機版滑動指示器 */}
                        <Box sx={{
                          display: 'none',
                          '@media (max-width: 1024px)': {
                            display: 'flex',
                            position: 'absolute',
                            bottom: '20px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            zIndex: 10,
                            gap: '12px',
                            background: 'rgba(0,0,0,0.1)',
                            padding: '8px 16px',
                            borderRadius: '20px',
                            backdropFilter: 'blur(10px)',
                          }
                        }}>
                          <Box sx={{
                            width: '12px',
                            height: '12px',
                            borderRadius: '50%',
                            backgroundColor: '#1976d2',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                          }} />
                          <Box sx={{
                            width: '12px',
                            height: '12px',
                            borderRadius: '50%',
                            backgroundColor: '#ccc',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                          }} />
                        </Box>
                        
                        {/* 手機版滑動手勢提示 - 已移除重複提示 */}
                        {/* 左側：近10筆瀏覽記錄 - 只有登入用戶才顯示 */}
                        {authUser?.email && (
                        <Paper sx={{ 
                          flex: 1, 
                          p: 0, 
                          m: 0, 
                          width: '100%', // 統一設為100%適配手機寬度
                          minWidth: '100%', // 統一設為100%適配手機寬度
                          height: '100%', 
                          overflow: 'auto',
                          // 手機版：固定寬度，支援滑動
                          '@media (max-width: 1024px)': {
                            flex: 'none',
                            width: '100%', // 統一100%寬度
                            minWidth: '100%', // 統一100%寬度
                            scrollSnapAlign: 'start',
                            position: 'relative',
                            padding: 0, // 取消所有間距，與視窗邊界貼齊
                            margin: 0, // 移除邊距
                            // 右側滑動提示箭頭
                            '&::after': {
                              content: '"→"',
                              position: 'absolute',
                              right: '16px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              fontSize: '24px',
                              color: '#1976d2',
                              fontWeight: 'bold',
                              textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                              pointerEvents: 'none',
                              animation: 'pulse 2s infinite',
                              '@keyframes pulse': {
                                '0%, 100%': { opacity: 0.6 },
                                '50%': { opacity: 1 },
                              }
                            }
                          }
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, position: 'relative', p: 0.625 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>瀏覽記錄</Typography>
                      <Button
                        variant="text"
                        size="small"
                        sx={{ minWidth: 0, fontSize: '1rem', color: '#1976d2', textTransform: 'none', p: 0, ml: 1 }}
                              onClick={() => { 
                                if (authUser?.email) {
                                  setCurrentSection('personalHistory'); 
                                  // 確保個人項目展開
                                  setExpandedMain(['home', 'myPoint', 'myPersonalFavorite', 'myCommunityPublic', 'teamPoint']);
                                }
                              }}
                      >
                        (more reviews)
                      </Button>
                            {/* 手機版滑動提示 - 可點擊翻頁 */}
                            <Box sx={{ 
                              display: 'none',
                              '@media (max-width: 1024px)': {
                                display: 'flex',
                                ml: 'auto',
                                alignItems: 'center',
                                gap: '4px',
                                color: '#1976d2',
                                fontSize: '0.75rem',
                                fontWeight: 'bold',
                                position: 'relative',
                                cursor: 'pointer',
                                padding: '4px 8px',
                                borderRadius: '12px',
                                backgroundColor: 'rgba(25, 118, 210, 0.1)',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  backgroundColor: 'rgba(25, 118, 210, 0.2)',
                                  transform: 'scale(1.05)',
                                },
                                '&:active': {
                                  transform: 'scale(0.95)',
                                }
                              }
                            }}
                            onClick={() => {
                              // 點擊時滑動到右側的社群區域
                              const container = document.querySelector('[data-scroll-container]');
                              if (container) {
                                container.scrollTo({
                                  left: container.scrollWidth / 2,
                                  behavior: 'smooth'
                                });
                              }
                            }}
                            >
                              <Box sx={{
                                width: '16px',
                                height: '2px',
                                backgroundColor: '#1976d2',
                                borderRadius: '1px',
                              }} />
                              <Typography variant="caption" sx={{ 
                                fontWeight: 'bold',
                                whiteSpace: 'nowrap',
                                overflow: 'visible',
                                textOverflow: 'unset'
                              }}>
                                滑動切換
                              </Typography>
                              <Box sx={{
                                width: '16px',
                                height: '2px',
                                backgroundColor: '#1976d2',
                                borderRadius: '1px',
                              }} />
                            </Box>
                    </Box>
                        {loading ? (
                          renderLoadingState('近期瀏覽記錄載入中...')
                        ) : personalVideos.length === 0 ? (
                      <Typography color="text.secondary">尚無瀏覽記錄</Typography>
                    ) : (
                      <>
                              <InfiniteScroll
                                dataLength={(filteredPersonalHistoryVideos ?? personalHistoryVideos).slice(0, personalLoadedCount).length}
                                next={loadMorePersonalVideos}
                                hasMore={personalLoadedCount < (filteredPersonalHistoryVideos ?? personalHistoryVideos).length}
                                loader={null}
                                endMessage={<Typography align="center" sx={{ my: 2, color: 'gray' }}>已載入全部</Typography>}
                                scrollThreshold={0.8}
                                scrollableTarget="personal-history-scroll-infinite"
                              >
                                {/* 手機模式：使用List佈局 */}
                                {/* Debug: isMobile = {isMobile ? 'true' : 'false'}, window.innerHeight = {window.innerHeight} */}
                                {/* 手機直式和橫式都使用List佈局 */}
                                {isMobile ? (
                                  <Box sx={{ width: '100%', height: 'calc(100% - 60px)', overflowY: 'auto' }}>
                                    {(filteredPersonalHistoryVideos ?? personalHistoryVideos).slice(0, personalLoadedCount).map((video, idx) => (
                                      <Box
                                        key={`personal-list-${video.id}-${idx}`}
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'flex-start',
                                          gap: 2,
                                          // 手機直式模式：保持5px左邊距，與邊界保持統一間距
                                          pl: 0.625, // 5px = 0.625 * 8px
                                          pr: 2,
                                          py: 2,
                                          borderBottom: '1px solid #e0e0e0',
                                          '&:last-child': { borderBottom: 'none' }
                                        }}
                                      >
                                        {/* 縮圖 */}
                                        <Box sx={{ flexShrink: 0, width: '120px' }}>
                                          {renderCellContent('thumbnail', video, idx, 'top5')}
                                        </Box>
                                        
                                        {/* 影片資訊 */}
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                          {renderCellContent('info', video, idx, 'top5')}
                                          
                                          {/* 手機橫式模式：整合功能按鈕 */}
                                          <Box sx={{ 
                                            display: 'flex',
                                            justifyContent: 'flex-start',
                                            alignItems: 'center',
                                            mt: 1,
                                            gap: 0.5
                                          }}>
                                            <IconButton
                                              size="small"
                                              color="primary"
                                              onClick={() => handleShareVideo(video)}
                                              title="分享"
                                              sx={{ 
                                                p: 0.5,
                                                '&:hover': {
                                                  backgroundColor: 'rgba(25, 118, 210, 0.1)'
                                                }
                                              }}
                                            >
                                              <ShareIcon fontSize="small" />
                                            </IconButton>
                                            
                                            {/* 整合的功能按鈕：珍藏/群組/公開 */}
                                            {ZONE_OPTIONS.map(opt => {
                                              // 🔧 修復：精確匹配當前觀點，避免找到錯誤的觀點
                                              const latestVideo = personalHistoryVideos.find(v => v.id === video.id) || 
                                                                personalVideos.find(v => v.id === video.id) || 
                                                                communityVideos.find(v => v.id === video.id) || 
                                                                videos.find(v => v.id === video.id) || 
                                                                video;
                  // 🔒 安全檢查：權限控制
                  const isOwner = latestVideo.userId === userId || latestVideo.userId === authUser?.uid || latestVideo.userId === authUser?.email;
                  const isLoggedIn = authUser && (authUser.uid || authUser.email);
                  
                  // 「公開」和「群組」按鈕：只有影片擁有者才能看到
                  if (opt.value === 'community' || opt.value === 'team') {
                    if (!isOwner) {
                      return null;  // 非擁有者不顯示「公開」和「群組」按鈕
                    }
                  }
                  
                  // 「珍藏」按鈕：所有登入用戶都可以看到（可以收藏任何影片）
                  if (opt.value === 'person') {
                    if (!isLoggedIn) {
                      return null;  // 未登入用戶不顯示「珍藏」按鈕
                    }
                  }
                  
                                              
                                              // 🔧 修復：改用 userFavoriteIds 判斷珍藏狀態，而不是 zones
                  const isPerson = opt.value === 'person' && userFavoriteIds.has(latestVideo.id);
                                              const isTeam = opt.value === 'team' && Array.isArray(latestVideo.groupIds) && latestVideo.groupIds.length > 0;
                                              const isCommunity = opt.value === 'community' && Array.isArray(latestVideo.zones) && latestVideo.zones.includes('community');
                                              
                                              return (
                                                <Button
                                                  key={opt.value}
                                                  size="small"
                                                  variant={(isPerson || isTeam || isCommunity) ? 'contained' : 'outlined'}
                                                  onClick={async () => {
                                                    if (opt.value === 'team') {
                                                      if (!authUser || !authUser.email) {
                                                        setSnackbarMessage('請先登入才能分享給好友群組');
                                                        setSnackbarOpen(true);
                                                        return;
                                                      }
                                                      setGroupDialogVideo(video);
                                                      setGroupDialogOpen(true);
                                                      if (!userGroups.length) {
                                                        const groups = await getUserGroups(authUser.email);
                                                        if (Array.isArray(groups)) {
                                                          setUserGroups(groups);
                                                        } else {
                                                          setUserGroups([]);
                                                        }
                                                      }
                                                      const groupIds = Array.isArray(video.groupIds) ? video.groupIds : [];
                                                      setSelectedGroups(groupIds);
                                                    } else if (opt.value === 'person') {
                                                      // 🔧 修復：改用 userFavoriteIds 判斷珍藏狀態
                      const wasChecked = userFavoriteIds.has(latestVideo.id);
                                                      
                                                      if (!wasChecked) {
                                                        // 🔧 修改：當「珍藏」是灰色時，點擊後直接執行珍藏操作（參考公開按鈕的實現）
                                                        // 调试日志已移除
                                                        
                                                        // 立即更新本地狀態，提供即時反饋（參考公開按鈕的實現）
                                                        const newZones = [...(latestVideo.zones || []), 'person'];
                                                        const updatedVideo = { ...latestVideo, zones: newZones };
                                                        
                                                        // 立即更新所有相關狀態，確保按鈕立刻變綠色
                                                        setVideos(prev => prev.map(v => v.id === latestVideo.id ? updatedVideo : v));
                                                        setPersonalVideos(prev => prev.map(v => v.id === latestVideo.id ? updatedVideo : v));
                                                        setCommunityVideos(prev => prev.map(v => v.id === latestVideo.id ? updatedVideo : v));
                                                        setPersonalHistoryVideos(prev => prev.map(v => v.id === latestVideo.id ? updatedVideo : v));
                                                        
                                                        // 異步添加到用戶珍藏列表
                                                        try {
                                                          const { addToUserFavorites } = await import('../services/userFavoritesService');
                                                          await addToUserFavorites(
                                                            userId,
                                                            latestVideo.videoId || latestVideo.id,
                                                            latestVideo.id
                                                          );
                                                          // 调试日志已移除
                                                          setSnackbarMessage('已加入我的收藏');
                                                          setSnackbarOpen(true);
                                                        } catch (error) {
                                                          console.error('❌ 珍藏操作失敗:', error);
                                                          setSnackbarMessage('珍藏操作失敗');
                                                          setSnackbarOpen(true);
                                                        }
                                                      } else {
                                                        const newZones = (latestVideo.zones || []).filter(z => z !== 'person');
                                                        const updatedVideo = { ...latestVideo, zones: newZones };
                                                        
                                                        setPersonalVideos(prev =>
                                                          prev.map(v =>
                                                            v.id === video.id ? updatedVideo : v
                                                          )
                                                        );
                                                        setVideos(prev =>
                                                          prev.map(v =>
                                                            v.id === video.id ? updatedVideo : v
                                                          )
                                                        );
                                                        setPersonalHistoryVideos(prev =>
                                                          prev.map(v =>
                                                            v.id === video.id ? updatedVideo : v
                                                          )
                                                        );
                                                        
                                                        handleZoneChangeAll(latestVideo, 'person', false);
                                                        setSnackbarMessage('已從個人觀看清單移除');
                                                        setSnackbarOpen(true);
                                                      }
                                                    } else if (opt.value === 'community') {
                                                      const wasChecked = Array.isArray(latestVideo.zones) && latestVideo.zones.includes('community');
                                                      
                                                      const newZones = wasChecked 
                                                        ? (latestVideo.zones || []).filter(z => z !== 'community')
                                                        : [...(latestVideo.zones || []), 'community'];
                                                      const updatedVideo = { ...latestVideo, zones: newZones };
                                                      
                                                      setPersonalVideos(prev =>
                                                        prev.map(v =>
                                                          v.id === video.id ? updatedVideo : v
                                                        )
                                                      );
                                                      setVideos(prev =>
                                                        prev.map(v =>
                                                          v.id === video.id ? updatedVideo : v
                                                        )
                                                      );
                                                      setPersonalHistoryVideos(prev =>
                                                        prev.map(v =>
                                                          v.id === video.id ? updatedVideo : v
                                                        )
                                                      );
                                                      
                                                      handleZoneChangeAll(latestVideo, 'community', !wasChecked);
                                                      setSnackbarMessage(wasChecked ? '已取消公開' : '已公開');
                                                      setSnackbarOpen(true);
                                                    }
                                                  }}
                                                  sx={{ 
                                                    p: 0.5,
                                                    minWidth: 'auto',
                                                    fontSize: '0.7rem',
                                                    // 強制覆蓋 Material-UI contained 按鈕的預設樣式
                                                    '&.MuiButton-contained': {
                                                      '&:hover': {
                                                        backgroundColor: '#1565c0 !important',
                                                        color: 'white !important'
                                                      }
                                                    },
                                                    '&.MuiButton-outlined': {
                                                      '&:hover': {
                                                        backgroundColor: 'rgba(25, 118, 210, 0.1)'
                                                      }
                                                    }
                                                  }}
                                                >
                                                  <Typography 
                                                    variant="caption" 
                                                    sx={{ 
                                                      fontSize: '0.7rem', 
                                                      lineHeight: 1,
                                                      // 當按鈕是 Active 狀態時，懸停字體改為深藍色
                                                      ...((isPerson || isTeam || isCommunity) && {
                                                        '&:hover': {
                                                          color: '#1565c0 !important'
                                                        }
                                                      })
                                                    }}
                                                  >
                                                    {opt.label}
                                                  </Typography>
                                                </Button>
                                              );
                                            })}
                                          </Box>
                                        </Box>
                                      </Box>
                                    ))}
                                  </Box>
                                ) : (
                                  /* 桌面模式：使用Table佈局 */
                                  <TableContainer>
                                  <Table size="small" sx={{ 
                tableLayout: 'fixed', 
                width: 'auto',
                '& th:first-of-type, & td:first-of-type': {
                  '@media (max-width: 1024px)': {
                    display: 'none'
                  }
                }
              }}>
                          <TableHead>
                            <TableRow>
                                          {COLUMNS.filter(col => PAGE_COLUMN_KEYS['personalHistory'].includes(col.key)).map(col => (
                                <TableCell
                                  key={col.key}
                                  data-col={col.key}
                                    align="center"
                                >
                                  {col.label}
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            <>
                                      {(filteredPersonalHistoryVideos ?? personalHistoryVideos).slice(0, personalLoadedCount).map((video, idx) => (
                                <TableRow key={`personal-${video.id}-${idx}`}>
                                              {COLUMNS.filter(col => PAGE_COLUMN_KEYS['personalHistory'].includes(col.key)).map(col => (
                                    <TableCell
                                      key={`personal-${video.id}-${col.key}-${idx}`}
                                      data-col={col.key}
                                      align="center"
                                                {...getTableCellStyle(col, false)}
                                    >
                                      {renderCellContent(col.key, video, idx, 'top5')}
                                    </TableCell>
                                  ))}
                                </TableRow>
                              ))}
                            </>
                          </TableBody>
                        </Table>
                      </TableContainer>
                                )}
                              </InfiniteScroll>
                      </>
                    )}
                  </Paper>
                        )}

                        {/* 右側：社群關注 */}
                        <Paper sx={{ 
                          flex: 1, 
                          p: 0, 
                          m: 0, 
                          width: '100%', // 統一設為100%適配手機寬度
                          minWidth: '100%', // 統一設為100%適配手機寬度
                          height: '100%', 
                          overflow: 'auto',
                          // 手機版：固定寬度，支援滑動
                          '@media (max-width: 1024px)': {
                            flex: 'none',
                            width: '100%', // 統一設為100%適配手機寬度
                            minWidth: '100%', // 統一設為100%適配手機寬度
                            scrollSnapAlign: 'start',
                            position: 'relative',
                            padding: 0, // 取消所有間距，與視窗邊界貼齊
                            margin: 0, // 移除邊距
                            // 左側滑動提示箭頭 - 只有登入用戶才顯示
                            ...(authUser?.email && {
                              '&::before': {
                                content: '"←"',
                                position: 'absolute',
                                left: '16px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                fontSize: '24px',
                                color: '#1976d2',
                                fontWeight: 'bold',
                                textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                                pointerEvents: 'none',
                                animation: 'pulse 2s infinite',
                                '@keyframes pulse': {
                                  '0%, 100%': { opacity: 0.6 },
                                  '50%': { opacity: 1 },
                                }
                              }
                            })
                          }
                        }}>
                          {/* 第一行：標題 + 篩選標籤 + 滑動切換按鈕 */}
                          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          社群關注
                      </Typography>
                              
                              {/* 篩選標籤 - 桌面版顯示 */}
                              <Stack direction="row" spacing={1} sx={{ 
                                display: 'flex',
                                '@media (max-width: 1024px)': {
                                  display: 'none'
                                }
                              }}>
                      <Button
                        variant={communitySortMode === 'flat' && communitySortKey === 'vbids' ? 'contained' : 'outlined'}
                        size="small"
                                  onClick={() => { setCommunitySortMode('flat'); setCommunitySortKey('vbids'); setCommunityLoadedCount(50); }}
                            sx={{ borderRadius: '20px' }}
                      >重點數</Button>
                      <Button
                                  variant={communitySortMode === 'flat' && communitySortKey === 'attentionCount' ? 'contained' : 'outlined'}
                        size="small"
                                  onClick={() => { setCommunitySortMode('flat'); setCommunitySortKey('attentionCount'); setCommunityLoadedCount(50); }}
                            sx={{ borderRadius: '20px' }}
                      >關注度</Button>
                      <Button
                        variant={communitySortMode === 'flat' && communitySortKey === 'favoriteCount' ? 'contained' : 'outlined'}
                        size="small"
                                  onClick={() => { setCommunitySortMode('flat'); setCommunitySortKey('favoriteCount'); setCommunityLoadedCount(50); }}
                            sx={{ borderRadius: '20px' }}
                      >收藏數</Button>
                          <Button
                            variant={communitySortMode === 'flat' && communitySortKey === 'time' ? 'contained' : 'outlined'}
                            size="small"
                                  onClick={() => { setCommunitySortMode('flat'); setCommunitySortKey('time'); setCommunityLoadedCount(50); }}
                                  sx={{ borderRadius: '20px' }}
                                >時間序</Button>
                              </Stack>
                            </Box>
                            
                            {/* 手機版滑動提示 - 可點擊翻頁回到瀏覽記錄 - 只有登入用戶才顯示 */}
                            {authUser?.email && (
                            <Box sx={{ 
                              display: 'none',
                              '@media (max-width: 1024px)': {
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                color: '#1976d2',
                                fontSize: '0.75rem',
                                fontWeight: 'bold',
                                cursor: 'pointer',
                                padding: '4px 8px',
                                borderRadius: '12px',
                                backgroundColor: 'rgba(25, 118, 210, 0.1)',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  backgroundColor: 'rgba(25, 118, 210, 0.2)',
                                  transform: 'scale(1.05)',
                                },
                                '&:active': {
                                  transform: 'scale(0.95)',
                                }
                              }
                            }}
                            onClick={() => {
                              // 點擊時滑動回到左側的瀏覽記錄區域
                              const container = document.querySelector('[data-scroll-container]');
                              if (container) {
                                container.scrollTo({
                                  left: 0,
                                  behavior: 'smooth'
                                });
                              }
                            }}
                            >
                              <Box sx={{
                                width: '16px',
                                height: '2px',
                                backgroundColor: '#1976d2',
                                borderRadius: '1px',
                              }} />
                              <Typography variant="caption" sx={{ 
                                fontWeight: 'bold',
                                whiteSpace: 'nowrap',
                                overflow: 'visible',
                                textOverflow: 'unset'
                              }}>
                                滑動切換
                              </Typography>
                              <Box sx={{
                                width: '16px',
                                height: '2px',
                                backgroundColor: '#1976d2',
                                borderRadius: '1px',
                              }} />
                            </Box>
                            )}
                          </Box>
                          
                          {/* 手機版篩選標籤 - 第二行顯示 */}
                          <Box sx={{ 
                            mb: 2,
                            display: 'none',
                            '@media (max-width: 1024px)': {
                              display: 'block'
                            }
                          }}>
                            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                              <Button
                                variant={communitySortMode === 'flat' && communitySortKey === 'vbids' ? 'contained' : 'outlined'}
                                size="small"
                                onClick={() => { setCommunitySortMode('flat'); setCommunitySortKey('vbids'); setCommunityLoadedCount(50); }}
                                sx={{ borderRadius: '20px' }}
                              >重點數</Button>
                              <Button
                                variant={communitySortMode === 'flat' && communitySortKey === 'attentionCount' ? 'contained' : 'outlined'}
                                size="small"
                                onClick={() => { setCommunitySortMode('flat'); setCommunitySortKey('attentionCount'); setCommunityLoadedCount(50); }}
                                sx={{ borderRadius: '20px' }}
                              >關注度</Button>
                              <Button
                                variant={communitySortMode === 'flat' && communitySortKey === 'favoriteCount' ? 'contained' : 'outlined'}
                                size="small"
                                onClick={() => { setCommunitySortMode('flat'); setCommunitySortKey('favoriteCount'); setCommunityLoadedCount(50); }}
                                sx={{ borderRadius: '20px' }}
                              >收藏數</Button>
                              <Button
                                variant={communitySortMode === 'flat' && communitySortKey === 'time' ? 'contained' : 'outlined'}
                                size="small"
                                onClick={() => { setCommunitySortMode('flat'); setCommunitySortKey('time'); setCommunityLoadedCount(50); }}
                            sx={{ borderRadius: '20px' }}
                          >時間序</Button>
                        </Stack>
                    </Box>
                        {loading ? (
                          renderLoadingState('社群影片載入中...')
                        ) : communitySortMode === 'group' ? (
                      // 原本的分組（今日/昨天/歷史重點）表格
                      <>
                        {/* 今日重點 */}
                        <TableContainer>
                          <Table size="small" sx={{ 
                tableLayout: 'fixed', 
                width: 'auto',
                '& th:first-of-type, & td:first-of-type': {
                  '@media (max-width: 1024px)': {
                    display: 'none'
                  }
                }
              }}>
                            {/* 桌面版顯示標題列，手機版隱藏 */}
                            <TableHead sx={{ 
                              '@media (max-width: 1024px)': {
                                display: 'none'
                              }
                            }}>
                              <TableRow>
                                {COLUMNS.filter(col => PAGE_COLUMN_KEYS['myPersonalFavorite'].includes(col.key)).map(col => (
                                  <TableCell
                                    key={col.key}
                                    data-col={col.key}
                                    align="center"
                                    {...getTableCellStyle(col, true)}
                                  >
                                    {col.label}
                                  </TableCell>
                                ))}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              <>
                              <TableRow>
                                <TableCell colSpan={COLUMNS.filter(col => PAGE_COLUMN_KEYS['myPersonalFavorite'].includes(col.key)).length} sx={{ background: '#f5f5f5', fontWeight: 700, fontSize: 18 }}>
                                  今日重點：
                                </TableCell>
                              </TableRow>
                              {filteredTodayVideos.length === 0 ? (
                                <TableRow>
                                  <TableCell colSpan={COLUMNS.filter(col => PAGE_COLUMN_KEYS['myPersonalFavorite'].includes(col.key)).length} align="center">無（沒有影片）</TableCell>
                                </TableRow>
                              ) : (
                                filteredTodayVideos.map((video, idx) => (
                                  <TableRow key={`today-${video.id}-${idx}`}>
                                    {COLUMNS.filter(col => PAGE_COLUMN_KEYS['myPersonalFavorite'].includes(col.key)).map(col => (
                                      <TableCell
                                        key={`today-${video.id}-${col.key}-${idx}`}
                                        data-col={col.key}
                                        align="center"
                                        {...getTableCellStyle(col, false)}
                                      >
                                        {renderCellContent(col.key, video, idx, 'teamPoint')}
                                      </TableCell>
                                    ))}
                                  </TableRow>
                                ))
                              )}
                                  {/* 昨日重點 */}
                              <TableRow>
                                <TableCell colSpan={COLUMNS.filter(col => PAGE_COLUMN_KEYS['myPersonalFavorite'].includes(col.key)).length} sx={{ background: '#f5f5f5', fontWeight: 700, fontSize: 18 }}>
                                      昨日重點：
                                </TableCell>
                              </TableRow>
                              {filteredYesterdayVideos.length === 0 ? (
                                <TableRow>
                                  <TableCell colSpan={COLUMNS.filter(col => PAGE_COLUMN_KEYS['myPersonalFavorite'].includes(col.key)).length} align="center">無（沒有影片）</TableCell>
                                </TableRow>
                              ) : (
                                filteredYesterdayVideos.map((video, idx) => (
                                  <TableRow key={`yesterday-${video.id}-${idx}`}>
                                    {COLUMNS.filter(col => PAGE_COLUMN_KEYS['myPersonalFavorite'].includes(col.key)).map(col => (
                                      <TableCell
                                        key={`yesterday-${video.id}-${col.key}-${idx}`}
                                        data-col={col.key}
                                        align="center"
                                        {...getTableCellStyle(col, false)}
                                      >
                                        {renderCellContent(col.key, video, idx, 'teamPoint')}
                                      </TableCell>
                                    ))}
                                  </TableRow>
                                ))
                              )}
                              {/* 歷史重點 */}
                              <TableRow>
                                <TableCell colSpan={COLUMNS.filter(col => PAGE_COLUMN_KEYS['myPersonalFavorite'].includes(col.key)).length} sx={{ background: '#f5f5f5', fontWeight: 700, fontSize: 18 }}>
                                  歷史重點：
                                </TableCell>
                              </TableRow>
                              {filteredHistoryVideos.length === 0 ? (
                                <TableRow>
                                  <TableCell colSpan={COLUMNS.filter(col => PAGE_COLUMN_KEYS['myPersonalFavorite'].includes(col.key)).length} align="center">無（沒有影片）</TableCell>
                                </TableRow>
                              ) : (
                                filteredHistoryVideos.map((video, idx) => (
                                  <TableRow key={`history-${video.id}-${idx}`}>
                                    {COLUMNS.filter(col => PAGE_COLUMN_KEYS['myPersonalFavorite'].includes(col.key)).map(col => (
                                      <TableCell
                                        key={`history-${video.id}-${col.key}-${idx}`}
                                        data-col={col.key}
                                        align="center"
                                        {...getTableCellStyle(col, false)}
                                      >
                                        {renderCellContent(col.key, video, idx, 'teamPoint')}
                                      </TableCell>
                                    ))}
                                  </TableRow>
                                ))
                              )}
                              </> 
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </>
                    ) : (
                          // 排序模式下，全部影片平鋪排序，使用無限滾動
                          <>
                      <InfiniteScroll
                        dataLength={communityVideosToShow.length}
                        next={loadMoreCommunityVideos}
                        hasMore={hasMoreCommunityVideos}
                        loader={null}
                        endMessage={<Typography align="center" sx={{ my: 2, color: 'gray' }}>已載入全部</Typography>}
                        scrollThreshold={0.8}
                        scrollableTarget="community-videos-scroll-infinite"
                      >
                                             {/* 手機模式：使用List佈局 */}
                       {isMobile ? (
                        <Box sx={{ width: '100%', height: 'calc(100% - 80px)', overflowY: 'auto' }}>
                          {communityVideosToShow.sort((a, b) => {
                            if (communitySortKey === 'time') {
                              // 🔧 修復：社群分頁時間序按 lastOpenDate 排序，突顯影片熱門程度
                              return new Date(b.lastOpenDate || b.addedDate || 0).getTime() - new Date(a.lastOpenDate || a.addedDate || 0).getTime();
                            } else if (communitySortKey === 'vbids') {
                              return (b.vbids?.length || 0) - (a.vbids?.length || 0);
                            } else if (communitySortKey === 'favoriteCount') {
                              return (b.favoriteCount || 0) - (a.favoriteCount || 0);
                            } else if (communitySortKey === 'attentionCount') {
                              return (b.attentionCount || 0) - (a.attentionCount || 0);
                            }
                            return 0;
                          }).map((video, idx) => (
                            <Box
                              key={`community-list-${video.id}-${idx}`}
                              sx={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 2,
                                // 手機直式模式：保持5px左邊距，與邊界保持統一間距
                                pl: 0.625, // 5px = 0.625 * 8px
                                pr: 2,
                                py: 2,
                                borderBottom: '1px solid #e0e0e0',
                                '&:last-child': { borderBottom: 'none' }
                              }}
                            >
                              {/* 縮圖 */}
                              <Box sx={{ flexShrink: 0, width: '120px' }}>
                                {renderCellContent('thumbnail', video, idx, 'community')}
                              </Box>
                              
                              {/* 影片資訊 */}
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                {renderCellContent('info', video, idx, 'community')}
                                
                                {/* 手機橫式模式：整合功能按鈕 */}
                                <Box sx={{ 
                                  display: 'flex',
                                  justifyContent: 'flex-start',
                                  alignItems: 'center',
                                  mt: 1,
                                  gap: 0.5
                                }}>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => handleShareVideo(video)}
                                    title="分享"
                                    sx={{ 
                                      p: 0.5,
                                      '&:hover': {
                                        backgroundColor: 'rgba(25, 118, 210, 0.1)'
                                      }
                                    }}
                                  >
                                    <ShareIcon fontSize="small" />
                                  </IconButton>
                                  
                                  {/* 整合的功能按鈕：珍藏/群組/公開 */}
                                  {ZONE_OPTIONS.map(opt => {
                                    // 🔧 修復：精確匹配當前觀點，避免找到錯誤的觀點
                                    const latestVideo = personalHistoryVideos.find(v => v.id === video.id) || 
                                                      personalVideos.find(v => v.id === video.id) || 
                                                      communityVideos.find(v => v.id === video.id) || 
                                                      videos.find(v => v.id === video.id) || 
                                                      video;
                  // 🔒 安全檢查：權限控制
                  const isOwner = latestVideo.userId === userId || latestVideo.userId === authUser?.uid || latestVideo.userId === authUser?.email;
                  const isLoggedIn = authUser && (authUser.uid || authUser.email);
                  
                  // 「公開」和「群組」按鈕：只有影片擁有者才能看到
                  if (opt.value === 'community' || opt.value === 'team') {
                    if (!isOwner) {
                      return null;  // 非擁有者不顯示「公開」和「群組」按鈕
                    }
                  }
                  
                  // 「珍藏」按鈕：所有登入用戶都可以看到（可以收藏任何影片）
                  if (opt.value === 'person') {
                    if (!isLoggedIn) {
                      return null;  // 未登入用戶不顯示「珍藏」按鈕
                    }
                  }
                  
                                    
                                    // 🔧 修復：改用 userFavoriteIds 判斷珍藏狀態，而不是 zones
                  const isPerson = opt.value === 'person' && userFavoriteIds.has(latestVideo.id);
                                    const isTeam = opt.value === 'team' && Array.isArray(latestVideo.groupIds) && latestVideo.groupIds.length > 0;
                                    const isCommunity = opt.value === 'community' && Array.isArray(latestVideo.zones) && latestVideo.zones.includes('community');
                                    
                                    return (
                                      <Button
                                        key={opt.value}
                                        size="small"
                                        variant={(isPerson || isTeam || isCommunity) ? 'contained' : 'outlined'}
                                        onClick={async () => {
                                          if (opt.value === 'team') {
                                            if (!authUser || !authUser.email) {
                                              setSnackbarMessage('請先登入才能分享給好友群組');
                                              setSnackbarOpen(true);
                                              return;
                                            }
                                            setGroupDialogVideo(video);
                                            setGroupDialogOpen(true);
                                            if (!userGroups.length) {
                                              const groups = await getUserGroups(authUser.email);
                                              if (Array.isArray(groups)) {
                                                setUserGroups(groups);
                                              } else {
                                                setUserGroups([]);
                                              }
                                            }
                                            const groupIds = Array.isArray(video.groupIds) ? video.groupIds : [];
                                            setSelectedGroups(groupIds);
                                          } else if (opt.value === 'person') {
                                            // 🔧 修復：改用 userFavoriteIds 判斷珍藏狀態
                      const wasChecked = userFavoriteIds.has(latestVideo.id);
                                            
                                            if (!wasChecked) {
                                              // 🔧 修改：當「珍藏」是灰色時，點擊後直接執行珍藏操作（參考公開按鈕的實現）
                                              // 调试日志已移除
                                              
                                              // 立即更新本地狀態，提供即時反饋（參考公開按鈕的實現）
                                              const newZones = [...(latestVideo.zones || []), 'person'];
                                              const updatedVideo = { ...latestVideo, zones: newZones };
                                              
                                              // 立即更新所有相關狀態，確保按鈕立刻變綠色
                                              setVideos(prev => prev.map(v => v.id === latestVideo.id ? updatedVideo : v));
                                              setPersonalVideos(prev => prev.map(v => v.id === latestVideo.id ? updatedVideo : v));
                                              setCommunityVideos(prev => prev.map(v => v.id === latestVideo.id ? updatedVideo : v));
                                              setPersonalHistoryVideos(prev => prev.map(v => v.id === latestVideo.id ? updatedVideo : v));
                                              
                                              // 異步添加到用戶珍藏列表
                                              try {
                                                const { addToUserFavorites } = await import('../services/userFavoritesService');
                                                await addToUserFavorites(
                                                  userId,
                                                  latestVideo.videoId || latestVideo.id,
                                                  latestVideo.id
                                                );
                                                // 调试日志已移除
                                                setSnackbarMessage('已加入我的收藏');
                                                setSnackbarOpen(true);
                                              } catch (error) {
                                                console.error('❌ 珍藏操作失敗:', error);
                                                setSnackbarMessage('珍藏操作失敗');
                                                setSnackbarOpen(true);
                                              }
                                            } else {
                                              const newZones = (latestVideo.zones || []).filter(z => z !== 'person');
                                              const updatedVideo = { ...latestVideo, zones: newZones };
                                              
                                              setPersonalVideos(prev =>
                                                prev.map(v =>
                                                  v.id === video.id ? updatedVideo : v
                                                )
                                              );
                                              setVideos(prev =>
                                                prev.map(v =>
                                                  v.id === video.id ? updatedVideo : v
                                                )
                                              );
                                              setPersonalHistoryVideos(prev =>
                                                prev.map(v =>
                                                  v.id === video.id ? updatedVideo : v
                                                )
                                              );
                                              
                                              handleZoneChangeAll(latestVideo, 'person', false);
                                              setSnackbarMessage('已從個人觀看清單移除');
                                              setSnackbarOpen(true);
                                            }
                                          } else if (opt.value === 'community') {
                                            const wasChecked = Array.isArray(latestVideo.zones) && latestVideo.zones.includes('community');
                                            
                                            const newZones = wasChecked 
                                              ? (latestVideo.zones || []).filter(z => z !== 'community')
                                              : [...(latestVideo.zones || []), 'community'];
                                            const updatedVideo = { ...latestVideo, zones: newZones };
                                            
                                            setPersonalVideos(prev =>
                                              prev.map(v =>
                                                v.id === video.id ? updatedVideo : v
                                              )
                                            );
                                            setVideos(prev =>
                                              prev.map(v =>
                                                v.id === video.id ? updatedVideo : v
                                              )
                                            );
                                            setPersonalHistoryVideos(prev =>
                                              prev.map(v =>
                                                v.id === video.id ? updatedVideo : v
                                              )
                                            );
                                            
                                            handleZoneChangeAll(latestVideo, 'community', !wasChecked);
                                            setSnackbarMessage(wasChecked ? '已取消公開' : '已公開');
                                            setSnackbarOpen(true);
                                          }
                                        }}
                                        sx={{ 
                                          p: 0.5,
                                          minWidth: 'auto',
                                          fontSize: '0.7rem',
                                          // 強制覆蓋 Material-UI contained 按鈕的預設樣式
                                          '&.MuiButton-contained': {
                                            '&:hover': {
                                              backgroundColor: '#1565c0 !important',
                                              color: 'white !important'
                                            }
                                          },
                                          '&.MuiButton-outlined': {
                                            '&:hover': {
                                              backgroundColor: 'rgba(25, 118, 210, 0.1)'
                                            }
                                          }
                                        }}
                                      >
                                        <Typography 
                                          variant="caption" 
                                          sx={{ 
                                            fontSize: '0.7rem', 
                                            lineHeight: 1,
                                            // 當按鈕是 Active 狀態時，懸停字體改為深藍色
                                            ...((isPerson || isTeam || isCommunity) && {
                                              '&:hover': {
                                                color: '#1565c0 !important'
                                              }
                                            })
                                          }}
                                        >
                                          {opt.label}
                                        </Typography>
                                      </Button>
                                    );
                                  })}
                                </Box>
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      ) : (
                        /* 桌面模式：使用Table佈局 */
                      <TableContainer>
                        <Table size="small" sx={{ 
                tableLayout: 'fixed', 
                width: 'auto',
                '& th:first-of-type, & td:first-of-type': {
                  '@media (max-width: 1024px)': {
                    display: 'none'
                  }
                }
              }}>
                          <TableHead>
                            <TableRow>
                              {COLUMNS.filter(col => PAGE_COLUMN_KEYS['myPersonalFavorite'].includes(col.key)).map(col => (
                                <TableCell
                                  key={col.key}
                                  data-col={col.key}
                                  align="center"
                                  {...getTableCellStyle(col, true)}
                                >
                                  {col.label}
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            <>
                              {communityVideosToShow.sort((a, b) => {
                                if (communitySortKey === 'time') {
                                  // 🔧 修復：社群分頁時間序按 lastOpenDate 排序，突顯影片熱門程度
                                  return new Date(b.lastOpenDate || b.addedDate || 0).getTime() - new Date(a.lastOpenDate || a.addedDate || 0).getTime();
                                } else if (communitySortKey === 'vbids') {
                                  return (b.vbids?.length || 0) - (a.vbids?.length || 0);
                                } else if (communitySortKey === 'favoriteCount') {
                                  return (b.favoriteCount || 0) - (a.favoriteCount || 0);
                                } else if (communitySortKey === 'attentionCount') {
                                  return (b.attentionCount || 0) - (a.attentionCount || 0);
                                }
                                return 0;
                              }).map((video, idx) => (
                                <TableRow key={`community-${video.id}-${idx}`}>
                                  {COLUMNS.filter(col => PAGE_COLUMN_KEYS['myPersonalFavorite'].includes(col.key)).map(col => (
                                    <TableCell
                                      key={`community-${video.id}-${col.key}`}
                                      data-col={col.key}
                                      align="center"
                                    >
                                            {renderCellContent(col.key, video, idx, 'community')}
                                    </TableCell>
                                  ))}
                                </TableRow>
                              ))}
                            </>
                          </TableBody>
                        </Table>
                      </TableContainer>
                      )}
                      </InfiniteScroll>
                          </>
                    )}
                  </Paper>
                    </Box>
                    </>
                  )}
                </Box>
              ) : currentSection === 'myPersonalFavorite' ? (
                loading ? (
                  renderLoadingState('個人珍藏載入中...')
                ) : (
                  <>{renderMyPersonalFavoriteContent}</>
                )
              ) : currentSection === 'mediaPoint' ? (
                <Box>
                  {loading ? (
                    renderLoadingState('社群重點載入中...')
                  ) : communityVideos.length === 0 ? (
                    <Typography color="text.secondary" sx={{ mt: 2 }}>尚無社群重點</Typography>
                  ) : (
                    <TableContainer>
                      <Table size="small" sx={{ 
                tableLayout: 'fixed', 
                width: 'auto',
                '& th:first-of-type, & td:first-of-type': {
                  '@media (max-width: 1024px)': {
                    display: 'none'
                  }
                }
              }}>
                        <TableHead>
                          <TableRow>
                            {COLUMNS.filter(col => PAGE_COLUMN_KEYS['myPersonalFavorite'].includes(col.key)).map(col => (
                              <TableCell
                                key={col.key}
                                data-col={col.key}
                                  align="center"
                            >
                              {col.label}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {communityVideos.map((video, idx) => (
                          <TableRow key={`community-${video.id}-${idx}`}>
                            {COLUMNS.filter(col => PAGE_COLUMN_KEYS['myPersonalFavorite'].includes(col.key)).map(col => (
                              <TableCell
                                key={`community-${video.id}-${col.key}`}
                                data-col={col.key}
                                align="center"
                                sx={col.key === 'tags' ? {
                                  maxWidth: '500px !important',
                                  width: '500px !important',
                                  minWidth: '500px !important',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis'
                                } : {}}
                              >
                                {renderCellContent(col.key, video, idx, 'mediaPoint')}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            ) : currentSection === 'acopy' ? (
              <Box>
                {loading ? (
                  renderLoadingState('交片區載入中...')
                ) : communityVideos.filter(v => Array.isArray(v.zones) && v.zones.includes('delivery')).length === 0 ? (
                  <Typography color="text.secondary" sx={{ mt: 2 }}>尚無交片區影片</Typography>
                ) : (
                  /* 手機模式：使用List佈局 */
                  isMobile ? (
                    <Box sx={{ width: '100%', height: 'calc(100vh - 200px)', overflowY: 'auto' }}>
                      {communityVideos.filter(v => Array.isArray(v.zones) && v.zones.includes('delivery')).map((video, idx) => (
                        <Box
                          key={`delivery-list-${video.id}-${idx}`}
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 2,
                            // 手機直式模式：保持5px左邊距，與邊界保持統一間距
                            pl: 0.625, // 5px = 0.625 * 8px
                            pr: 2,
                            py: 2,
                            borderBottom: '1px solid #e0e0e0',
                            '&:last-child': { borderBottom: 'none' }
                          }}
                        >
                          {/* 縮圖 */}
                          <Box sx={{ flexShrink: 0, width: '120px' }}>
                            {renderCellContent('thumbnail', video, idx, 'acopy')}
                          </Box>
                          
                          {/* 影片資訊 */}
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            {renderCellContent('info', video, idx, 'acopy')}
                            
                            {/* 手機橫式模式：整合功能按鈕 */}
                            <Box sx={{ 
                              display: 'flex',
                              justifyContent: 'flex-start',
                              alignItems: 'center',
                              mt: 1,
                              gap: 0.5
                            }}>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleShareVideo(video)}
                                title="分享"
                                sx={{ 
                                  p: 0.5,
                                  '&:hover': {
                                    backgroundColor: 'rgba(25, 118, 210, 0.1)'
                                  }
                                }}
                              >
                                <ShareIcon fontSize="small" />
                              </IconButton>
                              
                              {/* 整合的功能按鈕：珍藏/群組/公開 */}
                              {ZONE_OPTIONS.map(opt => {
                                // 🔧 修復：精確匹配當前觀點，避免找到錯誤的觀點
                                const latestVideo = personalHistoryVideos.find(v => v.id === video.id) || 
                                                  personalVideos.find(v => v.id === video.id) || 
                                                  communityVideos.find(v => v.id === video.id) || 
                                                  videos.find(v => v.id === video.id) || 
                                                  video;
                  // 🔒 安全檢查：權限控制
                  const isOwner = latestVideo.userId === userId || latestVideo.userId === authUser?.uid || latestVideo.userId === authUser?.email;
                  const isLoggedIn = authUser && (authUser.uid || authUser.email);
                  
                  // 「公開」和「群組」按鈕：只有影片擁有者才能看到
                  if (opt.value === 'community' || opt.value === 'team') {
                    if (!isOwner) {
                      return null;  // 非擁有者不顯示「公開」和「群組」按鈕
                    }
                  }
                  
                  // 「珍藏」按鈕：所有登入用戶都可以看到（可以收藏任何影片）
                  if (opt.value === 'person') {
                    if (!isLoggedIn) {
                      return null;  // 未登入用戶不顯示「珍藏」按鈕
                    }
                  }
                  
                                
                                // 🔧 修復：改用 userFavoriteIds 判斷珍藏狀態，而不是 zones
                  const isPerson = opt.value === 'person' && userFavoriteIds.has(latestVideo.id);
                                const isTeam = opt.value === 'team' && Array.isArray(latestVideo.groupIds) && latestVideo.groupIds.length > 0;
                                const isCommunity = opt.value === 'community' && Array.isArray(latestVideo.zones) && latestVideo.zones.includes('community');
                                
                                return (
                                  <Button
                                    key={opt.value}
                                    size="small"
                                    variant={(isPerson || isTeam || isCommunity) ? 'contained' : 'outlined'}
                                    onClick={async () => {
                                      if (opt.value === 'team') {
                                        if (!authUser || !authUser.email) {
                                          setSnackbarMessage('請先登入才能分享給好友群組');
                                          setSnackbarOpen(true);
                                          return;
                                        }
                                        setGroupDialogVideo(video);
                                        setGroupDialogOpen(true);
                                        if (!userGroups.length) {
                                          const groups = await getUserGroups(authUser.email);
                                          if (Array.isArray(groups)) {
                                            setUserGroups(groups);
                                          } else {
                                            setUserGroups([]);
                                          }
                                        }
                                        const groupIds = Array.isArray(video.groupIds) ? video.groupIds : [];
                                        setSelectedGroups(groupIds);
                                      } else if (opt.value === 'person') {
                                        // 🔧 修復：改用 userFavoriteIds 判斷珍藏狀態
                      const wasChecked = userFavoriteIds.has(latestVideo.id);
                                        
                                        if (!wasChecked) {
                                          // 🔧 修改：當「珍藏」是灰色時，點擊後直接執行珍藏操作（參考公開按鈕的實現）
                                          // 调试日志已移除
                                          
                                          // 立即更新本地狀態，提供即時反饋（參考公開按鈕的實現）
                                          const newZones = [...(latestVideo.zones || []), 'person'];
                                          const updatedVideo = { ...latestVideo, zones: newZones };
                                          
                                          // 立即更新所有相關狀態，確保按鈕立刻變綠色
                                          setVideos(prev => prev.map(v => v.id === latestVideo.id ? updatedVideo : v));
                                          setPersonalVideos(prev => prev.map(v => v.id === latestVideo.id ? updatedVideo : v));
                                          setCommunityVideos(prev => prev.map(v => v.id === latestVideo.id ? updatedVideo : v));
                                          setPersonalHistoryVideos(prev => prev.map(v => v.id === latestVideo.id ? updatedVideo : v));
                                          
                                          // 異步添加到用戶珍藏列表
                                          try {
                                            const { addToUserFavorites } = await import('../services/userFavoritesService');
                                            await addToUserFavorites(
                                              userId,
                                              latestVideo.videoId || latestVideo.id,
                                              latestVideo.id
                                            );
                                            // 调试日志已移除
                                            setSnackbarMessage('已加入我的收藏');
                                            setSnackbarOpen(true);
                                          } catch (error) {
                                            console.error('❌ 珍藏操作失敗:', error);
                                            setSnackbarMessage('珍藏操作失敗');
                                            setSnackbarOpen(true);
                                          }
                                        } else {
                                          const newZones = (latestVideo.zones || []).filter(z => z !== 'person');
                                          const updatedVideo = { ...latestVideo, zones: newZones };
                                          
                                          setPersonalVideos(prev =>
                                            prev.map(v =>
                                              v.id === video.id ? updatedVideo : v
                                            )
                                          );
                                          setVideos(prev =>
                                            prev.map(v =>
                                              v.id === video.id ? updatedVideo : v
                                            )
                                          );
                                          setPersonalHistoryVideos(prev =>
                                            prev.map(v =>
                                              v.id === video.id ? updatedVideo : v
                                            )
                                          );
                                          
                                          handleZoneChangeAll(latestVideo, 'person', false);
                                          setSnackbarMessage('已從個人觀看清單移除');
                                          setSnackbarOpen(true);
                                        }
                                      } else if (opt.value === 'community') {
                                        const wasChecked = Array.isArray(latestVideo.zones) && latestVideo.zones.includes('community');
                                        
                                        const newZones = wasChecked 
                                          ? (latestVideo.zones || []).filter(z => z !== 'community')
                                          : [...(latestVideo.zones || []), 'community'];
                                        const updatedVideo = { ...latestVideo, zones: newZones };
                                        
                                        setPersonalVideos(prev =>
                                          prev.map(v =>
                                            v.id === video.id ? updatedVideo : v
                                          )
                                        );
                                        setVideos(prev =>
                                          prev.map(v =>
                                            v.id === video.id ? updatedVideo : v
                                          )
                                        );
                                        setPersonalHistoryVideos(prev =>
                                          prev.map(v =>
                                            v.id === video.id ? updatedVideo : v
                                          )
                                        );
                                        
                                        handleZoneChangeAll(latestVideo, 'community', !wasChecked);
                                        setSnackbarMessage(wasChecked ? '已取消公開' : '已公開');
                                        setSnackbarOpen(true);
                                      }
                                    }}
                                    sx={{ 
                                      p: 0.5,
                                      minWidth: 'auto',
                                      fontSize: '0.7rem',
                                      // 強制覆蓋 Material-UI contained 按鈕的預設樣式
                                      '&.MuiButton-contained': {
                                        '&:hover': {
                                          backgroundColor: '#1565c0 !important',
                                          color: 'white !important'
                                        }
                                      },
                                      '&.MuiButton-outlined': {
                                        '&:hover': {
                                          backgroundColor: 'rgba(25, 118, 210, 0.1)'
                                        }
                                      }
                                    }}
                                  >
                                    <Typography 
                                      variant="caption" 
                                      sx={{ 
                                        fontSize: '0.7rem', 
                                        lineHeight: 1,
                                        // 當按鈕是 Active 狀態時，懸停字體改為深藍色
                                        ...((isPerson || isTeam || isCommunity) && {
                                          '&:hover': {
                                            color: '#1565c0 !important'
                                          }
                                        })
                                      }}
                                    >
                                      {opt.label}
                                    </Typography>
                                  </Button>
                                );
                              })}
                            </Box>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    /* 桌面模式：使用Table佈局 */
                  <TableContainer sx={{ width: 'auto', minWidth: 900, height: 'calc(100vh - 200px)' }}>
                    <Table size="small" sx={{ 
                tableLayout: 'fixed', 
                width: 'auto',
                '& th:first-of-type, & td:first-of-type': {
                  '@media (max-width: 1024px)': {
                    display: 'none'
                  }
                }
              }}>
                      <TableHead>
                        <TableRow>
                          {COLUMNS.filter(col => PAGE_COLUMN_KEYS['myPersonalFavorite'].includes(col.key)).map(col => (
                            <TableCell
                              key={col.key}
                              data-col={col.key}
                              align="center"
                              {...getTableCellStyle(col, true)}
                            >
                              {col.label}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {communityVideos.filter(v => Array.isArray(v.zones) && v.zones.includes('delivery')).map((video, idx) => (
                          <TableRow key={`delivery-${video.id}-${idx}`}>
                            {COLUMNS.filter(col => PAGE_COLUMN_KEYS['myPersonalFavorite'].includes(col.key)).map(col => (
                              <TableCell
                                key={`delivery-${video.id}-${col.key}-${idx}`}
                                data-col={col.key}
                                align="center"
                                {...getTableCellStyle(col, false)}
                              >
                                {renderCellContent(col.key, video, idx, 'myPersonalFavorite')}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  )
                )}
              </Box>
            ) : currentSection === 'myCommunityPublic' ? (
              <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                我的貢獻
                </Typography>
                {loading ? (
                  renderLoadingState('我的社群影片載入中...')
                ) : videos.length === 0 ? (
                  <Typography color="text.secondary" sx={{ mt: 2 }}>尚無我的社群影片</Typography>
                ) : (
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                    <TableContainer>
                    <Table size="small" sx={{ 
                tableLayout: 'fixed', 
                width: 'auto',
                '& th:first-of-type, & td:first-of-type': {
                  '@media (max-width: 1024px)': {
                    display: 'none'
                  }
                }
              }}>
                      <TableHead>
                        <TableRow>
                          {COLUMNS.filter(col => PAGE_COLUMN_KEYS['myCommunityPublic'].includes(col.key)).map(col => (
                            <TableCell
                              key={col.key}
                              data-col={col.key}
                                align="center"
                              {...getTableCellStyle(col, true)}
                            >
                              {col.label}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {videos.map((video, idx) => (
                          <TableRow key={`my-community-${video.id}-${idx}`}>
                            {COLUMNS.filter(col => PAGE_COLUMN_KEYS['myCommunityPublic'].includes(col.key)).map(col => (
                              <TableCell
                                key={`my-community-${video.id}-${col.key}-${idx}`}
                                align="center"
                                {...getTableCellStyle(col, false)}
                              >
                                {renderCellContent(col.key, video, idx, 'myCommunityPublic')}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                )}
              </Box>
            ) : currentSection === 'communityPublic' ? (
              <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                {/* 🔧 方案 A：合併標題和排序按鈕 */}
                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mr: 2 }}>
                    社群公開 ({(filteredCommunityPublicVideos ?? communityPublicVideosToShow).length}/{searchQuery ? (filteredCommunityPublicVideos?.length ?? communityPublicVideos.length) : communityPublicVideos.length})
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                    <Button
                      variant={communitySortMode === 'flat' && communitySortKey === 'vbids' ? 'contained' : 'outlined'}
                      size="small"
                      onClick={() => { setCommunitySortMode('flat'); setCommunitySortKey('vbids'); setCommunityLoadedCount(60); }}
                      sx={{ borderRadius: '20px' }}
                    >重點數</Button>
                    <Button
                      variant={communitySortMode === 'flat' && communitySortKey === 'favoriteCount' ? 'contained' : 'outlined'}
                      size="small"
                      onClick={() => { setCommunitySortMode('flat'); setCommunitySortKey('favoriteCount'); setCommunityLoadedCount(60); }}
                      sx={{ borderRadius: '20px' }}
                    >收藏數</Button>
                    <Button
                      variant={communitySortMode === 'flat' && communitySortKey === 'attentionCount' ? 'contained' : 'outlined'}
                      size="small"
                      onClick={() => { setCommunitySortMode('flat'); setCommunitySortKey('attentionCount'); setCommunityLoadedCount(60); }}
                      sx={{ borderRadius: '20px' }}
                    >關注度</Button>
                    <Button
                      variant={communitySortMode === 'flat' && communitySortKey === 'time' ? 'contained' : 'outlined'}
                      size="small"
                      onClick={() => { setCommunitySortMode('flat'); setCommunitySortKey('time'); setCommunityLoadedCount(60); }}
                      sx={{ borderRadius: '20px' }}
                    >時間序</Button>
                  </Stack>
                </Box>
                
                {loading ? (
                  renderLoadingState('社群公開影片載入中...')
                ) : (filteredCommunityPublicVideos ?? communityPublicVideosToShow).length === 0 ? (
                  <Typography color="text.secondary" sx={{ mt: 2 }}>
                    {searchQuery ? '🔍 未找到匹配的影片' : '目前沒有公開影片'}
                  </Typography>
                ) : (
                  /* 🔧 方案 A：使用 InfiniteScroll 替換手動載入按鈕 */
                  <InfiniteScroll
                    key={`community-public-scroll-${communityPublicLoadedCount}-${communityPublicVideos.length}`}
                    dataLength={communityPublicVideosToShow.length}
                    next={loadMoreCommunityPublicVideos}
                    hasMore={communityPublicHasMore}
                    loader={null}
                    endMessage={<Typography align="center" sx={{ my: 2, color: 'gray' }}>已載入全部 {communityPublicVideos.length} 筆影片</Typography>}
                    scrollThreshold={0.8}
                    scrollableTarget="community-public-videos-scroll"
                  >
                    <TableContainer id="community-public-videos-scroll">
                      <Table size="small" sx={{
                        tableLayout: 'fixed',
                        width: 'auto',
                        '& th:first-of-type, & td:first-of-type': {
                          '@media (max-width: 1024px)': {
                            display: 'none'
                          }
                        }
                      }}>
                        <TableHead>
                          <TableRow>
                            {COLUMNS.filter(col => PAGE_COLUMN_KEYS['communityPublic'].includes(col.key)).map(col => (
                              <TableCell
                                key={col.key}
                                data-col={col.key}
                                align="center"
                                {...getTableCellStyle(col, true)}
                              >
                                {col.label}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {/* 🔧 方案 A：添加排序邏輯 */}
                          {(filteredCommunityPublicVideos ?? communityPublicVideosToShow)
                            .sort((a, b) => {
                              if (communitySortKey === 'time') {
                                // 時間序按 lastOpenDate 排序，突顯影片熱門程度
                                return new Date(b.lastOpenDate || b.addedDate || 0).getTime() - new Date(a.lastOpenDate || a.addedDate || 0).getTime();
                              } else if (communitySortKey === 'vbids') {
                                return (b.vbids?.length || 0) - (a.vbids?.length || 0);
                              } else if (communitySortKey === 'favoriteCount') {
                                return (b.favoriteCount || 0) - (a.favoriteCount || 0);
                              } else if (communitySortKey === 'attentionCount') {
                                return (b.attentionCount || 0) - (a.attentionCount || 0);
                              }
                              return 0;
                            })
                            .map((video, idx) => (
                              <TableRow key={`community-${video.id}-${idx}`}>
                                {COLUMNS.filter(col => PAGE_COLUMN_KEYS['communityPublic'].includes(col.key)).map(col => (
                                  <TableCell
                                    key={`community-${video.id}-${col.key}-${idx}`}
                                    data-col={col.key}
                                    align="center"
                                    {...getTableCellStyle(col, false)}
                                  >
                                    {renderCellContent(col.key, video, idx, 'communityPublic')}
                                  </TableCell>
                                ))}
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </InfiniteScroll>
                )}
              </Box>
            ) : currentSection === 'follow' ? (
              <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                追蹤收藏
                </Typography>
                {loading ? (
                  renderLoadingState('追蹤收藏載入中...')
                ) : followedCommunityVideos.length === 0 ? (
                  <Typography color="text.secondary" sx={{ mt: 2 }}>尚無追蹤收藏</Typography>
                ) : (
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                    <TableContainer>
                    <Table size="small" sx={{ 
                tableLayout: 'fixed', 
                width: 'auto',
                '& th:first-of-type, & td:first-of-type': {
                  '@media (max-width: 1024px)': {
                    display: 'none'
                  }
                }
              }}>
                      <TableHead>
                        <TableRow>
                          {COLUMNS.filter(col => PAGE_COLUMN_KEYS['myPersonalFavorite'].includes(col.key)).map(col => (
                            <TableCell
                              key={col.key}
                              data-col={col.key}
                                align="center"
                            >
                              {col.label}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <>
                        {followedCommunityVideos.map((video, idx) => (
                          <TableRow key={`followed-${video.id}-${idx}`}>
                            {COLUMNS.filter(col => PAGE_COLUMN_KEYS['myPersonalFavorite'].includes(col.key)).map(col => (
                              <TableCell
                                key={`followed-${video.id}-${col.key}-${idx}`}
                                data-col={col.key}
                                align="center"
                            >
                              {renderCellContent(col.key, video, idx, 'follow')}
                            </TableCell>
                          ))}
                        </TableRow>
                         ))}
                        </>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                )}
              </Box>

            ) : currentSection === 'personalHistory' ? (
              renderPersonalHistoryContent
            ) : (
              <Box> 瀏覽紀錄</Box>
            )}
          </Box>
          
          <Snackbar
            open={snackbarOpen}
            autoHideDuration={3000}
            onClose={() => setSnackbarOpen(false)}
            message={snackbarMessage}
          />

          <UnifiedShareDialog
            open={shareDialogOpen}
            onClose={handleCloseShareDialog}
            video={currentShareVideo}
            user={authUser}
          />

            {/* 註冊 Dialog */}
            <Suspense fallback={<div>載入中...</div>}>
            <RegisterDialog
              open={showRegisterDialog}
              onClose={() => setShowRegisterDialog(false)}
              onShowLogin={() => {
                setShowRegisterDialog(false);
                setTimeout(() => setShowAuthDialog(true), 100);
              }}
            />
            </Suspense>

            {/* 建立團隊 Dialog */}
            <Dialog open={showCreateTeamDialog} onClose={() => setShowCreateTeamDialog(false)} maxWidth="xs" fullWidth>
              <DialogTitle sx={{ textAlign: 'center', fontWeight: 700 }}>建立團隊</DialogTitle>
              <DialogContent>
                <Box component="form" onSubmit={handleCreateTeam} sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                  <TextField 
                    label="公司/工作室/政府單位/團隊組織名稱" 
                    fullWidth 
                    size="small" 
                    required
                    value={teamForm.name}
                    onChange={(e) => setTeamForm(prev => ({ ...prev, name: e.target.value }))}
                  />
                  <TextField 
                    label="團隊代號（系統自動產生）" 
                    fullWidth 
                    size="small" 
                    disabled 
                    value={generatedTeamCode || ''} 
                  />
                  <TextField 
                    label="統一編號（發票）" 
                    fullWidth 
                    size="small"
                    value={teamForm.taxId}
                    onChange={(e) => setTeamForm(prev => ({ ...prev, taxId: e.target.value }))}
                  />
                  <TextField 
                    label="團隊管理員（預設為你）" 
                    fullWidth 
                    size="small" 
                    disabled 
                    value={currentUserEmail || ''} 
                  />
                  <TextField 
                    label="團隊人員（可加入CheckPlay ID）" 
                    fullWidth 
                    size="small" 
                    placeholder="請輸入Email，逗號分隔"
                    value={teamForm.members}
                    onChange={(e) => setTeamForm(prev => ({ ...prev, members: e.target.value }))}
                  />
                  <TextField 
                    label="客戶管理" 
                    fullWidth 
                    size="small" 
                    multiline 
                    rows={3}
                    placeholder="請依以下格式輸入客戶資料（每行一筆）：&#10;客戶名稱,email,key&#10;例如：&#10;王小明,ming@example.com,key123&#10;李小華,hua@example.com,key456"
                    value={teamForm.clients}
                    onChange={(e) => setTeamForm(prev => ({ ...prev, clients: e.target.value }))}
                  />
                  <Button type="submit" variant="contained" color="primary" fullWidth sx={{ mt: 2 }}>建立團隊</Button>
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setShowCreateTeamDialog(false)} color="primary">關閉</Button>
              </DialogActions>
            </Dialog>

              {/* 登入 Dialog */}
            <Suspense fallback={<div>載入中...</div>}>
            <AuthDialog
              open={showAuthDialog}
              onClose={() => setShowAuthDialog(false)}
              onShowRegister={() => {
                setShowAuthDialog(false);
                setShowRegisterDialog(true);
              }}
            />
            </Suspense>
          </Box>
        </Suspense>
      </ErrorBoundary>
      {showImportDialog && (
        <Dialog open={showImportDialog} onClose={() => setShowImportDialog(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ textAlign: 'center', fontWeight: 700, fontSize: 22, letterSpacing: 1 }}>
            匯入 YouTube 播放清單
          </DialogTitle>
          <DialogContent>
            {!accessToken ? (
              <Box sx={{ textAlign: 'center', p: 3 }}>
                <Typography sx={{ color: 'text.secondary', fontSize: 16, mb: 3, lineHeight: 1.7 }}>
                  請先登入 Google 帳號以授權存取您的 YouTube 播放清單
                </Typography>
                <Button
                  variant="contained"
                  onClick={handleGoogleLogin}
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontSize: 18,
                    borderRadius: '24px',
                    boxShadow: 2,
                    fontWeight: 700,
                    mb: 2,
                    background: '#4285F4',
                    '&:hover': { background: '#357ae8' }
                  }}
                >
                  GOOGLE 登入
                </Button>
              </Box>
            ) : loadingPlaylists ? (
              <Box sx={{ textAlign: 'center', p: 3 }}>
                <CircularProgress />
                <Typography sx={{ mt: 2 }}>載入播放清單中...</Typography>
              </Box>
            ) : loadingVideos ? (
              <Box sx={{ textAlign: 'center', p: 3 }}>
                <CircularProgress />
                <Typography sx={{ mt: 2 }}>正在獲取播放清單影片...</Typography>
              </Box>
            ) : importing ? (
              <Box sx={{ textAlign: 'center', p: 3 }}>
                <CircularProgress />
                <Typography sx={{ mt: 2, mb: 1 }}>
                  正在匯入影片... ({importProgress.current}/{importProgress.total})
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  成功: {importResults.success} | 失敗: {importResults.failed} | 跳過: {importResults.skipped}
                </Typography>
              </Box>
            ) : playlistVideos.length > 0 ? (
              <Box>
                <Typography sx={{ mb: 2 }}>
                  找到 {playlistVideos.length} 個影片，準備匯入：
                </Typography>
                <Box sx={{ maxHeight: 200, overflowY: 'auto', mb: 2 }}>
                  {playlistVideos.slice(0, 10).map((video, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <img 
                        src={video.snippet.thumbnails?.default?.url} 
                        alt="thumb" 
                        width={30} 
                        height={30} 
                        style={{ borderRadius: 4 }} 
                      />
                      <Typography variant="body2" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {video.snippet.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {video.playlistTitle}
                      </Typography>
                    </Box>
                  ))}
                  {playlistVideos.length > 10 && (
                    <Typography variant="caption" color="text.secondary">
                      ... 還有 {playlistVideos.length - 10} 個影片
                    </Typography>
                  )}
                </Box>
             <Box sx={{ mb: 2 }}>
               <FormControlLabel
                 control={
                   <Checkbox
                     checked={overwriteExisting}
                     onChange={(e) => setOverwriteExisting(e.target.checked)}
                     color="primary"
                   />
                 }
                 label="覆蓋已存在的影片"
                 sx={{ mb: 1 }}
               />
               <Typography variant="caption" color="text.secondary" display="block">
                 {overwriteExisting 
                   ? '將更新已存在影片的資訊（標題、描述等）' 
                   : '跳過已存在的影片，只匯入新影片'
                 }
               </Typography>
             </Box>
             
             <Button 
               variant="contained" 
               onClick={importVideosToSharedVbids}
               disabled={importing}
               fullWidth
               sx={{ mb: 2 }}
             >
               開始匯入 {playlistVideos.length} 個影片
             </Button>
                <Button 
                  size="small" 
                  onClick={() => {
                    setPlaylistVideos([]);
                    setSelectedPlaylists([]);
                  }}
                >
                  重新選擇播放清單
                </Button>
              </Box>
            ) : (
              <Box>
                <Typography sx={{ mb: 2 }}>請選擇要匯入的播放清單：</Typography>
                <Stack spacing={1}>
                  {playlists.map((pl: any) => (
                    <Box key={pl.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Checkbox
                        checked={selectedPlaylists.includes(pl.id)}
                        onChange={e => {
                          if (e.target.checked) {
                            setSelectedPlaylists(prev => [...prev, pl.id]);
                          } else {
                            setSelectedPlaylists(prev => prev.filter(id => id !== pl.id));
                          }
                        }}
                      />
                      <img src={pl.snippet?.thumbnails?.default?.url} alt="thumb" width={40} height={40} style={{ borderRadius: 4 }} />
                      <Box>
                        <Typography variant="subtitle2">{pl.snippet?.title}</Typography>
                        <Typography variant="caption" color="text.secondary">{pl.contentDetails?.itemCount || 0} 部影片</Typography>
                      </Box>
                    </Box>
                  ))}
                </Stack>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                  <Button size="small" onClick={() => setSelectedPlaylists(playlists.map(pl => pl.id))}>全選</Button>
                  <Button size="small" onClick={() => setSelectedPlaylists([])}>全不選</Button>
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
            <Button onClick={() => setShowImportDialog(false)} color="inherit" variant="outlined" sx={{ minWidth: 100, mr: 2 }}>
              關閉
            </Button>
            {playlistVideos.length === 0 && !loadingVideos && !importing && (
              <Button 
                variant="contained" 
                disabled={!accessToken || loadingPlaylists || !selectedPlaylists.length} 
                onClick={async () => {
                  // 先獲取播放清單影片
                  await fetchAllPlaylistVideos();
                }}
                sx={{ minWidth: 100 }}
              >
                獲取影片
              </Button>
            )}
          </DialogActions>
        </Dialog>
      )}
      {/* ProfileDialog 彈窗 */}
      {showProfileDialog && (
        <Suspense fallback={<ComponentFallback />}>
          <ProfileDialog open={showProfileDialog} onClose={() => setShowProfileDialog(false)} />
        </Suspense>
      )}
      {/* 新增 Dialog 元件 */}
      <Dialog open={showAdminGroupsDialog} onClose={() => setShowAdminGroupsDialog(false)}>
        <DialogTitle>可管理的群組清單</DialogTitle>
        <DialogContent>
          {adminGroups.length === 0 ? (
            <Typography color="text.secondary">你目前不是任何群組的管理員</Typography>
          ) : (
            adminGroups.map(group => (
              <Box key={group.id} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography sx={{ flex: 1 }}>{group.name}</Typography>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  onClick={() => {
                    setGroupToManage(group);
                    setShowGroupManagerInGroup(true);
                    setShowAdminGroupsDialog(false);
                  }}
                >
                  管理
                </Button>
              </Box>
            ))
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAdminGroupsDialog(false)}>關閉</Button>
        </DialogActions>
      </Dialog>
      {shareGroup && (
        <Dialog open={!!shareGroup} onClose={handleCloseShareGroup}>
          <DialogTitle>分享群組「{shareGroup.name}」</DialogTitle>
          <DialogContent sx={{ textAlign: 'center' }}>
            {/* QR Code */}
            <QRCodeSVG
              value={`${window.location.origin}/group/${shareGroup.id}`}
              size={180}
              style={{ margin: '16px auto' }}
            />
            <Typography variant="body2" sx={{ mt: 2, mb: 1 }}>
              掃描 QR Code 以加入群組
            </Typography>
            {/* 分享連結與複製 */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <TextField
              fullWidth
              value={`${window.location.origin}/group/${shareGroup.id}`}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={async () => {
                          await navigator.clipboard.writeText(`${window.location.origin}/group/${shareGroup.id}`);
                          setCopySuccess(true);
                          setTimeout(() => setCopySuccess(false), 2000);
                        }}
                        edge="end"
                      >
                        <ContentCopyIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ my: 1 }}
              />
            </Box>
            {copySuccess && (
              <Alert severity="success" sx={{ mb: 1 }}>
                已複製到剪貼簿！
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseShareGroup}>關閉</Button>
          </DialogActions>
        </Dialog>
      )}
      <Suspense fallback={<div>載入中...</div>}>
        <WelcomeDialog
          open={showWelcome}
          onClose={handleCloseWelcome}
        />
      </Suspense>

              {/* 群組選擇 Dialog  */}
              <GroupSelectorDialog
                open={groupDialogOpen}
                onClose={() => {
                  setGroupDialogOpen(false);
                  // 重新獲取所有群組，確保新建立的群組能正確顯示
                  fetchAllGroups();
                }}
                userGroups={userGroups || []}
                selectedGroups={selectedGroups || []}
                onChange={async (selectedGroups: string[]) => {
                  setSelectedGroups(selectedGroups);
          // 移除立即關閉對話框的邏輯，讓用戶可以選擇多個群組
          // 只有在點擊「儲存」按鈕時才關閉對話框
        }}
        onSave={async () => {
                  if (!groupDialogVideo) return;
          
          console.log('🔍 儲存群組變更:', { 
            videoId: groupDialogVideo.id, 
            selectedGroups, 
            currentZones: groupDialogVideo.zones 
          });
          
          try {
                  // 取得原本 zones，去除所有 group 名稱
                  const newZones = Array.from(new Set([
                    ...((Array.isArray(groupDialogVideo.zones) ? groupDialogVideo.zones : []).filter(z => !userGroups.map(g => g.id).includes(z))),
                    ...selectedGroups
                  ]));
            
                  // 儲存群組時，寫入 groupIds
                  const newGroupIds = [...selectedGroups];
            
            // 查询日志已移除
            
            // 立即更新本地狀態，提供即時反饋
            const updatedVideo = { ...groupDialogVideo, zones: newZones, groupIds: newGroupIds } as VideoItem;
            
                  setPersonalVideos(prev =>
                    prev.map(v =>
                v.id === groupDialogVideo.id ? updatedVideo : v
                    )
                  );
                  setCommunityVideos(prev =>
                    prev.map(v =>
                v.id === groupDialogVideo.id ? updatedVideo : v
                    )
                  );
                  setVideos(prev =>
                    prev.map(v =>
                v.id === groupDialogVideo.id ? updatedVideo : v
              )
            );
            
            // 🔧 修復：同時更新 personalHistoryVideos 狀態
            setPersonalHistoryVideos(prev =>
              prev.map(v =>
                v.id === groupDialogVideo.id ? updatedVideo : v
              )
            );
            
            // 🔧 修復：清除快取，強制重新載入
            const cacheKey = `${currentSection}_${userId}`;
            setVideosCache(prev => {
              const newCache = { ...prev };
              delete newCache[cacheKey];
              return newCache;
            });
            setCacheTimestamps(prev => {
              const newTimestamps = { ...prev };
              delete newTimestamps[cacheKey];
              return newTimestamps;
            });
            
            // 🔧 修復：移除強制重新渲染，避免性能問題
            // setForceUpdate(prev => prev + 1);
            
            // 更新 Firestore
            if (!isGuest && userId) {
              try {
                // 首先檢查文檔是否存在
                const docRef = safeDoc( 'sharedVbids', groupDialogVideo.id);
                const docSnap = await getDoc(docRef);
                
                if (docSnap.exists()) {
                  // 文檔存在，執行更新
                  await updateDoc(docRef, { 
                    zones: newZones, 
                    groupIds: newGroupIds 
                  });
                  // 调试日志已移除
                } else {
                  // 文檔不存在，嘗試通過 userId 和 videoId 查詢
                  // 调试日志已移除
                  const q = query(
                    safeCollection( 'sharedVbids'),
                    where('userId', '==', userId),
                    where('videoId', '==', groupDialogVideo.videoId)
                  );
                  const querySnapshot = await getDocs(q);
                  
                  if (!querySnapshot.empty) {
                    // 找到對應的文檔，使用正確的文檔 ID 更新
                    const correctDocRef = querySnapshot.docs[0].ref;
                    await updateDoc(correctDocRef, { 
                      zones: newZones, 
                      groupIds: newGroupIds 
                    });
                    // 调试日志已移除
                  } else {
                    console.warn('⚠️ 找不到對應的文檔，跳過 Firestore 更新');
                  }
                }
              } catch (error) {
                console.error('❌ Firestore 更新失敗:', error);
                // 不拋出錯誤，讓用戶界面繼續工作
              }
            }
            
            // 重新載入群組列表，確保顯示最新狀態
            if (authUser?.email) {
              const updatedGroups = await getUserGroups(authUser.email);
              setUserGroups(updatedGroups);
            }
            
                  setSnackbarMessage('已更新群組關聯');
                  setSnackbarOpen(true);
            
            // 🔧 修復：移除強制重新渲染，避免性能問題
            // setForceUpdate(prev => prev + 1);
            
            // 重新獲取所有群組，確保新建立的群組能正確顯示
            fetchAllGroups();
            
            // 關閉對話框
            setGroupDialogOpen(false);
            
          } catch (error) {
            console.error('❌ 儲存群組失敗:', error);
            setSnackbarMessage('儲存群組失敗，請稍後再試');
            setSnackbarOpen(true);
          }
        }}
                onShowTeamDialog={() => setShowTeamDialogInGroup(true)}
                onShowGroupManager={group => {
                  setGroupToManage(group);
                  setShowGroupManagerInGroup(true);
                  setGroupDialogOpen(false);
                }}
                onLeaveGroup={groupId => {
                  setLeavingGroupId(groupId);
                }}
                leavingGroupId={leavingGroupId}
                onCancelLeave={() => setLeavingGroupId(null)}
                onConfirmLeave={async () => {
                  if (!leavingGroupId) return;
                  setLeaving(true);
                  try {
                    // 執行退出群組邏輯
                    await handleLeaveGroup(leavingGroupId);
                    setLeavingGroupId(null);
                    setSnackbarMessage('已退出群組');
                    setSnackbarOpen(true);
                  } catch (e) {
                    setSnackbarMessage('退出群組失敗');
                    setSnackbarOpen(true);
                  }
                  setLeaving(false);
                }}
                leaving={leaving}
                currentUserEmail={authUser?.email || ''}
                onShowAdminGroupsList={() => setShowAdminGroupsDialog(true)}
              />
      
      {/* TeamDialog for creating new groups */}
      {showTeamDialogInGroup && (
        <Suspense fallback={<ComponentFallback />}>
          <TeamDialog
                open={showTeamDialogInGroup}
                onClose={() => setShowTeamDialogInGroup(false)}
                onGroupCreated={async () => {
                  if (authUser?.email) {
                    const groups = await getUserGroups(authUser.email);
                    setUserGroups(groups);
                  }
                }}
              />
            </Suspense>
          )}
      
      {/* GroupManager Dialog */}
              <Dialog open={showGroupManagerInGroup} onClose={() => {
                setShowGroupManagerInGroup(false);
                if (authUser?.email) {
                  getUserGroups(authUser.email).then(setUserGroups);
                }
              }} maxWidth="md" fullWidth>
                <Suspense fallback={<ComponentFallback />}>
                  <GroupManager group={groupToManage} onClose={() => {
                    setShowGroupManagerInGroup(false);
                    if (authUser?.email) {
                      getUserGroups(authUser.email).then(setUserGroups);
                    }
                  }} />
                </Suspense>
              </Dialog> 

              {/* Tag 編輯 Dialog */}
      {tagDialogOpen && (() => {
        // 使用預計算的穩定排序結果，避免每次渲染時重新排序
        const sortedSelectedTags = stableSortedSelectedTags;
        const sortedAllUserTags = stableSortedAllUserTags;
        
        return (
              <Dialog 
          open={tagDialogOpen} 
                maxWidth="sm"
                fullWidth
                PaperProps={{
                  sx: {
                    maxHeight: '70vh',
                    height: 'auto'
                  }
                }}          onClose={() => {
            // 查询日志已移除
            
            // 🔧 修復：關閉 Dialog 時強制刷新當前分頁，確保 Tag 更新後數據一致性
            if (currentSection && userId) {
              // 同步日志已移除
              
              // 延遲執行，確保所有狀態更新完成
              setTimeout(() => {
                if (currentSection === 'personalHistory') {
                  // 🔧 修復：強制刷新個人歷史記錄數據，使用統一的邏輯
                  // 同步日志已移除
                  // 清除快取，強制重新載入
                  const cacheKey = `personalHistory_${userId}`;
                  setPersonalHistoryCache(prev => {
                    const newCache = { ...prev };
                    delete newCache[cacheKey];
                    return newCache;
                  });
                  setPersonalHistoryCacheTimestamps(prev => {
                    const newTimestamps = { ...prev };
                    delete newTimestamps[cacheKey];
                    return newTimestamps;
                  });
                  
                  // 重置載入狀態，觸發重新載入
                  isLoadingPersonalHistoryRef.current = false;
                  lastUserIdRef.current = '';
                  
                  // 觸發 useEffect 重新執行
                  setLoading(true);
                } else {
                  // 其他分頁使用 loadVideos 強制刷新
                  loadVideos(currentSection, true);
                }
              }, 200);
            }
            
            setTagDialogOpen(false);
          }}
        >
                <DialogTitle sx={{ pb: 1 }}>影片標籤管理</DialogTitle>
                <DialogContent sx={{ pb: 2, minHeight: '40vh', maxHeight: '60vh' }}>
                  {/* 使用新的 VideoTagsManager 組件 */}
                  {tagDialogVideo && (
                    <VideoTagsManager
                      video={tagDialogVideo}
                    onUpdateTags={async (newTags) => {
                      if (!tagDialogVideo) return;
                      try {
                        const docRef = safeDoc( 'sharedVbids', tagDialogVideo.id);
                        await updateDoc(docRef, { tags: newTags });
                        setSelectedTags(newTags);
                        // 调试日志已移除
                      } catch (error) {
                        console.error('❌ 標籤更新失敗:', error);
                      }
                    }}
                    onUpdateYoutubeHashtags={async (newHashtags) => {
                      if (!tagDialogVideo) return;
                      try {
                        const docRef = safeDoc( 'sharedVbids', tagDialogVideo.id);
                        await updateDoc(docRef, { youtubeHashtags: newHashtags });
                        // 调试日志已移除
                      } catch (error) {
                        console.error('❌ YouTube 標籤更新失敗:', error);
                      }
                    }}
                    readOnly={false}
                    />
                  )}
                  
                  {/* 保留原有的多行輸入功能作為備用 */}
                  <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #e0e0e0' }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>快速添加標籤</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <TextField
                        label="標籤名稱（可用逗號、換行分隔）"
                    value={newTag}
                    onChange={e => setNewTag(e.target.value)}
                    fullWidth
                    multiline
                        minRows={1}
                        maxRows={3}
                    InputProps={{
                      sx: { '::placeholder': { fontSize: 13 } }
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && newTag.trim()) {
                        e.preventDefault();
                            const tagsToAdd = newTag.split(/[,\n]+/).map(t => t.trim()).filter(Boolean);
                            setSelectedTags(prev => {
                              const tagUsage = JSON.parse(localStorage.getItem('tagUsage') || '{}');
                              // 更新使用頻率
                              tagsToAdd.forEach(tag => {
                                tagUsage[tag] = (tagUsage[tag] || 0) + 1;
                              });
                              localStorage.setItem('tagUsage', JSON.stringify(tagUsage));
                              const newTags = Array.from(new Set([...prev, ...tagsToAdd]));
                              return newTags.sort((a, b) => (tagUsage[b] || 0) - (tagUsage[a] || 0));
                            });
                        setNewTag('');
                      }
                    }}
                  />
                  <Button
                    variant="contained"
                    size="small"
                        sx={{ 
                          minWidth: '80px',
                          height: '56px',
                          alignSelf: 'flex-start',
                        mt: 0
                      }}
                    onClick={async () => {
                      if (!newTag.trim()) return;
                        const tagsToAdd = newTag.split(/[,\n]+/).map(t => t.trim()).filter(Boolean);
                      for (const tag of tagsToAdd) {
                        await handleAddTag(tag);
                      }
              setSelectedTags(prev => {
                const tagUsage = JSON.parse(localStorage.getItem('tagUsage') || '{}');
                // 更新使用頻率
                tagsToAdd.forEach(tag => {
                  tagUsage[tag] = (tagUsage[tag] || 0) + 1;
                });
                localStorage.setItem('tagUsage', JSON.stringify(tagUsage));
                const newTags = Array.from(new Set([...prev, ...tagsToAdd]));
                return newTags.sort((a, b) => (tagUsage[b] || 0) - (tagUsage[a] || 0));
              });
                      setNewTag('');
                    }}
                  >新增</Button>
                    </Box>
                  </Box>
                  {/* 已有Tag列表，可勾選/取消、刪除 */}
                  {selectedTags.length > 0 && (
                    <Box sx={{ mt: 0.5, mb: 1 }}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.875rem' }}>
                        已選標籤 ({selectedTags.length})
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.25 }}>
                        {sortedSelectedTags.map((tag, idx) => (
                          <Box key={tag} sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            bgcolor: '#424242', 
                            color: 'white',
                            px: 0.75,
                            py: 0.25,
                            borderRadius: '8px',
                            fontSize: '0.875rem'
                          }}>
                          <Checkbox
                            checked={true}
                            onChange={e => handleTagCheckboxChange(tag, e.target.checked)}
                            size="small"
                              sx={{ 
                                color: 'white', 
                                '&.Mui-checked': { color: 'white' },
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
                              }}
                          />
                            <Box sx={{ flex: 1, mr: 0.25 }}>{tag}</Box>
                          <IconButton
                            size="small"
                              color="inherit"
                            onClick={() => setDeleteTagConfirm(tag as string)}
                              sx={{ 
                                p: 0.25, 
                                '&:hover': { 
                                  bgcolor: 'rgba(255,255,255,0.15)',
                                  transform: 'scale(1.1)'
                                },
                                transition: 'all 0.2s ease'
                              }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        ))}
                  </Box>
                    </Box>
                  )}
                  {allUserTags.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.875rem' }}>
                        你曾用過的標籤 ({allUserTags.length})
                      </Typography>
                      <Box sx={{ 
                        display: 'flex', 
                        gap: 1,
                        maxHeight: '400px'
                      }}>
                        {/* 左欄 - 前50%的標籤 */}
                        <Box sx={{ 
                          flex: 1,
                          display: 'flex', 
                          flexDirection: 'column', 
                          gap: 0.25,
                          overflowY: 'auto',
                          pr: 0.5
                        }}>
                          {sortedAllUserTags
                            .filter(tag => !selectedTags.includes(tag))
                            .slice(0, Math.ceil(sortedAllUserTags.filter(tag => !selectedTags.includes(tag)).length / 2))
                            .map(tag => {
                          const isEditing = editingTag === tag;
                          return (
                                <Box key={tag} sx={{ 
                                  display: 'flex', 
                                  alignItems: 'center',
                                  py: 0.25,
                                  px: 0.25,
                                  borderRadius: '4px',
                                  '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' }
                                }}>
                              <Checkbox
                                checked={selectedTags.includes(tag)}
                                onChange={e => {
                                  if (e.target.checked) {
                            setSelectedTags(prev => {
                              const tagUsage = JSON.parse(localStorage.getItem('tagUsage') || '{}');
                              tagUsage[tag] = (tagUsage[tag] || 0) + 1;
                              localStorage.setItem('tagUsage', JSON.stringify(tagUsage));
                              const newTags = Array.from(new Set([...prev, tag]));
                              return newTags.sort((a, b) => (tagUsage[b] || 0) - (tagUsage[a] || 0));
                            });
                            setAllUserTags(prev => {
                              const newTags = Array.from(new Set([...prev, tag]));
                              const tagUsage = JSON.parse(localStorage.getItem('tagUsage') || '{}');
                              return newTags.sort((a, b) => (tagUsage[b] || 0) - (tagUsage[a] || 0));
                                        });
                                  } else {
                            setSelectedTags(prev => {
                              const filteredTags = prev.filter(t => t !== tag);
                              const tagUsage = JSON.parse(localStorage.getItem('tagUsage') || '{}');
                              return filteredTags.sort((a, b) => (tagUsage[b] || 0) - (tagUsage[a] || 0));
                            });
                                  }
                                }}
                                size="small"
                              />
                              {isEditing ? (
                                <TextField
                                  value={editingValue}
                                  onChange={e => setEditingValue(e.target.value)}
                                  size="small"
                                  onBlur={async () => {
                                    await handleRenameTag(tag, editingValue);
                                    setEditingTag('');
                                  }}
                                  onKeyDown={async e => {
                                    if (e.key === 'Enter') {
                                      await handleRenameTag(tag, editingValue);
                                      setEditingTag('');
                                    }
                                  }}
                                      sx={{ 
                                        width: 120, 
                                        mr: 1,
                                        '& .MuiInputBase-input': { 
                                          fontSize: '0.875rem',
                                          py: 0.5
                                        }
                                      }}
                                  autoFocus
                                />
                              ) : (
                                <Box
                                      sx={{ 
                                        flex: 1, 
                                        cursor: 'pointer', 
                                        mr: 1,
                                        fontSize: '0.875rem',
                                        py: 0.25
                                      }}
                                  onClick={() => {
                                    setEditingTag(tag);
                                    setEditingValue(tag);
                                  }}
                                >{tag}</Box>
                              )}
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => {
                                  setDeleteTagConfirm(tag as string);
                                }}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          );
                        })}
                        </Box>
                        
                        {/* 右欄 - 後50%的標籤 */}
                        <Box sx={{ 
                          flex: 1,
                          display: 'flex', 
                          flexDirection: 'column', 
                          gap: 0.25,
                          overflowY: 'auto',
                          pl: 0.5
                        }}>
                          {sortedAllUserTags
                            .filter(tag => !selectedTags.includes(tag))
                            .slice(Math.ceil(sortedAllUserTags.filter(tag => !selectedTags.includes(tag)).length / 2))
                            .map(tag => {
                              const isEditing = editingTag === tag;
                              return (
                                <Box key={tag} sx={{ 
                                  display: 'flex', 
                                  alignItems: 'center',
                                  py: 0.25,
                                  px: 0.25,
                                  borderRadius: '4px',
                                  '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' }
                                }}>
                                  <Checkbox
                                    checked={selectedTags.includes(tag)}
                                    onChange={e => {
                                      if (e.target.checked) {
                                        setSelectedTags(prev => {
                                          const tagUsage = JSON.parse(localStorage.getItem('tagUsage') || '{}');
                                          tagUsage[tag] = (tagUsage[tag] || 0) + 1;
                                          localStorage.setItem('tagUsage', JSON.stringify(tagUsage));
                                          const newTags = Array.from(new Set([...prev, tag]));
                                          return newTags.sort((a, b) => (tagUsage[b] || 0) - (tagUsage[a] || 0));
                                        });
                                        setAllUserTags(prev => {
                                          const newTags = Array.from(new Set([...prev, tag]));
                                          const tagUsage = JSON.parse(localStorage.getItem('tagUsage') || '{}');
                                          return newTags.sort((a, b) => (tagUsage[b] || 0) - (tagUsage[a] || 0));
                                        });
                                      } else {
                                        setSelectedTags(prev => {
                                          const filteredTags = prev.filter(t => t !== tag);
                                          const tagUsage = JSON.parse(localStorage.getItem('tagUsage') || '{}');
                                          return filteredTags.sort((a, b) => (tagUsage[b] || 0) - (tagUsage[a] || 0));
                                        });
                                      }
                                    }}
                                    size="small"
                                  />
                                  {isEditing ? (
                                    <TextField
                                      value={editingValue}
                                      onChange={e => setEditingValue(e.target.value)}
                                      size="small"
                                      onBlur={async () => {
                                        await handleRenameTag(tag, editingValue);
                                        setEditingTag('');
                                      }}
                                      onKeyDown={async e => {
                                        if (e.key === 'Enter') {
                                          await handleRenameTag(tag, editingValue);
                                          setEditingTag('');
                                        }
                                      }}
                                      sx={{ 
                                        width: 120, 
                                        mr: 1,
                                        '& .MuiInputBase-input': { 
                                          fontSize: '0.875rem',
                                          py: 0.5
                                        }
                                      }}
                                      autoFocus
                                    />
                                  ) : (
                                    <Box
                                      sx={{ 
                                        flex: 1, 
                                        cursor: 'pointer', 
                                        mr: 1,
                                        fontSize: '0.875rem',
                                        py: 0.25
                                      }}
                                      onClick={() => {
                                        setEditingTag(tag);
                                        setEditingValue(tag);
                                      }}
                                    >{tag}</Box>
                                  )}
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => {
                                      setDeleteTagConfirm(tag as string);
                                    }}
                                  >
                                    <CloseIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              );
                            })}
                        </Box>
                      </Box>
                    </Box>
                  )}
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setTagDialogOpen(false)}>取消</Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={async () => {
                      if (!tagDialogVideo) return;
                      try {
                      const docRef = safeDoc( 'sharedVbids', tagDialogVideo.id);
                        
                        // 略過：等同於確定，加入 'person' 但不更新 tags
                        const newZones = Array.isArray(tagDialogVideo.zones) 
                          ? [...tagDialogVideo.zones, 'person']
                          : ['person'];
                        
                        await updateDoc(docRef, { 
                          zones: newZones
                        });
                        
                        // 同步本地 state
                        const updatedVideo = { 
                          ...tagDialogVideo, 
                          zones: newZones
                        };
                        
                        setVideos(prev => prev.map(v => v.id === tagDialogVideo.id ? updatedVideo : v));
                        setPersonalVideos(prev => prev.map(v => v.id === tagDialogVideo.id ? updatedVideo : v));
                        setCommunityVideos(prev => prev.map(v => v.id === tagDialogVideo.id ? updatedVideo : v));
                        
                      setTagDialogOpen(false);
                        setSnackbarMessage('已加入個人觀看清單');
                        setSnackbarOpen(true);
                      } catch (e) {
                        setSnackbarMessage('更新失敗');
                        setSnackbarOpen(true);
                      }
                    }}
                    sx={{ ml: 1 }}
                  >略過</Button>
                  <Button
                    variant="contained"
                    onClick={async () => {
                      if (!tagDialogVideo) return;
                      try {
                        const docRef = safeDoc( 'sharedVbids', tagDialogVideo.id);
                        
                        // 同時更新 tags 和 zones（加入 'person'）
                        const newZones = Array.isArray(tagDialogVideo.zones) 
                          ? [...tagDialogVideo.zones, 'person']
                          : ['person'];
                        
                        await updateDoc(docRef, { 
                          tags: selectedTags,
                          zones: newZones
                        });
                        
                        // 同步本地 state
                        const updatedVideo = { 
                          ...tagDialogVideo, 
                          tags: selectedTags,
                          zones: newZones
                        };
                        
                        setVideos(prev => prev.map(v => v.id === tagDialogVideo.id ? updatedVideo : v));
                        setPersonalVideos(prev => prev.map(v => v.id === tagDialogVideo.id ? updatedVideo : v));
                        setCommunityVideos(prev => prev.map(v => v.id === tagDialogVideo.id ? updatedVideo : v));
                        
                        setTagDialogOpen(false);
                        setSnackbarMessage('已加入個人觀看清單並更新標籤');
                        setSnackbarOpen(true);
                      } catch (e) {
                        setSnackbarMessage('更新失敗');
                        setSnackbarOpen(true);
                      }
                    }}
                    disabled={!tagDialogVideo}
                    color="primary"
                  >確定</Button>
              </DialogActions>
            </Dialog>
        );
      })()}

              {/* 刪除Tag確認Dialog */}
              <Dialog open={!!deleteTagConfirm} onClose={() => setDeleteTagConfirm('')}>
                <DialogTitle>確認永久刪除Tag？</DialogTitle>
                <DialogContent>
                  <Typography>此操作會將所有影片的「{deleteTagConfirm}」標籤一併移除，確定要刪除嗎？</Typography>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setDeleteTagConfirm('')}>取消</Button>
                  <Button color="error" variant="contained" onClick={async () => {
                    await handleDeleteTag(deleteTagConfirm);
                    setDeleteTagConfirm('');
                  }}>確定刪除</Button>
                </DialogActions>
              </Dialog>
      
      </Container>
    );
};

export default ManagePages; 
